import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import websocketManager from '../services/websocketManager';
import { logger } from '../utils/logger';
import { 
  normalizeWebSocketPayload, 
  logWebSocketDebug, 
  logFilterDecision
} from '../utils/websocketDebug';
import {
  parseDeviceStateByTraits,
  getDefaultTraitsForDeviceType,
  getStateDescriptionByTraits,
  getStateTextColorByTraits,
  isControllableByTraits,
} from '../utils/deviceStateParser';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  RefreshCw,
  Thermometer,
  Droplets,
  AlertCircle,
  Zap,
  Sun,
  Bell,
  BellRing,
  Download,
  X,
  Server,
  Workflow,
  Wind,
  Lightbulb,
  Activity,
  Users,
  Power,
  ToggleLeft,
  ToggleRight,
  Check,
  Square,
  CheckSquare,
  Palette,
  Volume2,
  Timer,
  Lock,
  Unlock,
  Eye,
  Gauge,
  Info,
  Settings2,
  Plus,
  Copy,
  Edit,
  Radio,
  BatteryFull,
  BatteryMedium,
  BatteryLow,
  BatteryWarning,
  GripVertical,
  Trophy,
  Leaf,
  ArrowDown,
  ArrowUp,
  Fan,
  DoorOpen,
  DoorClosed,
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Blinds
} from 'lucide-react';
import LayersIcon from '@mui/icons-material/Layers';
import AppSidebar from '../components/AppSidebar';
import DeviceManagerSidebar from '../components/DeviceManagerSidebar';
import AlertCenter from '../components/AlertCenter';
import { useAlertContext } from '../contexts/AlertContext';
import ZoneSelector from '../components/ZoneSelector';
import { ViewSwitcher, AdaptiveLightingView, HVACControlView, OccupancyAnalyticsView, BlindCurtainControlView, EnergyView, OverviewViewMode } from '../components/control-views';
import Footer from '../components/Footer';
import PowerMeterCard from '../components/PowerMeterCard';
import ClimateCard from '../components/ClimateCard';
import HVACControlModal from '../components/HVACControlModal';
import NotificationToast from '../components/NotificationToast';
import PhaseGauge from '../components/PhaseGauge';
import { DeviceModalRenderer } from '../components/deviceModals';
import { 
  DeviceIcon,
  Style01_Lightbulb,
  Style01_Switch,
  Style01_SmartOutlet,
  Style01_NetworkCheck,
  Style01_AcUnit,
  Style01_Fan,
  Style01_Thermometer,
  Style01_SensorOccupied,
  Style01_Door,
  Style01_RollerShades,
  Style01_Blinds,
  Style01_TouchApp,
  Style01_Hub,
  Style01_DeviceHub,
  Style01_Sensors,
  Style01_LuxLightMode,
  Style02_WaterDrop,
} from '../components/deviceIcons';
import AlertsPage from './AlertsPage';
import DeviceLogsPage from './DeviceLogsPage';
import GenerateReportPage from './GenerateReportPage';
import DeviceGroupCard, { SortableDeviceGroupCard } from '../components/DeviceGroupCard';
import CreateDeviceGroupModal from '../components/CreateDeviceGroupModal';
import DeviceGroupModal from '../components/DeviceGroupModal';
import { ultronTuringApi, getDeviceDisplayName } from '../services/ultronTuringApi';
import * as userPreferencesApi from '../services/userPreferencesApi';
import * as baselineCacheService from '../services/baselineCacheService';
// Theme is fixed to dark mode - useTheme removed
import { useTranslation } from '../contexts/LanguageContext';
import { APP_VERSION, BUILD_STRING, APP_NAME } from '../constants/version';

// Legacy alias for backwards compatibility
const LightbulbIcon = Style01_Lightbulb;
const SmartOutletIcon = Style01_SmartOutlet;
const SwitchIcon = Style01_Switch;
const PowerMeterIcon = Style01_NetworkCheck;
const AcUnitIcon = Style01_AcUnit;

// All device modals now use DeviceModalRenderer which handles lazy loading internally
// This ensures Firestore theme settings are properly loaded and applied

// Types for IntelliSpace devices
interface IntelliSpaceDevice {
  deviceId: string;
  baseSN: string;
  deviceName: string;  // Resolved display name (nickname > name > deviceName > sn)
  nickname?: string;   // Tenant-local nickname (for individual device display)
  originalName?: string;  // Original device name (without nickname) for merged multi-endpoint display
  systemDeviceName?: string;  // Main device name from system object (for merged multi-endpoint title)
  deviceType: 'power_meter' | 'power_meter_3phase' | 'th_sensor' | 'presence_sensor' | 'hvac' | 'switch' | 'light' | 'fan' | 'door_sensor' | 'button' | 'motion_sensor' | 'outlet' | 'curtain' | 'blind' | 'lux_sensor' | 'dehumidifier' | 'multi_sensor' | 'controller' | 'bridge' | 'erv';
  status: 'online' | 'offline';
  location?: string;
  groupId?: string;
  groupName?: string;
  traits?: string[];
  attributes?: any;
  // Power Meter specific
  watt?: number;
  wattHour?: number;
  voltage?: number;
  current?: number;
  real_power?: number;
  // TH Sensor specific
  temperature?: number;
  humidity?: number;
  // Air quality (for TH sensors with air quality capabilities)
  co2?: number;
  tvoc?: number;
  pm25?: number;
  // Presence Sensor specific
  presence?: boolean;
  occupancy?: boolean;
  luxValue?: number;  // Lux/Illuminance value for UTU32A sensors
  lux?: number;       // Direct lux value from status
  // HVAC specific
  isOn?: boolean;
  mode?: string;
  thermostatMode?: string;
  fanSpeed?: string;
  setTemperature?: number;
  thermostatTemperatureSetpoint?: number;
  // Light specific
  power?: boolean;
  brightness?: number;
  colorTemp?: number;
  colorTempMin?: number;
  colorTempMax?: number;
  // Switch specific
  switchOn?: boolean;
  // Door sensor specific
  isOpen?: boolean;
  detected?: boolean;  // true = open/detected, false = closed/not detected
  lastUpdated?: string | Date;
  // Motion sensor specific
  motionDetected?: boolean;
  // Button specific
  lastPressed?: string | Date;
  pressType?: 'click' | 'doubleClick' | 'longPress' | string;
  // Curtain/Blind specific
  openPercent?: number;  // 0-100, 0 = fully closed, 100 = fully open
  // Dehumidifier specific
  humidityAmbientPercent?: number;   // Current ambient humidity (%)
  humiditySetpointPercent?: number;  // Target humidity setpoint (%)
  currentModeSettings?: Record<string, string>;  // CustomModes current settings (Mode, FanSpeed, Timer)
  currentSensorStateData?: Array<{ name: string; rawValue: number }>;  // ERV: PM2.5, CO2 sensor data
  currentToggleSettings?: Record<string, boolean>;  // CustomToggles current settings (AutoDirection, AirClean, Lock)
  toggles?: string[];  // Available toggle names
  toggleValues?: Record<string, boolean>;  // Current toggle values
  bucket?: number[];  // Water tank status: [0] = normal, [1] = full
  notify?: Array<{ type: string; event: string }>;  // Device notifications (e.g., bucket full)
  // Battery
  battery?: number;
  // Common
  online?: boolean;
  lastUpdate?: string;
  // Multi-channel device (from endpointRelationship.classification === 'multi_channel')
  isMultiChannel?: boolean;
  // System info (for multi-channel classification)
  system?: {
    endpointRelationship?: {
      classification?: string;
    };
  };
  // Energy metrics
  energy?: number;
  powerFactor?: number;
}

type ViewType = 'overview' | 'control' | 'device-manager' | 'automation' | 'alerts' | 'device-logs' | 'generate-report';

// Overview View Mode type is imported from control-views

// localStorage key for caching device data
const DEVICES_CACHE_KEY = 'intellispace-devices-cache';

// Chart style constants (defined outside component to prevent re-renders)
const CHART_TICK_STYLE = { fill: '#9ca3af', fontSize: 10 };
const CHART_TICK_LINE_STYLE = { stroke: '#4b5563' };
const CHART_TICK_ORANGE = { fill: '#f97316', fontSize: 10 };
const CHART_TICK_BLUE = { fill: '#3b82f6', fontSize: 10 };
const CHART_MARGIN = { top: 10, right: 10, left: 0, bottom: 0 };
const CHART_TOOLTIP_STYLE = {
  backgroundColor: '#1f2937',
  border: '1px solid #374151',
  borderRadius: '8px',
  color: '#f3f4f6'
};

// Chart tick formatters
const formatPowerTick = (value: number): string => {
  // Value is power in W, convert to kWh for display (W × 1 hour = Wh = W/1000 kWh)
  const kWh = value / 1000;
  if (kWh >= 1000000) return `${(kWh / 1000000).toFixed(1)}G`;
  if (kWh >= 1000) return `${(kWh / 1000).toFixed(1)}M`;
  if (kWh >= 1) return `${kWh.toFixed(1)}k`;
  return String(value);
};
const formatTempTick = (value: number): string => `${value}°`;
const formatHumidityTick = (value: number): string => `${value}%`;

// Helper to format power for tooltip (displays as kWh: power W × 1 hour = Wh = W/1000 kWh)
const formatPowerValue = (value: number): string => {
  // Value is power in W, convert to kWh for display (assuming 1 hour duration)
  const kWh = value / 1000;
  if (kWh >= 1000000) return `${(kWh / 1000000).toFixed(2)} GWh`;
  if (kWh >= 1000) return `${(kWh / 1000).toFixed(2)} MWh`;
  if (kWh >= 1) return `${kWh.toFixed(2)} kWh`;
  return `${(kWh * 1000).toFixed(0)} Wh`;
};

/**
 * Extract lux value from various API response formats
 * Handles: 
 * - Direct number: lux = 155.99
 * - Array: lux = [155.99]
 * - Object with value array: lux = { value: [155.99] }
 * - Object with value number: lux = { value: 155.99 }
 */
const extractLuxValue = (luxData: any): number | undefined => {
  if (luxData === undefined || luxData === null) return undefined;
  
  // Direct number
  if (typeof luxData === 'number') {
    return Math.round(luxData * 100) / 100; // Round to 2 decimal places
  }
  
  // Array: take last value
  if (Array.isArray(luxData) && luxData.length > 0) {
    const val = luxData[luxData.length - 1];
    return typeof val === 'number' ? Math.round(val * 100) / 100 : undefined;
  }
  
  // Object with value property (API format: lux = { value: [155.99] })
  if (typeof luxData === 'object' && luxData.value !== undefined) {
    return extractLuxValue(luxData.value); // Recursively extract from value
  }
  
  return undefined;
};

// Memoized Power Chart Component - prevents re-renders from parent
interface PowerChartProps {
  data: Array<{ hour: string; power: number; powerPast: number | null; powerFuture: number | null; yesterday: number; lastWeek: number }>;
  todayLabel: string;
  yesterdayLabel: string;
  lastWeekLabel: string;
}

// Custom dot renderer - hollow circle, filled on hover
const renderHollowDot = (color: string) => (props: any): React.ReactElement => {
  const { cx, cy, payload, dataKey, index } = props;
  if (cx === undefined || cy === undefined || payload?.[dataKey] === null || payload?.[dataKey] === undefined) {
    return <circle key={`dot-empty-${index}`} r={0} />;
  }
  return (
    <circle key={`dot-${index}`} cx={cx} cy={cy} r={4} stroke={color} strokeWidth={2} fill="#1f2937" />
  );
};

const renderActiveDot = (color: string) => (props: any): React.ReactElement => {
  const { cx, cy, index } = props;
  if (cx === undefined || cy === undefined) {
    return <circle key={`active-dot-empty-${index}`} r={0} />;
  }
  return (
    <circle key={`active-dot-${index}`} cx={cx} cy={cy} r={5} stroke={color} strokeWidth={2} fill={color} />
  );
};

const PowerChart = React.memo<PowerChartProps>(({ data, todayLabel, yesterdayLabel, lastWeekLabel }) => (
  <ResponsiveContainer width="100%" height="100%">
    <AreaChart data={data} margin={CHART_MARGIN}>
      <defs>
        <linearGradient id="powerGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#eab308" stopOpacity={0.3}/>
          <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
        </linearGradient>
        <linearGradient id="powerGradientFuture" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#eab308" stopOpacity={0.1}/>
          <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
      <XAxis dataKey="hour" tick={CHART_TICK_STYLE} tickLine={CHART_TICK_LINE_STYLE} interval={2} />
      <YAxis tick={CHART_TICK_STYLE} tickLine={CHART_TICK_LINE_STYLE} tickFormatter={formatPowerTick} />
      <Tooltip
        contentStyle={CHART_TOOLTIP_STYLE}
        content={({ active, payload, label }) => {
          if (!active || !payload || payload.length === 0) return null;
          const todayValue = payload.find(p => p.dataKey === 'powerPast')?.value
            ?? payload.find(p => p.dataKey === 'powerFuture')?.value;
          const yesterdayValue = payload.find(p => p.dataKey === 'yesterday')?.value;
          const lastWeekValue = payload.find(p => p.dataKey === 'lastWeek')?.value;
          return (
            <div style={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '10px 14px', color: '#f3f4f6' }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>{label}</p>
              {todayValue !== null && todayValue !== undefined && (
                <p style={{ margin: '4px 0', color: '#eab308' }}>{todayLabel} : {formatPowerValue(Number(todayValue))}</p>
              )}
              {yesterdayValue !== null && yesterdayValue !== undefined && (
                <p style={{ margin: '4px 0', color: '#d1d5db' }}>{yesterdayLabel} : {formatPowerValue(Number(yesterdayValue))}</p>
              )}
              {lastWeekValue !== null && lastWeekValue !== undefined && (
                <p style={{ margin: '4px 0', color: '#3b82f6' }}>{lastWeekLabel} : {formatPowerValue(Number(lastWeekValue))}</p>
              )}
            </div>
          );
        }}
      />
      <Line type="monotone" dataKey="lastWeek" name="lastWeek" stroke="#3b82f6" strokeWidth={1.5} strokeDasharray="3 3" dot={renderHollowDot('#3b82f6') as any} activeDot={renderActiveDot('#3b82f6') as any} isAnimationActive={false} />
      <Line type="monotone" dataKey="yesterday" name="yesterday" stroke="#d1d5db" strokeWidth={1.5} dot={renderHollowDot('#d1d5db') as any} activeDot={renderActiveDot('#d1d5db') as any} isAnimationActive={false} />
      <Area type="monotone" dataKey="powerPast" name="today" stroke="#eab308" strokeWidth={2} fill="url(#powerGradient)" connectNulls={false} dot={renderHollowDot('#eab308') as any} activeDot={renderActiveDot('#eab308') as any} isAnimationActive={false} />
      <Area type="monotone" dataKey="powerFuture" stroke="#eab308" strokeWidth={2} strokeDasharray="5 5" fill="url(#powerGradientFuture)" connectNulls={false} dot={false} activeDot={false} legendType="none" isAnimationActive={false} />
    </AreaChart>
  </ResponsiveContainer>
));
PowerChart.displayName = 'PowerChart';

// Monthly Power Chart Component
interface MonthlyPowerChartProps {
  data: Array<{ day: string; power: number; powerPast: number | null; powerFuture: number | null; lastMonth: number }>;
  currentMonthLabel: string;
  lastMonthLabel: string;
}

const MonthlyPowerChart = React.memo<MonthlyPowerChartProps>(({ data, currentMonthLabel, lastMonthLabel }) => (
  <ResponsiveContainer width="100%" height="100%">
    <AreaChart data={data} margin={CHART_MARGIN}>
      <defs>
        <linearGradient id="monthlyPowerGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#eab308" stopOpacity={0.3}/>
          <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
        </linearGradient>
        <linearGradient id="monthlyPowerGradientFuture" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="#eab308" stopOpacity={0.1}/>
          <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
      <XAxis dataKey="day" tick={CHART_TICK_STYLE} tickLine={CHART_TICK_LINE_STYLE} interval={4} />
      <YAxis tick={CHART_TICK_STYLE} tickLine={CHART_TICK_LINE_STYLE} tickFormatter={formatPowerTick} />
      <Tooltip
        contentStyle={CHART_TOOLTIP_STYLE}
        content={({ active, payload, label }) => {
          if (!active || !payload || payload.length === 0) return null;
          const currentValue = payload.find(p => p.dataKey === 'powerPast')?.value
            ?? payload.find(p => p.dataKey === 'powerFuture')?.value;
          const lastMonthValue = payload.find(p => p.dataKey === 'lastMonth')?.value;
          return (
            <div style={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '10px 14px', color: '#f3f4f6' }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>{label}</p>
              {currentValue !== null && currentValue !== undefined && (
                <p style={{ margin: '4px 0', color: '#eab308' }}>{currentMonthLabel} : {formatPowerValue(Number(currentValue))}</p>
              )}
              {lastMonthValue !== null && lastMonthValue !== undefined && (
                <p style={{ margin: '4px 0', color: '#d1d5db' }}>{lastMonthLabel} : {formatPowerValue(Number(lastMonthValue))}</p>
              )}
            </div>
          );
        }}
      />
      <Line type="monotone" dataKey="lastMonth" name="lastMonth" stroke="#d1d5db" strokeWidth={1.5} dot={renderHollowDot('#d1d5db') as any} activeDot={renderActiveDot('#d1d5db') as any} isAnimationActive={false} />
      <Area type="monotone" dataKey="powerPast" name="currentMonth" stroke="#eab308" strokeWidth={2} fill="url(#monthlyPowerGradient)" connectNulls={false} dot={renderHollowDot('#eab308') as any} activeDot={renderActiveDot('#eab308') as any} isAnimationActive={false} />
      <Area type="monotone" dataKey="powerFuture" stroke="#eab308" strokeWidth={2} strokeDasharray="5 5" fill="url(#monthlyPowerGradientFuture)" connectNulls={false} dot={false} activeDot={false} legendType="none" isAnimationActive={false} />
    </AreaChart>
  </ResponsiveContainer>
));
MonthlyPowerChart.displayName = 'MonthlyPowerChart';

// Memoized Climate Chart Component - prevents re-renders from parent
interface ClimateChartProps {
  data: Array<{ hour: string; temp: number | null; humidity: number | null }>;
  hasData?: boolean;
  realtimeTemp?: number | null;
  realtimeHumidity?: number | null;
}

const ClimateChart = React.memo<ClimateChartProps>(({ data, hasData = true, realtimeTemp, realtimeHumidity }) => {
  // If no historical data available, show realtime values
  if (!hasData) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-4">
        <div className="text-slate-500 text-sm mb-4">
          歷史數據暫不可用
        </div>
        {(realtimeTemp !== null || realtimeHumidity !== null) && (
          <div className="flex items-center gap-8">
            {realtimeTemp !== null && (
              <div className="flex flex-col items-center">
                <span className="text-orange-500 text-4xl font-bold">{realtimeTemp?.toFixed(1)}°C</span>
                <span className="text-slate-400 text-xs mt-1">即時溫度</span>
              </div>
            )}
            {realtimeHumidity !== null && (
              <div className="flex flex-col items-center">
                <span className="text-blue-500 text-4xl font-bold">{realtimeHumidity}%</span>
                <span className="text-slate-400 text-xs mt-1">即時濕度</span>
              </div>
            )}
          </div>
        )}
        <div className="text-slate-600 text-xs mt-4">
          歷史數據 API 暫時無法取得數據
        </div>
      </div>
    );
  }
  
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
        <XAxis dataKey="hour" tick={CHART_TICK_STYLE} tickLine={CHART_TICK_LINE_STYLE} interval={2} />
        <YAxis yAxisId="temp" orientation="left" tick={CHART_TICK_ORANGE} tickLine={CHART_TICK_LINE_STYLE} domain={[(dataMin: number) => typeof dataMin === 'number' && isFinite(dataMin) ? dataMin - 2 : 15, (dataMax: number) => typeof dataMax === 'number' && isFinite(dataMax) ? dataMax + 2 : 35]} tickFormatter={formatTempTick} />
        <YAxis yAxisId="humidity" orientation="right" tick={CHART_TICK_BLUE} tickLine={CHART_TICK_LINE_STYLE} domain={[0, 100]} tickFormatter={formatHumidityTick} />
        <Tooltip
          contentStyle={CHART_TOOLTIP_STYLE}
          content={({ active, payload, label }) => {
            if (!active || !payload || payload.length === 0) return null;
            const tempValue = payload.find(p => p.dataKey === 'temp')?.value;
            const humidityValue = payload.find(p => p.dataKey === 'humidity')?.value;
            return (
              <div style={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '10px 14px', color: '#f3f4f6' }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>{label}</p>
                {humidityValue !== null && humidityValue !== undefined && (
                  <p style={{ margin: '4px 0', color: '#3b82f6' }}>濕度 : {humidityValue}%</p>
                )}
                {tempValue !== null && tempValue !== undefined && (
                  <p style={{ margin: '4px 0', color: '#f97316' }}>溫度 : {Number(tempValue).toFixed(1)}°C</p>
                )}
              </div>
            );
          }}
        />
        <Line yAxisId="temp" type="monotone" dataKey="temp" stroke="#f97316" strokeWidth={2} dot={renderHollowDot('#f97316') as any} activeDot={renderActiveDot('#f97316') as any} name="temp" isAnimationActive={false} connectNulls={false} />
        <Line yAxisId="humidity" type="monotone" dataKey="humidity" stroke="#3b82f6" strokeWidth={2} dot={renderHollowDot('#3b82f6') as any} activeDot={renderActiveDot('#3b82f6') as any} name="humidity" isAnimationActive={false} connectNulls={false} />
      </LineChart>
    </ResponsiveContainer>
  );
});
ClimateChart.displayName = 'ClimateChart';

interface IntelliSpaceDashboardProps {
  onSettingsClick: () => void;
}

const IntelliSpaceDashboard: React.FC<IntelliSpaceDashboardProps> = ({ onSettingsClick }) => {
  // Theme is fixed to dark mode
  const { t } = useTranslation();
  const { unreadCount: alertUnreadCount } = useAlertContext();
  
  // Initialize devices from localStorage cache
  const getInitialDevices = (): IntelliSpaceDevice[] => {
    try {
      const cached = localStorage.getItem(DEVICES_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        logger.log('📦 [IntelliSpace] Loaded cached devices:', parsed.length);
        return parsed;
      }
    } catch (e) {
      logger.error('Failed to load cached devices:', e);
    }
    return [];
  };
  
  // State
  const [devices, setDevices] = useState<IntelliSpaceDevice[]>(getInitialDevices);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [currentView, setCurrentView] = useState<ViewType>('overview');
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [showAlertCenter, setShowAlertCenter] = useState(false);
  const [showDeviceManager, setShowDeviceManager] = useState(false);
  const [selectedZoneFilter, setSelectedZoneFilter] = useState<string>('all');
  const [sortByDeviceType, setSortByDeviceType] = useState<string>('all');
  const [overviewViewMode, setOverviewViewMode] = useState<OverviewViewMode>('energy');
  const [deviceZones, setDeviceZones] = useState<userPreferencesApi.DeviceZone[]>([]);
  const OVERVIEW_ZONE_SELECTION_KEY = 'intellispace-overview-selected-zones-by-tab';
  const [selectedOverviewZonesByTab, setSelectedOverviewZonesByTab] = useState<Record<string, string[]>>(() => {
    try {
      const saved = localStorage.getItem(OVERVIEW_ZONE_SELECTION_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [showOverviewZoneSelector, setShowOverviewZoneSelector] = useState(false);
  const [tempSelectedOverviewZones, setTempSelectedOverviewZones] = useState<string[]>([]);
  const [deviceControlGroups, setDeviceControlGroups] = useState<userPreferencesApi.DeviceControlGroup[]>([]);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [selectedControlGroup, setSelectedControlGroup] = useState<userPreferencesApi.DeviceControlGroup | null>(null);
  const [pinnedZone, setPinnedZone] = useState<string | null>(() => {
    try {
      // Try new key first, fallback to legacy key for migration
      const newValue = localStorage.getItem('intellispace-pinned-zone');
      if (newValue) return newValue;
      const legacyValue = localStorage.getItem('intellispace-pinned-group');
      if (legacyValue) {
        // Migrate to new key
        localStorage.setItem('intellispace-pinned-zone', legacyValue);
        localStorage.removeItem('intellispace-pinned-group');
        return legacyValue;
      }
      return null;
    } catch { return null; }
  });

  useEffect(() => {
    if (deviceZones.length === 0) return;
    const all = deviceZones.map(z => z.id);
    setSelectedOverviewZonesByTab(prev => {
      const next = { ...prev };
      const tabs = ['energy', 'adaptive-lighting', 'hvac-control', 'occupancy-analytics', 'blind-curtain'];
      tabs.forEach(tab => {
        const current = Array.isArray(next[tab]) ? next[tab] : [];
        if (current.length === 0) {
          next[tab] = all;
        } else {
          next[tab] = current.filter(id => deviceZones.some(z => z.id === id));
        }
      });
      try {
        localStorage.setItem(OVERVIEW_ZONE_SELECTION_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, [deviceZones]);
  const [deviceOrder, setDeviceOrder] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-device-order');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [excludedRankingDevices, setExcludedRankingDevices] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-excluded-ranking-devices');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showExcludeRankingModal, setShowExcludeRankingModal] = useState(false);
  const [tempExcludedRankingDevices, setTempExcludedRankingDevices] = useState<string[]>([]);
  const [showOccupiedTooltip, setShowOccupiedTooltip] = useState(false);
  const [occupiedTooltipPosition, setOccupiedTooltipPosition] = useState<{ x: number; y: number; showAbove: boolean } | null>(null);
  const [showHvacTooltip, setShowHvacTooltip] = useState(false);
  const [hvacTooltipPosition, setHvacTooltipPosition] = useState<{ x: number; y: number; showAbove: boolean } | null>(null);
  const [showLightTooltip, setShowLightTooltip] = useState(false);
  const [lightTooltipPosition, setLightTooltipPosition] = useState<{ x: number; y: number; showAbove: boolean } | null>(null);
  const [showSwitchTooltip, setShowSwitchTooltip] = useState(false);
  const [switchTooltipPosition, setSwitchTooltipPosition] = useState<{ x: number; y: number; showAbove: boolean } | null>(null);
  const [websocketDeviceSNs, setWebsocketDeviceSNs] = useState<string[]>([]);
  const [selectedDevice, setSelectedDeviceRaw] = useState<IntelliSpaceDevice | null>(null);
  // ✅ Snapshot of device state when modal opens - prevents background updates from causing re-renders
  const [modalDeviceSnapshot, setModalDeviceSnapshot] = useState<IntelliSpaceDevice | null>(null);
  
  // Wrapper to set both selectedDevice and snapshot together
  const setSelectedDevice = useCallback((device: IntelliSpaceDevice | null) => {
    setSelectedDeviceRaw(device);
    if (device) {
      // Create a deep copy as snapshot to prevent reference updates
      setModalDeviceSnapshot(JSON.parse(JSON.stringify(device)));
    } else {
      setModalDeviceSnapshot(null);
    }
  }, []);
  const [companyName, setCompanyName] = useState<string>('ULTRON');
  const [isExecutingCommand, setIsExecutingCommand] = useState(false);
  
  // TH Sensor Modal - 24h history chart data
  const [thSensorChartData, setThSensorChartData] = useState<Array<{ hour: string; temp: number | null; humidity: number | null }>>([]);
  const [isLoadingThSensorChart, setIsLoadingThSensorChart] = useState(false);
  
  // Lux Sensor Modal - 24h history chart data
  const [luxChartData, setLuxChartData] = useState<Array<{ hour: string; lux: number | null }>>([]);
  const [isLoadingLuxChart, setIsLoadingLuxChart] = useState(false);
  
  // Occupancy/Presence Sensor Modal - 24h history chart data (step chart)
  const [occupancyChartData, setOccupancyChartData] = useState<Array<{ time: string; timestamp: number; value: number; label: string }>>([]);
  const [isLoadingOccupancyChart, setIsLoadingOccupancyChart] = useState(false);
  
  // Door Sensor Modal - 24h history chart data (open/close timeline)
  const [doorSensorChartData, setDoorSensorChartData] = useState<Array<{ time: string; timestamp: number; value: number; label: string }>>([]);
  const [isLoadingDoorSensorChart, setIsLoadingDoorSensorChart] = useState(false);
  
  // Power Meter Modal - 24h history chart data (total watt only)
  const [energyChartData, setEnergyChartData] = useState<Array<{ hour: string; watt: number | null }>>([]);
  const [isLoadingEnergyChart, setIsLoadingEnergyChart] = useState(false);
  
  // Multi-channel device display preference: merged baseSNs (show as single block)
  // Key = baseSN, if in set = show merged (single block), not in set = show individual endpoints
  // Loaded from userPreferences API (Firestore)
  const [mergedDeviceSNs, setMergedDeviceSNs] = useState<Set<string>>(new Set());
  
  const executingDeviceIdRef = useRef<string | null>(null);
  
  // Track pending command updates to prevent stale data race conditions
  // Uses a hybrid strategy: timestamp check + timeout + update count + state sequence tracking
  // Simplified PendingCommandState interface - uses unified 30s lock with timestamp-based filtering
  interface PendingCommandState {
    expectedState: { 
      on?: boolean;                    // For OnOff trait
      fanSpeed?: string;               // For FanSpeed trait
      brightness?: number;             // For Brightness trait (lights)
      thermostatTemperatureSetpoint?: number;  // For TemperatureSetting trait (HVAC)
      thermostatMode?: string;         // For ThermostatMode/Modes trait (HVAC)
      openPercent?: number;            // For OpenClose trait (curtain/blind)
      colorTemp?: number;              // For ColorSetting trait (lights)
    };
    timestamp: number;                 // Command execution time
    commandId: number;                 // Unique command ID to track which command this is for
  }
  const pendingCommandUpdates = useRef<Map<string, PendingCommandState>>(new Map());
  const commandIdCounter = useRef<number>(0);  // Global counter for command IDs
  const pendingVerificationTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());  // Track verification timers
  const scheduleStatusVerificationRef = useRef<((deviceId: string, commandId: number, timeoutMs?: number) => void) | null>(null);
  
  /**
   * Cancel verification timer for a device (called when state is confirmed via WebSocket)
   */
  const cancelVerificationTimer = (deviceId: string) => {
    const timer = pendingVerificationTimers.current.get(deviceId);
    if (timer) {
      clearTimeout(timer);
      pendingVerificationTimers.current.delete(deviceId);
      logger.log(`✅ [IntelliSpace] Cancelled verification timer for ${deviceId} - state confirmed via WebSocket`);
    }
  };

  /**
   * Find pending lock for a device - checks both exact deviceId and baseSN variations
   * This handles composite devices (like dehumidifier) where pending may be set with endpoint suffix
   */
  const findPendingLock = (deviceId: string): { pending: PendingCommandState | null; lockKey: string | null } => {
    // Try exact match first
    let pending = pendingCommandUpdates.current.get(deviceId);
    if (pending) return { pending, lockKey: deviceId };
    
    // Try baseSN match (for composite devices like dehumidifier)
    const baseSN = deviceId.split('#')[0];
    
    // Check if there's a lock with endpoint suffix (use forEach to avoid ES2015 iteration issue)
    let result: { pending: PendingCommandState | null; lockKey: string | null } = { pending: null, lockKey: null };
    pendingCommandUpdates.current.forEach((value, key) => {
      if (result.pending === null && (key === baseSN || key.startsWith(baseSN + '#'))) {
        result = { pending: value, lockKey: key };
      }
    });
    
    return result;
  };

  /**
   * Check if device state matches expected state from pending command
   * Used for simplified optimistic lock verification
   */
  const stateMatchesExpected = (
    device: Partial<IntelliSpaceDevice>,
    expected: PendingCommandState['expectedState']
  ): boolean => {
    // Helper to normalize boolean values (handles string "true"/"false", numbers 0/1, etc.)
    const toBool = (val: any): boolean | undefined => {
      if (val === undefined || val === null) return undefined;
      if (typeof val === 'boolean') return val;
      if (typeof val === 'string') return val.toLowerCase() === 'true' || val === '1';
      if (typeof val === 'number') return val !== 0;
      return Boolean(val);
    };
    
    // OnOff - normalize to boolean for comparison
    // Priority: check the most recently updated property first
    // For switches: switchOn is updated by WebSocket, isOn might be stale
    // For lights/HVAC: isOn is typically updated
    if (expected.on !== undefined) {
      // Get all possible on states
      const isOnBool = toBool(device.isOn);
      const switchOnBool = toBool(device.switchOn);
      const powerBool = toBool(device.power);
      
      // Accept if ANY of the on-state properties matches expected
      // This handles the case where different device types use different properties
      const matches = (isOnBool === expected.on) || 
                      (switchOnBool === expected.on) || 
                      (powerBool === expected.on);
      
      if (!matches) return false;
    }
    // FanSpeed - string comparison
    if (expected.fanSpeed !== undefined) {
      if (device.fanSpeed === undefined) return false;
      if (String(device.fanSpeed) !== String(expected.fanSpeed)) return false;
    }
    // Brightness - allow 5% tolerance
    if (expected.brightness !== undefined) {
      if (device.brightness === undefined) return false;
      if (Math.abs(Number(device.brightness) - expected.brightness) > 5) return false;
    }
    // OpenPercent - allow 5% tolerance for curtains
    if (expected.openPercent !== undefined) {
      if (device.openPercent === undefined) return false;
      if (Math.abs(Number(device.openPercent) - expected.openPercent) > 5) return false;
    }
    // Temperature - allow 0.5°C tolerance
    if (expected.thermostatTemperatureSetpoint !== undefined) {
      if (device.setTemperature === undefined) return false;
      if (Math.abs(Number(device.setTemperature) - expected.thermostatTemperatureSetpoint) > 0.5) return false;
    }
    // Mode - case-insensitive comparison
    if (expected.thermostatMode !== undefined) {
      if (device.mode === undefined) return false;
      if (String(device.mode).toLowerCase() !== String(expected.thermostatMode).toLowerCase()) return false;
    }
    // ColorTemp - allow 100K tolerance
    if (expected.colorTemp !== undefined) {
      if (device.colorTemp === undefined) return false;
      if (Math.abs(Number(device.colorTemp) - expected.colorTemp) > 100) return false;
    }
    return true;
  };
  
  // Track pending nickname updates to prevent stale data from overwriting local changes
  // When a user saves a nickname, we store it here temporarily until cloud sync completes
  const pendingNicknameUpdates = useRef<Map<string, { nickname: string; timestamp: number }>>(new Map());
  
  // Power Meter, Switch, HVAC, and TH Sensor selection states for Overview cards
  // Initialized from localStorage, synced to Firestore
  const [selectedPowerMeters, setSelectedPowerMeters] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-selected-power-meters');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [selectedLights, setSelectedLights] = useState<string[]>(() => {
    const saved = localStorage.getItem('intellispace-selected-lights');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedSwitches, setSelectedSwitches] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-selected-switches');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [selectedHvac, setSelectedHvac] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-selected-hvac');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [selectedThSensors, setSelectedThSensors] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-selected-th-sensors');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [selectedPresenceSensors, setSelectedPresenceSensors] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-selected-presence-sensors');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [selectionsLoaded, setSelectionsLoaded] = useState(false);
  const [showPowerMeterSelector, setShowPowerMeterSelector] = useState(false);
  const [showPresenceSensorSelector, setShowPresenceSensorSelector] = useState(false);
  const [tempSelectedPresenceSensors, setTempSelectedPresenceSensors] = useState<string[]>([]);
  const [selectedLuxSensors, setSelectedLuxSensors] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-selected-lux-sensors');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [tempSelectedLuxSensors, setTempSelectedLuxSensors] = useState<string[]>([]);
  const [showHvacMeterSelector, setShowHvacMeterSelector] = useState(false);
  const [showLightingMeterSelector, setShowLightingMeterSelector] = useState(false);
  
  // HVAC and Lighting meter selections for power breakdown
  const [selectedHvacMeters, setSelectedHvacMeters] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-selected-hvac-meters');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [selectedLightingMeters, setSelectedLightingMeters] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-selected-lighting-meters');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showSwitchSelector, setShowSwitchSelector] = useState(false);
  const [showLightSelector, setShowLightSelector] = useState(false);
  // NOTE: selectedLightDevice, lightModalBrightness, lightModalColorTemp removed - Light now uses unified template system
  const [editingNickname, setEditingNickname] = useState(false);
  const [tempNickname, setTempNickname] = useState('');
  const [showHvacSelector, setShowHvacSelector] = useState(false);
  const [showThSensorSelector, setShowThSensorSelector] = useState(false);
  const [showLuxSensorSelector, setShowLuxSensorSelector] = useState(false);
  const [showBlindCurtainSelector, setShowBlindCurtainSelector] = useState(false);
  
  // Baseline power for energy saving calculation
  const [baselineLightingPower, setBaselineLightingPower] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('intellispace-baseline-lighting-power');
      return saved ? parseFloat(saved) : 0;
    } catch { return 0; }
  });
  const [baselineLightingSchedule, setBaselineLightingSchedule] = useState<number[]>(() => {
    try {
      const saved = localStorage.getItem('intellispace-baseline-lighting-schedule');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [baselineHvacPower, setBaselineHvacPower] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('intellispace-baseline-hvac-power');
      return saved ? parseFloat(saved) : 0;
    } catch { return 0; }
  });
  const [showBaselinePowerModal, setShowBaselinePowerModal] = useState<'lighting' | 'hvac' | null>(null);
  const [tempBaselinePower, setTempBaselinePower] = useState<string>('');

  // Temporary states for selectors (only save on confirm)
  const [tempSelectedPowerMeters, setTempSelectedPowerMeters] = useState<string[]>([]);
  const [tempSelectedSwitches, setTempSelectedSwitches] = useState<string[]>([]);
  const [tempSelectedLights, setTempSelectedLights] = useState<string[]>([]);
  const [tempSelectedHvac, setTempSelectedHvac] = useState<string[]>([]);
  const [tempSelectedThSensors, setTempSelectedThSensors] = useState<string[]>([]);
  const [tempSelectedHvacMeters, setTempSelectedHvacMeters] = useState<string[]>([]);
  const [tempSelectedLightingMeters, setTempSelectedLightingMeters] = useState<string[]>([]);
  const [tempSelectedThreePhaseMeter, setTempSelectedThreePhaseMeter] = useState<string | null>(null);
  const [isSwitchToggling, setIsSwitchToggling] = useState(false);
  const [isLightToggling, setIsLightToggling] = useState(false);
  const [isHvacToggling, setIsHvacToggling] = useState(false);
  
  // Curtain/Blind slider debounce - local state during drag
  const [sliderDragValue, setSliderDragValue] = useState<number | null>(null);
  
  // Reset sliderDragValue when selected device changes (modal open/close)
  useEffect(() => {
    setSliderDragValue(null);
  }, [selectedDevice]);
  
  // ✅ Fetch real-time device status when modal opens
  useEffect(() => {
    const fetchRealTimeStatus = async () => {
      if (!selectedDevice) {
        return;
      }
      
      const deviceId = selectedDevice.deviceId;
      logger.log(`🔄 [IntelliSpace] Modal opened - fetching real-time status for ${deviceId}`);
      
      try {
        const statusResponse = await ultronTuringApi.queryStatus([deviceId], { detailed: true });
        
        if (statusResponse?.data && Array.isArray(statusResponse.data)) {
          const latestStatus = statusResponse.data[0];
          if (latestStatus?.state) {
            const state = latestStatus.state;
            logger.log(`✅ [IntelliSpace] Got real-time status for ${deviceId}:`, state);
            
            // Update the modal device snapshot with real-time data
            setModalDeviceSnapshot(prev => {
              if (!prev || prev.deviceId !== deviceId) return prev;
              
              const updated = { ...prev };
              
              // Update common state properties
              if (state.on !== undefined) {
                updated.isOn = state.on;
              }
              if (state.currentModeSettings?.on !== undefined) {
                updated.isOn = state.currentModeSettings.on;
              }
              if (state.brightness !== undefined) {
                updated.brightness = state.brightness;
              }
              if (state.color?.temperatureK !== undefined) {
                updated.colorTemp = state.color.temperatureK;
              }
              if (state.thermostatTemperatureSetpoint !== undefined) {
                updated.thermostatTemperatureSetpoint = state.thermostatTemperatureSetpoint;
                updated.setTemperature = state.thermostatTemperatureSetpoint;
              }
              // ✅ Update BOTH mode and thermostatMode for consistency
              if (state.thermostatMode !== undefined) {
                updated.mode = state.thermostatMode;
                updated.thermostatMode = state.thermostatMode;
              }
              if (state.currentFanSpeedSetting !== undefined) {
                updated.fanSpeed = state.currentFanSpeedSetting;
              }
              if (state.openPercent !== undefined) {
                updated.openPercent = state.openPercent;
              }
              if (state.thermostatTemperatureAmbient !== undefined) {
                updated.temperature = state.thermostatTemperatureAmbient;
              }
              // Update toggle settings
              if (state.currentToggleSettings !== undefined) {
                updated.toggleValues = state.currentToggleSettings;
              }
              
              logger.log(`✅ [IntelliSpace] Updated modal snapshot for ${deviceId}:`, updated);
              return updated;
            });
            
            // Also update the main devices array
            setDevices(prev => prev.map(d => {
              if (d.deviceId !== deviceId) return d;
              
              const updated = { ...d };
              if (state.on !== undefined) {
                updated.isOn = state.on;
              }
              if (state.currentModeSettings?.on !== undefined) {
                updated.isOn = state.currentModeSettings.on;
              }
              if (state.brightness !== undefined) {
                updated.brightness = state.brightness;
              }
              if (state.color?.temperatureK !== undefined) {
                updated.colorTemp = state.color.temperatureK;
              }
              if (state.thermostatTemperatureSetpoint !== undefined) {
                updated.thermostatTemperatureSetpoint = state.thermostatTemperatureSetpoint;
                updated.setTemperature = state.thermostatTemperatureSetpoint;
              }
              // ✅ Update BOTH mode and thermostatMode for consistency
              if (state.thermostatMode !== undefined) {
                updated.mode = state.thermostatMode;
                updated.thermostatMode = state.thermostatMode;
              }
              if (state.currentFanSpeedSetting !== undefined) {
                updated.fanSpeed = state.currentFanSpeedSetting;
              }
              if (state.openPercent !== undefined) {
                updated.openPercent = state.openPercent;
              }
              if (state.thermostatTemperatureAmbient !== undefined) {
                updated.temperature = state.thermostatTemperatureAmbient;
              }
              if (state.currentToggleSettings !== undefined) {
                updated.toggleValues = state.currentToggleSettings;
              }
              return updated;
            }));
          }
        }
      } catch (error) {
        logger.warn(`⚠️ [IntelliSpace] Failed to fetch real-time status for ${deviceId}:`, error);
      }
    };
    
    fetchRealTimeStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice?.deviceId]);
  
  // Fetch 24h history for TH Sensor when modal opens
  useEffect(() => {
    const fetchThSensorHistory = async () => {
      if (!selectedDevice) {
        return;
      }
      
      // If device is not a TH sensor, clear data and return
      if (selectedDevice.deviceType !== 'th_sensor') {
        setThSensorChartData([]);
        return;
      }
      
      setIsLoadingThSensorChart(true);
      try {
        const deviceSN = selectedDevice.deviceId;
        const report = await ultronTuringApi.generateReport([deviceSN], {
          duration: '24h',
          granularity: 'hour',
          format: 'summary',
          utcOffset: '+08:00'
        });
        
        if (report.success && report.data) {
          // Extract chart data from report
          const chartData: Array<{ hour: string; temp: number | null; humidity: number | null }> = [];
          
          // Check if data has chartData array
          const reportData = report.data as any;
          // Helper to format time as MM/DD HH:00
          const formatTimeLabel = (timeStr: string) => {
            const d = new Date(timeStr);
            const month = (d.getMonth() + 1).toString().padStart(2, '0');
            const day = d.getDate().toString().padStart(2, '0');
            const hour = d.getHours().toString().padStart(2, '0');
            return `${month}/${day} ${hour}:00`;
          };
          
          if (reportData.chartData && Array.isArray(reportData.chartData)) {
            for (const point of reportData.chartData) {
              const hour = point.time ? formatTimeLabel(point.time) : '';
              chartData.push({
                hour,
                temp: point.temperature ?? point.temp ?? null,
                humidity: point.humidity ?? point.rh ?? null
              });
            }
          } else {
            // ✅ Try to extract from device-keyed data (MCP returns { "UTU307-xxx": { chartData: [...] } })
            const deviceData = reportData[deviceSN] || Object.values(reportData)[0];
            // ✅ 修正：MCP 返回的是 chartData，不是 dataPoints
            if (deviceData?.chartData && Array.isArray(deviceData.chartData)) {
              for (const point of deviceData.chartData) {
                const hour = point.time ? formatTimeLabel(point.time) : '';
                chartData.push({
                  hour,
                  temp: point.temperature ?? point.temp ?? null,
                  humidity: point.humidity ?? point.rh ?? null
                });
              }
            }
          }
          
          setThSensorChartData(chartData);
        } else {
          setThSensorChartData([]);
        }
      } catch (error) {
        console.error('Failed to fetch TH sensor history:', error);
        setThSensorChartData([]);
      } finally {
        setIsLoadingThSensorChart(false);
      }
    };
    
    fetchThSensorHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice?.deviceId]);
  
  // Fetch 24h history for Lux Sensor when modal opens (only when device actually has lux data)
  useEffect(() => {
    const fetchLuxHistory = async () => {
      // Check if device actually has lux data from QueryStatus (not just by deviceType)
      const hasLux = selectedDevice && selectedDevice.lux !== undefined;
      
      if (!selectedDevice) {
        return;
      }
      
      // If device doesn't have lux data, clear chart and return
      if (!hasLux) {
        setLuxChartData([]);
        return;
      }
      
      setIsLoadingLuxChart(true);
      try {
        const baseSN = selectedDevice.deviceId;
        
        // For multi-endpoint devices, try to find the lux endpoint
        // Check if device has capabilities info with lux endpoint
        let luxEndpointSN = baseSN;
        const deviceAny = selectedDevice as any;
        if (deviceAny.capabilities && Array.isArray(deviceAny.capabilities)) {
          const luxCapability = deviceAny.capabilities.find((cap: any) => 
            cap.attributes?.sensorProfile?.lux || 
            cap.attributes?.sensorConfigurable?.includes('lux')
          );
          if (luxCapability?.deviceSN) {
            luxEndpointSN = luxCapability.deviceSN;
          }
        } else if (selectedDevice.deviceType === 'multi_sensor' && !baseSN.includes('#')) {
          // Default to #2 for multi_sensor if no capabilities info (common lux endpoint)
          luxEndpointSN = `${baseSN}#2`;
        }
        
        const report = await ultronTuringApi.generateReport([luxEndpointSN], {
          duration: '24h',
          granularity: 'hour',
          format: 'summary',
          utcOffset: '+08:00',
          metrics: ['lux']
        });
        
        if (report.success && report.data) {
          const chartData: Array<{ hour: string; lux: number | null }> = [];
          const reportData = report.data as any;
          
          // Helper to format time as MM/DD HH:00
          const formatTimeLabel = (timeStr: string) => {
            const d = new Date(timeStr);
            const month = (d.getMonth() + 1).toString().padStart(2, '0');
            const day = d.getDate().toString().padStart(2, '0');
            const hour = d.getHours().toString().padStart(2, '0');
            return `${month}/${day} ${hour}:00`;
          };
          
          // Extract from device-keyed data
          const deviceData = reportData[luxEndpointSN] || Object.values(reportData)[0];
          if (deviceData?.chartData && Array.isArray(deviceData.chartData)) {
            for (const point of deviceData.chartData) {
              const hour = point.time ? formatTimeLabel(point.time) : '';
              // Support different lux field names
              const luxValue = point.lux ?? point.illuminance ?? point.light ?? null;
              chartData.push({
                hour,
                lux: typeof luxValue === 'number' ? luxValue : null
              });
            }
          }
          
          setLuxChartData(chartData);
        } else {
          setLuxChartData([]);
        }
      } catch (error) {
        console.error('Failed to fetch Lux sensor history:', error);
        setLuxChartData([]);
      } finally {
        setIsLoadingLuxChart(false);
      }
    };
    
    fetchLuxHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice?.deviceId]);
  
  // Fetch 24h history for Occupancy/Presence Sensor when modal opens
  useEffect(() => {
    const fetchOccupancyHistory = async () => {
      // Check if device has occupancy/presence capability
      const hasOccupancy = selectedDevice && (
        selectedDevice.deviceType === 'presence_sensor' ||
        selectedDevice.deviceType === 'motion_sensor' ||
        selectedDevice.occupancy !== undefined ||
        selectedDevice.presence !== undefined ||
        selectedDevice.traits?.some(t => 
          t.toLowerCase().includes('occupancy') || 
          t.toLowerCase().includes('detector') ||
          t.toLowerCase().includes('presence')
        )
      );
      
      if (!selectedDevice) {
        return;
      }
      
      // If device doesn't have occupancy data, clear chart and return
      if (!hasOccupancy) {
        setOccupancyChartData([]);
        return;
      }
      
      setIsLoadingOccupancyChart(true);
      try {
        const deviceSN = selectedDevice.deviceId;
        const result = await ultronTuringApi.analyzeDeviceLogs(deviceSN, {
          period: 'week',
          format: 'detailed'
        });
        
        if (result.success && result.data) {
          const chartData: Array<{ time: string; timestamp: number; value: number; label: string }> = [];
          const resultData = result.data as any;
          
          // Extract recentEvents with occupancy data
          const recentEvents = resultData.recentEvents || [];
          const now = Date.now();
          const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
          
          // Helper to format time as MM/DD HH:mm
          const formatTimeLabel = (timestamp: number) => {
            const d = new Date(timestamp);
            const month = (d.getMonth() + 1).toString().padStart(2, '0');
            const day = d.getDate().toString().padStart(2, '0');
            const hour = d.getHours().toString().padStart(2, '0');
            const minute = d.getMinutes().toString().padStart(2, '0');
            return `${month}/${day} ${hour}:${minute}`;
          };
          
          // Filter events within 24h and extract occupancy state
          for (const event of recentEvents) {
            const eventTimestamp = event.timestamp;
            
            // Only include events within 24 hours
            if (eventTimestamp < twentyFourHoursAgo) continue;
            
            // Extract occupancy value from event
            const occupancyValue = event.message?.occupancy ?? 
                                   event.details?.actionValue ?? 
                                   (event.event === '有人' ? true : event.event === '無人' ? false : undefined);
            
            if (occupancyValue !== undefined) {
              chartData.push({
                time: formatTimeLabel(eventTimestamp),
                timestamp: eventTimestamp,
                value: occupancyValue ? 1 : 0,
                label: occupancyValue ? '有人' : '無人'
              });
            }
          }
          
          // Sort by timestamp (oldest first for step chart)
          chartData.sort((a, b) => a.timestamp - b.timestamp);
          
          // Add current state point if we have data
          if (chartData.length > 0) {
            const lastState = chartData[chartData.length - 1];
            chartData.push({
              time: formatTimeLabel(now),
              timestamp: now,
              value: lastState.value,
              label: lastState.label
            });
          }
          
          setOccupancyChartData(chartData);
        } else {
          setOccupancyChartData([]);
        }
      } catch (error) {
        console.error('Failed to fetch Occupancy sensor history:', error);
        setOccupancyChartData([]);
      } finally {
        setIsLoadingOccupancyChart(false);
      }
    };
    
    fetchOccupancyHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice?.deviceId]);
  
  // Fetch 24h history for Door Sensor when modal opens
  useEffect(() => {
    const fetchDoorSensorHistory = async () => {
      // Check if device is a door sensor
      const isDoorSensor = selectedDevice && (
        selectedDevice.deviceType === 'door_sensor' ||
        selectedDevice.isOpen !== undefined ||
        selectedDevice.traits?.some(t => 
          t.toLowerCase().includes('openclose') || 
          t.toLowerCase().includes('contact')
        )
      );
      
      if (!selectedDevice) {
        return;
      }
      
      // If device is not a door sensor, clear chart and return
      if (!isDoorSensor) {
        setDoorSensorChartData([]);
        return;
      }
      
      setIsLoadingDoorSensorChart(true);
      try {
        const deviceSN = selectedDevice.deviceId;
        const result = await ultronTuringApi.analyzeDeviceLogs(deviceSN, {
          period: 'week',
          format: 'detailed'
        });
        
        if (result.success && result.data) {
          const chartData: Array<{ time: string; timestamp: number; value: number; label: string }> = [];
          const resultData = result.data as any;
          
          // Extract recentEvents with open/close data
          const recentEvents = resultData.recentEvents || [];
          const now = Date.now();
          const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
          
          // Helper to format time as MM/DD HH:mm
          const formatTimeLabel = (timestamp: number) => {
            const d = new Date(timestamp);
            const month = (d.getMonth() + 1).toString().padStart(2, '0');
            const day = d.getDate().toString().padStart(2, '0');
            const hour = d.getHours().toString().padStart(2, '0');
            const minute = d.getMinutes().toString().padStart(2, '0');
            return `${month}/${day} ${hour}:${minute}`;
          };
          
          // Track the last known state before 24h window for initial state
          let lastKnownStateBeforeWindow: { value: boolean; timestamp: number } | null = null;
          
          // Process all events to extract open/close state
          for (const event of recentEvents) {
            const eventTimestamp = event.timestamp;
            
            // Extract open/close value from event
            // Priority: event string > details.actionValue > message.detected > openPercent/isOpen
            let openValue: boolean | undefined;
            
            // First priority: Parse from event string (most reliable - "已打開" / "已關上")
            if (event.event) {
              const eventStr = event.event.toLowerCase();
              if (eventStr.includes('打開') || eventStr.includes('開啟') || eventStr.includes('opened')) {
                openValue = true;
              } else if (eventStr.includes('關閉') || eventStr.includes('關上') || eventStr.includes('closed')) {
                openValue = false;
              }
            }
            
            // Second priority: details.actionValue
            if (openValue === undefined && event.details?.actionValue !== undefined) {
              const av = String(event.details.actionValue).toLowerCase();
              if (av === 'open' || av === 'opened') {
                openValue = true;
              } else if (av === 'close' || av === 'closed') {
                openValue = false;
              }
            }
            
            // Third priority: message.detected array
            if (openValue === undefined && event.message?.detected !== undefined) {
              const detectedArr = event.message.detected;
              const detected = Array.isArray(detectedArr) ? detectedArr[0] : detectedArr;
              if (detected) {
                const detectedStr = String(detected).toLowerCase();
                if (detectedStr === 'open' || detectedStr === 'opened') {
                  openValue = true;
                } else if (detectedStr === 'close' || detectedStr === 'closed') {
                  openValue = false;
                }
              }
            }
            
            // Fourth priority: openPercent or isOpen
            if (openValue === undefined) {
              if (event.message?.openPercent !== undefined) {
                openValue = event.message.openPercent > 0;
              } else if (event.message?.isOpen !== undefined) {
                openValue = event.message.isOpen;
              }
            }
            
            if (openValue !== undefined) {
              // Track events before 24h window
              if (eventTimestamp < twentyFourHoursAgo) {
                if (!lastKnownStateBeforeWindow || eventTimestamp > lastKnownStateBeforeWindow.timestamp) {
                  lastKnownStateBeforeWindow = { value: openValue, timestamp: eventTimestamp };
                }
              } else {
                // Event is within 24h window
                chartData.push({
                  time: formatTimeLabel(eventTimestamp),
                  timestamp: eventTimestamp,
                  value: openValue ? 1 : 0,
                  label: openValue ? '打開' : '關閉'
                });
              }
            }
          }
          
          // Sort by timestamp (oldest first for timeline)
          chartData.sort((a, b) => a.timestamp - b.timestamp);
          
          // If we have a known state before 24h window, add it as starting point
          if (lastKnownStateBeforeWindow && chartData.length === 0) {
            // No events in 24h, but we know the state from before - use that state for entire period
            chartData.push({
              time: formatTimeLabel(twentyFourHoursAgo),
              timestamp: twentyFourHoursAgo,
              value: lastKnownStateBeforeWindow.value ? 1 : 0,
              label: lastKnownStateBeforeWindow.value ? '打開' : '關閉'
            });
          } else if (lastKnownStateBeforeWindow && chartData.length > 0) {
            // We have events in 24h, add the known state as the starting point
            const firstEventTime = chartData[0].timestamp;
            chartData.unshift({
              time: formatTimeLabel(twentyFourHoursAgo),
              timestamp: twentyFourHoursAgo,
              value: lastKnownStateBeforeWindow.value ? 1 : 0,
              label: lastKnownStateBeforeWindow.value ? '打開' : '關閉'
            });
          }
          
          // If still no data but we have device current state, show that state for entire period
          if (chartData.length === 0 && selectedDevice.isOpen !== undefined) {
            const currentIsOpen = selectedDevice.isOpen;
            chartData.push({
              time: formatTimeLabel(twentyFourHoursAgo),
              timestamp: twentyFourHoursAgo,
              value: currentIsOpen ? 1 : 0,
              label: currentIsOpen ? '打開' : '關閉'
            });
            chartData.push({
              time: formatTimeLabel(now),
              timestamp: now,
              value: currentIsOpen ? 1 : 0,
              label: currentIsOpen ? '打開' : '關閉'
            });
          } else if (chartData.length > 0) {
            // Add current state point
            const lastState = chartData[chartData.length - 1];
            chartData.push({
              time: formatTimeLabel(now),
              timestamp: now,
              value: lastState.value,
              label: lastState.label
            });
          }
          
          setDoorSensorChartData(chartData);
        } else {
          setDoorSensorChartData([]);
        }
      } catch (error) {
        console.error('Failed to fetch Door sensor history:', error);
        setDoorSensorChartData([]);
      } finally {
        setIsLoadingDoorSensorChart(false);
      }
    };
    
    fetchDoorSensorHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice?.deviceId]);
  
  // Track current request to prevent race conditions (using counter instead of deviceId)
  const energyRequestCounterRef = useRef<number>(0);
  
  // Fetch 24h history for Power Meter when modal opens
  useEffect(() => {
    // Increment counter for each new request attempt
    const thisRequestId = ++energyRequestCounterRef.current;
    
    const fetchEnergyHistory = async () => {
      // Check if device is a power meter (including 3-phase)
      const isPowerMeter = selectedDevice && (
        selectedDevice.deviceType === 'power_meter' ||
        selectedDevice.deviceType === 'power_meter_3phase' ||
        selectedDevice.watt !== undefined ||
        selectedDevice.wattHour !== undefined
      );
      
      if (!selectedDevice) {
        return;
      }
      
      // If device doesn't have power metrics, clear data and return
      if (!isPowerMeter) {
        setEnergyChartData([]);
        return;
      }
      
      // ✅ Clear previous data immediately to prevent showing stale data from another device
      setEnergyChartData([]);
      setIsLoadingEnergyChart(true);
      
      try {
        // Use full deviceId for power meters (with endpoint suffix if present)
        const deviceSN = selectedDevice.deviceId;
        console.log(`📊 [Energy] ========== FETCHING ENERGY HISTORY ==========`);
        console.log(`📊 [Energy] Request #${thisRequestId} for:`, deviceSN);
        console.log(`📊 [Energy] selectedDevice.deviceId:`, selectedDevice.deviceId);
        console.log(`📊 [Energy] selectedDevice.baseSN:`, selectedDevice.baseSN);
        console.log(`📊 [Energy] selectedDevice.deviceName:`, selectedDevice.deviceName);
        
        const report = await ultronTuringApi.generateReport([deviceSN], {
          duration: '24h',
          granularity: 'hour',
          format: 'summary',  // summary 格式包含已計算好的 energy 欄位
          utcOffset: '+8:00'
        });
        
        console.log(`📊 [Energy] Report response:`, report);
        
        if (report.success && report.data) {
          const chartData: Array<{ hour: string; watt: number | null }> = [];
          const reportData = report.data as any;
          
          // Helper to format time as MM/DD HH:00
          const formatTimeLabel = (timeStr: string) => {
            const d = new Date(timeStr);
            const month = (d.getMonth() + 1).toString().padStart(2, '0');
            const day = d.getDate().toString().padStart(2, '0');
            const hour = d.getHours().toString().padStart(2, '0');
            return `${month}/${day} ${hour}:00`;
          };
          
          const reportDataKeys = Object.keys(reportData);
          console.log(`📊 [Energy] reportData keys:`, reportDataKeys);
          console.log(`📊 [Energy] Looking for deviceSN:`, deviceSN);
          
          // ✅ Strict match: ONLY use exact deviceSN match
          // Do NOT fallback to prevent showing wrong endpoint data
          let deviceData = reportData[deviceSN];
          let matchedKey = deviceSN;
          
          // Log which key was matched
          console.log(`📊 [Energy] Exact match for "${deviceSN}":`, !!deviceData);
          
          // If not found, log error but do NOT fallback
          if (!deviceData) {
            console.error(`📊 [Energy] ❌ No data found for exact deviceSN "${deviceSN}"`);
            console.error(`📊 [Energy] ❌ Available keys:`, reportDataKeys);
            console.error(`📊 [Energy] ❌ This indicates a deviceId mismatch!`);
            // Do NOT fallback - this prevents showing wrong endpoint data
            setEnergyChartData([]);
            setIsLoadingEnergyChart(false);
            return;
          }
          
          console.log(`📊 [Energy] ✅ Device data deviceSN:`, deviceData?.deviceSN);
          console.log(`📊 [Energy] ✅ totalConsumption:`, deviceData?.totalConsumption);
          console.log(`📊 [Energy] ✅ chartData length:`, deviceData?.chartData?.length || 0);
          
          // ✅ 方法1 (優先): summary 格式 - chartData 陣列包含已計算好的 energy
          if (deviceData?.chartData && Array.isArray(deviceData.chartData)) {
            console.log(`📊 [Energy] Using chartData format with energy field, ${deviceData.chartData.length} points`);
            
            // 按時間排序（升序）
            const sortedChartData = [...deviceData.chartData].sort((a: any, b: any) => {
              const timeA = new Date(a.time || 0).getTime();
              const timeB = new Date(b.time || 0).getTime();
              return timeA - timeB;
            });
            
            for (const point of sortedChartData) {
              const hour = point.time ? formatTimeLabel(point.time) : '';
              // energy 是已計算好的每小時用電量 (kWh)
              const energyValue = point.energy ?? null;
              chartData.push({
                hour,
                watt: typeof energyValue === 'number' ? energyValue : null
              });
            }
          }
          // ✅ 方法2: detailed 格式 - watt_hour 陣列（需計算差值）
          else if (deviceData?.watt_hour && Array.isArray(deviceData.watt_hour)) {
            console.log(`📊 [Energy] Using watt_hour format, ${deviceData.watt_hour.length} points`);
            
            // 按時間戳排序（升序）
            const wattHourData = [...deviceData.watt_hour].sort((a: any, b: any) => 
              (a.timestamp || 0) - (b.timestamp || 0)
            );
            
            // 計算每小時的用電量（當前小時的 max - 前一小時的 max = 該小時消耗量）
            for (let i = 1; i < wattHourData.length; i++) {
              const prev = wattHourData[i - 1];
              const curr = wattHourData[i];
              const timestamp = curr.timestamp * 1000; // Unix timestamp (秒) to ms
              const date = new Date(timestamp);
              const month = (date.getMonth() + 1).toString().padStart(2, '0');
              const day = date.getDate().toString().padStart(2, '0');
              const hour = date.getHours().toString().padStart(2, '0');
              const timeLabel = `${month}/${day} ${hour}:00`;
              
              // 累積電量差值 = 這一小時消耗的電量
              const prevMax = prev.max ?? prev.value ?? 0;
              const currMax = curr.max ?? curr.value ?? 0;
              const energyDiff = currMax - prevMax;
              
              chartData.push({
                hour: timeLabel,
                watt: energyDiff >= 0 ? energyDiff : null
              });
            }
          }
          // ✅ 方法3: dataPoints 陣列格式
          else if (deviceData?.dataPoints && Array.isArray(deviceData.dataPoints)) {
            console.log(`📊 [Energy] Using dataPoints format, ${deviceData.dataPoints.length} points`);
            const sortedPoints = [...deviceData.dataPoints].sort((a: any, b: any) => {
              const timeA = new Date(a.time || a.timestamp || 0).getTime();
              const timeB = new Date(b.time || b.timestamp || 0).getTime();
              return timeA - timeB;
            });
            
            // 計算每小時的用電量
            for (let i = 1; i < sortedPoints.length; i++) {
              const prev = sortedPoints[i - 1];
              const curr = sortedPoints[i];
              const timeStr = curr.time || new Date(curr.timestamp).toISOString();
              const timeLabel = formatTimeLabel(timeStr);
              
              // 計算累積電量差值
              const prevEnergy = prev.watt_hour ?? prev.energy ?? prev.max ?? 0;
              const currEnergy = curr.watt_hour ?? curr.energy ?? curr.max ?? 0;
              const energyDiff = currEnergy - prevEnergy;
              
              chartData.push({
                hour: timeLabel,
                watt: energyDiff >= 0 ? energyDiff : null
              });
            }
          }
          // ✅ 方法4: 直接是陣列格式
          else if (Array.isArray(deviceData)) {
            console.log(`📊 [Energy] Using array format, ${deviceData.length} points`);
            for (const point of deviceData) {
              const hour = point.time ? formatTimeLabel(point.time) : (point.timestamp ? formatTimeLabel(new Date(point.timestamp * 1000).toISOString()) : '');
              const energyValue = point.watt_hour ?? point.energy ?? point.value ?? null;
              chartData.push({
                hour,
                watt: typeof energyValue === 'number' ? energyValue : null
              });
            }
          }
          // ✅ 方法5: 嘗試從 report 頂層獲取
          else if (report.chartData && Array.isArray((report as any).chartData)) {
            console.log(`📊 [Energy] Using top-level chartData format`);
            for (const point of (report as any).chartData) {
              const hour = point.time ? formatTimeLabel(point.time) : '';
              const energyValue = point.energy ?? point.watt_hour ?? null;
              chartData.push({
                hour,
                watt: typeof energyValue === 'number' ? energyValue : null
              });
            }
          }
          else {
            console.log(`📊 [Energy] Unknown format, deviceData keys:`, deviceData ? Object.keys(deviceData) : 'null');
            console.log(`📊 [Energy] Full report structure:`, JSON.stringify(report, null, 2).substring(0, 2000));
          }
          
          console.log(`📊 [Energy] Final chartData:`, chartData.length, 'points');
          
          // ✅ Check if this is still the current request (prevent race condition)
          if (energyRequestCounterRef.current !== thisRequestId) {
            console.log(`📊 [Energy] ⚠️ Request #${thisRequestId} is stale, ignoring (current: #${energyRequestCounterRef.current})`);
            return;
          }
          
          console.log(`📊 [Energy] ✅ Request #${thisRequestId} - Setting chart data for ${deviceSN}`);
          setEnergyChartData(chartData);
        } else {
          if (energyRequestCounterRef.current === thisRequestId) {
            setEnergyChartData([]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch energy history:', error);
        if (energyRequestCounterRef.current === thisRequestId) {
          setEnergyChartData([]);
        }
      } finally {
        if (energyRequestCounterRef.current === thisRequestId) {
          setIsLoadingEnergyChart(false);
        }
      }
    };
    
    fetchEnergyHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice?.deviceId]);
  
  // Device favorites (favoriteOpenPercent for curtains/blinds)
  const [deviceFavorites, setDeviceFavorites] = useState<Record<string, number>>({});
  
  // Three-phase meter selection (single select)
  const [selectedThreePhaseMeter, setSelectedThreePhaseMeter] = useState<string | null>(() => {
    try {
      const saved = localStorage.getItem('intellispace-selected-three-phase-meter');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [showThreePhaseMeterSelector, setShowThreePhaseMeterSelector] = useState(false);

  // Load selections from Firestore on mount
  useEffect(() => {
    const loadSelectionsFromFirestore = async () => {
      try {
        const selections = await userPreferencesApi.getIntelliSpaceSelections();
        if (selections.powerMeters?.length) {
          setSelectedPowerMeters(selections.powerMeters);
          localStorage.setItem('intellispace-selected-power-meters', JSON.stringify(selections.powerMeters));
        }
        if (selections.switches?.length) {
          setSelectedSwitches(selections.switches);
          localStorage.setItem('intellispace-selected-switches', JSON.stringify(selections.switches));
        }
        if (selections.hvac?.length) {
          setSelectedHvac(selections.hvac);
          localStorage.setItem('intellispace-selected-hvac', JSON.stringify(selections.hvac));
        }
        if (selections.thSensors?.length) {
          setSelectedThSensors(selections.thSensors);
          localStorage.setItem('intellispace-selected-th-sensors', JSON.stringify(selections.thSensors));
        }
        if (selections.lights?.length) {
          setSelectedLights(selections.lights);
          localStorage.setItem('intellispace-selected-lights', JSON.stringify(selections.lights));
        }
        if (selections.deviceOrder?.length) {
          setDeviceOrder(selections.deviceOrder);
          localStorage.setItem('intellispace-device-order', JSON.stringify(selections.deviceOrder));
        }
        if (selections.excludedRankingDevices) {
          setExcludedRankingDevices(selections.excludedRankingDevices);
          localStorage.setItem('intellispace-excluded-ranking-devices', JSON.stringify(selections.excludedRankingDevices));
        }
        if (selections.baselineLightingPower !== undefined) {
          setBaselineLightingPower(selections.baselineLightingPower);
        }
        if (selections.baselineHvacPower !== undefined) {
          setBaselineHvacPower(selections.baselineHvacPower);
        }
        logger.log('✅ [IntelliSpace] Loaded selections from Firestore:', selections);
        
        // Load device favorites (favoriteOpenPercent) from deviceSettings
        // Keys are device.deviceId values (consistent with save function)
        const prefs = await userPreferencesApi.getUserPreferences();
        if (prefs.deviceSettings) {
          const favorites: Record<string, number> = {};
          Object.entries(prefs.deviceSettings).forEach(([deviceId, settings]) => {
            if (settings?.favoriteOpenPercent !== undefined) {
              favorites[deviceId] = settings.favoriteOpenPercent;
            }
          });
          setDeviceFavorites(favorites);
          logger.log('✅ [IntelliSpace] Loaded device favorites:', favorites);
        }
        
        // Load multi-channel display modes from userPreferences
        const multiChannelModes = await userPreferencesApi.getAllMultiChannelDisplayModes();
        if (multiChannelModes && Object.keys(multiChannelModes).length > 0) {
          // Convert 'merged' modes to Set (only 'merged' devices are in the Set)
          const mergedSet = new Set<string>();
          Object.entries(multiChannelModes).forEach(([baseSN, mode]) => {
            if (mode === 'merged') {
              mergedSet.add(baseSN);
            }
          });
          setMergedDeviceSNs(mergedSet);
          logger.log('✅ [IntelliSpace] Loaded multi-channel display modes:', multiChannelModes);
        }
      } catch (error) {
        logger.error('❌ [IntelliSpace] Failed to load selections from Firestore:', error);
      } finally {
        setSelectionsLoaded(true);
      }
    };
    loadSelectionsFromFirestore();
  }, []);

  // Persist selections to localStorage and Firestore (debounced)
  const saveSelectionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Skip if selections not yet loaded from Firestore
    if (!selectionsLoaded) return;
    
    // Save to localStorage immediately
    localStorage.setItem('intellispace-selected-power-meters', JSON.stringify(selectedPowerMeters));
    localStorage.setItem('intellispace-selected-switches', JSON.stringify(selectedSwitches));
    localStorage.setItem('intellispace-selected-hvac', JSON.stringify(selectedHvac));
    localStorage.setItem('intellispace-selected-th-sensors', JSON.stringify(selectedThSensors));
    localStorage.setItem('intellispace-selected-lights', JSON.stringify(selectedLights));
    localStorage.setItem('intellispace-selected-presence-sensors', JSON.stringify(selectedPresenceSensors));
    localStorage.setItem('intellispace-selected-hvac-meters', JSON.stringify(selectedHvacMeters));
    localStorage.setItem('intellispace-selected-lighting-meters', JSON.stringify(selectedLightingMeters));
    localStorage.setItem('intellispace-selected-three-phase-meter', JSON.stringify(selectedThreePhaseMeter));
    
    // Debounce Firestore save (1 second)
    if (saveSelectionsTimeoutRef.current) {
      clearTimeout(saveSelectionsTimeoutRef.current);
    }
    saveSelectionsTimeoutRef.current = setTimeout(async () => {
      try {
        await userPreferencesApi.saveIntelliSpaceSelections({
          powerMeters: selectedPowerMeters,
          switches: selectedSwitches,
          hvac: selectedHvac,
          thSensors: selectedThSensors,
          lights: selectedLights,
          excludedRankingDevices: excludedRankingDevices
        });
        logger.log('✅ [IntelliSpace] Selections saved to Firestore');
      } catch (error) {
        logger.error('❌ [IntelliSpace] Failed to save selections to Firestore:', error);
      }
    }, 1000);
    
    return () => {
      if (saveSelectionsTimeoutRef.current) {
        clearTimeout(saveSelectionsTimeoutRef.current);
      }
    };
  }, [selectedPowerMeters, selectedSwitches, selectedHvac, selectedThSensors, selectedLights, selectedHvacMeters, selectedLightingMeters, selectedThreePhaseMeter, selectionsLoaded]);
  
  // Hourly chart data states
  const [hourlyPowerData, setHourlyPowerData] = useState<Array<{hour: string, power: number, energy?: number}>>([]);
  const [yesterdayPowerData, setYesterdayPowerData] = useState<Array<{hour: string, power: number, energy?: number}>>([]);
  const [lastWeekPowerData, setLastWeekPowerData] = useState<Array<{hour: string, power: number, energy?: number}>>([]);
  const [hourlyClimateData, setHourlyClimateData] = useState<Array<{hour: string, temp: number | null, humidity: number | null}>>([]);
  const [chartCurrentHour, setChartCurrentHour] = useState(() => new Date().getHours()); // Dynamic hour for chart split
  // Today's energy per device (for ranking) - deviceId -> kWh
  const [todayEnergyPerDevice, setTodayEnergyPerDevice] = useState<Record<string, number>>({});
  
  // Weekly energy per device (for 7-day ranking) - deviceId -> kWh
  const [weeklyEnergyPerDevice, setWeeklyEnergyPerDevice] = useState<Record<string, number>>({});
  const [isLoadingWeeklyEnergy, setIsLoadingWeeklyEnergy] = useState(false);
  
  // Monthly chart data states
  const [monthlyPowerData, setMonthlyPowerData] = useState<Array<{day: string, power: number, energy?: number}>>([]);
  const [lastMonthPowerData, setLastMonthPowerData] = useState<Array<{day: string, power: number, energy?: number}>>([]);
  
  // ✅ 昨日全日用電量（日粒度 API）
  const [yesterdayDailyEnergy, setYesterdayDailyEnergy] = useState<number>(0);
  
  // ✅ 今日用電量（即時計算：當前 watt_hour - 00:00 基準值）
  // 初始化時嘗試從 localStorage 讀取快取值
  const [todayMinuteEnergy, setTodayMinuteEnergy] = useState<number>(() => {
    // 這裡無法訪問 selectedPowerMeters，所以先設為 0，稍後在 useEffect 中處理
    return 0;
  });
  // ✅ 今日用電量 loading 狀態（如果有快取則不顯示 loading）
  const [isTodayEnergyLoading, setIsTodayEnergyLoading] = useState<boolean>(true);
  
  // ✅ 00:00 watt_hour 基準值（用於即時計算今日用電量）
  // Map: deviceSN -> 00:00 watt_hour baseline
  const wattHourBaselineRef = useRef<Map<string, number>>(new Map());
  // Map: deviceSN -> 當前 watt_hour（WebSocket 即時更新）
  const currentWattHourRef = useRef<Map<string, number>>(new Map());
  // ✅ WebSocket 最後收到 watt_hour 更新的時間（用於 fallback polling 判斷）
  const lastWsWattHourUpdateRef = useRef<number>(Date.now());

  // Refresh interval - dynamically read from settings
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const storedSettings = localStorage.getItem('ultron-energy-settings');
    if (storedSettings) {
      try {
        const parsedSettings = JSON.parse(storedSettings);
        return (parsedSettings.powerMeterRefreshInterval || 30) * 1000;
      } catch { return 30000; }
    }
    return 30000;
  });

  // Listen for settings changes (e.g., when user changes refresh interval in Settings)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedSettings = localStorage.getItem('ultron-energy-settings');
      if (storedSettings) {
        try {
          const parsedSettings = JSON.parse(storedSettings);
          const newInterval = (parsedSettings.powerMeterRefreshInterval || 30) * 1000;
          setRefreshInterval(newInterval);
          logger.log(`📊 [IntelliSpace] Refresh interval updated to ${newInterval}ms`);
        } catch { /* ignore */ }
      }
    };
    
    // Listen for storage events (from other tabs) and custom events (same tab)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('settings-updated', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('settings-updated', handleStorageChange);
    };
  }, []);

  const isDebugMode = localStorage.getItem('ultron-debug-mode') === 'true';

  // Device filtering logic for IntelliSpace
  // Now disabled - include ALL devices without filtering
  const filterIntelliSpaceDevices = useCallback((_device: any): boolean => {
    // No filtering - include all devices
    return true;
  }, []);

  // Determine device type based on API-returned data (type, traits, attributes, sensorProfile)
  // NO SN prefix checks - use only API metadata for classification
  const getDeviceType = useCallback((device: any): IntelliSpaceDevice['deviceType'] => {
    const traits = device.traits || [];
    // Support both formats: normalized sensorProfile (new) or attributes.sensorProfile (old)
    const sensorProfile = device.sensorProfile || device.attributes?.sensorProfile || {};
    const rawType = (device.type || '').toUpperCase();
    const attributeType = (device.attributes?.type || device.attributes?.["1"]?.type || '').toUpperCase();
    
    // Get detector attributes for sensor classification (from API)
    // ✅ FIX: Check multiple possible locations for detectorEvents/detectorStates
    const deviceAttributes = device.attributes || {};
    const capAttributes = device.capabilities?.[0]?.attributes || {};
    const detectorEvents: string[] = deviceAttributes.detectorEvents || 
                                     capAttributes.detectorEvents || 
                                     [];
    const detectorStates: string[] = deviceAttributes.detectorStates || 
                                     capAttributes.detectorStates || 
                                     [];
    
    // ═══════════════════════════════════════════════════════════════════════════
    // CAPABILITY FLAGS (calculated once, used for classification)
    // ═══════════════════════════════════════════════════════════════════════════
    
    // Power metrics (for power meter / switch with meter)
    const hasPowerMetrics = sensorProfile.watt !== undefined || 
                           sensorProfile.watt_hour !== undefined ||
                           sensorProfile.voltage !== undefined || 
                           sensorProfile.current !== undefined ||
                           sensorProfile.real_power !== undefined ||
                           device.watt !== undefined ||
                           device.wattHour !== undefined ||
                           device.voltage !== undefined ||
                           device.current !== undefined ||
                           device.realPower !== undefined;
    
    // Three-phase power meter detection (has R/S/T phase attributes)
    const attrs = deviceAttributes?.["1"] || deviceAttributes || {};
    const hasThreePhaseMetrics = attrs.voltage_r !== undefined || 
                                 attrs.current_r !== undefined ||
                                 attrs.real_power_r !== undefined ||
                                 sensorProfile.voltage_r !== undefined ||
                                 sensorProfile.current_r !== undefined ||
                                 sensorProfile.real_power_r !== undefined;
    
    // Temperature & Humidity (for TH sensor)
    const hasTempHumidity = sensorProfile.temp !== undefined || 
                           sensorProfile.rh !== undefined ||
                           sensorProfile.temperature !== undefined || 
                           sensorProfile.humidity !== undefined ||
                           device.temperature !== undefined ||
                           device.humidity !== undefined;
    
    // OnOff trait (controllable devices)
    const hasOnOffTrait = traits.includes('OnOff');
    
    // Button capability (from detectorEvents with click/doubleClick/longPress)
    // ✅ FIX: Button is ONLY when detectorEvents contains button-specific events
    // Detector trait alone is NOT enough - presence sensors also have Detector trait!
    const hasButtonCapability = detectorEvents.includes('click') || 
                                detectorEvents.includes('doubleClick') || 
                                detectorEvents.includes('longPress');
    
    // Door sensor capability (from detectorEvents)
    const hasDoorCapability = detectorEvents.includes('opened') || 
                              detectorEvents.includes('closed');
    
    // Motion sensor capability (from detectorEvents)
    const hasMotionCapability = detectorEvents.includes('motionDetected');
    
    // Occupancy/Presence capability (from detectorStates and traits)
    const hasOccupancyCapability = detectorStates.includes('occupancy') || 
                                   traits.includes('Occupancy') ||
                                   sensorProfile.presence !== undefined ||
                                   sensorProfile.occupancy !== undefined ||
                                   device.occupancy !== undefined;
    
    // Lux/Illuminance capability
    const hasLux = sensorProfile.lux !== undefined || 
                  traits.includes('Illuminance') ||
                  device.luxValue !== undefined ||
                  (device as any).lux !== undefined;
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 1: EXPLICIT DEVICE TYPES (Trust API's type classification first)
    // ═══════════════════════════════════════════════════════════════════════════
    
    // HVAC - AC units with temperature control
    if (rawType === 'AC_UNIT' || attributeType === 'AC_UNIT') {
      return 'hvac';
    }
    
    // FAN - standalone fans (no temperature control)
    if (rawType === 'FAN' || attributeType === 'FAN') {
      return 'fan';
    }
    
    // ERV - Energy Recovery Ventilator / 全熱交換機
    // ERV devices may come as 'HEATER' type but have SensorState trait with PM2.5/CO2
    // or device name contains "全熱交換"
    const deviceDisplayName = device.deviceName || device.nickname || '';
    const isErvByName = deviceDisplayName.includes('全熱交換') || 
                        deviceDisplayName.toLowerCase().includes('erv') ||
                        deviceDisplayName.toLowerCase().includes('ventilator');
    const hasSensorState = traits.includes('SensorState');
    const hasErvSensors = hasSensorState && rawType === 'HEATER';
    
    if (rawType === 'ERV' || rawType === 'VENTILATOR' || rawType === 'HRV' || 
        rawType === 'ENERGY_RECOVERY_VENTILATOR' || attributeType === 'ERV' ||
        isErvByName || hasErvSensors) {
      return 'erv';
    }
    
    // HEATER - floor heating / heater devices (地熱)
    // These have TemperatureControl trait and can be controlled (but NOT ERV)
    if (rawType === 'HEATER') {
      return 'hvac'; // Treat as HVAC for control purposes
    }
    
    // LIGHT - lighting devices
    if (rawType === 'LIGHT' || rawType === 'BULB' || rawType === 'LED_STRIP') {
      return 'light';
    }
    
    // OUTLET / PLUG - smart plugs (may have power meter combo)
    if (rawType === 'OUTLET' || rawType === 'PLUG' || attributeType === 'OUTLET' || attributeType === 'PLUG') {
      return 'outlet';
    }
    
    // SWITCH - smart switches (may have power meter combo)
    if (rawType === 'SWITCH') {
      return 'switch';
    }
    
    // POWER_METER - dedicated power meters (check for 3-phase first)
    if (rawType === 'POWER_METER') {
      // Three-phase power meter detection
      if (hasThreePhaseMetrics) {
        return 'power_meter_3phase';
      }
      return 'power_meter';
    }
    
    // BLINDS / ROLLER_SHADE - motorized blinds (捲簾)
    if (rawType === 'BLINDS' || rawType === 'BLIND' || rawType === 'ROLLER_SHADE') {
      return 'blind';
    }
    
    // CURTAIN - motorized curtains (窗簾)
    if (rawType === 'CURTAIN') {
      return 'curtain';
    }
    
    // ✅ Bridge devices - not controllable, just show status
    if (rawType === 'BRIDGE' || rawType === 'ULTRON_BRIDGE' || 
        rawType.includes('BRIDGE')) {
      return 'bridge';
    }
    
    // ✅ Matter Controller / Gateway devices - not controllable, just show status
    if (rawType === 'ULTRON_MATTERCONTROLLER' || rawType === 'MATTERCONTROLLER' || 
        rawType === 'GATEWAY' || rawType === 'HUB' ||
        traits.includes('MatterController')) {
      return 'controller';
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 2: MULTI-SENSOR CHECK (MUST come before any individual sensor type checks!)
    // ═══════════════════════════════════════════════════════════════════════════
    
    // ✅ Multi-Sensor Device - has 3+ sensor capabilities (TH + occupancy + lux, etc.)
    // Check this FIRST before any individual sensor type to properly classify composite sensors
    const sensorCapabilityCount = [
      hasTempHumidity,           // Temperature/Humidity
      hasOccupancyCapability,    // Presence/Occupancy
      hasLux,                    // Lux/Illuminance
      hasMotionCapability,       // Motion detection
    ].filter(Boolean).length;
    
    // ✅ Return multi_sensor if 3+ sensor capabilities
    if (sensorCapabilityCount >= 3) {
      return 'multi_sensor';
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 3: INDIVIDUAL SENSOR TYPES (only if NOT multi_sensor)
    // ═══════════════════════════════════════════════════════════════════════════
    
    // For ULTRON_SENSOR or SENSOR type with ONLY temp/humidity (not multi-sensor)
    if (rawType === 'ULTRON_SENSOR' || rawType === 'SENSOR' || rawType === 'TH_SENSOR') {
      if (hasTempHumidity && !hasOccupancyCapability && !hasLux) {
        return 'th_sensor';
      }
    }
    
    // Button - has click/doubleClick/longPress events
    if (hasButtonCapability) {
      return 'button';
    }
    
    // Door Sensor - has opened/closed events
    if (hasDoorCapability) {
      return 'door_sensor';
    }
    
    // Motion Sensor - has motionDetected events
    if (hasMotionCapability) {
      return 'motion_sensor';
    }
    
    // Presence Sensor - ONLY devices with occupancy/presence detection
    if (hasOccupancyCapability) {
      return 'presence_sensor';
    }
    
    // Lux Sensor - has illuminance/lux but no occupancy or temp/humidity
    if (hasLux && !hasTempHumidity && !hasOccupancyCapability) {
      return 'lux_sensor';
    }
    
    // TH Sensor - has temperature/humidity but no occupancy
    if (hasTempHumidity && !hasOccupancyCapability) {
      return 'th_sensor';
    }
    
    // Power Meter - has power metrics but NO OnOff trait (pure meter, not switch)
    if (hasPowerMetrics && !hasOnOffTrait) {
      // Three-phase power meter detection
      if (hasThreePhaseMetrics) {
        return 'power_meter_3phase';
      }
      return 'power_meter';
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // STEP 3: TRAIT-BASED FALLBACK (for devices with unknown type but known traits)
    // ═══════════════════════════════════════════════════════════════════════════
    
    // Light - has Brightness or ColorSetting traits
    if (traits.includes('Brightness') || traits.includes('ColorSetting')) {
      return 'light';
    }
    
    // HVAC - has TemperatureSetting trait
    if (traits.includes('TemperatureSetting')) {
      return 'hvac';
    }
    
    // Fan - has FanSpeed but NO TemperatureSetting
    if (traits.includes('FanSpeed') && !traits.includes('TemperatureSetting') && !traits.includes('HumiditySetting')) {
      return 'fan';
    }
    
    // Dehumidifier - has HumiditySetting trait
    if (traits.includes('HumiditySetting')) {
      return 'dehumidifier';
    }
    
    // Switch with meter - has OnOff trait and power metrics
    if (hasOnOffTrait && hasPowerMetrics) {
      return 'switch';
    }
    
    // Switch - has OnOff trait only
    if (hasOnOffTrait) {
      return 'switch';
    }
    
    // ✅ FIX: Don't default to switch - sensors without OnOff trait are NOT controllable
    // If no controllable traits detected, treat as controller (non-controllable device)
    return 'controller'; // Safe fallback - non-controllable device type
  }, []);

  // Load devices
  const loadDevices = useCallback(async (forceRefresh: boolean = false, isManualRefresh: boolean = false) => {
    logger.log('🚀 [IntelliSpace] loadDevices called:', { forceRefresh, isManualRefresh });
    try {
      if (devices.length === 0 && !isManualRefresh) {
        setIsLoading(true);
      }
      if (isManualRefresh) {
        setIsRefreshing(true);
      }
      setError(null);

      // Load device zones from Firestore (primary) via userPreferencesApi
      logger.log('📂 [IntelliSpace] Fetching virtual zones...');
      const zones = await userPreferencesApi.getVirtualZones('intelliSpace');
      logger.log('📂 [IntelliSpace] Virtual zones loaded:', { count: zones.length, zones: zones.map(z => ({ id: z.id, name: z.name, deviceCount: z.devices.length })) });

      // Load device control groups
      const controlGroups = await userPreferencesApi.getDeviceControlGroups();
      setDeviceControlGroups(controlGroups);
      logger.log('📂 [IntelliSpace] Device control groups loaded:', controlGroups.length);
      
      // ✅ Query real locations from API to resolve groupId to location name
      let locationsMap = new Map<string, string>(); // Map groupId -> location name
      try {
        const locationsResult = await ultronTuringApi.queryLocations();
        logger.log('📍 [IntelliSpace] QueryLocations result:', locationsResult);
        
        // Handle different response formats
        let locations: Array<{ id?: string; groupId?: string; name?: string }> = [];
        if (locationsResult.success && locationsResult.data) {
          if (Array.isArray(locationsResult.data)) {
            locations = locationsResult.data;
          } else if (typeof locationsResult.data === 'object' && locationsResult.data !== null) {
            const dataObj = locationsResult.data as { locations?: any[]; groups?: any[] };
            locations = dataObj.locations || dataObj.groups || [];
          }
        } else if (Array.isArray(locationsResult)) {
          locations = locationsResult;
        }
        
        // Build map: groupId -> name (support both id and groupId fields)
        locations.forEach(loc => {
          const id = loc.id || loc.groupId;
          const name = loc.name;
          if (id && name) {
            locationsMap.set(id, name);
          }
        });
        
        logger.log(`📍 [IntelliSpace] Loaded ${locationsMap.size} locations for name resolution`);
      } catch (error) {
        logger.warn('⚠️ [IntelliSpace] Failed to query locations, will use virtual groups only:', error);
      }
      
      // Get all device SNs that are in at least one zone
      const devicesInZones = new Set<string>();
      zones.forEach((zone: { devices: string[] }) => {
        zone.devices.forEach((deviceSN: string) => devicesInZones.add(deviceSN));
      });
      
      logger.log(`📋 [IntelliSpace] Found ${devicesInZones.size} devices in ${zones.length} zones`);

      if (devicesInZones.size === 0) {
        setError(t.intelliSpace?.noDevicesImported || 'No devices imported. Please go to Device Manager to import devices.');
        setDevices([]);
        setDeviceZones(zones);
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      // Update deviceZones state
      setDeviceZones(zones);

      // Load imported devices from Firestore (source of truth - no localStorage fallback)
      let importedDevices = await userPreferencesApi.getImportedDevices('intelliSpace');
      logger.log(`📋 [IntelliSpace] Loaded ${importedDevices.length} devices from Firestore`);
      
      // ✅ Extract deviceSNs from simplified importedDevices (which may only have deviceSN field)
      // ✅ 規劃原則：importedDevices 應該包含所有已匯入的設備
      // 如果 importedDevices 不為空，使用所有 importedDevices（不限制在 groups 中）
      // 如果 importedDevices 為空，才使用 devicesInZones
      const originalImportedCount = importedDevices.length;
      
      if (importedDevices.length > 0) {
        // 使用所有 importedDevices，但記錄哪些在 groups 中，哪些不在
        const devicesInZonesList: string[] = [];
        const devicesNotInZones: string[] = [];
        
        importedDevices.forEach((d: any) => {
          const deviceSN = d.deviceSN || d.sn || d.deviceId;
          if (!deviceSN) return;
          
          // Check if in groups (exact or baseSN match)
          let inGroup = devicesInZones.has(deviceSN);
          if (!inGroup) {
            const baseSN = deviceSN.split('#')[0];
            devicesInZones.forEach(groupDeviceSN => {
              if (inGroup) return;
              const groupBaseSN = groupDeviceSN.split('#')[0];
              if (baseSN === groupBaseSN) {
                inGroup = true;
              }
            });
          }
          
          if (inGroup) {
            devicesInZonesList.push(deviceSN);
          } else {
            devicesNotInZones.push(deviceSN);
          }
        });
        
        logger.log(`📋 [IntelliSpace] importedDevices: ${originalImportedCount} total, ${devicesInZonesList.length} in groups, ${devicesNotInZones.length} not in groups`);
        if (devicesNotInZones.length > 0) {
          logger.warn(`⚠️ [IntelliSpace] ${devicesNotInZones.length} devices in importedDevices but not in any group: ${devicesNotInZones.join(', ')}`);
        }
        
        // ✅ 使用所有 importedDevices，不過濾（因為 importedDevices 是單一資料來源）
        // 只過濾掉沒有 deviceSN 的設備
        importedDevices = importedDevices.filter((d: any) => {
          const deviceSN = d.deviceSN || d.sn || d.deviceId;
          return !!deviceSN;
        });
      } else {
        logger.log(`📋 [IntelliSpace] importedDevices is empty, will use devicesInZones (${devicesInZones.size} devices)`);
      }
      
      // ✅ FIX: ALWAYS use devicesInZones as source of truth for which devices to query
      // devicesInZones contains all devices in virtual groups (21 devices)
      // importedDevices may be out of sync or incomplete (12 devices)
      let deviceSNs: string[];
      
      // Convert devicesInZones Set to Array and normalize format
      const groupDeviceArray: string[] = [];
      devicesInZones.forEach(sn => groupDeviceArray.push(sn));
      deviceSNs = groupDeviceArray.map(sn => {
        // If deviceSN doesn't have endpoint suffix, add #1 for single-endpoint devices
        if (sn && !sn.includes('#')) {
          return `${sn}#1`;
        }
        return sn;
      }).filter((sn): sn is string => !!sn);
      
      logger.log(`📊 [IntelliSpace] Using devicesInZones as source: ${deviceSNs.length} devices (importedDevices had ${importedDevices.length})`);
      
      logger.log('📊 [IntelliSpace] Querying status for devices:', deviceSNs);
      const statusResponse = await ultronTuringApi.queryStatus(deviceSNs, { detailed: true });
      logger.log('📊 [IntelliSpace] QueryStatus response:', JSON.stringify(statusResponse, null, 2));

      // Create status map
      const statusMap: Record<string, any> = {};
      // Try multiple possible response structures
      const responseData = statusResponse?.devices || statusResponse?.data || statusResponse;
      
      logger.log('📊 [IntelliSpace] Processing responseData type:', typeof responseData, Array.isArray(responseData) ? 'array' : 'object');
      
      if (responseData) {
        if (typeof responseData === 'object' && !Array.isArray(responseData)) {
          Object.entries(responseData).forEach(([deviceId, status]: [string, any]) => {
            // Skip non-device properties like 'success', 'error', etc.
            if (typeof status !== 'object' || status === null) return;
            
            const flattenedStatus: Record<string, any> = { ...status };
            
            // Flatten nested structures
            if (status.stateData) {
              Object.assign(flattenedStatus, status.stateData);
            }
            if (status.iotState) {
              Object.assign(flattenedStatus, status.iotState);
            }
            // ✅ Fix: Also flatten state object and extract on/brightness/color from nested locations
            if (status.state) {
              Object.assign(flattenedStatus, status.state);
              // Extract on from currentModeSettings if direct on is not available
              if (flattenedStatus.on === undefined && status.state.currentModeSettings?.on !== undefined) {
                flattenedStatus.on = status.state.currentModeSettings.on;
              }
            }
            
            statusMap[deviceId] = flattenedStatus;
            logger.log(`📊 [IntelliSpace] Status for ${deviceId}:`, flattenedStatus);
          });
        } else if (Array.isArray(responseData)) {
          // ✅ Helper to flatten nested values: { lux: { value: [9] } } -> { lux: 9 }
          const flattenValues = (values: Record<string, any>) => {
            const flat: Record<string, any> = {};
            if (!values) return flat;
            
            Object.entries(values).forEach(([key, data]) => {
              if (data && typeof data === 'object' && 'value' in data) {
                // Handle array values: lux: { value: [9] } -> lux: 9
                flat[key] = Array.isArray(data.value) ? data.value[0] : data.value;
              }
            });
            return flat;
          };
          
          responseData.forEach((item: any) => {
            // ✅ Handle NEW format: { system: {...}, states: [...] }
            if (item.system && Array.isArray(item.states)) {
              const system = item.system;
              const baseSN = system.baseSN || system.sn;
              const online = system.online;
              
              if (baseSN) {
                // Store baseSN level status
                statusMap[baseSN] = { ...system, online, baseSN };
                
                // Process each endpoint's state
                item.states.forEach((state: any) => {
                  // ✅ FIX: Validate that we have a valid device identifier
                  // Priority: state.deviceSN > baseSN#endpoint > baseSN (fallback for single-endpoint devices)
                  let deviceSN: string;
                  if (state.deviceSN) {
                    deviceSN = state.deviceSN;
                  } else if (state.endpoint !== undefined && state.endpoint !== null) {
                    deviceSN = `${baseSN}#${state.endpoint}`;
                  } else {
                    // Fallback to baseSN for devices without explicit endpoint
                    deviceSN = baseSN;
                  }
                  
                  const values = state.values || {};
                  const flattenedValues = flattenValues(values);
                  
                  statusMap[deviceSN] = {
                    ...system,
                    online,
                    baseSN,
                    endpoint: state.endpoint,
                    type: state.type,
                    nickname: state.nickname,
                    values: values,
                    // Spread flattened values for direct access (e.g., device.occupancy, device.lux, device.watt)
                    ...flattenedValues
                  };
                  
                  // ✅ FIX: Also update baseSN entry with flattened values
                  // This is needed for merged devices (single/composite) that use baseSN as their deviceId
                  statusMap[baseSN] = {
                    ...statusMap[baseSN],
                    values: { ...(statusMap[baseSN]?.values || {}), ...values },
                    ...flattenedValues
                  };
                  
                  logger.log(`📊 [IntelliSpace] NEW format status for ${deviceSN} and ${baseSN}:`, {
                    online,
                    endpoint: state.endpoint,
                    valueKeys: Object.keys(values),
                    flattenedKeys: Object.keys(flattenedValues)
                  });
                });
              }
              return; // Skip old format processing for this item
            }
            
            // OLD format: Try multiple possible keys for device ID
            const deviceId = item.deviceSN || item.device || item.sn || item.deviceId;
            if (deviceId) {
              const flattenedStatus: Record<string, any> = { ...item };
              
              // Flatten stateData if exists
              if (item.stateData) {
                Object.assign(flattenedStatus, item.stateData);
              }
              // Flatten state if exists (QueryStatus uses 'state' not 'iotState')
              if (item.state) {
                Object.assign(flattenedStatus, item.state);
                // ✅ Fix: Extract on from currentModeSettings if direct on is not available
                if (flattenedStatus.on === undefined && item.state.currentModeSettings?.on !== undefined) {
                  flattenedStatus.on = item.state.currentModeSettings.on;
                }
              }
              if (item.iotState) {
                // Flatten iotState root level
                Object.assign(flattenedStatus, item.iotState);
                // Also preserve nested iotState["1"] for sensor data (UTU32A lux)
                if (item.iotState["1"]) {
                  flattenedStatus.iotState = item.iotState;
                }
              }
              // Also preserve attributes for nested access (e.g., attributes["1"] for UTU32A lux)
              if (item.attributes) {
                flattenedStatus.attributes = item.attributes;
                // Also flatten attributes["1"] if it exists (for sensor data)
                if (item.attributes["1"]) {
                  Object.assign(flattenedStatus, item.attributes["1"]);
                }
              }
              
              statusMap[deviceId] = flattenedStatus;
              
              // Also map by baseSN for easier lookup
              if (item.baseSN && item.baseSN !== deviceId) {
                statusMap[item.baseSN] = flattenedStatus;
              }
            }
          });
        }
      }
      
      logger.log('📊 [IntelliSpace] Final statusMap keys:', Object.keys(statusMap));

      // ✅ Query full device information from API (device shadow) to get traits, attributes, type, etc.
      // This is needed because importedDevices may only contain deviceSN field
      let fullDeviceData: Record<string, any> = {};
      try {
        // Extract baseSNs for queryDevices (API may return all endpoints for a baseSN)
        const baseSNs = Array.from(new Set(deviceSNs.map(sn => sn.split('#')[0])));
        const queriedDevices = await ultronTuringApi.queryDevices(undefined, undefined, {
          sns: baseSNs,
          detailed: true
        });
        
        // Build a map of deviceSN -> full device data
        // ✅ FIX: QueryDevices returns { system, capabilities[], customControls } structure
        // We need to expand capabilities[] into individual endpoint records
        queriedDevices.forEach((device: any) => {
          const system = device.system || device;
          const baseSN = system.baseSN || device.baseSN || (device.deviceSN || device.sn)?.split('#')[0];
          const capabilities = device.capabilities || [];
          // ✅ NEW: Extract customControls from QueryDevices response (contains mode/control labels)
          const customControls = device.customControls || {};
          
          // ✅ FIX: Get systemDeviceName from API response (ultronTuringApi.ts sets this)
          // Priority: device.systemDeviceName (from API) > system.deviceName > device.name
          const systemDeviceName = device.systemDeviceName || system.deviceName || device.name || device.displayName || baseSN;
          
          if (!baseSN) return;
          
          // If device has capabilities array, expand each endpoint into fullDeviceData
          if (capabilities.length > 0) {
            capabilities.forEach((cap: any) => {
              // ✅ FIX: QueryDevices returns deviceSN without endpoint suffix (e.g., "BF2Z01-4B265S3")
              // but QueryStatus returns with endpoint suffix (e.g., "BF2Z01-4B265S3#Light")
              // We need to use the format that matches statusMap: baseSN#endpoint
              const endpointSN = `${baseSN}#${cap.endpoint}`;
              // Also store with the original deviceSN for backward compatibility
              const originalDeviceSN = cap.deviceSN || baseSN;
              
              // Merge system info with capability info
              const expandedDevice = {
                ...system,
                ...cap,
                baseSN: baseSN,
                deviceSN: endpointSN,  // Use endpoint format to match statusMap
                sn: endpointSN,
                // Preserve endpoint-specific nickname, fallback to system deviceName
                nickname: cap.nickname || systemDeviceName,
                deviceName: cap.nickname || systemDeviceName,
                displayName: cap.nickname || systemDeviceName,
                // System device name (main device name, for merged multi-endpoint display)
                systemDeviceName: systemDeviceName,
                // System device type (for composite devices like DEHUMIDIFIER)
                systemDeviceType: system.deviceType,
                // ✅ NEW: Include customControls for mode/control labels (used by ERV, Fan, etc.)
                customControls: customControls
              };
              
              // Store with endpoint suffix (matches statusMap format)
              fullDeviceData[endpointSN] = expandedDevice;
              // Also store with original deviceSN (for backward compatibility)
              if (originalDeviceSN !== endpointSN) {
                fullDeviceData[originalDeviceSN] = expandedDevice;
              }
              
              logger.log(`📊 [IntelliSpace] Expanded capability: ${endpointSN} -> ${expandedDevice.nickname}`);
            });
            
            // Also store baseSN pointing to first capability (for backward compatibility)
            const firstCap = capabilities[0];
            fullDeviceData[baseSN] = {
              ...system,
              ...firstCap,
              baseSN: baseSN,
              deviceSN: firstCap.deviceSN || `${baseSN}#${firstCap.endpoint}`,
              nickname: firstCap.nickname || systemDeviceName,
              systemDeviceName: systemDeviceName,
              systemDeviceType: system.deviceType,
              // ✅ NEW: Include customControls for mode/control labels
              customControls: customControls
            };
          } else {
            // No capabilities array - use device as-is (legacy format)
            const deviceSN = device.deviceSN || device.sn || baseSN;
            fullDeviceData[deviceSN] = {
              ...device,
              systemDeviceName: systemDeviceName
            };
            if (baseSN !== deviceSN) {
              fullDeviceData[baseSN] = fullDeviceData[deviceSN];
            }
          }
        });
        
        logger.log(`📊 [IntelliSpace] Queried ${queriedDevices.length} full device records from API (${baseSNs.length} baseSNs)`);
        logger.log(`📊 [IntelliSpace] fullDeviceData keys:`, Object.keys(fullDeviceData));
        logger.log(`📊 [IntelliSpace] devicesInZones:`, Array.from(devicesInZones));
        
        // ✅ If queryDevices returned fewer devices than expected, try to extract device info from queryStatus response
        if (queriedDevices.length < deviceSNs.length && statusResponse) {
          logger.warn(`⚠️ [IntelliSpace] queryDevices returned ${queriedDevices.length} devices, but we queried ${deviceSNs.length} deviceSNs. Attempting to extract device info from queryStatus response...`);
          
          // Try to extract device metadata from statusResponse
          const responseData = statusResponse?.devices || statusResponse?.data || statusResponse;
          if (responseData && typeof responseData === 'object') {
            const statusDevices = Array.isArray(responseData) ? responseData : Object.values(responseData);
            statusDevices.forEach((statusItem: any) => {
              if (!statusItem || typeof statusItem !== 'object') return;
              
              const statusDeviceSN = statusItem.deviceSN || statusItem.device || statusItem.sn || statusItem.deviceId;
              if (!statusDeviceSN) return;
              
              // Only add if not already in fullDeviceData
              if (!fullDeviceData[statusDeviceSN] && !fullDeviceData[statusDeviceSN.split('#')[0]]) {
                // Extract device info from status response
                const resolvedName = statusItem.deviceName || statusItem.nickname || statusItem.name || '';
                const deviceInfo: any = {
                  deviceSN: statusDeviceSN,
                  sn: statusDeviceSN.split('#')[0],
                  baseSN: statusDeviceSN.split('#')[0],
                  online: statusItem.online !== false,
                  type: statusItem.type || 'SENSOR',
                  traits: statusItem.traits || [],
                  attributes: statusItem.attributes || {},
                  // ✅ FIX: Set all name fields for ERV detection in getDeviceType
                  systemDeviceName: resolvedName,
                  deviceName: resolvedName,
                  nickname: statusItem.nickname || resolvedName,
                  displayName: resolvedName
                };
                
                // Try to infer device type from attributes
                if (statusItem.attributes?.sensorProfile) {
                  deviceInfo.attributes = { ...deviceInfo.attributes, sensorProfile: statusItem.attributes.sensorProfile };
                }
                
                fullDeviceData[statusDeviceSN] = deviceInfo;
                const baseSN = statusDeviceSN.split('#')[0];
                if (baseSN !== statusDeviceSN) {
                  fullDeviceData[baseSN] = deviceInfo;
                }
                
                logger.log(`📊 [IntelliSpace] Extracted device info from queryStatus for ${statusDeviceSN}`);
              }
            });
          }
        }
      } catch (error) {
        logger.warn('⚠️ [IntelliSpace] Failed to query full device data, will use importedDevices as-is:', error);
        
        // ✅ Try to extract device info from queryStatus response as fallback
        try {
          const responseData = statusResponse?.devices || statusResponse?.data || statusResponse;
          if (responseData && typeof responseData === 'object') {
            const statusDevices = Array.isArray(responseData) ? responseData : Object.values(responseData);
            statusDevices.forEach((statusItem: any) => {
              if (!statusItem || typeof statusItem !== 'object') return;
              
              const statusDeviceSN = statusItem.deviceSN || statusItem.device || statusItem.sn || statusItem.deviceId;
              if (!statusDeviceSN) return;
              
              const fallbackName = statusItem.deviceName || statusItem.nickname || statusItem.name || '';
              const deviceInfo: any = {
                deviceSN: statusDeviceSN,
                sn: statusDeviceSN.split('#')[0],
                baseSN: statusDeviceSN.split('#')[0],
                online: statusItem.online !== false,
                type: statusItem.type || 'SENSOR',
                traits: statusItem.traits || [],
                attributes: statusItem.attributes || {},
                // ✅ FIX: Set all name fields for ERV detection
                systemDeviceName: fallbackName,
                deviceName: fallbackName,
                nickname: statusItem.nickname || fallbackName,
                displayName: fallbackName
              };
              
              fullDeviceData[statusDeviceSN] = deviceInfo;
              const baseSN = statusDeviceSN.split('#')[0];
              if (baseSN !== statusDeviceSN) {
                fullDeviceData[baseSN] = deviceInfo;
              }
            });
            
            logger.log(`📊 [IntelliSpace] Extracted ${Object.keys(fullDeviceData).length} device records from queryStatus as fallback`);
          }
        } catch (fallbackError) {
          logger.warn('⚠️ [IntelliSpace] Failed to extract device info from queryStatus as fallback:', fallbackError);
        }
      }

      // ✅ Filter importedDevices using full device data (if available) or fallback to importedDevices
      // Merge importedDevices with full device data from API
      // ✅ FIX: When importedDevices is empty but devicesInZones has items, use fullDeviceData directly
      let devicesWithFullData: any[];
      
      if (importedDevices.length === 0 && devicesInZones.size > 0 && Object.keys(fullDeviceData).length > 0) {
        logger.log(`📊 [IntelliSpace] Using fullDeviceData directly since importedDevices is empty`);
        // Use fullDeviceData directly - filter to ONLY devices explicitly in groups
        // ✅ FIX: Removed incorrect baseSN matching that caused unimported endpoints to appear
        devicesWithFullData = Object.values(fullDeviceData).filter((device: any) => {
          const deviceSN = device.deviceSN || device.sn || device.deviceId;
          if (!deviceSN) return false;
          
          // ✅ 匹配邏輯：精確匹配 OR baseSN 匹配
          // 1. 精確匹配：devicesInZones 包含完整 deviceSN
          // 2. baseSN 匹配：devicesInZones 包含 baseSN（用於展開的 multi-endpoint 設備）
          if (devicesInZones.has(deviceSN)) return true;
          
          const baseSN = deviceSN.split('#')[0];
          if (baseSN !== deviceSN && devicesInZones.has(baseSN)) return true;
          
          return false;
        });
        logger.log(`📊 [IntelliSpace] Filtered fullDeviceData to ${devicesWithFullData.length} devices in groups (exact or baseSN match)`);
      } else {
        // ✅ FIX: Use devicesInZones as source of truth, merge with importedDevices for metadata
        // This ensures all devices in virtual groups are shown, not just those in importedDevices
        
        // Build a map of importedDevices for quick lookup
        const importedDevicesMap = new Map<string, any>();
        importedDevices.forEach((device: any) => {
          const sn = device.deviceSN || device.sn || device.deviceId;
          if (sn) {
            importedDevicesMap.set(sn, device);
            // Also map by baseSN for fallback lookup
            const baseSN = sn.split('#')[0];
            if (baseSN !== sn && !importedDevicesMap.has(baseSN)) {
              importedDevicesMap.set(baseSN, device);
            }
          }
        });
        
        // Use fullDeviceData filtered by devicesInZones
        // ✅ 匹配邏輯：精確匹配 OR baseSN 匹配（用於展開的 multi-endpoint 設備）
        devicesWithFullData = Object.values(fullDeviceData).filter((device: any) => {
          const deviceSN = device.deviceSN || device.sn || device.deviceId;
          if (!deviceSN) return false;
          
          // 精確匹配 OR baseSN 匹配
          if (devicesInZones.has(deviceSN)) return true;
          
          const baseSN = deviceSN.split('#')[0];
          if (baseSN !== deviceSN && devicesInZones.has(baseSN)) return true;
          
          return false;
        }).map((fullDevice: any) => {
          const deviceSN = fullDevice.deviceSN || fullDevice.sn || fullDevice.deviceId;
          const baseSN = deviceSN?.split('#')[0];
          
          // Try to find matching importedDevice for user metadata (nickname, etc.)
          // ✅ FIX: Only use exact match for full property spread
          // baseSN fallback should ONLY be used for nickname/displayName, not all properties
          const importedDeviceExact = importedDevicesMap.get(deviceSN);
          const baseSNFallback = !importedDeviceExact && baseSN ? importedDevicesMap.get(baseSN) : undefined;
          
          // Merge: API data takes priority, but preserve user-set values from importedDevices
          // Priority for nickname/displayName:
          // 1. Exact match importedDevice (user explicitly set for this endpoint)
          // 2. fullDevice (from API - correct per-endpoint nickname)
          // 3. baseSN fallback importedDevice (only if nothing else available)
          return { 
            // Only spread exact match - avoids stale baseSN fallback data leaking through
            ...(importedDeviceExact || {}),
            ...fullDevice,      // API data overwrites
            deviceSN: deviceSN,
            // For nickname/displayName only, allow baseSN fallback as last resort
            nickname: importedDeviceExact?.nickname || fullDevice.nickname || baseSNFallback?.nickname,
            displayName: importedDeviceExact?.displayName || fullDevice.displayName || baseSNFallback?.displayName,
            // Explicitly preserve systemDeviceName from fullDevice for merged multi-endpoint display
            systemDeviceName: fullDevice.systemDeviceName
          };
        });
        
        logger.log(`📊 [IntelliSpace] Built devicesWithFullData from fullDeviceData filtered by devicesInZones: ${devicesWithFullData.length} devices`);
        logger.log(`📊 [IntelliSpace] devicesWithFullData SNs:`, devicesWithFullData.map((d: any) => d.deviceSN || d.sn));
      }

      logger.log(`📊 [IntelliSpace] Merged ${devicesWithFullData.length} devices with full data (${Object.keys(fullDeviceData).length} from API)`);

      // ✅ Now filter using filterIntelliSpaceDevices with full device data
      const filteredDevices = devicesWithFullData.filter(filterIntelliSpaceDevices);
      
      // Debug: Log devices that were filtered out
      const filteredOutDevices = devicesWithFullData.filter((d: any) => !filterIntelliSpaceDevices(d));
      if (filteredOutDevices.length > 0) {
        logger.warn(`⚠️ [IntelliSpace] ${filteredOutDevices.length} devices filtered OUT:`, filteredOutDevices.map((d: any) => ({
          sn: d.deviceSN || d.sn,
          type: d.type,
          traits: d.traits,
          hasSensorProfile: !!(d.sensorProfile || d.attributes?.sensorProfile)
        })));
      }
      
      if (filteredDevices.length === 0) {
        // ✅ Enhanced error logging to help debug "時有時無" issue
        logger.warn('⚠️ [IntelliSpace] No compatible devices found after filtering:', {
          importedDevicesCount: importedDevices.length,
          devicesWithFullDataCount: devicesWithFullData.length,
          fullDeviceDataCount: Object.keys(fullDeviceData).length,
          sampleDevices: devicesWithFullData.slice(0, 3).map((d: any) => ({
            deviceSN: d.deviceSN || d.sn || d.deviceId,
            hasTraits: !!d.traits,
            hasAttributes: !!d.attributes,
            hasType: !!d.type,
            traits: d.traits,
            type: d.type
          }))
        });
        setError(t.intelliSpace?.noDevicesImported || 'No compatible devices found.');
        setDevices([]);
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      logger.log(`📊 [IntelliSpace] Filtered to ${filteredDevices.length} IntelliSpace-compatible devices (from ${devicesWithFullData.length} merged devices)`);

      // Map devices with status
      const mappedDevices: IntelliSpaceDevice[] = filteredDevices.map((device: any) => {
        // ✅ Use deviceSN field (from simplified importedDevices or full device data)
        const originalSN = device.deviceSN || device.sn || device.deviceId;
        const baseSN = originalSN.split('#')[0];
        // ✅ For single-endpoint devices, use baseSN#1 as fullSN for mapping
        const fullSN = originalSN.includes('#') ? originalSN : `${baseSN}#1`;
        
        // Try multiple lookup strategies for statusMap
        // Priority order:
        // 1. Try exact match with originalSN first (preserves #1, #2, #Light etc.)
        // 2. Try fullSN if different from originalSN
        // 3. Try known endpoint names (e.g., #Light, #FAN) for devices without explicit endpoint
        // 4. Try baseSN (only has system info, no state!)
        
        let realTimeStatus = statusMap[originalSN];
        let actualDeviceSN = originalSN;
        
        // If originalSN doesn't have endpoint, try fullSN (#1 suffix)
        if (!realTimeStatus && fullSN !== originalSN) {
          realTimeStatus = statusMap[fullSN];
          if (realTimeStatus) actualDeviceSN = fullSN;
        }
        
        // ✅ FIX: Try known endpoint names when:
        // 1. originalSN has no # (single endpoint device)
        // 2. OR originalSN has numeric endpoint (#1, #2) but NOT found - might be #Light, #FAN format
        // IMPORTANT: Do NOT remap #2 to #1 - only remap numeric endpoints to named endpoints
        if (!realTimeStatus) {
          const originalEndpoint = originalSN.includes('#') ? originalSN.split('#')[1] : null;
          const isNumericEndpoint = originalEndpoint && /^\d+$/.test(originalEndpoint);
          
          // For numeric endpoints not found, try named endpoints (e.g., #1 -> #Light)
          // For named endpoints, only try exact numeric match
          const endpointsToTry = isNumericEndpoint
            ? ['Light', 'FAN', 'CURTAIN', 'OUTLET1', 'SENSOR', 'ULTRON_SENSOR', 'DEHUMIDIFIER']
            : (!originalEndpoint ? ['Light', 'FAN', 'CURTAIN', 'OUTLET1', 'SENSOR', 'ULTRON_SENSOR', 'DEHUMIDIFIER', '1', '2', '3', '4'] : []);
          
          for (const ep of endpointsToTry) {
            const epKey = `${baseSN}#${ep}`;
            if (statusMap[epKey]) {
              realTimeStatus = statusMap[epKey];
              actualDeviceSN = epKey;
              logger.log(`📍 [StatusMap] Found ${actualDeviceSN} via knownEndpoints (original: ${originalSN})`);
              break;
            }
          }
        }
        
        if (!realTimeStatus) {
          // ✅ FIX: 優先從 statusMap 中查找包含 baseSN 的完整 deviceSN
          // 這樣可以確保 deviceId 是完整的 deviceSN（如 UT3702-xxx#OUTLET1）
          // 而不是 baseSN（UT3702-xxx），確保 GenerateReport API 能正確返回數據
          const matchingKey = Object.keys(statusMap).find(key => 
            key.startsWith(baseSN + '#') && statusMap[key]
          );
          if (matchingKey) {
            realTimeStatus = statusMap[matchingKey];
            actualDeviceSN = matchingKey;
            logger.log(`📍 [StatusMap] Found complete deviceSN ${matchingKey} for baseSN ${baseSN}`);
          } else {
            // Fallback to baseSN only if no complete deviceSN found
            realTimeStatus = statusMap[baseSN];
            if (realTimeStatus) actualDeviceSN = baseSN;
          }
        }
        
        if (realTimeStatus) {
          // ✅ Debug: Log all possible locations for 'on' state
          logger.log(`📊 [IntelliSpace] Found status for ${actualDeviceSN} (tried: ${fullSN}):`, {
            directOn: realTimeStatus.on,
            endpoint: realTimeStatus.endpoint,
            valuesOn: realTimeStatus.values?.on,
            brightness: realTimeStatus.brightness,
            allKeys: Object.keys(realTimeStatus).slice(0, 15) // limit keys
          });
          
          // ✅ FIX: Merge realTimeStatus into device BEFORE calling getDeviceType
          // This ensures status-based values (occupancy, lux, temperature) are available for classification
          // Use robust extraction to handle nested formats like { value: [X] }
          const extractValue = (val: any): any => {
            if (val === undefined || val === null) return undefined;
            if (typeof val === 'object' && 'value' in val) {
              const v = val.value;
              return Array.isArray(v) ? v[0] : v;
            }
            return val;
          };
          
          const mergedOccupancy = extractValue(realTimeStatus.occupancy) ?? 
                                   extractValue(realTimeStatus.presence);
          const mergedLux = extractValue(realTimeStatus.lux) ?? 
                            extractValue(realTimeStatus.illuminance);
          const mergedTemp = extractValue(realTimeStatus.temperature) ?? 
                             extractValue(realTimeStatus.temp) ??
                             extractValue(realTimeStatus.temperatureAmbientCelsius);
          const mergedHumidity = extractValue(realTimeStatus.humidity) ?? 
                                  extractValue(realTimeStatus.rh) ??
                                  extractValue(realTimeStatus.humidityAmbientPercent);
          
          Object.assign(device, {
            occupancy: mergedOccupancy,
            lux: mergedLux,
            luxValue: typeof mergedLux === 'number' ? mergedLux : undefined,
            temperature: mergedTemp,
            humidity: mergedHumidity,
          });
          
          // Debug: Log merged values for classification
          logger.log(`🔷 [IntelliSpace] Merged status for classification ${baseSN}:`, {
            occupancy: device.occupancy,
            lux: device.lux,
            temperature: device.temperature,
            humidity: device.humidity
          });
        } else {
          logger.warn(`⚠️ [IntelliSpace] No status found for ${fullSN} in statusMap`);
        }
        
        // ✅ Determine device type - also consider endpoint from API response
        // Now uses merged status values for accurate classification
        let deviceType = getDeviceType(device);
        
        // ✅ Fix: Override device type based on type/endpoint from API (more accurate)
        // First check systemDeviceType from QueryDevices (for composite devices like DEHUMIDIFIER)
        // Then check the 'type' field from QueryStatus (e.g., 'BLINDS'), then fallback to 'endpoint'
        // ✅ FIX: Also check device.type field (from capabilities[].type in QueryDevices response)
        const systemDeviceType = (device?.systemDeviceType || device?.type || device?.deviceType || '').toUpperCase();
        const deviceTypeFromStatus = (realTimeStatus?.type || '').toUpperCase();
        const ep = realTimeStatus?.endpoint || '';
        
        
        // ✅ Check system device type first (for composite devices like DEHUMIDIFIER with sensor endpoints)
        if (systemDeviceType === 'DEHUMIDIFIER') {
          deviceType = 'dehumidifier';
        }
        // Check type field from QueryStatus (more reliable for devices like BLINDS with hex endpoints)
        else if (deviceTypeFromStatus === 'BLINDS' || deviceTypeFromStatus === 'BLIND' || deviceTypeFromStatus === 'ROLLER_SHADE') {
          deviceType = 'blind';
        } else if (deviceTypeFromStatus === 'CURTAIN') {
          deviceType = 'curtain';
        } else if (deviceTypeFromStatus === 'LIGHT' || deviceTypeFromStatus === 'BULB') {
          deviceType = 'light';
        } else if (deviceTypeFromStatus === 'FAN') {
          deviceType = 'fan';
        } else if (deviceTypeFromStatus === 'DEHUMIDIFIER') {
          deviceType = 'dehumidifier';
        } else if (deviceTypeFromStatus === 'AC_UNIT') {
          deviceType = 'hvac';
        } else if (ep) {
          // Fallback to endpoint-based detection (for legacy devices)
          if (ep === 'Light') deviceType = 'light';
          else if (ep === 'FAN') deviceType = 'fan';
          else if (ep === 'CURTAIN') deviceType = 'curtain';
          else if (ep === 'BLIND' || ep === 'BLINDS' || ep === 'ROLLER_SHADE') deviceType = 'blind';
          else if (ep === 'OUTLET1' || ep.startsWith('OUTLET')) deviceType = 'switch';
          else if (ep === 'SENSOR' || ep === 'ULTRON_SENSOR') {
            // Check if it's a TH sensor or presence sensor
            // Support multiple field naming conventions: Google Home (temperatureAmbientCelsius) and API (temp/rh)
            const hasTemp = realTimeStatus.temperatureAmbientCelsius !== undefined || 
                           realTimeStatus.temp !== undefined || 
                           realTimeStatus.temperature !== undefined;
            const hasHumidity = realTimeStatus.humidityAmbientPercent !== undefined || 
                               realTimeStatus.rh !== undefined || 
                               realTimeStatus.humidity !== undefined;
            if (hasTemp || hasHumidity) {
              deviceType = 'th_sensor';
            } else if (realTimeStatus.occupancy !== undefined || realTimeStatus.lux !== undefined) {
              deviceType = 'presence_sensor';
            }
          }
          else if (ep === 'DEHUMIDIFIER') deviceType = 'dehumidifier';
        }
        
        // ✅ Improved online status detection - check multiple sources
        // Note: online status is at baseSN level (system.online), not endpoint level
        let isOnline = false;
        
        // Check the baseSN level for online status (from system.online)
        const baseSNStatus = statusMap[baseSN];
        if (baseSNStatus?.online === true || baseSNStatus?.online === 'true') {
          isOnline = true;
        } else if (baseSNStatus?.online === false || baseSNStatus?.online === 'false') {
          isOnline = false;
        }
        
        // Also check endpoint level status if available
        if (realTimeStatus?.online === true || realTimeStatus?.online === 'true') {
          isOnline = true;
        } else if (realTimeStatus?.online === false || realTimeStatus?.online === 'false') {
          isOnline = false;
        }

        // ✅ Resolve location name from groupId using API locations only
        let locationName = device.groupName || device.location;
        let resolvedGroupName = device.groupName;
        
        // Only resolve from API locations (no virtual groups fallback)
        if (!resolvedGroupName && device.groupId && locationsMap.size > 0) {
          const apiLocationName = locationsMap.get(device.groupId);
          if (apiLocationName) {
            resolvedGroupName = apiLocationName;
            locationName = apiLocationName;
            logger.log(`✅ [IntelliSpace] Resolved location from API for ${fullSN}: groupId "${device.groupId}" → "${apiLocationName}"`);
          } else {
            // If groupId looks like an ID (long alphanumeric), log warning
            if (device.groupId.length > 15) {
              logger.warn(`⚠️ [IntelliSpace] Could not resolve location ID "${device.groupId}" for ${fullSN} from API locations`);
            }
          }
        }
        
        // Extract data based on device type
        // ✅ FIX: Prioritize pending nickname updates (user just saved) over API values
        // This prevents stale API data from overwriting recently saved nicknames
        const pendingNickname = pendingNicknameUpdates.current.get(actualDeviceSN) 
          || pendingNicknameUpdates.current.get(baseSN);
        
        // Extract raw nickname (tenant-local, for individual device display)
        const rawNickname = pendingNickname?.nickname 
          || realTimeStatus?.nickname 
          || device.nickname;
        
        // Resolved display name with fallback chain
        const resolvedDeviceName = rawNickname 
          || device.displayName 
          || device.name 
          || device.deviceName 
          || actualDeviceSN;
        
        // Original name without nickname (for merged multi-endpoint display)
        const originalDeviceName = device.name || device.deviceName || baseSN;
        
        // Check if device is multi_channel from endpointRelationship classification
        const isMultiChannel = device.endpointRelationship?.classification === 'multi_channel';
        
        const mappedDevice: IntelliSpaceDevice = {
          deviceId: actualDeviceSN, // ✅ Use the actual deviceSN that matches the API
          baseSN: baseSN,
          deviceName: resolvedDeviceName,
          nickname: rawNickname, // Tenant-local nickname (may be undefined)
          originalName: originalDeviceName, // Name without nickname for merged display
          systemDeviceName: device.systemDeviceName, // Main device name from system object
          deviceType,
          status: isOnline ? 'online' : 'offline',
          location: locationName,
          groupId: device.groupId,
          groupName: resolvedGroupName,
          traits: device.traits,
          attributes: device.attributes,
          online: isOnline,
          // Use device's lastSeen from API if available, otherwise current time
          // Priority: realTimeStatus.timestamp > realTimeStatus.lastSeen > device.lastSeen
          // This ensures we get the most accurate timestamp from the API response
          lastUpdate: realTimeStatus?.timestamp || realTimeStatus?.lastSeen || 
                      realTimeStatus?.iotState?.timestamp || device.lastSeen || 
                      new Date().toISOString(),
          // Multi-channel device flag (from endpointRelationship.classification)
          isMultiChannel: isMultiChannel,
        };

        // Add type-specific data
        if (realTimeStatus) {
          // Helper to extract value from array or direct value
          const getValue = (obj: any, ...keys: string[]): any => {
            for (const key of keys) {
              let val = obj[key];
              if (val !== undefined) {
                // Handle nested { value: ... } format from API
                if (typeof val === 'object' && val !== null && 'value' in val) {
                  val = val.value;
                }
                // Handle array format [value]
                if (Array.isArray(val)) {
                  return val.length > 0 ? val[val.length - 1] : undefined;
                }
                return val;
              }
            }
            return undefined;
          };
          
          switch (deviceType) {
            case 'power_meter_3phase':
            case 'power_meter':
              // ✅ Extract power based on what QueryStatus actually returns
              // Helper to extract array value or direct value
              const getArrayVal = (val: any): number | undefined => {
                if (val === undefined || val === null) return undefined;
                if (Array.isArray(val)) return val.length > 0 ? Number(val[0]) : undefined;
                return Number(val);
              };
              
              // ✅ Power Meter: Check 'on' state for devices with OnOff trait
              let powerMeterOnValue = getValue(realTimeStatus, 'on', 'isOn', 'power');
              // Handle { value: true } format from API
              if (powerMeterOnValue !== undefined && typeof powerMeterOnValue === 'object' && powerMeterOnValue.value !== undefined) {
                powerMeterOnValue = powerMeterOnValue.value;
              }
              if (powerMeterOnValue === undefined) {
                const stateObj = realTimeStatus.state || realTimeStatus.iotState;
                if (stateObj) {
                  powerMeterOnValue = stateObj.on ?? stateObj.currentModeSettings?.on;
                  if (typeof powerMeterOnValue === 'object' && powerMeterOnValue?.value !== undefined) {
                    powerMeterOnValue = powerMeterOnValue.value;
                  }
                }
              }
              if (powerMeterOnValue === undefined && realTimeStatus.values?.on !== undefined) {
                const onVal = realTimeStatus.values.on;
                powerMeterOnValue = onVal?.value !== undefined 
                  ? (Array.isArray(onVal.value) ? onVal.value[0] : onVal.value)
                  : onVal;
              }
              mappedDevice.isOn = powerMeterOnValue === true;
              mappedDevice.switchOn = powerMeterOnValue === true;
              
              // Use real_power if available, otherwise fallback to watt
              // Some devices (like UT3M15) use 'watt', others (like UTU500) use 'real_power'
              const realPower = realTimeStatus.real_power || realTimeStatus.state?.real_power;
              const wattPower = realTimeStatus.watt || realTimeStatus.state?.watt;
              
              if (realPower !== undefined) {
                mappedDevice.watt = getArrayVal(realPower);
                logger.log(`⚡ [IntelliSpace] Power meter ${fullSN}: ${mappedDevice.watt}W, on=${mappedDevice.isOn} (from real_power)`);
              } else if (wattPower !== undefined) {
                mappedDevice.watt = getArrayVal(wattPower);
                logger.log(`⚡ [IntelliSpace] Power meter ${fullSN}: ${mappedDevice.watt}W, on=${mappedDevice.isOn} (from watt)`);
              }
              // ✅ Ensure all power metrics are numbers with NaN validation (consistent with WebSocket path)
              const rawWattHour = getValue(realTimeStatus, 'watt_hour', 'wattHour', 'energy', 'kwh');
              if (rawWattHour !== undefined) {
                const numWattHour = Number(rawWattHour);
                if (!isNaN(numWattHour)) mappedDevice.wattHour = numWattHour;
              }
              const rawVoltage = getValue(realTimeStatus, 'voltage', 'volt', 'v');
              if (rawVoltage !== undefined) {
                const numVoltage = Number(rawVoltage);
                if (!isNaN(numVoltage)) mappedDevice.voltage = numVoltage;
              }
              const rawCurrent = getValue(realTimeStatus, 'current', 'amp', 'a');
              if (rawCurrent !== undefined) {
                const numCurrent = Number(rawCurrent);
                if (!isNaN(numCurrent)) mappedDevice.current = numCurrent;
              }
              
              // Three-phase data
              if (realTimeStatus.voltage_r !== undefined || realTimeStatus.state?.voltage_r !== undefined ||
                  realTimeStatus.real_power_r !== undefined || realTimeStatus.state?.real_power_r !== undefined) {
                const threePhaseAttrs = {
                  voltage_r: getArrayVal(realTimeStatus.voltage_r || realTimeStatus.state?.voltage_r),
                  voltage_s: getArrayVal(realTimeStatus.voltage_s || realTimeStatus.state?.voltage_s),
                  voltage_t: getArrayVal(realTimeStatus.voltage_t || realTimeStatus.state?.voltage_t),
                  current_r: getArrayVal(realTimeStatus.current_r || realTimeStatus.state?.current_r),
                  current_s: getArrayVal(realTimeStatus.current_s || realTimeStatus.state?.current_s),
                  current_t: getArrayVal(realTimeStatus.current_t || realTimeStatus.state?.current_t),
                  real_power_r: getArrayVal(realTimeStatus.real_power_r || realTimeStatus.state?.real_power_r),
                  real_power_s: getArrayVal(realTimeStatus.real_power_s || realTimeStatus.state?.real_power_s),
                  real_power_t: getArrayVal(realTimeStatus.real_power_t || realTimeStatus.state?.real_power_t),
                };
                mappedDevice.attributes = {
                  ...mappedDevice.attributes,
                  ...threePhaseAttrs,
                };
                
                // ✅ FIX: If no total watt value, compute from three-phase power data
                // Use same validation as WebSocket path: !isNaN && >= 0 (allows zero)
                if (mappedDevice.watt === undefined) {
                  const powerR = Number(threePhaseAttrs.real_power_r) || 0;
                  const powerS = Number(threePhaseAttrs.real_power_s) || 0;
                  const powerT = Number(threePhaseAttrs.real_power_t) || 0;
                  const totalWatt = powerR + powerS + powerT;
                  if (!isNaN(totalWatt) && totalWatt >= 0) {
                    mappedDevice.watt = totalWatt;
                    logger.log(`⚡ [IntelliSpace] Power meter ${fullSN}: ${totalWatt}W (computed from R=${powerR}W + S=${powerS}W + T=${powerT}W)`);
                  }
                }
              }
              break;
            case 'th_sensor':
              // Get temperature and validate - check multiple possible key names
              // New format: temperatureAmbientCelsius (from Google Smart Home trait)
              const rawTemp = getValue(realTimeStatus, 'temp', 'temperature', 'Temperature', 'temperatureAmbientCelsius');
              if (rawTemp !== undefined && rawTemp !== null) {
                const tempNum = Number(rawTemp);
                // Validate temperature: -50 to 60°C (reasonable range)
                if (!isNaN(tempNum) && isFinite(tempNum) && tempNum >= -50 && tempNum <= 60) {
                  mappedDevice.temperature = tempNum;
                } else {
                  logger.warn(`⚠️ [IntelliSpace] Invalid temperature value for ${fullSN}: ${rawTemp}`);
                }
              }
              // Get humidity and validate - check multiple possible key names
              // New format: humidityAmbientPercent (from Google Smart Home trait)
              const rawHumidity = getValue(realTimeStatus, 'rh', 'humidity', 'Humidity', 'humidityAmbientPercent');
              if (rawHumidity !== undefined && rawHumidity !== null) {
                const humidityNum = Number(rawHumidity);
                // Validate humidity: 0 to 100% (reasonable range)
                if (!isNaN(humidityNum) && isFinite(humidityNum) && humidityNum >= 0 && humidityNum <= 100) {
                  mappedDevice.humidity = humidityNum;
                } else {
                  logger.warn(`⚠️ [IntelliSpace] Invalid humidity value for ${fullSN}: ${rawHumidity}`);
                }
              }
              // For TH sensors, 'power' typically represents battery level (%)
              const thBattery = getValue(realTimeStatus, 'power', 'battery', 'batteryLevel');
              if (thBattery !== undefined) {
                mappedDevice.battery = Number(thBattery);
              }
              // ✅ FIX: Also extract occupancy and lux for composite devices classified as th_sensor
              // Some composite devices (e.g., Matter sensors) have TH + occupancy + lux
              const thOccupancy = getValue(realTimeStatus, 'occupancy', 'Occupancy', 'presence', 'Presence');
              if (thOccupancy !== undefined) {
                mappedDevice.occupancy = Boolean(thOccupancy);
                mappedDevice.presence = Boolean(thOccupancy);
              }
              // Extract lux if available (for composite TH + lux sensors)
              const thLux = getValue(realTimeStatus, 'lux', 'illuminance', 'Illuminance');
              if (thLux !== undefined && thLux !== null) {
                const luxNum = Number(thLux);
                if (!isNaN(luxNum) && isFinite(luxNum) && luxNum >= 0) {
                  mappedDevice.luxValue = luxNum;
                  mappedDevice.lux = luxNum;
                }
              }
              if (mappedDevice.temperature !== undefined) {
                logger.log(`🌡️ [IntelliSpace] TH Sensor ${fullSN}: ${mappedDevice.temperature}°C, ${mappedDevice.humidity}%, battery=${mappedDevice.battery}%, lux=${mappedDevice.lux}, occupancy=${mappedDevice.occupancy}`);
              }
              break;
            case 'presence_sensor':
              mappedDevice.presence = getValue(realTimeStatus, 'presence', 'Presence');
              mappedDevice.occupancy = getValue(realTimeStatus, 'occupancy', 'Occupancy');
              // ✅ FIX: Handle { value: X } format from API (same as power meter)
              if (mappedDevice.presence !== undefined && typeof mappedDevice.presence === 'object' && (mappedDevice.presence as any).value !== undefined) {
                mappedDevice.presence = (mappedDevice.presence as any).value;
              }
              if (mappedDevice.occupancy !== undefined && typeof mappedDevice.occupancy === 'object' && (mappedDevice.occupancy as any).value !== undefined) {
                mappedDevice.occupancy = (mappedDevice.occupancy as any).value;
              }
              // Extract Lux value for presence sensors with illuminance capability (supports multiple formats)
              let luxValue: number | undefined;
              
              // Debug: Log full realTimeStatus structure for presence sensors with lux capability
              const hasLuxCapability = device.sensorProfile?.lux !== undefined || 
                                       device.attributes?.sensorProfile?.lux !== undefined ||
                                       (device.traits || []).includes('Illuminance');
              if (hasLuxCapability) {
                logger.log(`🔍 [IntelliSpace] Presence sensor ${fullSN} - Full realTimeStatus:`, JSON.stringify(realTimeStatus, null, 2));
              }
              
              // Check multiple possible locations for lux data
              // Using extractLuxValue helper to handle all formats including { value: [...] }
              // 1. Check root level (flattened from state)
              if (realTimeStatus.lux !== undefined) {
                luxValue = extractLuxValue(realTimeStatus.lux);
                if (luxValue !== undefined) logger.log(`☀️ [IntelliSpace] Found lux from root level: ${luxValue}`);
              }
              // 2. Check iotState["1"] or iotState["2"] (WebSocket format)
              if (luxValue === undefined && realTimeStatus.iotState) {
                // Check endpoint "2" first (Matter sensors often have lux on endpoint 2)
                const sensorData = realTimeStatus.iotState["2"] || realTimeStatus.iotState["1"];
                if (sensorData) {
                  if (sensorData.illuminance !== undefined) {
                    luxValue = extractLuxValue(sensorData.illuminance);
                    if (luxValue !== undefined) logger.log(`☀️ [IntelliSpace] Found lux from iotState.illuminance: ${luxValue}`);
                  }
                  if (luxValue === undefined && sensorData.lux !== undefined) {
                    luxValue = extractLuxValue(sensorData.lux);
                    if (luxValue !== undefined) logger.log(`☀️ [IntelliSpace] Found lux from iotState.lux: ${luxValue}`);
                  }
                }
              }
              // 3. Check attributes["1"] or attributes["2"] (QueryStatus format)
              if (luxValue === undefined && realTimeStatus.attributes) {
                const sensorData = realTimeStatus.attributes["2"] || realTimeStatus.attributes["1"];
                if (sensorData) {
                  if (sensorData.illuminance !== undefined) {
                    luxValue = extractLuxValue(sensorData.illuminance);
                    if (luxValue !== undefined) logger.log(`☀️ [IntelliSpace] Found lux from attributes.illuminance: ${luxValue}`);
                  }
                  if (luxValue === undefined && sensorData.lux !== undefined) {
                    luxValue = extractLuxValue(sensorData.lux);
                    if (luxValue !== undefined) logger.log(`☀️ [IntelliSpace] Found lux from attributes.lux: ${luxValue}`);
                  }
                  if (luxValue === undefined && sensorData.events?.[0]?.data?.lux) {
                    luxValue = extractLuxValue(sensorData.events[0].data.lux);
                    if (luxValue !== undefined) logger.log(`☀️ [IntelliSpace] Found lux from attributes.events: ${luxValue}`);
                  }
                }
              }
              // 4. Check iotState.events[0].data.lux (alternative format)
              if (luxValue === undefined && realTimeStatus.iotState?.events?.[0]?.data?.lux) {
                luxValue = extractLuxValue(realTimeStatus.iotState.events[0].data.lux);
                if (luxValue !== undefined) logger.log(`☀️ [IntelliSpace] Found lux from iotState.events: ${luxValue}`);
              }
              // 5. Check illuminance at root level
              if (luxValue === undefined && realTimeStatus.illuminance !== undefined) {
                luxValue = extractLuxValue(realTimeStatus.illuminance);
                if (luxValue !== undefined) logger.log(`☀️ [IntelliSpace] Found lux from root illuminance: ${luxValue}`);
              }
              
              if (luxValue !== undefined) {
                mappedDevice.luxValue = Number(luxValue);
                mappedDevice.lux = Number(luxValue);  // ✅ Also set lux for consistent access
                logger.log(`✅ [IntelliSpace] Presence Sensor ${fullSN}: lux=${mappedDevice.luxValue}, occupancy=${mappedDevice.occupancy}`);
              } else {
                logger.warn(`⚠️ [IntelliSpace] Presence Sensor ${fullSN}: No lux value found.`);
                logger.warn(`⚠️ [IntelliSpace] realTimeStatus keys:`, Object.keys(realTimeStatus || {}));
                if (realTimeStatus?.attributes) {
                  logger.warn(`⚠️ [IntelliSpace] attributes keys:`, Object.keys(realTimeStatus.attributes));
                  if (realTimeStatus.attributes["1"]) {
                    logger.warn(`⚠️ [IntelliSpace] attributes["1"] keys:`, Object.keys(realTimeStatus.attributes["1"]));
                  }
                }
                if (realTimeStatus?.iotState) {
                  logger.warn(`⚠️ [IntelliSpace] iotState keys:`, Object.keys(realTimeStatus.iotState));
                  if (realTimeStatus.iotState["1"]) {
                    logger.warn(`⚠️ [IntelliSpace] iotState["1"] keys:`, Object.keys(realTimeStatus.iotState["1"]));
                  }
                }
              }
              break;
            case 'lux_sensor':
              // Lux-only sensor: extract lux value (same logic as presence_sensor but without occupancy)
              let luxSensorValue: number | undefined;
              
              // 1. Check root level (flattened from state)
              if (realTimeStatus.lux !== undefined) {
                luxSensorValue = extractLuxValue(realTimeStatus.lux);
                if (luxSensorValue !== undefined) logger.log(`☀️ [IntelliSpace] Lux sensor ${fullSN} - Found lux from root level: ${luxSensorValue}`);
              }
              // 2. Check illuminance at root level
              if (luxSensorValue === undefined && realTimeStatus.illuminance !== undefined) {
                luxSensorValue = extractLuxValue(realTimeStatus.illuminance);
                if (luxSensorValue !== undefined) logger.log(`☀️ [IntelliSpace] Lux sensor ${fullSN} - Found lux from illuminance: ${luxSensorValue}`);
              }
              // 3. Check iotState (WebSocket format)
              if (luxSensorValue === undefined && realTimeStatus.iotState) {
                const sensorData = realTimeStatus.iotState["2"] || realTimeStatus.iotState["1"];
                if (sensorData) {
                  if (sensorData.illuminance !== undefined) {
                    luxSensorValue = extractLuxValue(sensorData.illuminance);
                  } else if (sensorData.lux !== undefined) {
                    luxSensorValue = extractLuxValue(sensorData.lux);
                  }
                  if (luxSensorValue !== undefined) logger.log(`☀️ [IntelliSpace] Lux sensor ${fullSN} - Found lux from iotState: ${luxSensorValue}`);
                }
              }
              
              if (luxSensorValue !== undefined) {
                mappedDevice.luxValue = Number(luxSensorValue);
                mappedDevice.lux = Number(luxSensorValue);  // ✅ Also set lux for consistent access
                logger.log(`✅ [IntelliSpace] Lux Sensor ${fullSN}: lux=${mappedDevice.luxValue}`);
              } else {
                logger.warn(`⚠️ [IntelliSpace] Lux Sensor ${fullSN}: No lux value found in realTimeStatus`);
              }
              break;
            case 'multi_sensor':
              // ✅ Multi-Sensor Device: Extract ALL sensor values (TH + occupancy + lux + motion)
              // Temperature
              const multiTemp = getValue(realTimeStatus, 'temp', 'temperature', 'Temperature', 'temperatureAmbientCelsius');
              if (multiTemp !== undefined && multiTemp !== null) {
                const tempNum = Number(multiTemp);
                if (!isNaN(tempNum) && isFinite(tempNum) && tempNum >= -50 && tempNum <= 60) {
                  mappedDevice.temperature = tempNum;
                }
              }
              // Humidity
              const multiHumidity = getValue(realTimeStatus, 'rh', 'humidity', 'Humidity', 'humidityAmbientPercent');
              if (multiHumidity !== undefined && multiHumidity !== null) {
                const humidityNum = Number(multiHumidity);
                if (!isNaN(humidityNum) && isFinite(humidityNum) && humidityNum >= 0 && humidityNum <= 100) {
                  mappedDevice.humidity = humidityNum;
                }
              }
              // Occupancy/Presence
              const multiOccupancy = getValue(realTimeStatus, 'occupancy', 'Occupancy', 'presence', 'Presence');
              if (multiOccupancy !== undefined) {
                mappedDevice.occupancy = Boolean(multiOccupancy);
                mappedDevice.presence = Boolean(multiOccupancy);
              }
              // Lux
              const multiLux = getValue(realTimeStatus, 'lux', 'illuminance', 'Illuminance');
              if (multiLux !== undefined && multiLux !== null) {
                const luxNum = Number(multiLux);
                if (!isNaN(luxNum) && isFinite(luxNum) && luxNum >= 0) {
                  mappedDevice.luxValue = luxNum;
                  mappedDevice.lux = luxNum;
                }
              }
              // Motion (if available)
              const multiMotion = getValue(realTimeStatus, 'motionDetected', 'motion');
              if (multiMotion !== undefined) {
                mappedDevice.motionDetected = Boolean(multiMotion);
              }
              // Battery
              const multiBattery = getValue(realTimeStatus, 'power', 'battery', 'batteryLevel');
              if (multiBattery !== undefined) {
                mappedDevice.battery = Number(multiBattery);
              }
              logger.log(`🔷 [IntelliSpace] Multi-Sensor ${fullSN}: temp=${mappedDevice.temperature}°C, humidity=${mappedDevice.humidity}%, lux=${mappedDevice.lux}, occupancy=${mappedDevice.occupancy}`);
              break;
            case 'motion_sensor':
              // Motion sensor: check detected.value for "motionDetected"
              // API returns: values.detected.value = ["motionDetected"] when motion detected
              let motionDetected = false;
              
              // Check realTimeStatus.detected (flattened from values)
              if (realTimeStatus.detected !== undefined) {
                const detectedValue = realTimeStatus.detected?.value ?? realTimeStatus.detected;
                if (Array.isArray(detectedValue)) {
                  motionDetected = detectedValue.includes('motionDetected');
                } else if (typeof detectedValue === 'boolean') {
                  motionDetected = detectedValue;
                } else if (typeof detectedValue === 'string') {
                  motionDetected = detectedValue === 'motionDetected';
                }
              }
              // Check values.detected.value (API format)
              else if (realTimeStatus.values?.detected?.value !== undefined) {
                const detectedValue = realTimeStatus.values.detected.value;
                if (Array.isArray(detectedValue)) {
                  motionDetected = detectedValue.includes('motionDetected');
                } else if (typeof detectedValue === 'boolean') {
                  motionDetected = detectedValue;
                } else if (typeof detectedValue === 'string') {
                  motionDetected = detectedValue === 'motionDetected';
                }
              }
              // Check iotState for motion (alternative format)
              else if (realTimeStatus.iotState?.motionDetected !== undefined) {
                motionDetected = realTimeStatus.iotState.motionDetected === true;
              }
              // Check attributes.motionDetected (another alternative)
              else if (realTimeStatus.attributes?.motionDetected !== undefined) {
                motionDetected = realTimeStatus.attributes.motionDetected === true;
              }
              // Check for motion field directly
              else if (realTimeStatus.motion !== undefined) {
                motionDetected = realTimeStatus.motion === true;
              }
              
              mappedDevice.motionDetected = motionDetected;
              logger.log(`👁️ [IntelliSpace] Motion Sensor ${fullSN}: detected=${motionDetected}`);
              break;
            case 'hvac':
              mappedDevice.isOn = getValue(realTimeStatus, 'on', 'isOn', 'power');
              // ✅ Set BOTH mode and thermostatMode for UI consistency
              const hvacMode = getValue(realTimeStatus, 'thermostatMode', 'mode');
              mappedDevice.mode = hvacMode;
              mappedDevice.thermostatMode = hvacMode;
              // Get fanSpeed - prefer numeric format from currentModeSettings.FanSpeed for reliable comparison
              let hvacFanSpeed = getValue(realTimeStatus, 'currentFanSpeedSetting', 'fanSpeed', 'fan_speed');
              // Handle { value: ... } format
              if (hvacFanSpeed && typeof hvacFanSpeed === 'object' && hvacFanSpeed.value !== undefined) {
                hvacFanSpeed = hvacFanSpeed.value;
              }
              // Prefer currentModeSettings.FanSpeed (numeric format "0", "1", "2", "3")
              // flattenValues already converts: { value: { FanSpeed: "1" } } -> { FanSpeed: "1" }
              // So check realTimeStatus.currentModeSettings.FanSpeed (flattened) first
              if (realTimeStatus.currentModeSettings?.FanSpeed !== undefined) {
                hvacFanSpeed = realTimeStatus.currentModeSettings.FanSpeed;
              } else if (realTimeStatus.currentModeSettings?.value?.FanSpeed !== undefined) {
                // Fallback: raw format before flattening
                hvacFanSpeed = realTimeStatus.currentModeSettings.value.FanSpeed;
              } else if (realTimeStatus.values?.currentModeSettings?.value?.FanSpeed !== undefined) {
                // Fallback: access raw values object
                hvacFanSpeed = realTimeStatus.values.currentModeSettings.value.FanSpeed;
              }
              mappedDevice.fanSpeed = hvacFanSpeed;
              logger.log(`❄️ [IntelliSpace] HVAC ${fullSN}: fanSpeed=${hvacFanSpeed}, currentModeSettings=`, realTimeStatus.currentModeSettings);
              mappedDevice.setTemperature = getValue(realTimeStatus, 'thermostatTemperatureSetpoint', 'setTemperature', 'targetTemp');
              // Get temperature and validate
              const hvacRawTemp = getValue(realTimeStatus, 'thermostatTemperatureAmbient', 'temp', 'temperature');
              if (hvacRawTemp !== undefined && hvacRawTemp !== null) {
                const hvacTempNum = Number(hvacRawTemp);
                // Validate temperature: -50 to 60°C (reasonable range)
                if (!isNaN(hvacTempNum) && isFinite(hvacTempNum) && hvacTempNum >= -50 && hvacTempNum <= 60) {
                  mappedDevice.temperature = hvacTempNum;
                } else {
                  logger.warn(`⚠️ [IntelliSpace] Invalid HVAC temperature value for ${fullSN}: ${hvacRawTemp}`);
                }
              }
              if (mappedDevice.isOn !== undefined) {
                logger.log(`❄️ [IntelliSpace] HVAC ${fullSN}: on=${mappedDevice.isOn}, mode=${mappedDevice.mode}`);
              }
              break;
            case 'fan':
              // ✅ Fan: Check multiple possible locations for 'on' state
              let fanOnValue = getValue(realTimeStatus, 'on', 'isOn', 'power');
              
              // Handle { value: true } format from API
              if (fanOnValue !== undefined && typeof fanOnValue === 'object' && fanOnValue.value !== undefined) {
                fanOnValue = fanOnValue.value;
              }
              
              if (fanOnValue === undefined) {
                const stateObj = realTimeStatus.state || realTimeStatus.iotState;
                if (stateObj) {
                  fanOnValue = stateObj.on ?? stateObj.currentModeSettings?.on;
                  // Also check { value: ... } format
                  if (typeof fanOnValue === 'object' && fanOnValue?.value !== undefined) {
                    fanOnValue = fanOnValue.value;
                  }
                }
              }
              // Also try from values object (API format)
              if (fanOnValue === undefined && realTimeStatus.values?.on !== undefined) {
                const onVal = realTimeStatus.values.on;
                fanOnValue = onVal?.value !== undefined 
                  ? (Array.isArray(onVal.value) ? onVal.value[0] : onVal.value)
                  : onVal;
              }
              
              mappedDevice.isOn = fanOnValue === true;
              mappedDevice.power = fanOnValue === true;
              
              // Get fan speed - also handle { value: ... } format
              let fanSpeedValue = getValue(realTimeStatus, 'currentFanSpeedSetting', 'fanSpeed', 'fan_speed');
              if (fanSpeedValue !== undefined && typeof fanSpeedValue === 'object' && fanSpeedValue.value !== undefined) {
                fanSpeedValue = fanSpeedValue.value;
              }
              // Try from currentModeSettings.FanSpeed
              if (fanSpeedValue === undefined && realTimeStatus.currentModeSettings?.FanSpeed !== undefined) {
                fanSpeedValue = realTimeStatus.currentModeSettings.FanSpeed;
              }
              if (fanSpeedValue === undefined && realTimeStatus.values?.currentModeSettings?.value?.FanSpeed !== undefined) {
                fanSpeedValue = realTimeStatus.values.currentModeSettings.value.FanSpeed;
              }
              mappedDevice.fanSpeed = fanSpeedValue;
              
              // Get custom toggle settings (Light, Buzzer, etc.)
              let toggleSettings = realTimeStatus.currentToggleSettings;
              if (toggleSettings !== undefined && typeof toggleSettings === 'object' && toggleSettings.value !== undefined) {
                toggleSettings = toggleSettings.value;
              }
              if (toggleSettings === undefined && realTimeStatus.values?.currentToggleSettings?.value !== undefined) {
                toggleSettings = realTimeStatus.values.currentToggleSettings.value;
              }
              if (toggleSettings) {
                (mappedDevice as any).currentToggleSettings = toggleSettings;
              }
              
              logger.log(`🌀 [IntelliSpace] Fan ${fullSN}: on=${mappedDevice.isOn}, fanSpeed=${mappedDevice.fanSpeed}, toggles=${JSON.stringify(toggleSettings)}`);
              break;
            case 'erv':
              // ✅ ERV (全熱交換機): Extract on, fanSpeed, mode, temperature, humidity, and sensor data
              let ervOnValue = getValue(realTimeStatus, 'on', 'isOn', 'power');
              if (ervOnValue !== undefined && typeof ervOnValue === 'object' && (ervOnValue as any).value !== undefined) {
                ervOnValue = (ervOnValue as any).value;
              }
              if (ervOnValue === undefined && realTimeStatus.values?.on !== undefined) {
                const onVal = realTimeStatus.values.on;
                ervOnValue = onVal?.value !== undefined 
                  ? (Array.isArray(onVal.value) ? onVal.value[0] : onVal.value)
                  : onVal;
              }
              mappedDevice.isOn = ervOnValue === true;
              mappedDevice.power = ervOnValue === true;
              
              // Thermostat mode (fan-only, heat)
              const ervThermostatMode = getValue(realTimeStatus, 'thermostatMode', 'mode');
              if (ervThermostatMode) {
                mappedDevice.thermostatMode = ervThermostatMode;
                mappedDevice.mode = ervThermostatMode;
              }
              
              // Get fan speed from currentModeSettings
              let ervFanSpeed = realTimeStatus.currentModeSettings?.FanSpeed;
              if (ervFanSpeed === undefined && realTimeStatus.values?.currentModeSettings?.value?.FanSpeed !== undefined) {
                ervFanSpeed = realTimeStatus.values.currentModeSettings.value.FanSpeed;
              }
              mappedDevice.fanSpeed = ervFanSpeed;
              
              // Get currentModeSettings (FanSpeed, Mode, Timer)
              let ervModeSettings = realTimeStatus.currentModeSettings;
              if (ervModeSettings === undefined && realTimeStatus.values?.currentModeSettings?.value !== undefined) {
                ervModeSettings = realTimeStatus.values.currentModeSettings.value;
              }
              if (ervModeSettings) {
                (mappedDevice as any).currentModeSettings = ervModeSettings;
              }
              
              // Get temperature (thermostatTemperatureAmbient)
              const ervTemp = getValue(realTimeStatus, 'thermostatTemperatureAmbient', 'temperature', 'temp');
              if (ervTemp !== undefined && ervTemp !== null) {
                const tempNum = Number(ervTemp);
                if (!isNaN(tempNum) && isFinite(tempNum) && tempNum >= -50 && tempNum <= 60) {
                  mappedDevice.temperature = tempNum;
                }
              }
              
              // Get humidity (humidityAmbientPercent)
              const ervHumidity = getValue(realTimeStatus, 'humidityAmbientPercent', 'humidity', 'rh');
              if (ervHumidity !== undefined && ervHumidity !== null) {
                const humidityNum = Number(ervHumidity);
                if (!isNaN(humidityNum) && isFinite(humidityNum) && humidityNum >= 0 && humidityNum <= 100) {
                  mappedDevice.humidity = humidityNum;
                }
              }
              
              // Get sensor state data (PM2.5, CO2)
              let ervSensorData = realTimeStatus.currentSensorStateData;
              // Check multiple possible locations for sensor data
              if (ervSensorData === undefined && realTimeStatus.values?.currentSensorStateData?.value !== undefined) {
                ervSensorData = realTimeStatus.values.currentSensorStateData.value;
              }
              if (ervSensorData === undefined && realTimeStatus.values?.currentSensorStateData !== undefined) {
                const sensorObj = realTimeStatus.values.currentSensorStateData;
                ervSensorData = sensorObj?.value || sensorObj;
              }
              if (ervSensorData) {
                // Ensure it's an array
                const sensorArray = Array.isArray(ervSensorData) ? ervSensorData : [];
                (mappedDevice as any).currentSensorStateData = sensorArray;
              }
              
              // ✅ Also get pm25 and co2 from Ultron Sensor endpoint (ERV is composite: HEATER + Ultron Sensor)
              // The HEATER endpoint has currentSensorStateData, but Ultron Sensor has direct pm25/co2 values
              const ervSensorEndpointSN = `${baseSN}#Ultron Sensor`;
              const ervSensorStatus = statusMap[ervSensorEndpointSN];
              console.log(`🌀 [IntelliSpace] ERV ${baseSN}: Looking for Ultron Sensor at "${ervSensorEndpointSN}"`, {
                found: !!ervSensorStatus,
                ervSensorStatus,
                statusMapKeys: Object.keys(statusMap).filter(k => k.includes(baseSN)),
              });
              if (ervSensorStatus) {
                // Extract pm25 from Ultron Sensor
                let sensorPm25 = ervSensorStatus.pm25 ?? ervSensorStatus.pm2_5;
                if (sensorPm25 !== undefined && typeof sensorPm25 === 'object' && sensorPm25.value !== undefined) {
                  sensorPm25 = sensorPm25.value;
                }
                if (sensorPm25 !== undefined) {
                  const pm25Array = Array.isArray(sensorPm25) ? sensorPm25 : [sensorPm25];
                  (mappedDevice as any).pm25 = pm25Array;
                }
                
                // Extract co2 from Ultron Sensor
                let sensorCo2 = ervSensorStatus.co2;
                if (sensorCo2 !== undefined && typeof sensorCo2 === 'object' && sensorCo2.value !== undefined) {
                  sensorCo2 = sensorCo2.value;
                }
                if (sensorCo2 !== undefined) {
                  const co2Array = Array.isArray(sensorCo2) ? sensorCo2 : [sensorCo2];
                  (mappedDevice as any).co2 = co2Array;
                }
                
                logger.log(`🌀 [IntelliSpace] ERV ${fullSN}: merged Ultron Sensor data: pm25=${(mappedDevice as any).pm25}, co2=${(mappedDevice as any).co2}`);
              }
              
              // Fallback: try to get from HEATER endpoint directly (less common)
              if (!(mappedDevice as any).pm25) {
                const ervPm25 = getValue(realTimeStatus, 'pm25', 'pm2_5');
                if (ervPm25 !== undefined) {
                  const pm25Array = Array.isArray(ervPm25) ? ervPm25 : [ervPm25];
                  (mappedDevice as any).pm25 = pm25Array;
                }
              }
              if (!(mappedDevice as any).co2) {
                const ervCo2 = getValue(realTimeStatus, 'co2', 'carbonDioxideLevel');
                if (ervCo2 !== undefined) {
                  const co2Array = Array.isArray(ervCo2) ? ervCo2 : [ervCo2];
                  (mappedDevice as any).co2 = co2Array;
                }
              }
              
              logger.log(`🌀 [IntelliSpace] ERV ${fullSN}: on=${mappedDevice.isOn}, fanSpeed=${ervFanSpeed}, temp=${mappedDevice.temperature}, humidity=${mappedDevice.humidity}, pm25=${(mappedDevice as any).pm25}, co2=${(mappedDevice as any).co2}, sensorData=`, ervSensorData);
              break;
            case 'switch':
              // ✅ Fix: Check multiple possible locations for 'on' state (same as light)
              let switchOnValue = getValue(realTimeStatus, 'on', 'switchOn');
              if (switchOnValue === undefined) {
                const stateObj = realTimeStatus.state || realTimeStatus.iotState;
                if (stateObj) {
                  switchOnValue = stateObj.on ?? stateObj.currentModeSettings?.on;
                }
              }
              mappedDevice.switchOn = switchOnValue === true;
              // Switch devices may also have power metrics
              mappedDevice.watt = getValue(realTimeStatus, 'watt', 'real_power', 'watts');
              mappedDevice.wattHour = getValue(realTimeStatus, 'watt_hour', 'wattHour', 'energy', 'kwh');
              mappedDevice.voltage = getValue(realTimeStatus, 'voltage', 'volt', 'v');
              mappedDevice.current = getValue(realTimeStatus, 'current', 'amp', 'a');
              break;
            case 'outlet':
              // ✅ Outlet/Plug: Check multiple possible locations for 'on' state
              // Similar to switch, but outlet devices may also have power metering
              let outletOnValue = getValue(realTimeStatus, 'on', 'switchOn', 'isOn');
              
              // Handle { value: true } format from API
              if (outletOnValue !== undefined && typeof outletOnValue === 'object' && (outletOnValue as any).value !== undefined) {
                outletOnValue = (outletOnValue as any).value;
              }
              
              if (outletOnValue === undefined) {
                const stateObj = realTimeStatus.state || realTimeStatus.iotState;
                if (stateObj) {
                  outletOnValue = stateObj.on ?? stateObj.currentModeSettings?.on;
                  // Also check { value: ... } format
                  if (typeof outletOnValue === 'object' && (outletOnValue as any)?.value !== undefined) {
                    outletOnValue = (outletOnValue as any).value;
                  }
                }
              }
              // Also try from values object (API format)
              if (outletOnValue === undefined && realTimeStatus.values?.on !== undefined) {
                const onVal = realTimeStatus.values.on;
                outletOnValue = onVal?.value !== undefined 
                  ? (Array.isArray(onVal.value) ? onVal.value[0] : onVal.value)
                  : onVal;
              }
              
              mappedDevice.switchOn = outletOnValue === true;
              mappedDevice.isOn = outletOnValue === true;
              mappedDevice.power = outletOnValue === true;
              
              // Outlet devices often have power metering
              mappedDevice.watt = getValue(realTimeStatus, 'watt', 'real_power', 'watts');
              mappedDevice.wattHour = getValue(realTimeStatus, 'watt_hour', 'wattHour', 'energy', 'kwh');
              mappedDevice.voltage = getValue(realTimeStatus, 'voltage', 'volt', 'v');
              mappedDevice.current = getValue(realTimeStatus, 'current', 'amp', 'a');
              
              logger.log(`🔌 [IntelliSpace] Outlet ${fullSN}: on=${mappedDevice.isOn}, watt=${mappedDevice.watt}W`);
              break;
            case 'light':
              // ✅ Fix: Check multiple possible locations for 'on' state
              // 1. Direct: realTimeStatus.on
              // 2. Nested in state: realTimeStatus.state?.on or state?.currentModeSettings?.on
              // 3. From values (API format): realTimeStatus.values?.on
              let lightOnValue = getValue(realTimeStatus, 'on', 'isOn', 'power');
              
              if (lightOnValue === undefined) {
                // Try nested in state object
                const stateObj = realTimeStatus.state || realTimeStatus.iotState;
                if (stateObj) {
                  lightOnValue = stateObj.on ?? stateObj.currentModeSettings?.on;
                }
              }
              if (lightOnValue === undefined && realTimeStatus.values?.on !== undefined) {
                // Try from values object (API format)
                const onVal = realTimeStatus.values.on;
                lightOnValue = onVal?.value !== undefined 
                  ? (Array.isArray(onVal.value) ? onVal.value[0] : onVal.value)
                  : onVal;
              }
              mappedDevice.isOn = lightOnValue === true;
              mappedDevice.power = lightOnValue === true;
              // ✅ Also check nested locations for brightness
              let brightnessValue = getValue(realTimeStatus, 'brightness');
              if (brightnessValue === undefined) {
                const stateObj = realTimeStatus.state || realTimeStatus.iotState;
                if (stateObj?.brightness !== undefined) {
                  brightnessValue = stateObj.brightness;
                }
              }
              mappedDevice.brightness = brightnessValue;
              // Read color temperature - check multiple locations
              let colorTempValue: number | undefined;
              // 1. Direct color object
              if (realTimeStatus.color?.temperatureK) {
                colorTempValue = realTimeStatus.color.temperatureK;
              }
              // 2. Nested in state
              if (!colorTempValue) {
                const stateObj = realTimeStatus.state || realTimeStatus.iotState;
                if (stateObj?.color?.temperatureK) {
                  colorTempValue = stateObj.color.temperatureK;
                }
              }
              if (colorTempValue) {
                mappedDevice.colorTemp = colorTempValue;
              }
              // Read color temperature range from attributes
              const colorTempRange = realTimeStatus.colorTemperatureRange || device.attributes?.colorTemperatureRange;
              if (colorTempRange) {
                mappedDevice.colorTempMin = colorTempRange.temperatureMinK || 2700;
                mappedDevice.colorTempMax = colorTempRange.temperatureMaxK || 6500;
              }
              if (mappedDevice.isOn !== undefined) {
                logger.log(`💡 [IntelliSpace] Light ${fullSN}: on=${mappedDevice.isOn}, brightness=${mappedDevice.brightness}, colorTemp=${mappedDevice.colorTemp}K`);
              }
              break;
            
            case 'door_sensor':
              // Door sensor: check detected.value for "opened"/"closed"
              // API returns: values.detected.value = ["opened"] when door is open
              let doorIsOpen = false;
              
              // Check realTimeStatus.detected (flattened from values)
              if (realTimeStatus.detected !== undefined) {
                const detectedValue = realTimeStatus.detected?.value ?? realTimeStatus.detected;
                if (Array.isArray(detectedValue)) {
                  doorIsOpen = detectedValue.includes('opened');
                } else if (typeof detectedValue === 'string') {
                  doorIsOpen = detectedValue === 'opened';
                }
              }
              // Check values.detected.value (API format)
              else if (realTimeStatus.values?.detected?.value !== undefined) {
                const detectedValue = realTimeStatus.values.detected.value;
                if (Array.isArray(detectedValue)) {
                  doorIsOpen = detectedValue.includes('opened');
                } else if (typeof detectedValue === 'string') {
                  doorIsOpen = detectedValue === 'opened';
                }
              }
              // Check iotState for detected (alternative format)
              else if (realTimeStatus.iotState?.detected !== undefined) {
                const detectedValue = realTimeStatus.iotState.detected?.value ?? realTimeStatus.iotState.detected;
                if (Array.isArray(detectedValue)) {
                  doorIsOpen = detectedValue.includes('opened');
                } else if (typeof detectedValue === 'string') {
                  doorIsOpen = detectedValue === 'opened';
                }
              }
              
              mappedDevice.isOpen = doorIsOpen;
              break;
            
            case 'curtain':
            case 'blind':
              // Curtain/Blind: extract openPercent from state
              // API returns: endpoints.CURTAIN.state.openPercent = 50
              let openPercentValue: number | undefined;
              
              // 1. Check direct openPercent (flattened from state)
              if (realTimeStatus.openPercent !== undefined) {
                openPercentValue = Array.isArray(realTimeStatus.openPercent) 
                  ? realTimeStatus.openPercent[0] 
                  : realTimeStatus.openPercent;
              }
              // 2. Check state.openPercent
              else if (realTimeStatus.state?.openPercent !== undefined) {
                openPercentValue = Array.isArray(realTimeStatus.state.openPercent)
                  ? realTimeStatus.state.openPercent[0]
                  : realTimeStatus.state.openPercent;
              }
              // 3. Check iotState.openPercent (WebSocket format)
              else if (realTimeStatus.iotState?.openPercent !== undefined) {
                openPercentValue = Array.isArray(realTimeStatus.iotState.openPercent)
                  ? realTimeStatus.iotState.openPercent[0]
                  : realTimeStatus.iotState.openPercent;
              }
              // 4. Check values.openPercent.value (API format)
              else if (realTimeStatus.values?.openPercent?.value !== undefined) {
                openPercentValue = Array.isArray(realTimeStatus.values.openPercent.value)
                  ? realTimeStatus.values.openPercent.value[0]
                  : realTimeStatus.values.openPercent.value;
              }
              
              // Also check openState for open/closed status
              let curtainIsOpen = false;
              if (realTimeStatus.openState !== undefined) {
                curtainIsOpen = realTimeStatus.openState === 'OPEN' || realTimeStatus.openState === true;
              } else if (realTimeStatus.state?.openState !== undefined) {
                curtainIsOpen = realTimeStatus.state.openState === 'OPEN' || realTimeStatus.state.openState === true;
              }
              
              if (openPercentValue !== undefined) {
                const openPercentNum = Number(openPercentValue);
                if (!isNaN(openPercentNum) && isFinite(openPercentNum) && openPercentNum >= 0 && openPercentNum <= 100) {
                  mappedDevice.openPercent = openPercentNum;
                  mappedDevice.isOpen = openPercentNum > 0;
                  logger.log(`🪟 [IntelliSpace] ${deviceType} ${fullSN}: openPercent=${openPercentNum}%`);
                }
              } else {
                // Fallback to open/closed state
                mappedDevice.isOpen = curtainIsOpen;
                logger.log(`🪟 [IntelliSpace] ${deviceType} ${fullSN}: isOpen=${curtainIsOpen} (no openPercent)`);
              }
              break;
            
            case 'dehumidifier':
              // Dehumidifier: extract on/off, humidity settings, custom modes/toggles
              // Similar to HVAC but focused on humidity control instead of temperature
              
              // 1. Extract on/off state
              let dehumidifierOnValue = getValue(realTimeStatus, 'on', 'isOn', 'power');
              if (dehumidifierOnValue !== undefined && typeof dehumidifierOnValue === 'object' && (dehumidifierOnValue as any).value !== undefined) {
                dehumidifierOnValue = (dehumidifierOnValue as any).value;
              }
              if (dehumidifierOnValue === undefined && realTimeStatus.values?.on !== undefined) {
                const onVal = realTimeStatus.values.on;
                dehumidifierOnValue = onVal?.value !== undefined 
                  ? (Array.isArray(onVal.value) ? onVal.value[0] : onVal.value)
                  : onVal;
              }
              mappedDevice.isOn = dehumidifierOnValue === true;
              mappedDevice.power = dehumidifierOnValue === true;
              
              // 2. Extract humidity setpoint (target humidity)
              let humiditySetpoint = getValue(realTimeStatus, 'humiditySetpointPercent', 'humiditySetpoint', 'targetHumidity');
              if (humiditySetpoint !== undefined && typeof humiditySetpoint === 'object' && (humiditySetpoint as any).value !== undefined) {
                humiditySetpoint = (humiditySetpoint as any).value;
              }
              if (humiditySetpoint === undefined && realTimeStatus.values?.humiditySetpointPercent?.value !== undefined) {
                humiditySetpoint = realTimeStatus.values.humiditySetpointPercent.value;
              }
              if (humiditySetpoint !== undefined) {
                mappedDevice.humiditySetpointPercent = Number(humiditySetpoint);
              }
              
              // 3. Extract ambient humidity (current room humidity)
              let humidityAmbient = getValue(realTimeStatus, 'humidityAmbientPercent', 'humidityAmbient', 'currentHumidity', 'rh', 'humidity');
              if (humidityAmbient !== undefined && typeof humidityAmbient === 'object' && (humidityAmbient as any).value !== undefined) {
                humidityAmbient = (humidityAmbient as any).value;
              }
              if (humidityAmbient === undefined && realTimeStatus.values?.humidityAmbientPercent?.value !== undefined) {
                humidityAmbient = realTimeStatus.values.humidityAmbientPercent.value;
              }
              if (humidityAmbient !== undefined) {
                mappedDevice.humidityAmbientPercent = Number(humidityAmbient);
                // Also set humidity for backward compatibility with sensor displays
                mappedDevice.humidity = Number(humidityAmbient);
              }
              
              // 4. Extract currentModeSettings (Mode, FanSpeed, Timer)
              let modeSettings = realTimeStatus.currentModeSettings;
              if (modeSettings !== undefined && typeof modeSettings === 'object' && modeSettings.value !== undefined) {
                modeSettings = modeSettings.value;
              }
              if (modeSettings === undefined && realTimeStatus.values?.currentModeSettings?.value !== undefined) {
                modeSettings = realTimeStatus.values.currentModeSettings.value;
              }
              if (modeSettings) {
                mappedDevice.currentModeSettings = modeSettings;
                // Also extract fanSpeed for compatibility
                if (modeSettings.FanSpeed !== undefined) {
                  mappedDevice.fanSpeed = modeSettings.FanSpeed;
                }
              }
              
              // 5. Extract currentToggleSettings (AutoDirection, AirClean, Lock)
              let dehumidifierToggleSettings = realTimeStatus.currentToggleSettings;
              if (dehumidifierToggleSettings !== undefined && typeof dehumidifierToggleSettings === 'object' && dehumidifierToggleSettings.value !== undefined) {
                dehumidifierToggleSettings = dehumidifierToggleSettings.value;
              }
              if (dehumidifierToggleSettings === undefined && realTimeStatus.values?.currentToggleSettings?.value !== undefined) {
                dehumidifierToggleSettings = realTimeStatus.values.currentToggleSettings.value;
              }
              if (dehumidifierToggleSettings) {
                mappedDevice.currentToggleSettings = dehumidifierToggleSettings;
              }
              
              // 6. Extract bucket (water tank status) from Ultron Sensor endpoint
              // bucket: [0] = normal, [1] = full
              // notify: [{ type: 'bucket', event: 'Normal' | 'FallingCritical' | 'Full' }]
              const sensorEndpointSN = `${baseSN}#Ultron Sensor`;
              const sensorStatus = statusMap[sensorEndpointSN];
              if (sensorStatus) {
                // Extract bucket value
                let bucketValue = sensorStatus.bucket;
                if (bucketValue !== undefined && typeof bucketValue === 'object' && bucketValue.value !== undefined) {
                  bucketValue = bucketValue.value;
                }
                if (bucketValue === undefined && sensorStatus.values?.bucket?.value !== undefined) {
                  bucketValue = sensorStatus.values.bucket.value;
                }
                if (bucketValue !== undefined) {
                  mappedDevice.bucket = Array.isArray(bucketValue) ? bucketValue : [bucketValue];
                }
                
                // Extract notify array for bucket alerts
                let notifyValue = sensorStatus.notify;
                if (notifyValue !== undefined && typeof notifyValue === 'object' && notifyValue.value !== undefined) {
                  notifyValue = notifyValue.value;
                }
                if (notifyValue === undefined && sensorStatus.values?.notify?.value !== undefined) {
                  notifyValue = sensorStatus.values.notify.value;
                }
                if (notifyValue !== undefined && Array.isArray(notifyValue)) {
                  mappedDevice.notify = notifyValue;
                }
                
                logger.log(`🪣 [IntelliSpace] Dehumidifier ${fullSN}: bucket=${JSON.stringify(mappedDevice.bucket)}, notify=${JSON.stringify(mappedDevice.notify)}`);
              }
              
              logger.log(`💧 [IntelliSpace] Dehumidifier ${fullSN}: on=${mappedDevice.isOn}, humidity=${mappedDevice.humidityAmbientPercent}%, setpoint=${mappedDevice.humiditySetpointPercent}%, modes=${JSON.stringify(modeSettings)}, toggles=${JSON.stringify(dehumidifierToggleSettings)}`);
              break;
          }
          
          // Extract battery level for all device types (if available)
          const batteryValue = getValue(realTimeStatus, 'battery', 'batteryLevel', 'battery_level', 'Battery');
          if (batteryValue !== undefined) {
            mappedDevice.battery = Number(batteryValue);
            logger.log(`🔋 [IntelliSpace] ${fullSN}: battery=${mappedDevice.battery}%`);
          }
          
          // =========================================================================
          // TRAIT-BASED PARSING SUPPLEMENT
          // Parse device state using traits to fill in any gaps the switch-case missed
          // This enables new device types to work automatically if they have correct traits
          // =========================================================================
          const deviceTraits = mappedDevice.traits || getDefaultTraitsForDeviceType(deviceType);
          if (deviceTraits.length > 0) {
            const traitParsedState = parseDeviceStateByTraits(realTimeStatus, deviceTraits, {
              deviceType,
              isWebSocket: false,
              debug: false,
            });
            
            // Supplement missing fields from trait-based parsing
            // Only fill in undefined fields to preserve device-type-specific logic
            if (mappedDevice.isOn === undefined && traitParsedState.isOn !== undefined) {
              mappedDevice.isOn = traitParsedState.isOn;
            }
            if (mappedDevice.switchOn === undefined && traitParsedState.switchOn !== undefined) {
              mappedDevice.switchOn = traitParsedState.switchOn;
            }
            if (mappedDevice.power === undefined && traitParsedState.power !== undefined) {
              mappedDevice.power = traitParsedState.power;
            }
            if (mappedDevice.brightness === undefined && traitParsedState.brightness !== undefined) {
              mappedDevice.brightness = traitParsedState.brightness;
            }
            if (mappedDevice.colorTemp === undefined && traitParsedState.colorTemp !== undefined) {
              mappedDevice.colorTemp = traitParsedState.colorTemp;
            }
            if (mappedDevice.fanSpeed === undefined && traitParsedState.fanSpeed !== undefined) {
              mappedDevice.fanSpeed = traitParsedState.fanSpeed;
            }
            if (mappedDevice.openPercent === undefined && traitParsedState.openPercent !== undefined) {
              mappedDevice.openPercent = traitParsedState.openPercent;
            }
            if (mappedDevice.isOpen === undefined && traitParsedState.isOpen !== undefined) {
              mappedDevice.isOpen = traitParsedState.isOpen;
            }
            if (mappedDevice.temperature === undefined && traitParsedState.temperature !== undefined) {
              mappedDevice.temperature = traitParsedState.temperature;
            }
            if (mappedDevice.humidity === undefined && traitParsedState.humidity !== undefined) {
              mappedDevice.humidity = traitParsedState.humidity;
            }
            if (mappedDevice.setTemperature === undefined && traitParsedState.setTemperature !== undefined) {
              mappedDevice.setTemperature = traitParsedState.setTemperature;
            }
            if (mappedDevice.mode === undefined && traitParsedState.mode !== undefined) {
              mappedDevice.mode = traitParsedState.mode;
            }
            if (mappedDevice.watt === undefined && traitParsedState.watt !== undefined) {
              mappedDevice.watt = traitParsedState.watt;
            }
            if (mappedDevice.wattHour === undefined && traitParsedState.wattHour !== undefined) {
              mappedDevice.wattHour = traitParsedState.wattHour;
            }
            if (mappedDevice.voltage === undefined && traitParsedState.voltage !== undefined) {
              mappedDevice.voltage = traitParsedState.voltage;
            }
            if (mappedDevice.current === undefined && traitParsedState.current !== undefined) {
              mappedDevice.current = traitParsedState.current;
            }
            if (mappedDevice.presence === undefined && traitParsedState.presence !== undefined) {
              mappedDevice.presence = traitParsedState.presence;
            }
            if (mappedDevice.occupancy === undefined && traitParsedState.occupancy !== undefined) {
              mappedDevice.occupancy = traitParsedState.occupancy;
            }
            if (mappedDevice.luxValue === undefined && traitParsedState.luxValue !== undefined) {
              mappedDevice.luxValue = traitParsedState.luxValue;
            }
            if (mappedDevice.motionDetected === undefined && traitParsedState.motionDetected !== undefined) {
              mappedDevice.motionDetected = traitParsedState.motionDetected;
            }
            if (mappedDevice.co2 === undefined && traitParsedState.co2 !== undefined) {
              mappedDevice.co2 = traitParsedState.co2;
            }
            if (mappedDevice.tvoc === undefined && traitParsedState.tvoc !== undefined) {
              mappedDevice.tvoc = traitParsedState.tvoc;
            }
            if (mappedDevice.pm25 === undefined && traitParsedState.pm25 !== undefined) {
              mappedDevice.pm25 = traitParsedState.pm25;
            }
            if (mappedDevice.battery === undefined && traitParsedState.battery !== undefined) {
              mappedDevice.battery = traitParsedState.battery;
            }
            // Merge attributes from trait-based parsing
            if (traitParsedState.attributes) {
              mappedDevice.attributes = {
                ...mappedDevice.attributes,
                ...traitParsedState.attributes,
              };
            }
          }
        }

        // Respect API's online status completely - don't use data presence to infer status
        // If API explicitly says online: false, device is offline regardless of any cached data
        if (realTimeStatus) {
          // If API explicitly says online: false, respect it - don't override
          if (realTimeStatus.online === false || realTimeStatus.online === 'false') {
            mappedDevice.status = 'offline';
            mappedDevice.online = false;
          } else if (isOnline) {
            // Device is online according to API
            mappedDevice.status = 'online';
            mappedDevice.online = true;
          } else {
            // Device is offline (online is not true and not explicitly false)
            mappedDevice.status = 'offline';
            mappedDevice.online = false;
          }
        }

        return mappedDevice;
      });

      // Deduplicate devices:
      // 1. Always deduplicate exact same deviceId (duplicates from API)
      // 2. For data channels, prefer CONTROL endpoints over SENSOR endpoints
      //    Priority: DEHUMIDIFIER > Light > FAN > CURTAIN > ULTRON_SENSOR > others
      // 3. Keep different numbered channels like #0, #3, #5 (these are different device units)
      
      // Helper function to get endpoint priority (higher = better)
      const getEndpointPriority = (deviceId: string): number => {
        const suffix = deviceId.includes('#') ? deviceId.split('#')[1].toUpperCase() : '';
        // Control endpoints have highest priority
        if (suffix === 'DEHUMIDIFIER') return 100;
        if (suffix === 'LIGHT') return 90;
        if (suffix === 'FAN') return 85;
        if (suffix === 'CURTAIN' || suffix === 'BLIND') return 80;
        if (suffix === 'OUTLET1' || suffix.startsWith('OUTLET')) return 75;
        // Sensor endpoints have lower priority
        if (suffix === 'ULTRON_SENSOR' || suffix === 'ULTRON SENSOR') return 50;
        if (suffix === 'SENSOR') return 45;
        if (suffix.includes('DATA') || suffix.includes('_DATA')) return 10;
        return 30; // Default for unknown non-numeric suffixes
      };
      
      const uniqueDevices = mappedDevices.reduce((acc: IntelliSpaceDevice[], current: IntelliSpaceDevice) => {
        // First: Check if exact same deviceId already exists (always deduplicate exact duplicates)
        const exactDuplicate = acc.find(d => d.deviceId === current.deviceId);
        if (exactDuplicate) {
          logger.log(`⚠️ [IntelliSpace] Skipping exact duplicate: ${current.deviceId}`);
          return acc;
        }
        
        const currentBaseSN = current.deviceId.split('#')[0];
        const currentSuffix = current.deviceId.includes('#') ? current.deviceId.split('#')[1] : '';
        
        // Check if suffix is a data channel (non-numeric) like mokoTH_data, ULTRON_SENSOR, DEHUMIDIFIER
        const isDataChannel = currentSuffix && isNaN(Number(currentSuffix));
        
        if (!isDataChannel) {
          // Numbered channel (like #0, #3, #5) - keep as they are different device units
          acc.push(current);
        } else {
          // Data channel - check for duplicates with same base SN
          const existingIndex = acc.findIndex(d => {
            const dBaseSN = d.deviceId.split('#')[0];
            const dSuffix = d.deviceId.includes('#') ? d.deviceId.split('#')[1] : '';
            const dIsDataChannel = dSuffix && isNaN(Number(dSuffix));
            return dBaseSN === currentBaseSN && dIsDataChannel;
          });
          
          if (existingIndex === -1) {
            acc.push(current);
          } else {
            const existing = acc[existingIndex];
            const currentPriority = getEndpointPriority(current.deviceId);
            const existingPriority = getEndpointPriority(existing.deviceId);
            
            if (currentPriority > existingPriority) {
              logger.log(`⚠️ [IntelliSpace] Replacing ${existing.deviceId} (priority=${existingPriority}) with higher priority ${current.deviceId} (priority=${currentPriority})`);
              acc[existingIndex] = current;
            } else {
              logger.log(`⚠️ [IntelliSpace] Skipping lower priority data channel: ${current.deviceId} (priority=${currentPriority} vs existing ${existingPriority})`);
            }
          }
        }
        return acc;
      }, []);

      logger.log(`📋 [IntelliSpace] Filtered ${mappedDevices.length} -> ${uniqueDevices.length} unique devices`);

      // Simplified Optimistic Lock: stale data filtering for Polling updates
      // Uses same unified 30s timeout as WebSocket filtering
      setDevices(currentDevices => {
        // Create a map of current devices for easy lookup
        const currentDeviceMap = new Map(currentDevices.map(d => [d.deviceId, d]));
        const LOCK_TIMEOUT = 30000; // Unified 30 second timeout
        
        return uniqueDevices.map(newDevice => {
          const { pending, lockKey } = findPendingLock(newDevice.deviceId);
          if (pending) {
            const now = Date.now();
            const timeSinceCommand = now - pending.timestamp;
            const MIN_TIMESTAMP_DELTA = 500; // Same as WebSocket
            
            // Strategy 0: Check device lastUpdate timestamp (if available)
            // If the device's lastUpdate is newer than command time, accept it (manual operation)
            const deviceTimestamp = newDevice.lastUpdate 
              ? new Date(newDevice.lastUpdate).getTime() 
              : undefined;
            
            if (deviceTimestamp && deviceTimestamp > pending.timestamp + MIN_TIMESTAMP_DELTA) {
              if (isDebugMode) logger.log(`🕐 [IntelliSpace] Device timestamp newer for ${newDevice.deviceId} (device: ${deviceTimestamp} > cmd: ${pending.timestamp} + ${MIN_TIMESTAMP_DELTA}ms), accepting Polling update`);
              if (lockKey) pendingCommandUpdates.current.delete(lockKey);
              if (lockKey) cancelVerificationTimer(lockKey);
              return newDevice;
            }
            
            // Strategy 1: Timeout - if more than 30 seconds, accept any update
            if (timeSinceCommand > LOCK_TIMEOUT) {
              if (isDebugMode) logger.log(`⏰ [IntelliSpace] Timeout reached for ${newDevice.deviceId} (${timeSinceCommand}ms > ${LOCK_TIMEOUT}ms), accepting Polling update`);
              if (lockKey) pendingCommandUpdates.current.delete(lockKey);
              return newDevice;
            }
            
            // Strategy 2: State matches expectation - accept and unlock
            if (stateMatchesExpected(newDevice, pending.expectedState)) {
              if (isDebugMode) logger.log(`✅ [IntelliSpace] State matches expected for ${newDevice.deviceId} via Polling. Clearing pending lock.`);
              if (lockKey) pendingCommandUpdates.current.delete(lockKey);
              if (lockKey) cancelVerificationTimer(lockKey);
              return newDevice;
            }
            
            // Strategy 3: State mismatch - keep current device (filter stale data)
            const currentDevice = currentDeviceMap.get(newDevice.deviceId);
            if (currentDevice) {
              if (isDebugMode) logger.log(`🛡️ [IntelliSpace] Filtering Polling update for ${newDevice.deviceId}. State mismatch during lock period (${timeSinceCommand}ms / ${LOCK_TIMEOUT}ms)`);
              return currentDevice;
            }
          }
          return newDevice;
        });
      });
      setLastRefresh(new Date());
      
      // Cache devices to localStorage for instant loading next time
      try {
        localStorage.setItem(DEVICES_CACHE_KEY, JSON.stringify(uniqueDevices));
        logger.log('💾 [IntelliSpace] Cached devices to localStorage');
      } catch (e) {
        logger.error('Failed to cache devices:', e);
      }

      // ✅ Clean up Overview selections - remove devices that no longer exist
      const validDeviceIds = new Set(uniqueDevices.map(d => d.deviceId));
      
      // Clean selectedPowerMeters
      setSelectedPowerMeters(prev => {
        const cleaned = prev.filter(id => validDeviceIds.has(id));
        if (cleaned.length !== prev.length) {
          logger.log(`🧹 [IntelliSpace] Cleaned selectedPowerMeters: ${prev.length} -> ${cleaned.length}`);
          localStorage.setItem('intellispace-selected-power-meters', JSON.stringify(cleaned));
        }
        return cleaned;
      });
      
      // Clean selectedThSensors
      setSelectedThSensors(prev => {
        const cleaned = prev.filter(id => validDeviceIds.has(id));
        if (cleaned.length !== prev.length) {
          logger.log(`🧹 [IntelliSpace] Cleaned selectedThSensors: ${prev.length} -> ${cleaned.length}`);
          localStorage.setItem('intellispace-selected-th-sensors', JSON.stringify(cleaned));
        }
        return cleaned;
      });
      
      // Clean selectedSwitches
      setSelectedSwitches(prev => {
        const cleaned = prev.filter(id => validDeviceIds.has(id));
        if (cleaned.length !== prev.length) {
          logger.log(`🧹 [IntelliSpace] Cleaned selectedSwitches: ${prev.length} -> ${cleaned.length}`);
          localStorage.setItem('intellispace-selected-switches', JSON.stringify(cleaned));
        }
        return cleaned;
      });
      
      // Clean selectedHvac
      setSelectedHvac(prev => {
        const cleaned = prev.filter(id => validDeviceIds.has(id));
        if (cleaned.length !== prev.length) {
          logger.log(`🧹 [IntelliSpace] Cleaned selectedHvac: ${prev.length} -> ${cleaned.length}`);
          localStorage.setItem('intellispace-selected-hvac', JSON.stringify(cleaned));
        }
        return cleaned;
      });
      
      // Clean selectedLights
      setSelectedLights(prev => {
        const cleaned = prev.filter(id => validDeviceIds.has(id));
        if (cleaned.length !== prev.length) {
          logger.log(`🧹 [IntelliSpace] Cleaned selectedLights: ${prev.length} -> ${cleaned.length}`);
          localStorage.setItem('intellispace-selected-lights', JSON.stringify(cleaned));
        }
        return cleaned;
      });
      
      // Clean selectedPresenceSensors
      setSelectedPresenceSensors(prev => {
        const cleaned = prev.filter(id => validDeviceIds.has(id));
        if (cleaned.length !== prev.length) {
          logger.log(`🧹 [IntelliSpace] Cleaned selectedPresenceSensors: ${prev.length} -> ${cleaned.length}`);
          localStorage.setItem('intellispace-selected-presence-sensors', JSON.stringify(cleaned));
        }
        return cleaned;
      });
      
      // Clean selectedHvacMeters
      setSelectedHvacMeters(prev => {
        const cleaned = prev.filter(id => validDeviceIds.has(id));
        if (cleaned.length !== prev.length) {
          logger.log(`🧹 [IntelliSpace] Cleaned selectedHvacMeters: ${prev.length} -> ${cleaned.length}`);
          localStorage.setItem('intellispace-selected-hvac-meters', JSON.stringify(cleaned));
        }
        return cleaned;
      });
      
      // Clean selectedLightingMeters
      setSelectedLightingMeters(prev => {
        const cleaned = prev.filter(id => validDeviceIds.has(id));
        if (cleaned.length !== prev.length) {
          logger.log(`🧹 [IntelliSpace] Cleaned selectedLightingMeters: ${prev.length} -> ${cleaned.length}`);
          localStorage.setItem('intellispace-selected-lighting-meters', JSON.stringify(cleaned));
        }
        return cleaned;
      });
      
      // Clean excludedRankingDevices
      setExcludedRankingDevices(prev => {
        const cleaned = prev.filter(id => validDeviceIds.has(id));
        if (cleaned.length !== prev.length) {
          logger.log(`🧹 [IntelliSpace] Cleaned excludedRankingDevices: ${prev.length} -> ${cleaned.length}`);
          localStorage.setItem('intellispace-excluded-ranking-devices', JSON.stringify(cleaned));
        }
        return cleaned;
      });
      
      // Clean mergedDeviceSNs - remove devices that no longer exist
      const validBaseSNs = new Set(uniqueDevices.map(d => d.baseSN));
      setMergedDeviceSNs(prev => {
        const cleanedArray: string[] = [];
        let needsCleanup = false;
        
        prev.forEach(baseSN => {
          if (validBaseSNs.has(baseSN)) {
            cleanedArray.push(baseSN);
          } else {
            needsCleanup = true;
            // Remove from Firestore
            userPreferencesApi.setMultiChannelDisplayMode(baseSN, 'expanded').catch(error => {
              logger.error(`Failed to remove multi-channel display mode for ${baseSN}:`, error);
            });
          }
        });
        
        if (needsCleanup) {
          logger.log(`🧹 [IntelliSpace] Cleaned mergedDeviceSNs: ${prev.size} -> ${cleanedArray.length}`);
        }
        
        return new Set(cleanedArray);
      });

      // Setup WebSocket subscriptions (use unique base SNs, not deviceId with endpoint suffix)
      const uniqueBaseSNs = Array.from(new Set(uniqueDevices.map(d => d.baseSN)));
      setWebsocketDeviceSNs(uniqueBaseSNs);

    } catch (error) {
      logger.error('Error loading IntelliSpace devices:', error);
      setError(t.intelliSpace?.loadError || 'Failed to load devices');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIntelliSpaceDevices, getDeviceType, t.intelliSpace]);

  // Deep merge for nested delta objects (e.g., color.temperature)
  const deepMerge = (target: any, source: any): any => {
    const result = { ...target };
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = deepMerge(target[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
    return result;
  };

  // WebSocket handler - with enhanced debug logging and payload normalization
  const handleWebSocketUpdate = useCallback((data: any) => {
    // 🔍 DEBUG: Log full raw payload before any processing
    logWebSocketDebug('Raw WebSocket Event', data, { source: 'handleWebSocketUpdate' });
    
    // Normalize the payload using the unified parser
    const normalized = normalizeWebSocketPayload(data);
    if (!normalized) {
      if (isDebugMode) logger.warn('⚠️ [IntelliSpace] WebSocket update could not be normalized:', data);
      return;
    }
    
    const { deviceSN: targetDeviceId, baseSN, endpoint: wsEndpoint, iotState: normalizedIotState } = normalized;

    // Debug: Log normalized payload (only in debug mode)
    if (isDebugMode) {
      console.log('📡 [IntelliSpace] WebSocket normalized:', { 
      targetDeviceId,
      baseSN, 
      endpoint: wsEndpoint, 
      stateKeys: Object.keys(normalizedIotState),
    });
    }
    
    // Clear executing state if this device was being controlled
    if (executingDeviceIdRef.current && (targetDeviceId === executingDeviceIdRef.current || executingDeviceIdRef.current.startsWith(baseSN))) {
      logger.log('✅ [IntelliSpace] Command completed, received WebSocket update for:', targetDeviceId);
      setIsExecutingCommand(false);
      executingDeviceIdRef.current = null;
    }

    // Track devices that need verification scheduling (collected during state update, scheduled after)
    // This avoids side effects inside the state updater function
    const devicesToVerify: Array<{ deviceId: string; commandId: number }> = [];
    
    setDevices(prev => {
      let matchedDevice = false;
      
      // Debug: Log available devices for matching (only in debug mode)
      if (isDebugMode) {
        const availableDeviceIds = prev.map(d => d.deviceId).slice(0, 10);
        console.log(`🔍 [IntelliSpace] Looking for ${targetDeviceId} in ${prev.length} devices:`, availableDeviceIds);
      }
      
      const updated = prev.map(device => {
        // ✅ IMPORTANT: WebSocket only pushes ONE endpoint at a time
        // Device matching strategies:
        // 1. Exact match: device.deviceId === targetDeviceId (e.g., both are "UTU32A-xxx#1")
        // 2. baseSN match: WebSocket sends "UTU32A-xxx#1", dashboard has "UTU32A-xxx" (no endpoint)
        // 3. Reverse match: WebSocket sends "UTU32A-xxx", dashboard has "UTU32A-xxx#1"
        
        const isExactMatch = device.deviceId === targetDeviceId;
        
        // Match by baseSN when WebSocket has endpoint but dashboard device doesn't
        // e.g., WS: "UTU32A-xxx#1", Dashboard: "UTU32A-xxx"
        const wsHasEndpoint = wsEndpoint || targetDeviceId.includes('#');
        const deviceHasEndpoint = device.deviceId.includes('#');
        const isBaseSNMatch = wsHasEndpoint && !deviceHasEndpoint && device.baseSN === baseSN;
        
        // Match by baseSN when dashboard has endpoint but WebSocket doesn't
        // e.g., WS: "UTU32A-xxx", Dashboard: "UTU32A-xxx#1"
        const isReverseMatch = !wsHasEndpoint && deviceHasEndpoint && device.baseSN === baseSN;
        
        // Also match if device.baseSN matches and both don't have endpoints
        const isBothNoEndpoint = !wsHasEndpoint && !deviceHasEndpoint && device.baseSN === baseSN;
        
        if (!isExactMatch && !isBaseSNMatch && !isReverseMatch && !isBothNoEndpoint) {
          return device;
        }

        matchedDevice = true;
        // Only log match in debug mode
        if (isDebugMode) {
          const matchType = isExactMatch ? 'exact' : isBaseSNMatch ? 'baseSN' : isReverseMatch ? 'reverse' : 'noEndpoint';
          console.log(`✅ [WS] Matched ${device.deviceId} (${matchType})`);
        }

        // ✅ Use normalized iotState from the unified parser (already handles all formats)
        const iotState = normalizedIotState;
        if (!iotState || Object.keys(iotState).length === 0) {
          if (isDebugMode) logger.warn('⚠️ [IntelliSpace] WebSocket update has empty iotState:', { deviceId: device.deviceId, normalized });
          return device; // 無 state 則不更新
        }
        const updatedDevice = { ...device, status: 'online' as const, lastUpdate: new Date().toISOString() };

        // ✅ IMPORTANT: WebSocket only pushes ONE endpoint at a time
        // ✅ If wsEndpoint is specified, the data is for that specific endpoint only
        // ✅ If no wsEndpoint, data is for the device without endpoint (single-endpoint device)
        // ✅ We should use the data directly, NOT try to extract from arrays
        const getArrayValue = (arr: any, fallback: any) => {
        // ✅ Handle nested { value: ... } format from API
        let val = arr;
        if (typeof val === 'object' && val !== null && 'value' in val) {
          val = val.value;
        }
        
        // ✅ If WebSocket specified an endpoint, value is direct (for that endpoint only)
        if (wsEndpoint && !Array.isArray(val)) {
          // Value is already for the specific endpoint, use it directly
          return val;
        }
        
        // ✅ If no wsEndpoint specified:
        // - If value is array, it might be for all endpoints, but we only update the matched device
        // - If value is NOT array, it's a single value for this device
        // Since we already matched by exact deviceId or baseSN (without endpoint),
        // we can use the value directly
        if (Array.isArray(val)) {
          // For arrays, take the last value (latest reading)
          // But since WebSocket pushes one endpoint at a time, this shouldn't happen
          // If it does, log a warning
          if (isDebugMode && val.length > 1) {
            logger.warn(`⚠️ [IntelliSpace] WebSocket sent array for single endpoint update: ${device.deviceId}`, val);
          }
          return val.length > 0 ? val[val.length - 1] : fallback;
        }
        
          // ✅ Single value - use it directly
          return val;
        };

        // Debug: Log device type matching for WebSocket updates
        logger.log(`📡 [IntelliSpace] WebSocket processing for ${device.deviceId} (type: ${device.deviceType})`, {
          stateKeys: Object.keys(iotState),
          hasCurrentModeSettings: !!iotState.currentModeSettings,
          hasOn: iotState.on !== undefined,
        });

        // =========================================================================
        // PHASE 1: TRAIT-BASED PARSING FIRST
        // Parse WebSocket delta using traits to extract ALL available state
        // This ensures we capture state from any field format
        // DeviceType-specific logic below can then override/refine these values
        // =========================================================================
        const deviceTraits = device.traits || getDefaultTraitsForDeviceType(device.deviceType);
        if (deviceTraits.length > 0) {
          const traitParsedState = parseDeviceStateByTraits(iotState, deviceTraits, {
            deviceType: device.deviceType,
            isWebSocket: true,
            debug: isDebugMode,
          });
          
          // Apply trait-parsed values as base (deviceType-specific logic can override)
          if (traitParsedState.isOn !== undefined) updatedDevice.isOn = traitParsedState.isOn;
          if (traitParsedState.switchOn !== undefined) updatedDevice.switchOn = traitParsedState.switchOn;
          if (traitParsedState.power !== undefined) updatedDevice.power = traitParsedState.power;
          if (traitParsedState.brightness !== undefined) updatedDevice.brightness = traitParsedState.brightness;
          if (traitParsedState.colorTemp !== undefined) updatedDevice.colorTemp = traitParsedState.colorTemp;
          if (traitParsedState.fanSpeed !== undefined) updatedDevice.fanSpeed = traitParsedState.fanSpeed;
          if (traitParsedState.openPercent !== undefined) updatedDevice.openPercent = traitParsedState.openPercent;
          if (traitParsedState.isOpen !== undefined) updatedDevice.isOpen = traitParsedState.isOpen;
          if (traitParsedState.temperature !== undefined) updatedDevice.temperature = traitParsedState.temperature;
          if (traitParsedState.humidity !== undefined) updatedDevice.humidity = traitParsedState.humidity;
          if (traitParsedState.setTemperature !== undefined) updatedDevice.setTemperature = traitParsedState.setTemperature;
          if (traitParsedState.mode !== undefined) updatedDevice.mode = traitParsedState.mode;
          if (traitParsedState.watt !== undefined) updatedDevice.watt = traitParsedState.watt;
          if (traitParsedState.wattHour !== undefined) updatedDevice.wattHour = traitParsedState.wattHour;
          if (traitParsedState.voltage !== undefined) updatedDevice.voltage = traitParsedState.voltage;
          if (traitParsedState.current !== undefined) updatedDevice.current = traitParsedState.current;
          if (traitParsedState.presence !== undefined) updatedDevice.presence = traitParsedState.presence;
          if (traitParsedState.occupancy !== undefined) updatedDevice.occupancy = traitParsedState.occupancy;
          if (traitParsedState.luxValue !== undefined) updatedDevice.luxValue = traitParsedState.luxValue;
          if (traitParsedState.motionDetected !== undefined) updatedDevice.motionDetected = traitParsedState.motionDetected;
          if (traitParsedState.co2 !== undefined) updatedDevice.co2 = traitParsedState.co2;
          if (traitParsedState.tvoc !== undefined) updatedDevice.tvoc = traitParsedState.tvoc;
          if (traitParsedState.pm25 !== undefined) updatedDevice.pm25 = traitParsedState.pm25;
          if (traitParsedState.battery !== undefined) updatedDevice.battery = traitParsedState.battery;
          if (traitParsedState.attributes) {
            updatedDevice.attributes = { ...updatedDevice.attributes, ...traitParsedState.attributes };
          }
        }

        // =========================================================================
        // PHASE 2: DEVICE-TYPE SPECIFIC LOGIC
        // This can override/refine trait-based values with device-specific handling
        // =========================================================================
        switch (device.deviceType) {
          case 'power_meter_3phase':
          case 'power_meter':
            // Support both 'watt' and 'real_power' for different meter types
            const wattValue = iotState.watt ?? iotState.real_power;
            if (wattValue !== undefined) {
              const rawWatt = getArrayValue(wattValue, device.watt);
              const newWatt = rawWatt !== undefined ? Number(rawWatt) : undefined;
              if (newWatt !== undefined && !isNaN(newWatt)) updatedDevice.watt = newWatt;
            }
            if (iotState.watt_hour !== undefined) {
              const rawWh = getArrayValue(iotState.watt_hour, device.wattHour);
              const newWh = rawWh !== undefined ? Number(rawWh) : undefined;
              if (newWh !== undefined && !isNaN(newWh)) {
                updatedDevice.wattHour = newWh;
                // ✅ 記錄 WebSocket 最後更新時間（用於 fallback polling 判斷）
                lastWsWattHourUpdateRef.current = Date.now();
                // ✅ 即時更新今日用電量（使用 targetDeviceId/deviceSN 作為 key）
                let baseline = wattHourBaselineRef.current.get(targetDeviceId);
                
                // ✅ 如果 baseline 未設置，更新 currentWattHourRef 並等待下次更新計算差值
                if (baseline === undefined) {
                  // 暫時將當前值設為 baseline（供下次更新計算增量）
                  const prevValue = currentWattHourRef.current.get(targetDeviceId);
                  if (prevValue === undefined) {
                    // 第一次收到這個設備的 watt_hour，暫存為基準
                    currentWattHourRef.current.set(targetDeviceId, newWh);
                    if (isDebugMode) {
                      logger.log(`📊 [IntelliSpace] First watt_hour received for ${targetDeviceId}: ${newWh.toFixed(3)} kWh (stored as temp baseline)`);
                    }
                  } else if (newWh > prevValue) {
                    // 有之前的值，可以計算增量（雖然沒有 00:00 基準值）
                    currentWattHourRef.current.set(targetDeviceId, newWh);
                    // 使用增量更新今日用電量
                    const increment = newWh - prevValue;
                    setTodayMinuteEnergy(prev => prev + increment);
                    if (isDebugMode) {
                      logger.log(`⚡ [IntelliSpace] Incremental energy update (no baseline): ${targetDeviceId} += ${increment.toFixed(3)} kWh`);
                    }
                  }
                } else {
                  // ✅ 有 baseline，正常計算
                  const currentValue = currentWattHourRef.current.get(targetDeviceId) || baseline;
                  if (newWh >= currentValue) {
                    currentWattHourRef.current.set(targetDeviceId, newWh);
                    // 重新計算總今日用電量
                    const newTotalEnergy = calculateTodayEnergy();
                    // ✅ 只有當新計算的值 > 0 時才更新，避免覆蓋正確的值
                    if (newTotalEnergy > 0) {
                      setTodayMinuteEnergy(newTotalEnergy);
                      if (isDebugMode) {
                        logger.log(`⚡ [IntelliSpace] Real-time energy update: ${targetDeviceId} = ${(newWh - baseline).toFixed(3)} kWh (total: ${newTotalEnergy.toFixed(3)} kWh)`);
                      }
                    } else {
                      // 如果計算結果為 0，計算單設備增量並累加
                      const deviceEnergy = newWh - baseline;
                      if (deviceEnergy > 0 && selectedPowerMeters.includes(targetDeviceId)) {
                        const prevDeviceEnergy = currentValue - baseline;
                        const increment = deviceEnergy - prevDeviceEnergy;
                        if (increment > 0) {
                          setTodayMinuteEnergy(prev => prev + increment);
                        }
                      }
                    }
                  } else if (isDebugMode) {
                    logger.warn(`⚠️ [IntelliSpace] Ignoring out-of-order watt_hour: ${targetDeviceId} received ${newWh}, current is ${currentValue}`);
                  }
                }
              }
            }
            if (iotState.voltage !== undefined) {
              const rawV = getArrayValue(iotState.voltage, device.voltage);
              const newV = rawV !== undefined ? Number(rawV) : undefined;
              if (newV !== undefined && !isNaN(newV)) updatedDevice.voltage = newV;
            }
            if (iotState.current !== undefined) {
              const rawA = getArrayValue(iotState.current, device.current);
              const newA = rawA !== undefined ? Number(rawA) : undefined;
              if (newA !== undefined && !isNaN(newA)) updatedDevice.current = newA;
            }
            // Three-phase data via WebSocket - write to both root and ["1"] for compatibility
            // Only include defined values to avoid storing undefined properties
            if (iotState.voltage_r !== undefined || iotState.voltage_s !== undefined || iotState.voltage_t !== undefined ||
                iotState.current_r !== undefined || iotState.current_s !== undefined || iotState.current_t !== undefined ||
                iotState.real_power_r !== undefined || iotState.real_power_s !== undefined || iotState.real_power_t !== undefined) {
              const threePhaseData: Record<string, number> = {};
              if (iotState.voltage_r !== undefined) threePhaseData.voltage_r = iotState.voltage_r;
              if (iotState.voltage_s !== undefined) threePhaseData.voltage_s = iotState.voltage_s;
              if (iotState.voltage_t !== undefined) threePhaseData.voltage_t = iotState.voltage_t;
              if (iotState.current_r !== undefined) threePhaseData.current_r = iotState.current_r;
              if (iotState.current_s !== undefined) threePhaseData.current_s = iotState.current_s;
              if (iotState.current_t !== undefined) threePhaseData.current_t = iotState.current_t;
              if (iotState.real_power_r !== undefined) threePhaseData.real_power_r = iotState.real_power_r;
              if (iotState.real_power_s !== undefined) threePhaseData.real_power_s = iotState.real_power_s;
              if (iotState.real_power_t !== undefined) threePhaseData.real_power_t = iotState.real_power_t;
              
              if (Object.keys(threePhaseData).length > 0) {
                updatedDevice.attributes = deepMerge(updatedDevice.attributes || {}, {
                  ...threePhaseData,
                  "1": threePhaseData,
                });
                
                // ✅ FIX: Compute total watt from three-phase power data
                // Use the new values from iotState if available, otherwise preserve existing values from attributes
                const existingAttrs = updatedDevice.attributes || {};
                const powerR = iotState.real_power_r ?? existingAttrs.real_power_r ?? existingAttrs["1"]?.real_power_r ?? 0;
                const powerS = iotState.real_power_s ?? existingAttrs.real_power_s ?? existingAttrs["1"]?.real_power_s ?? 0;
                const powerT = iotState.real_power_t ?? existingAttrs.real_power_t ?? existingAttrs["1"]?.real_power_t ?? 0;
                const totalWatt = Number(powerR) + Number(powerS) + Number(powerT);
                if (!isNaN(totalWatt) && totalWatt >= 0) {
                  updatedDevice.watt = totalWatt;
                }
                
                if (isDebugMode) logger.log(`⚡ [IntelliSpace] Three-phase WebSocket update for ${device.deviceId}: R=${powerR}W, S=${powerS}W, T=${powerT}W, Total=${totalWatt}W`);
              }
            }
            // Three-phase energy (watt_hour_r/s/t) - check each value individually
            // Write to both root and ["1"] for compatibility (same pattern as three-phase power)
            if (iotState.watt_hour_r !== undefined || iotState.watt_hour_s !== undefined || iotState.watt_hour_t !== undefined) {
              const phaseEnergyData: Record<string, number> = {};
              if (iotState.watt_hour_r !== undefined) phaseEnergyData.watt_hour_r = iotState.watt_hour_r;
              if (iotState.watt_hour_s !== undefined) phaseEnergyData.watt_hour_s = iotState.watt_hour_s;
              if (iotState.watt_hour_t !== undefined) phaseEnergyData.watt_hour_t = iotState.watt_hour_t;
              
              if (Object.keys(phaseEnergyData).length > 0) {
                updatedDevice.attributes = deepMerge(updatedDevice.attributes || {}, {
                  ...phaseEnergyData,
                  "1": phaseEnergyData,
                });
              }
            }
            // Power attributes - write to both root and ["1"] for compatibility
            // Collect all power attributes that are defined
            const powerAttrsData: Record<string, number> = {};
            if (iotState.apparent_power !== undefined) powerAttrsData.apparent_power = iotState.apparent_power;
            if (iotState.reactive_power !== undefined) powerAttrsData.reactive_power = iotState.reactive_power;
            if (iotState.power_factor !== undefined) powerAttrsData.power_factor = iotState.power_factor;
            if (iotState.frequency !== undefined) powerAttrsData.frequency = iotState.frequency;
            
            if (Object.keys(powerAttrsData).length > 0) {
              updatedDevice.attributes = deepMerge(updatedDevice.attributes || {}, {
                ...powerAttrsData,
                "1": powerAttrsData,
              });
            }
            break;
          case 'th_sensor':
            if (iotState.temp !== undefined) {
              const newTemp = getArrayValue(iotState.temp, device.temperature);
              if (newTemp !== undefined && newTemp !== null) {
                const tempNum = Number(newTemp);
                // Validate temperature: -50 to 60°C (reasonable range)
                if (!isNaN(tempNum) && isFinite(tempNum) && tempNum >= -50 && tempNum <= 60) {
                  updatedDevice.temperature = tempNum;
                } else {
                  logger.warn(`⚠️ [IntelliSpace] Invalid temperature value from WebSocket: ${newTemp}`);
                }
              }
            }
            if (iotState.rh !== undefined) {
              const newRh = getArrayValue(iotState.rh, device.humidity);
              if (newRh !== undefined && newRh !== null) {
                const humidityNum = Number(newRh);
                // Validate humidity: 0 to 100% (reasonable range)
                if (!isNaN(humidityNum) && isFinite(humidityNum) && humidityNum >= 0 && humidityNum <= 100) {
                  updatedDevice.humidity = humidityNum;
                } else {
                  logger.warn(`⚠️ [IntelliSpace] Invalid humidity value from WebSocket: ${newRh}`);
                }
              }
            }
            // For TH sensors, 'power' represents battery level
            if (iotState.power !== undefined) {
              const newBattery = getArrayValue(iotState.power, device.battery);
              if (newBattery !== undefined) updatedDevice.battery = Number(newBattery);
            }
            // CO2
            if (iotState.co2 !== undefined) {
              const co2Value = getArrayValue(iotState.co2, device.co2);
              if (co2Value !== undefined) {
                const co2Num = Number(co2Value);
                updatedDevice.co2 = co2Num;
                updatedDevice.attributes = deepMerge(updatedDevice.attributes || {}, { co2: co2Num });
              }
            }
            // TVOC
            if (iotState.tvoc !== undefined) {
              const tvocValue = getArrayValue(iotState.tvoc, device.tvoc);
              if (tvocValue !== undefined) {
                const tvocNum = Number(tvocValue);
                updatedDevice.tvoc = tvocNum;
                updatedDevice.attributes = deepMerge(updatedDevice.attributes || {}, { tvoc: tvocNum });
              }
            }
            // PM2.5
            if (iotState.pm25 !== undefined) {
              const pm25Value = getArrayValue(iotState.pm25, device.pm25);
              if (pm25Value !== undefined) {
                const pm25Num = Number(pm25Value);
                updatedDevice.pm25 = pm25Num;
                updatedDevice.attributes = deepMerge(updatedDevice.attributes || {}, { pm25: pm25Num });
              }
            }
            break;
          case 'presence_sensor':
            // Update presence/occupancy - check multiple locations
            let newPresence: boolean | undefined;
            let newOccupancy: boolean | undefined;
            
            // Check iotState first
            if (iotState.presence !== undefined) {
              newPresence = getArrayValue(iotState.presence, device.presence);
            }
            if (iotState.occupancy !== undefined) {
              newOccupancy = getArrayValue(iotState.occupancy, device.occupancy);
            }
            
            // Check data.iotState["1"] or data.states["1"] (UTU32A format)
            if (newPresence === undefined || newOccupancy === undefined) {
              const sensorData = data.iotState?.["1"] || data.states?.["1"] || data.attributes?.["1"] || iotState["1"];
              if (sensorData) {
                if (newPresence === undefined && sensorData.presence !== undefined) {
                  newPresence = Array.isArray(sensorData.presence) 
                    ? sensorData.presence[sensorData.presence.length - 1] 
                    : sensorData.presence;
                }
                if (newOccupancy === undefined && sensorData.occupancy !== undefined) {
                  newOccupancy = Array.isArray(sensorData.occupancy) 
                    ? sensorData.occupancy[sensorData.occupancy.length - 1] 
                    : sensorData.occupancy;
                }
              }
            }
            
            // Check data.attributes root level (fallback)
            if (newPresence === undefined && data.attributes?.presence !== undefined) {
              newPresence = Array.isArray(data.attributes.presence) 
                ? data.attributes.presence[data.attributes.presence.length - 1] 
                : data.attributes.presence;
            }
            if (newOccupancy === undefined && data.attributes?.occupancy !== undefined) {
              newOccupancy = Array.isArray(data.attributes.occupancy) 
                ? data.attributes.occupancy[data.attributes.occupancy.length - 1] 
                : data.attributes.occupancy;
            }
            
            // ✅ FIX: Handle { value: X } format from API (same as initial load fix)
            if (newPresence !== undefined && typeof newPresence === 'object' && (newPresence as any).value !== undefined) {
              newPresence = (newPresence as any).value;
            }
            if (newOccupancy !== undefined && typeof newOccupancy === 'object' && (newOccupancy as any).value !== undefined) {
              newOccupancy = (newOccupancy as any).value;
            }
            
            if (newPresence !== undefined) {
              updatedDevice.presence = newPresence;
              if (isDebugMode) logger.log(`👤 [IntelliSpace] WebSocket updated presence for ${device.deviceId}: ${newPresence}`);
            }
            if (newOccupancy !== undefined) {
              updatedDevice.occupancy = newOccupancy;
              if (isDebugMode) logger.log(`👤 [IntelliSpace] WebSocket updated occupancy for ${device.deviceId}: ${newOccupancy}`);
            }
            
            // Update Lux value for UTU32A sensors
            // ✅ PRIORITY FIX: Check normalized iotState FIRST (WebSocket delta format)
            // WebSocket sends: { deviceSN: "UTU32A-xxx#1", lux: 128 } -> normalized as iotState.lux
            let newLux: number | undefined;
            
            // ✅ 1. Check normalized iotState FIRST (most common WebSocket format)
            if (iotState.lux !== undefined) {
              newLux = extractLuxValue(iotState.lux);
            }
            if (newLux === undefined && iotState.illuminance !== undefined) {
              newLux = extractLuxValue(iotState.illuminance);
            }
            
            // 2. Check nested iotState["1"] (some devices nest data)
            if (newLux === undefined && iotState["1"]) {
              const nested = iotState["1"];
              if (nested.lux !== undefined) {
                newLux = extractLuxValue(nested.lux);
              } else if (nested.illuminance !== undefined) {
                newLux = extractLuxValue(nested.illuminance);
              }
            }
            
            // 3. Fallback to data.iotState["1"] or data.attributes (legacy formats)
            if (newLux === undefined) {
              const sensorData = data.iotState?.["1"] || data.states?.["1"] || 
                                 data.attributes?.["1"] || data.attributes?.Light;
            if (sensorData) {
                if (sensorData.lux !== undefined) {
                  newLux = extractLuxValue(sensorData.lux);
                } else if (sensorData.illuminance !== undefined) {
                newLux = extractLuxValue(sensorData.illuminance);
                }
              }
            }
            
            // 4. Check data.attributes root level (final fallback)
            if (newLux === undefined && data.attributes?.lux !== undefined) {
              newLux = extractLuxValue(data.attributes.lux);
            }
            if (newLux === undefined && data.attributes?.illuminance !== undefined) {
              newLux = extractLuxValue(data.attributes.illuminance);
            }
            
            if (newLux !== undefined) {
              updatedDevice.luxValue = newLux;
              // Only log lux updates in debug mode
              if (isDebugMode) console.log(`☀️ [WS] ${device.deviceId} lux=${newLux}`);
            }
            break;
          case 'lux_sensor':
            // Update Lux value for pure lux sensors (same logic as presence_sensor lux handling)
            let sensorDataLux: any = null;
            
            // Check data.iotState["2"] first (Matter lux sensors often on endpoint 2)
            if (data.iotState && (data.iotState["2"] || data.iotState["1"])) {
              sensorDataLux = data.iotState["2"] || data.iotState["1"];
              if (isDebugMode) logger.log(`☀️ [IntelliSpace] WebSocket lux_sensor from data.iotState for ${device.deviceId}`);
            }
            // Check data.states (old WebSocket format)
            else if (data.states && (data.states["2"] || data.states["1"])) {
              sensorDataLux = data.states["2"] || data.states["1"];
            }
            // Check data.attributes
            else if (data.attributes && (data.attributes["2"] || data.attributes["1"])) {
              sensorDataLux = data.attributes["2"] || data.attributes["1"];
            }
            // Fallback to iotState
            if (!sensorDataLux) {
              sensorDataLux = iotState["2"] || iotState["1"] || iotState;
            }
            
            let wsLuxValue: number | undefined;
            
            // Extract lux from sensorDataLux
            if (sensorDataLux) {
              if (sensorDataLux.illuminance !== undefined) {
                wsLuxValue = extractLuxValue(sensorDataLux.illuminance);
              }
              if (wsLuxValue === undefined && sensorDataLux.lux !== undefined) {
                wsLuxValue = extractLuxValue(sensorDataLux.lux);
              }
            }
            
            // Check root level iotState
            if (wsLuxValue === undefined && iotState.illuminance !== undefined) {
              wsLuxValue = extractLuxValue(iotState.illuminance);
            }
            if (wsLuxValue === undefined && iotState.lux !== undefined) {
              wsLuxValue = extractLuxValue(iotState.lux);
            }
            
            if (wsLuxValue !== undefined) {
              updatedDevice.luxValue = wsLuxValue;
              if (isDebugMode) logger.log(`✅ [IntelliSpace] WebSocket updated lux_sensor ${device.deviceId}: ${wsLuxValue}`);
            }
            break;
          case 'motion_sensor':
            // Update motion detection state from WebSocket
            // Check detected.value for "motionDetected"
            let wsMotionDetected = false;
            
            // Check iotState.detected (flattened)
            if (iotState.detected !== undefined) {
              const detectedValue = iotState.detected?.value ?? iotState.detected;
              if (Array.isArray(detectedValue)) {
                wsMotionDetected = detectedValue.includes('motionDetected');
              } else if (typeof detectedValue === 'boolean') {
                wsMotionDetected = detectedValue;
              } else if (typeof detectedValue === 'string') {
                wsMotionDetected = detectedValue === 'motionDetected';
              }
            }
            // Check data.iotState for detected
            else if (data.iotState?.detected !== undefined) {
              const detectedValue = data.iotState.detected?.value ?? data.iotState.detected;
              if (Array.isArray(detectedValue)) {
                wsMotionDetected = detectedValue.includes('motionDetected');
              } else if (typeof detectedValue === 'boolean') {
                wsMotionDetected = detectedValue;
              } else if (typeof detectedValue === 'string') {
                wsMotionDetected = detectedValue === 'motionDetected';
              }
            }
            // Check data.states for detected
            else if (data.states) {
              const stateData = Array.isArray(data.states) 
                ? data.states.find((s: any) => s.endpoint === 'pir_sensor')
                : data.states;
              if (stateData?.values?.detected?.value !== undefined) {
                const detectedValue = stateData.values.detected.value;
                if (Array.isArray(detectedValue)) {
                  wsMotionDetected = detectedValue.includes('motionDetected');
                } else if (typeof detectedValue === 'boolean') {
                  wsMotionDetected = detectedValue;
                } else if (typeof detectedValue === 'string') {
                  wsMotionDetected = detectedValue === 'motionDetected';
                }
              }
            }
            // Check for motionDetected field directly
            else if (iotState.motionDetected !== undefined) {
              wsMotionDetected = iotState.motionDetected === true;
            }
            else if (iotState.motion !== undefined) {
              wsMotionDetected = iotState.motion === true;
            }
            
            updatedDevice.motionDetected = wsMotionDetected;
            if (isDebugMode) logger.log(`👁️ [IntelliSpace] WebSocket updated motionDetected for ${device.deviceId}: ${wsMotionDetected}`);
            break;
          case 'door_sensor':
            // Update door open/close state from WebSocket
            // Check detected.value for "opened"/"closed"
            let wsDoorIsOpen = false;
            
            // Check iotState.detected (flattened)
            if (iotState.detected !== undefined) {
              const detectedValue = iotState.detected?.value ?? iotState.detected;
              if (Array.isArray(detectedValue)) {
                wsDoorIsOpen = detectedValue.includes('opened');
              } else if (typeof detectedValue === 'string') {
                wsDoorIsOpen = detectedValue === 'opened';
              }
            }
            // Check data.iotState for detected
            else if (data.iotState?.detected !== undefined) {
              const detectedValue = data.iotState.detected?.value ?? data.iotState.detected;
              if (Array.isArray(detectedValue)) {
                wsDoorIsOpen = detectedValue.includes('opened');
              } else if (typeof detectedValue === 'string') {
                wsDoorIsOpen = detectedValue === 'opened';
              }
            }
            // Check data.states for detected
            else if (data.states) {
              const stateData = Array.isArray(data.states) 
                ? data.states.find((s: any) => s.endpoint === 'door_sensor' || s.endpoint === 'contact_sensor')
                : data.states;
              if (stateData?.values?.detected?.value !== undefined) {
                const detectedValue = stateData.values.detected.value;
                if (Array.isArray(detectedValue)) {
                  wsDoorIsOpen = detectedValue.includes('opened');
                } else if (typeof detectedValue === 'string') {
                  wsDoorIsOpen = detectedValue === 'opened';
                }
              }
            }
            
            updatedDevice.isOpen = wsDoorIsOpen;
            if (isDebugMode) logger.log(`🚪 [IntelliSpace] WebSocket updated isOpen for ${device.deviceId}: ${wsDoorIsOpen}`);
            break;
          case 'hvac':
            if (iotState.on !== undefined) {
              const newOn = getArrayValue(iotState.on, device.isOn);
              if (newOn !== undefined) updatedDevice.isOn = newOn;
            }
            // Handle mode from multiple sources (priority: thermostatMode > mode > currentModeSettings.Mode)
            // ✅ Update BOTH mode and thermostatMode for UI consistency
            if (iotState.thermostatMode !== undefined) {
              const newMode = getArrayValue(iotState.thermostatMode, device.mode);
              if (newMode !== undefined) {
                updatedDevice.mode = newMode;
                updatedDevice.thermostatMode = newMode;
              }
            } else if (iotState.mode !== undefined) {
              const newMode = getArrayValue(iotState.mode, device.mode);
              if (newMode !== undefined) {
                updatedDevice.mode = newMode;
                updatedDevice.thermostatMode = newMode;
              }
            } else if (iotState.currentModeSettings?.Mode !== undefined) {
              const newMode = getArrayValue(iotState.currentModeSettings.Mode, device.mode);
              if (newMode !== undefined) {
                updatedDevice.mode = newMode;
                updatedDevice.thermostatMode = newMode;
              }
            }
            // Handle fanSpeed from multiple sources - comprehensive extraction
            let wsFanSpeed = iotState.fanSpeed ?? iotState.currentFanSpeedSetting ?? iotState.fan_speed;
            // Unwrap currentModeSettings if it's in { value: ... } format
            let hvacModeSettings = iotState.currentModeSettings;
            if (hvacModeSettings && typeof hvacModeSettings === 'object' && hvacModeSettings.value !== undefined) {
              hvacModeSettings = hvacModeSettings.value;
            }
            // Check currentModeSettings.FanSpeed (common in WebSocket responses)
            if (wsFanSpeed === undefined && hvacModeSettings?.FanSpeed !== undefined) {
              wsFanSpeed = hvacModeSettings.FanSpeed;
            }
            // Check data.currentModeSettings (raw data might have it at root)
            if (wsFanSpeed === undefined && data.currentModeSettings) {
              let rawModeSettings = data.currentModeSettings;
              if (typeof rawModeSettings === 'object' && rawModeSettings.value !== undefined) {
                rawModeSettings = rawModeSettings.value;
              }
              if (rawModeSettings?.FanSpeed !== undefined) {
                wsFanSpeed = rawModeSettings.FanSpeed;
              }
            }
            if (wsFanSpeed !== undefined) {
              const newFan = getArrayValue(wsFanSpeed, device.fanSpeed);
              if (newFan !== undefined) {
                updatedDevice.fanSpeed = String(newFan);
                logger.log(`❄️ [IntelliSpace] WebSocket updated fanSpeed for HVAC ${device.deviceId}: ${newFan}`);
              }
            }
            // Handle setTemperature - with NaN validation
            if (iotState.thermostatTemperatureSetpoint !== undefined) {
              const newSetTemp = getArrayValue(iotState.thermostatTemperatureSetpoint, device.setTemperature);
              if (newSetTemp !== undefined) {
                const setTempNum = Number(newSetTemp);
                // Validate setpoint: 16 to 32°C (typical HVAC range)
                if (!isNaN(setTempNum) && isFinite(setTempNum) && setTempNum >= 16 && setTempNum <= 32) {
                  updatedDevice.setTemperature = setTempNum;
                } else {
                  logger.warn(`⚠️ [IntelliSpace] Invalid HVAC setTemperature value from WebSocket: ${newSetTemp}`);
                }
              }
            }
            // Ambient temperature (current environment temperature) - with NaN validation
            if (iotState.thermostatTemperatureAmbient !== undefined) {
              const newTemp = getArrayValue(iotState.thermostatTemperatureAmbient, device.temperature);
              if (newTemp !== undefined) {
                const tempNum = Number(newTemp);
                // Validate temperature: -50 to 60°C (reasonable range)
                if (!isNaN(tempNum) && isFinite(tempNum) && tempNum >= -50 && tempNum <= 60) {
                  updatedDevice.temperature = tempNum;
                } else {
                  logger.warn(`⚠️ [IntelliSpace] Invalid HVAC temperature value from WebSocket: ${newTemp}`);
                }
              }
            }
            // Ambient humidity - with NaN validation
            if (iotState.thermostatHumidityAmbient !== undefined) {
              const newHumidity = getArrayValue(iotState.thermostatHumidityAmbient, device.humidity);
              if (newHumidity !== undefined) {
                const humidityNum = Number(newHumidity);
                // Validate humidity: 0 to 100% (reasonable range)
                if (!isNaN(humidityNum) && isFinite(humidityNum) && humidityNum >= 0 && humidityNum <= 100) {
                  updatedDevice.humidity = humidityNum;
                } else {
                  logger.warn(`⚠️ [IntelliSpace] Invalid HVAC humidity value from WebSocket: ${newHumidity}`);
                }
              }
            }
            break;
          case 'switch':
            if (iotState.on !== undefined) {
              const newOn = getArrayValue(iotState.on, device.switchOn);
              if (newOn !== undefined) updatedDevice.switchOn = newOn;
            }
            // Switch devices may also have power metrics
            if (iotState.watt !== undefined) {
              const rawWatt = getArrayValue(iotState.watt, device.watt);
              const newWatt = rawWatt !== undefined ? Number(rawWatt) : undefined;
              if (newWatt !== undefined && !isNaN(newWatt)) updatedDevice.watt = newWatt;
            }
            if (iotState.watt_hour !== undefined) {
              const rawWh = getArrayValue(iotState.watt_hour, device.wattHour);
              const newWh = rawWh !== undefined ? Number(rawWh) : undefined;
              if (newWh !== undefined && !isNaN(newWh)) updatedDevice.wattHour = newWh;
            }
            if (iotState.voltage !== undefined) {
              const rawV = getArrayValue(iotState.voltage, device.voltage);
              const newV = rawV !== undefined ? Number(rawV) : undefined;
              if (newV !== undefined && !isNaN(newV)) updatedDevice.voltage = newV;
            }
            if (iotState.current !== undefined) {
              const rawA = getArrayValue(iotState.current, device.current);
              const newA = rawA !== undefined ? Number(rawA) : undefined;
              if (newA !== undefined && !isNaN(newA)) updatedDevice.current = newA;
            }
            break;
          case 'light':
            if (iotState.on !== undefined) {
              const newOn = getArrayValue(iotState.on, device.isOn);
              if (newOn !== undefined) {
                updatedDevice.isOn = newOn;
                updatedDevice.power = newOn;
              }
            }
            if (iotState.brightness !== undefined) {
              const newBrightness = getArrayValue(iotState.brightness, device.brightness);
              if (newBrightness !== undefined) updatedDevice.brightness = newBrightness;
            }
            // Color temperature - check multiple formats
            // Format 1: iotState.color.temperatureK (nested)
            // Format 2: iotState.colorTemperature or colorTemp (direct)
            // Format 3: data.color.temperatureK (raw data)
            let wsColorTemp: number | undefined;
            if (iotState.color?.temperatureK !== undefined) {
              wsColorTemp = Array.isArray(iotState.color.temperatureK) 
                ? getArrayValue(iotState.color.temperatureK, device.colorTemp)
                : iotState.color.temperatureK;
            } else if (iotState.colorTemperature !== undefined) {
              wsColorTemp = getArrayValue(iotState.colorTemperature, device.colorTemp);
            } else if (iotState.colorTemp !== undefined) {
              wsColorTemp = getArrayValue(iotState.colorTemp, device.colorTemp);
            } else if (data.color?.temperatureK !== undefined) {
              wsColorTemp = Array.isArray(data.color.temperatureK) 
                ? getArrayValue(data.color.temperatureK, device.colorTemp)
                : data.color.temperatureK;
            }
            if (wsColorTemp !== undefined) {
              updatedDevice.colorTemp = wsColorTemp;
              logger.log(`💡 [IntelliSpace] WebSocket updated colorTemp for Light ${device.deviceId}: ${wsColorTemp}K`);
            }
            // RGB color (spectrumRGB)
            if (iotState.color?.spectrumRGB !== undefined) {
              updatedDevice.attributes = deepMerge(updatedDevice.attributes || {}, { spectrumRGB: iotState.color.spectrumRGB });
            }
            // HSV color (spectrumHsv)
            if (iotState.color?.spectrumHsv !== undefined) {
              updatedDevice.attributes = deepMerge(updatedDevice.attributes || {}, { spectrumHsv: iotState.color.spectrumHsv });
            }
            break;
          case 'fan':
            if (iotState.on !== undefined) {
              const newOn = getArrayValue(iotState.on, device.isOn);
              if (newOn !== undefined) {
                updatedDevice.isOn = newOn;
                updatedDevice.power = newOn;
              }
            }
            
            // ✅ Handle fanSpeed from multiple sources - comprehensive extraction
            // WebSocket delta format: { currentModeSettings: { FanSpeed: "1" } }
            // or wrapped format: { currentModeSettings: { value: { FanSpeed: "1" } } }
            let fanSpeedValue = iotState.fanSpeed ?? iotState.currentFanSpeedSetting ?? iotState.fan_speed;
            
            // Unwrap currentModeSettings if it's in { value: ... } format
            let fanModeSettings = iotState.currentModeSettings;
            if (fanModeSettings && typeof fanModeSettings === 'object' && fanModeSettings.value !== undefined) {
              fanModeSettings = fanModeSettings.value;
            }
            
            // Check currentModeSettings.FanSpeed (WebSocket delta format)
            if (fanSpeedValue === undefined && fanModeSettings?.FanSpeed !== undefined) {
              fanSpeedValue = fanModeSettings.FanSpeed;
            }
            
            // Check data.currentModeSettings (raw data might have it at root)
            if (fanSpeedValue === undefined && data.currentModeSettings) {
              let rawFanModeSettings = data.currentModeSettings;
              if (typeof rawFanModeSettings === 'object' && rawFanModeSettings.value !== undefined) {
                rawFanModeSettings = rawFanModeSettings.value;
              }
              if (rawFanModeSettings?.FanSpeed !== undefined) {
                fanSpeedValue = rawFanModeSettings.FanSpeed;
              }
            }
            
            // Also check currentToggleSettings for toggle updates
            let fanToggleSettings = iotState.currentToggleSettings;
            if (fanToggleSettings !== undefined && typeof fanToggleSettings === 'object' && fanToggleSettings.value !== undefined) {
              fanToggleSettings = fanToggleSettings.value;
            }
            if (fanToggleSettings) {
              (updatedDevice as any).currentToggleSettings = fanToggleSettings;
            }
            
            if (fanSpeedValue !== undefined) {
              const newFanSpeed = getArrayValue(fanSpeedValue, device.fanSpeed);
              if (newFanSpeed !== undefined) {
                updatedDevice.fanSpeed = String(newFanSpeed);
              }
            }
            
            // Always log fan WebSocket updates for debugging
            logger.log(`🌀 [IntelliSpace] Fan WebSocket update for ${device.deviceId}:`, {
              on: updatedDevice.isOn,
              fanSpeed: updatedDevice.fanSpeed,
              rawCurrentModeSettings: iotState.currentModeSettings,
              unwrappedModeSettings: fanModeSettings,
            });
            break;
          case 'erv':
            // ERV (全熱交換機): Handle on, thermostatMode, fanSpeed, temp, humidity, sensorData
            if (iotState.on !== undefined) {
              const newOn = getArrayValue(iotState.on, device.isOn);
              if (newOn !== undefined) {
                updatedDevice.isOn = newOn;
                updatedDevice.power = newOn;
              }
            }
            
            // Thermostat mode (fan-only, heat)
            if (iotState.thermostatMode !== undefined) {
              const newMode = getArrayValue(iotState.thermostatMode, device.thermostatMode);
              if (newMode !== undefined) {
                updatedDevice.thermostatMode = newMode;
                updatedDevice.mode = newMode;
              }
            }
            
            // Fan speed from currentModeSettings
            let ervModeSettings = iotState.currentModeSettings;
            if (ervModeSettings && typeof ervModeSettings === 'object' && ervModeSettings.value !== undefined) {
              ervModeSettings = ervModeSettings.value;
            }
            if (ervModeSettings?.FanSpeed !== undefined) {
              updatedDevice.fanSpeed = String(ervModeSettings.FanSpeed);
            }
            if (ervModeSettings) {
              (updatedDevice as any).currentModeSettings = ervModeSettings;
            }
            
            // Temperature
            if (iotState.thermostatTemperatureAmbient !== undefined) {
              const newTemp = getArrayValue(iotState.thermostatTemperatureAmbient, device.temperature);
              if (newTemp !== undefined) {
                const tempNum = Number(newTemp);
                if (!isNaN(tempNum) && isFinite(tempNum) && tempNum >= -50 && tempNum <= 60) {
                  updatedDevice.temperature = tempNum;
                }
              }
            }
            
            // Humidity
            if (iotState.humidityAmbientPercent !== undefined) {
              const newHumidity = getArrayValue(iotState.humidityAmbientPercent, device.humidity);
              if (newHumidity !== undefined) {
                const humidityNum = Number(newHumidity);
                if (!isNaN(humidityNum) && isFinite(humidityNum) && humidityNum >= 0 && humidityNum <= 100) {
                  updatedDevice.humidity = humidityNum;
                }
              }
            }
            
            // Sensor state data (PM2.5, CO2)
            let ervSensorData = iotState.currentSensorStateData;
            if (ervSensorData && typeof ervSensorData === 'object' && !Array.isArray(ervSensorData) && ervSensorData.value !== undefined) {
              ervSensorData = ervSensorData.value;
            }
            if (ervSensorData) {
              // Ensure it's an array
              const sensorArray = Array.isArray(ervSensorData) ? ervSensorData : [];
              (updatedDevice as any).currentSensorStateData = sensorArray;
            }
            
            // ✅ Also handle direct pm25/co2 values (from Ultron Sensor endpoint WebSocket updates)
            if (iotState.pm25 !== undefined || iotState.pm2_5 !== undefined) {
              let pm25Val = iotState.pm25 ?? iotState.pm2_5;
              if (pm25Val && typeof pm25Val === 'object' && pm25Val.value !== undefined) {
                pm25Val = pm25Val.value;
              }
              if (pm25Val !== undefined) {
                (updatedDevice as any).pm25 = Array.isArray(pm25Val) ? pm25Val : [pm25Val];
              }
            }
            if (iotState.co2 !== undefined) {
              let co2Val = iotState.co2;
              if (co2Val && typeof co2Val === 'object' && co2Val.value !== undefined) {
                co2Val = co2Val.value;
              }
              if (co2Val !== undefined) {
                (updatedDevice as any).co2 = Array.isArray(co2Val) ? co2Val : [co2Val];
              }
            }
            
            logger.log(`🌀 [IntelliSpace] ERV WebSocket update for ${device.deviceId}:`, {
              on: updatedDevice.isOn,
              thermostatMode: updatedDevice.thermostatMode,
              fanSpeed: updatedDevice.fanSpeed,
              temperature: updatedDevice.temperature,
              humidity: updatedDevice.humidity,
              pm25: (updatedDevice as any).pm25,
              co2: (updatedDevice as any).co2,
              sensorData: ervSensorData,
            });
            break;
          case 'outlet':
            if (iotState.on !== undefined) {
              const newOn = getArrayValue(iotState.on, device.switchOn);
              if (newOn !== undefined) {
                updatedDevice.switchOn = newOn;
                updatedDevice.isOn = newOn;
                updatedDevice.power = newOn;  // Keep consistent with initial load (line 2243)
              }
            }
            // Power metering
            if (iotState.watt !== undefined) {
              const rawWatt = getArrayValue(iotState.watt, device.watt);
              const newWatt = rawWatt !== undefined ? Number(rawWatt) : undefined;
              if (newWatt !== undefined && !isNaN(newWatt)) updatedDevice.watt = newWatt;
            }
            if (iotState.watt_hour !== undefined) {
              const rawWh = getArrayValue(iotState.watt_hour, device.wattHour);
              const newWh = rawWh !== undefined ? Number(rawWh) : undefined;
              if (newWh !== undefined && !isNaN(newWh)) updatedDevice.wattHour = newWh;
            }
            if (iotState.voltage !== undefined) {
              const rawV = getArrayValue(iotState.voltage, device.voltage);
              const newV = rawV !== undefined ? Number(rawV) : undefined;
              if (newV !== undefined && !isNaN(newV)) updatedDevice.voltage = newV;
            }
            if (iotState.current !== undefined) {
              const rawA = getArrayValue(iotState.current, device.current);
              const newA = rawA !== undefined ? Number(rawA) : undefined;
              if (newA !== undefined && !isNaN(newA)) updatedDevice.current = newA;
            }
            break;
          case 'curtain':
          case 'blind':
            // Curtain/Blind devices - handle openPercent (0-100, 0 = closed, 100 = fully open)
            // Check multiple possible locations for openPercent
            let wsOpenPercent: number | undefined;
            
            // Helper to unwrap value
            const unwrapValue = (val: any): any => {
              if (val && typeof val === 'object' && val.value !== undefined) {
                return Array.isArray(val.value) ? val.value[0] : val.value;
              }
              return Array.isArray(val) ? val[0] : val;
            };
            
            // 1. Direct from iotState (delta)
            if (iotState.openPercent !== undefined) {
              wsOpenPercent = unwrapValue(iotState.openPercent);
            }
            // 2. Check data.iotState.openPercent (alternative format)
            else if (data.iotState?.openPercent !== undefined) {
              wsOpenPercent = unwrapValue(data.iotState.openPercent);
            }
            // 3. Check data.state.openPercent (API format)
            else if (data.state?.openPercent !== undefined) {
              wsOpenPercent = unwrapValue(data.state.openPercent);
            }
            // 4. Check data.delta.openPercent (raw delta format)
            else if (data.delta?.openPercent !== undefined) {
              wsOpenPercent = unwrapValue(data.delta.openPercent);
            }
            // 5. Check openDirection (some devices report direction instead of percent)
            if (wsOpenPercent === undefined && (iotState.openDirection !== undefined || data.iotState?.openDirection !== undefined)) {
              const direction = iotState.openDirection ?? data.iotState?.openDirection;
              // UP/OPEN = opening, DOWN/CLOSE = closing
              if (isDebugMode) {
                logger.log(`🪟 [IntelliSpace] Curtain/Blind ${device.deviceId} direction update: ${direction}`);
              }
            }
            
            if (wsOpenPercent !== undefined) {
              const openPercentNum = Number(wsOpenPercent);
              // Validate openPercent: 0 to 100%
              if (!isNaN(openPercentNum) && isFinite(openPercentNum) && openPercentNum >= 0 && openPercentNum <= 100) {
                updatedDevice.openPercent = openPercentNum;
                // Also update isOpen based on openPercent (considered open if > 0)
                updatedDevice.isOpen = openPercentNum > 0;
                if (isDebugMode) {
                  logger.log(`🪟 [IntelliSpace] WebSocket updated ${device.deviceType} ${device.deviceId}: openPercent=${openPercentNum}%`);
                }
              }
            }
            
            // Check for openState (some devices report this instead of or in addition to openPercent)
            const wsOpenState = iotState.openState ?? data.iotState?.openState ?? data.state?.openState;
            if (wsOpenState !== undefined) {
              const isCurrentlyOpen = wsOpenState === 'OPEN' || wsOpenState === true;
              updatedDevice.isOpen = isCurrentlyOpen;
              if (isDebugMode) {
                logger.log(`🪟 [IntelliSpace] WebSocket updated ${device.deviceType} ${device.deviceId}: openState=${wsOpenState}, isOpen=${isCurrentlyOpen}`);
              }
            }
            break;
          case 'button':
            // Buttons typically only have events, no persistent state, but can record last pressed
            if (iotState.lastPressed !== undefined) {
              updatedDevice.attributes = deepMerge(updatedDevice.attributes || {}, { lastPressed: iotState.lastPressed });
            }
            break;
        }

        // Update battery for all device types (if available)
        if (iotState.battery !== undefined) {
          const newBattery = getArrayValue(iotState.battery, device.battery);
          if (newBattery !== undefined) updatedDevice.battery = Number(newBattery);
        }

        // Update online status (common for all device types)
        if (data.online !== undefined) {
          updatedDevice.online = data.online;
          (updatedDevice as IntelliSpaceDevice).status = data.online ? 'online' : 'offline';
        }

        // Note: Trait-based parsing is now executed BEFORE the switch-case (see PHASE 1 above)
        // This ensures all fields are captured from any format, and deviceType-specific logic refines them

        // Simplified Optimistic Lock: stale data filtering with unified 30s timeout
        // This prevents the "On-Off-On flicker" issue where stale WebSocket data overwrites optimistic updates
        // Strategy priority:
        //   1. Timestamp newer than command -> accept (new event, e.g. manual operation)
        //   2. Timeout (30s) -> accept and trigger verification
        //   3. State matches expected -> accept and unlock
        //   4. State mismatch with old/no timestamp -> ignore (stale data)
        const { pending, lockKey } = findPendingLock(device.deviceId);
        
        // Only log when there's a pending lock (stale filter is active)
        const wsOnValue = iotState.on !== undefined ? getArrayValue(iotState.on, undefined) : undefined;
        if (pending) {
          console.log(`🔒 [WS Stale Filter] ${device.deviceId}: wsOn=${wsOnValue}, expected=${pending.expectedState.on}`);
        }
        
        if (pending) {
          const now = Date.now();
          const timeSinceCommand = now - pending.timestamp;
          const LOCK_TIMEOUT = 30000; // Unified 30 second timeout for all device types
          const MIN_TIMESTAMP_DELTA = 500; // Minimum delta to avoid clock skew issues
          
          // 🔍 DEBUG: Log stale filtering analysis
          if (isDebugMode) {
            logWebSocketDebug('Stale Filter Analysis (Simplified)', {
              deviceId: device.deviceId,
              commandId: pending.commandId,
              pending: {
                expectedState: pending.expectedState,
                timeSinceCommand,
              },
              incoming: {
                on: iotState.on,
                fanSpeed: iotState.fanSpeed || iotState.currentModeSettings?.FanSpeed,
                brightness: iotState.brightness,
                openPercent: iotState.openPercent,
                timestamp: normalized.timestamp,
              }
            });
          }
          
          // Strategy 1: WebSocket timestamp is significantly newer than command time
          // This indicates a new event (e.g. user manually operated the device during lock period)
          // Use normalized.timestamp which now always has a value (falls back to receivedAt)
          const wsTimestamp = normalized.timestamp;
          // Only trust the timestamp if it's from the server (not our local receivedAt fallback)
          // We can detect this by checking if timestamp differs from receivedAt
          const isServerTimestamp = wsTimestamp && wsTimestamp !== normalized.receivedAt;
          
          if (isServerTimestamp && wsTimestamp > pending.timestamp + MIN_TIMESTAMP_DELTA) {
            if (isDebugMode) {
              logFilterDecision(device.deviceId, { 
                accept: true, 
                reason: `timestamp_newer (ws: ${wsTimestamp} > cmd: ${pending.timestamp} + ${MIN_TIMESTAMP_DELTA}ms)` 
              }, pending);
            }
            if (lockKey) pendingCommandUpdates.current.delete(lockKey);
            if (lockKey) cancelVerificationTimer(lockKey);
            return updatedDevice;
          }
          
          // Strategy 2: Timeout reached - unlock and trigger verification
          if (timeSinceCommand > LOCK_TIMEOUT) {
            if (isDebugMode) {
              logFilterDecision(device.deviceId, { 
                accept: true, 
                reason: `timeout (${timeSinceCommand}ms > ${LOCK_TIMEOUT}ms)` 
              }, pending);
            }
            // Mark for verification after state update (avoid side effect in state updater)
            // Note: Do NOT delete pending state here - let the verification callback
            // delete it in its finally block. This ensures the callback can see the
            // pending state and perform verification instead of skipping.
            devicesToVerify.push({ deviceId: device.deviceId, commandId: pending.commandId });
            return updatedDevice;
          }
          
          // Strategy 3: Check if WebSocket state matches expected - accept and unlock if match
          // ✅ IMPORTANT: Compare WebSocket values (iotState) directly with expected, NOT updatedDevice
          // This ensures we only accept updates that actually match what we expect
          const wsOnValue = iotState.on !== undefined ? getArrayValue(iotState.on, undefined) : undefined;
          const wsOnBool = wsOnValue === true || wsOnValue === 1 || wsOnValue === 'true';
          
          const wsStateMatches = (() => {
            // Check if WebSocket 'on' matches expected 'on'
            if (pending.expectedState.on !== undefined && wsOnValue !== undefined) {
              return wsOnBool === pending.expectedState.on;
            }
            // If WebSocket doesn't have 'on' field, check updatedDevice
            return stateMatchesExpected(updatedDevice, pending.expectedState);
          })();
          
          if (wsStateMatches) {
            // State matches - accept update and clear lock
            if (lockKey) pendingCommandUpdates.current.delete(lockKey);
            if (lockKey) cancelVerificationTimer(lockKey);
            return updatedDevice;
          }
          
          // Strategy 4: State mismatch - BLOCK stale data
          console.log(`🛡️ [Stale Filter] BLOCK ${device.deviceId}: wsOn=${wsOnBool} != expected=${pending.expectedState.on}`);
          return device;
        }

      return updatedDevice;
      });
      
      if (!matchedDevice && isDebugMode) {
        // Only warn in debug mode (some devices may not be in dashboard)
        console.warn(`⚠️ [WS] No match for ${targetDeviceId}`);
      }
      
      // ✅ CRITICAL: Only trigger re-render if at least one device object changed
      // This prevents "flicker" caused by .map() creating new array even when no changes occurred
      const hasActualChange = updated.some((device, idx) => device !== prev[idx]);
      return hasActualChange ? updated : prev;
    });
    
    // ✅ Also update modalDeviceSnapshot if the WebSocket update is for the currently selected device
    // This ensures the modal shows real-time state from WebSocket
    setModalDeviceSnapshot(prev => {
      if (!prev) {
        console.log(`📱 [Modal WS] No modal open, skipping update for ${targetDeviceId}`);
        return prev;
      }
      
      // Check if this WebSocket update is for the selected device
      const prevBaseSN = prev.deviceId.split('#')[0];
      const isMatch = prev.deviceId === targetDeviceId || prevBaseSN === baseSN;
      
      if (!isMatch) {
        console.log(`📱 [Modal WS] Device mismatch: modal=${prev.deviceId}, ws=${targetDeviceId}, skipping`);
        return prev;
      }
      
      // ✅ Check for pending commands - skip stale WebSocket data during optimistic lock period
      const { pending } = findPendingLock(prev.deviceId);
      if (pending) {
        const timeSinceCommand = Date.now() - pending.timestamp;
        const LOCK_TIMEOUT = 30000;
        
        // Only accept WebSocket updates during lock period if they match expected state
        if (timeSinceCommand < LOCK_TIMEOUT) {
          console.log(`📱 [Modal WS] 🔒 Pending command exists for ${targetDeviceId}, checking state match...`);
          const iotState = normalizedIotState;
          
          // For on/off, check if it matches
          if (pending.expectedState.on !== undefined) {
            const wsOn = iotState.on;
            if (wsOn !== undefined && Boolean(wsOn) !== pending.expectedState.on) {
              console.log(`📱 [Modal WS] 🛡️ BLOCK stale on: ws=${wsOn}, expected=${pending.expectedState.on}`);
              return prev;
            }
          }
          
          // For temperature setpoint (HVAC)
          if (pending.expectedState.thermostatTemperatureSetpoint !== undefined) {
            const wsTemp = iotState.thermostatTemperatureSetpoint;
            if (wsTemp !== undefined && Math.abs(Number(wsTemp) - pending.expectedState.thermostatTemperatureSetpoint) > 0.5) {
              console.log(`📱 [Modal WS] 🛡️ BLOCK stale temp: ws=${wsTemp}, expected=${pending.expectedState.thermostatTemperatureSetpoint}`);
              return prev;
            }
          }
          
          // For mode (HVAC)
          if (pending.expectedState.thermostatMode !== undefined) {
            const wsMode = iotState.thermostatMode ?? iotState.mode;
            if (wsMode !== undefined && String(wsMode).toLowerCase() !== String(pending.expectedState.thermostatMode).toLowerCase()) {
              console.log(`📱 [Modal WS] 🛡️ BLOCK stale mode: ws=${wsMode}, expected=${pending.expectedState.thermostatMode}`);
              return prev;
            }
          }
          
          // For fanSpeed (HVAC/Fan)
          if (pending.expectedState.fanSpeed !== undefined) {
            const wsFan = iotState.currentFanSpeedSetting ?? iotState.fanSpeed ?? iotState.currentModeSettings?.FanSpeed;
            if (wsFan !== undefined && String(wsFan) !== String(pending.expectedState.fanSpeed)) {
              console.log(`📱 [Modal WS] 🛡️ BLOCK stale fanSpeed: ws=${wsFan}, expected=${pending.expectedState.fanSpeed}`);
              return prev;
            }
          }
          
          // For brightness (Light)
          if (pending.expectedState.brightness !== undefined) {
            const wsBrightness = iotState.brightness;
            if (wsBrightness !== undefined && Math.abs(Number(wsBrightness) - pending.expectedState.brightness) > 5) {
              console.log(`📱 [Modal WS] 🛡️ BLOCK stale brightness: ws=${wsBrightness}, expected=${pending.expectedState.brightness}`);
              return prev;
            }
          }
          
          // For colorTemp (Light)
          if (pending.expectedState.colorTemp !== undefined) {
            const wsColorTemp = iotState.color?.temperatureK ?? iotState.colorTemperature;
            if (wsColorTemp !== undefined && Math.abs(Number(wsColorTemp) - pending.expectedState.colorTemp) > 100) {
              console.log(`📱 [Modal WS] 🛡️ BLOCK stale colorTemp: ws=${wsColorTemp}, expected=${pending.expectedState.colorTemp}`);
              return prev;
            }
          }
          
          // For openPercent (Curtain/Blind)
          if (pending.expectedState.openPercent !== undefined) {
            const wsOpen = iotState.openPercent;
            if (wsOpen !== undefined && Math.abs(Number(wsOpen) - pending.expectedState.openPercent) > 5) {
              console.log(`📱 [Modal WS] 🛡️ BLOCK stale openPercent: ws=${wsOpen}, expected=${pending.expectedState.openPercent}`);
              return prev;
            }
          }
        }
      }
      
      console.log(`📱 [Modal WS] ✅ Updating modal for ${targetDeviceId}:`, normalizedIotState);
      
      // Apply the same updates from iotState
      const updated = { ...prev };
      const iotState = normalizedIotState;
      
      // Update common state properties from WebSocket
      if (iotState.on !== undefined) {
        updated.isOn = typeof iotState.on === 'boolean' ? iotState.on : Boolean(iotState.on);
        console.log(`📱 [Modal WS] Updated isOn: ${updated.isOn}`);
      }
      if (iotState.thermostatMode !== undefined) {
        updated.mode = String(iotState.thermostatMode);
        updated.thermostatMode = String(iotState.thermostatMode);
        console.log(`📱 [Modal WS] Updated thermostatMode: ${updated.thermostatMode}`);
      } else if (iotState.mode !== undefined) {
        updated.mode = String(iotState.mode);
        updated.thermostatMode = String(iotState.mode);
        console.log(`📱 [Modal WS] Updated mode: ${updated.mode}`);
      }
      if (iotState.thermostatTemperatureSetpoint !== undefined) {
        updated.thermostatTemperatureSetpoint = Number(iotState.thermostatTemperatureSetpoint);
        updated.setTemperature = Number(iotState.thermostatTemperatureSetpoint);
        console.log(`📱 [Modal WS] Updated setTemperature: ${updated.setTemperature}`);
      }
      if (iotState.currentFanSpeedSetting !== undefined || iotState.fanSpeed !== undefined) {
        updated.fanSpeed = String(iotState.currentFanSpeedSetting ?? iotState.fanSpeed);
        console.log(`📱 [Modal WS] Updated fanSpeed: ${updated.fanSpeed}`);
      }
      // ✅ Also check currentModeSettings for fanSpeed (WebSocket format for HVAC)
      if (iotState.currentModeSettings?.FanSpeed !== undefined) {
        updated.fanSpeed = String(iotState.currentModeSettings.FanSpeed);
        console.log(`📱 [Modal WS] Updated fanSpeed from currentModeSettings: ${updated.fanSpeed}`);
      }
      if (iotState.brightness !== undefined) {
        updated.brightness = Number(iotState.brightness);
        console.log(`📱 [Modal WS] Updated brightness: ${updated.brightness}`);
      }
      if (iotState.openPercent !== undefined) {
        updated.openPercent = Number(iotState.openPercent);
        console.log(`📱 [Modal WS] Updated openPercent: ${updated.openPercent}`);
      }
      
      console.log(`📱 [Modal WS] Final updated state:`, updated);
      return updated;
    });
    
    // Schedule verification for devices that timed out (outside of state updater to avoid side effects)
    // Use ref to access scheduleStatusVerification (defined later in the component)
    devicesToVerify.forEach(({ deviceId, commandId }) => {
      scheduleStatusVerificationRef.current?.(deviceId, commandId, 0);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDebugMode]);

  // WebSocket connection state
  const [wsConnected, setWsConnected] = useState(false);
  const [wsSubscribed, setWsSubscribed] = useState(false);
  const wsSubscribedRef = useRef(false);
  const handleWebSocketUpdateRef = useRef(handleWebSocketUpdate);

  // Keep ref up to date
  useEffect(() => {
    handleWebSocketUpdateRef.current = handleWebSocketUpdate;
  }, [handleWebSocketUpdate]);

  // Use global WebSocket manager - subscribe and register callbacks
  useEffect(() => {
    if (websocketDeviceSNs.length === 0) return;

    // Connect and subscribe (only if not already subscribed)
    if (!wsSubscribedRef.current) {
      console.log(`🔌 [WS] Connecting and subscribing to ${websocketDeviceSNs.length} devices...`);
      websocketManager.connect().then(() => {
        if (wsSubscribedRef.current) return; // Double check
        const state = websocketManager.getState();
        console.log(`🔌 [WS] Connected: ${state.isConnected}, Auth: ${state.isAuthenticated}`);
        setWsConnected(state.isConnected);
        console.log(`🔌 [WS] Subscribing devices:`, websocketDeviceSNs.slice(0, 5), '...');
        websocketManager.subscribeDevices(websocketDeviceSNs);
        setWsSubscribed(true);
        wsSubscribedRef.current = true;
        console.log(`🔌 [WS] ✅ Subscription complete`);
      });
    }

    // ✅ ALWAYS register callbacks (even if already subscribed)
    // Register update callback (use ref to avoid re-registering)
    console.log(`🔌 [WS] Registering update callback for ${websocketDeviceSNs.length} devices`);
    const unsubscribeUpdate = websocketManager.onUpdate('intellispace', (data: any) => {
      console.log(`📡 [WS] Received update:`, data);
      handleWebSocketUpdateRef.current(data);
    });

    // Register alert callback
    const unsubscribeAlert = websocketManager.onAlert((alert: any) => {
      window.dispatchEvent(new CustomEvent('new-alert', { detail: alert }));
    });

    // Register connect callback
    const unsubscribeConnect = websocketManager.onConnect(() => {
      setWsConnected(true);
    });

    return () => {
      unsubscribeUpdate();
      unsubscribeAlert();
      unsubscribeConnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websocketDeviceSNs.length]); // Only depend on length, not the array itself

  // Debounced save to localStorage when devices are updated via WebSocket
  useEffect(() => {
    if (devices.length === 0) return;
    
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(DEVICES_CACHE_KEY, JSON.stringify(devices));
        logger.log('💾 [IntelliSpace] Updated device cache (debounced)');
      } catch (e) {
        logger.error('Failed to update device cache:', e);
      }
    }, 2000); // Debounce 2 seconds
    
    return () => clearTimeout(timeoutId);
  }, [devices]);
  
  // Load virtual zones
  useEffect(() => {
    const loadDeviceZones = async () => {
      try {
        const zones = await userPreferencesApi.getVirtualZones('intelliSpace');
        setDeviceZones(zones);
      } catch (error) {
        logger.error('Failed to load device zones:', error);
        setDeviceZones([]);
      }
    };
    loadDeviceZones();
  }, []);

  // Initial load
  useEffect(() => {
    logger.log('🔥 [IntelliSpace] useEffect triggered - calling loadDevices');
    loadDevices();
  }, []);

  // Polling
  useEffect(() => {
    if (!autoRefresh || devices.length === 0) return;

    const intervalId = setInterval(() => {
      loadDevices(true, false);
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [autoRefresh, refreshInterval, devices.length, loadDevices]);

  // Load company name
  useEffect(() => {
    const savedCompanyName = localStorage.getItem('ultron-company-name');
    if (savedCompanyName) setCompanyName(savedCompanyName);
  }, []);

  // Fetch real hourly chart data from API
  const [isLoadingChartData, setIsLoadingChartData] = useState(false);
  const chartDataFetchedRef = useRef(false);
  const isLoadingChartDataRef = useRef(false);
  
  // Function to fetch chart data
  // ✅ 统一的数据提取和转换函数（在组件级别定义，供多个函数使用）
  // 使用台北时区的日期格式化器
  const timezone = 'Asia/Taipei';
  const taipeiFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  // ✅ 辅助函数：处理 MCP API 返回的特殊时间格式
  // API 返回的时间已经是 UTC+8，但格式可能有问题：
  // ✅ 统一的数据提取函数：从 generateReport 响应中提取 chartData
  const extractChartDataFromReport = useCallback((result: any): any[] => {
    let chartData: any[] = [];
    
    if (!result) {
      logger.warn('⚠️ [extractChartDataFromReport] No result provided');
      return chartData;
    }
    
    if (!result.success) {
      logger.warn('⚠️ [extractChartDataFromReport] API call was not successful:', result);
      return chartData;
    }
    
    const reportData = result?.data || {};
    logger.log('🔍 [extractChartDataFromReport] Inspecting reportData:', {
      hasData: !!result.data,
      dataKeys: Object.keys(reportData),
      sampleData: reportData
    });
    
    // 方法1: 检查根级别的 chartData
    if (reportData.chartData && Array.isArray(reportData.chartData)) {
      chartData = reportData.chartData;
      logger.log('✅ [extractChartDataFromReport] Found chartData at root level:', chartData.length);
    } 
    // 方法2: 检查设备对象中的 chartData（如 data["UTU500-xxx#1"].chartData）
    else {
      const knownMetadataKeys = ['devices', 'totalConsumption', 'averagePower', 'peakPower', 'averageVoltage', 'averageCurrent', 'deviceType', 'availableMetrics', 'returnedMetrics', 'dataPoints', 'chartData', 'metadata'];
      const allKeys = Object.keys(reportData);
      logger.log('🔍 [extractChartDataFromReport] All keys in reportData:', allKeys);
      
      const deviceKeys = allKeys.filter(key => {
        if (knownMetadataKeys.includes(key)) return false;
        const value = reportData[key];
        const isDeviceObject = typeof value === 'object' && value !== null && !Array.isArray(value);
        if (isDeviceObject) {
          logger.log(`🔍 [extractChartDataFromReport] Found potential device key: ${key}`, {
            hasChartData: !!(value as any)?.chartData,
            chartDataLength: Array.isArray((value as any)?.chartData) ? (value as any).chartData.length : 0
          });
        }
        return isDeviceObject;
      });
      
      logger.log('🔍 [extractChartDataFromReport] Device keys found:', deviceKeys);
      
      deviceKeys.forEach(deviceKey => {
        const deviceData = reportData[deviceKey] as any;
        if (deviceData?.chartData && Array.isArray(deviceData.chartData)) {
          chartData = chartData.concat(deviceData.chartData);
          logger.log(`✅ [extractChartDataFromReport] Found chartData in device ${deviceKey}:`, deviceData.chartData.length);
        } 
        // ✅ 方法3: 處理溫濕度數據格式（temp/rh 陣列）
        else if (deviceData?.temp && Array.isArray(deviceData.temp)) {
          // 將 temp/rh 陣列轉換為統一格式
          const tempData = deviceData.temp;
          const rhData = deviceData.rh || [];
          
          // 按 timestamp 合併 temp 和 rh 數據
          const mergedData: any[] = [];
          const timestampMap = new Map<number, any>();
          
          tempData.forEach((point: any) => {
            const ts = point.timestamp;
            // ✅ 過濾無效數據：value = 0 或 min = 0 表示數據異常
            if (point.value === 0 || point.min === 0) {
              logger.log(`⚠️ [extractChartDataFromReport] Skipping invalid temp data: ${ts}, value=${point.value}, min=${point.min}`);
              return;
            }
            timestampMap.set(ts, {
              time: new Date(ts * 1000).toISOString(),
              timestamp: ts,
              temp: point.value,
              temperature: point.value,
              temp_avg: point.value,
              temp_min: point.min,
              temp_max: point.max
            });
          });
          
          rhData.forEach((point: any) => {
            const ts = point.timestamp;
            // ✅ 過濾無效數據：value = 0 或 min = 0 表示數據異常
            if (point.value === 0 || point.min === 0) {
              logger.log(`⚠️ [extractChartDataFromReport] Skipping invalid rh data: ${ts}, value=${point.value}, min=${point.min}`);
              return;
            }
            const existing = timestampMap.get(ts);
            if (existing) {
              existing.humidity = point.value;
              existing.rh = point.value;
              existing.rh_avg = point.value;
              existing.rh_min = point.min;
              existing.rh_max = point.max;
            } else {
              timestampMap.set(ts, {
                time: new Date(ts * 1000).toISOString(),
                timestamp: ts,
                humidity: point.value,
                rh: point.value,
                rh_avg: point.value,
                rh_min: point.min,
                rh_max: point.max
              });
            }
          });
          
          timestampMap.forEach(value => mergedData.push(value));
          chartData = chartData.concat(mergedData);
          logger.log(`✅ [extractChartDataFromReport] Converted temp/rh data for device ${deviceKey}:`, mergedData.length);
        }
        else {
          logger.warn(`⚠️ [extractChartDataFromReport] Device ${deviceKey} has no chartData or temp/rh arrays:`, {
            hasChartData: !!deviceData?.chartData,
            hasTemp: !!deviceData?.temp,
            hasRh: !!deviceData?.rh
          });
        }
      });
    }
    
    logger.log('📊 [extractChartDataFromReport] Final chartData count:', chartData.length);
    return chartData;
  }, []);
  
  // ✅ 获取台北时区的年、月、日（不依赖本地时区）
  const getTaipeiDateParts = useCallback((date: Date): { year: number, month: number, day: number } => {
    // 使用 Intl.DateTimeFormat 来获取台北时区的日期部分
    const taipeiYearFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Taipei', year: 'numeric' });
    const taipeiMonthFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Taipei', month: 'numeric' });
    const taipeiDayFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Taipei', day: 'numeric' });
    
    return {
      year: parseInt(taipeiYearFormatter.format(date), 10),
      month: parseInt(taipeiMonthFormatter.format(date), 10) - 1, // 0-indexed to match JS Date
      day: parseInt(taipeiDayFormatter.format(date), 10)
    };
  }, []);

  // ✅ 将日粒度电力数据转换为图表格式
  const convertDailyPowerData = useCallback((
    chartData: any[],
    todayStr: string
  ): { currentMonth: Array<{day: string, power: number, energy?: number}>, lastMonth: Array<{day: string, power: number, energy?: number}> } => {
    // 计算当前月份和上个月
    const [year, month, _day] = todayStr.split('-').map(Number);
    const currentDate = new Date(year, month - 1, _day);
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-indexed
    const lastMonthDate = new Date(currentYear, currentMonth - 1, 1);
    const lastMonthYear = lastMonthDate.getFullYear();
    const lastMonth = lastMonthDate.getMonth(); // 0-indexed
    const daysInCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const daysInLastMonth = new Date(currentYear, currentMonth, 0).getDate();
    
    // 按月份分组数据
    const currentMonthMap = new Map<number, number>(); // day -> energy (kWh)
    const lastMonthMap = new Map<number, number>(); // day -> energy (kWh)
    
    // 添加调试日志
    logger.log('📊 [convertDailyPowerData] Input:', {
      totalPoints: chartData.length,
      samplePoints: chartData.slice(0, 3).map((p: any) => ({ time: p.time, energy: p.energy })),
      todayStr,
      currentYear,
      currentMonth,
      lastMonthYear,
      lastMonth
    });
    
    chartData.forEach((point: any) => {
      const time = point.time || point.timestamp;
      if (!time) return;
      
      const date = new Date(time);
      if (isNaN(date.getTime())) {
        logger.warn('⚠️ [convertDailyPowerData] Failed to parse time:', time);
        return;
      }
      
      // ✅ 使用台北时区的日期部分，确保时区一致性
      const { year: pointYear, month: pointMonth, day: pointDay } = getTaipeiDateParts(date);
      
      // 提取每日能量（kWh）
      let dailyEnergyKWh = 0;
      if (point.energy !== undefined) {
        dailyEnergyKWh = typeof point.energy === 'number' ? point.energy : parseFloat(String(point.energy)) || 0;
      } else if (point.periodEnergy !== undefined) {
        dailyEnergyKWh = typeof point.periodEnergy === 'number' ? point.periodEnergy : parseFloat(String(point.periodEnergy)) || 0;
      } else if (point.power !== undefined || point.avgPower !== undefined) {
        const dailyPowerW = typeof point.power === 'number' ? point.power : 
                     (typeof point.avgPower === 'number' ? point.avgPower : 0);
        dailyEnergyKWh = (dailyPowerW * 24) / 1000; // W * 24h / 1000 = kWh
      }
      
      // 分组到本月或上月
      if (pointYear === currentYear && pointMonth === currentMonth) {
        const existing = currentMonthMap.get(pointDay) || 0;
        currentMonthMap.set(pointDay, existing + dailyEnergyKWh);
        logger.log(`✅ [convertDailyPowerData] Matched current month: ${pointYear}-${pointMonth+1}-${pointDay} energy=${dailyEnergyKWh}`);
      } else if (pointYear === lastMonthYear && pointMonth === lastMonth) {
        const existing = lastMonthMap.get(pointDay) || 0;
        lastMonthMap.set(pointDay, existing + dailyEnergyKWh);
        logger.log(`✅ [convertDailyPowerData] Matched last month: ${pointYear}-${pointMonth+1}-${pointDay} energy=${dailyEnergyKWh}`);
      } else {
        logger.log(`❓ [convertDailyPowerData] No match: ${pointYear}-${pointMonth+1}-${pointDay} vs current=${currentYear}-${currentMonth+1} last=${lastMonthYear}-${lastMonth+1}`);
      }
    });
    
    // 构建本月数据数组（无数据的日期不设置 energy，让图表不绘制）
    const currentMonthData: Array<{day: string, power: number, energy?: number}> = [];
    for (let day = 1; day <= daysInCurrentMonth; day++) {
      const dayStr = day.toString().padStart(2, '0');
      const energy = currentMonthMap.get(day);
      if (energy !== undefined) {
        currentMonthData.push({ day: dayStr, power: (energy * 1000) / 24, energy });
      } else {
        currentMonthData.push({ day: dayStr, power: 0 }); // 无 energy 字段，图表不绘制此点
      }
    }
    
    // 构建上月数据数组
    const lastMonthData: Array<{day: string, power: number, energy?: number}> = [];
    for (let day = 1; day <= daysInLastMonth; day++) {
      const dayStr = day.toString().padStart(2, '0');
      const energy = lastMonthMap.get(day);
      if (energy !== undefined) {
        lastMonthData.push({ day: dayStr, power: (energy * 1000) / 24, energy });
      } else {
        lastMonthData.push({ day: dayStr, power: 0 }); // 无 energy 字段，图表不绘制此点
      }
    }
    
    return { currentMonth: currentMonthData, lastMonth: lastMonthData };
  }, [getTaipeiDateParts]);

  // ✅ 将小时粒度温湿度数据转换为图表格式
  const convertHourlyClimateData = useCallback((
    chartData: any[],
    todayStr: string
  ): Array<{hour: string, temp: number | null, humidity: number | null}> => {
    const hourlyData: Array<{hour: string, temp: number | null, humidity: number | null}> = [];
    const hourlyDataMap = new Map<string, { temp: number, humidity: number, count: number }>();
    
    // 按时间排序数据点
    const sortedPoints = [...chartData].sort((a, b) => {
      const dateA = new Date(a.time || a.timestamp);
      const dateB = new Date(b.time || b.timestamp);
      const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
      const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
      return timeA - timeB;
    });
    
    // 處理過去 24 小時的數據點（不再只限今日）
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    const recentPoints = sortedPoints.filter((point: any) => {
      const time = point.time || point.timestamp;
      if (!time) return false;
      try {
        const pointDate = new Date(time);
        if (isNaN(pointDate.getTime())) return false;
        
        // 只保留過去 24 小時內的數據
        return pointDate >= twentyFourHoursAgo && pointDate <= now;
      } catch {
        return false;
      }
    });
    
    // 提取每个小时的数据（多个设备取平均值，只计入有数据的设备）
    recentPoints.forEach((point: any) => {
      const time = point.time || point.timestamp;
      if (!time) return;
      
      try {
        // ✅ 直接从 ISO 8601 时间字符串提取小时（API 返回 +08:00 时区）
        const hourMatch = String(time).match(/T(\d{2}):/);
        if (!hourMatch) return;
        const hour = `${hourMatch[1]}:00`;
        
        // 提取温度和湿度（支持多種欄位名稱：原始值、平均值、最大/最小值）
        const rawTemp = point.temperature ?? point.temp ?? 
                       point.temp_avg ?? point.temperature_avg ?? 
                       point.temp_max ?? point.temperature_max ?? null;
        const rawHumidity = point.humidity ?? point.rh ?? 
                           point.rh_avg ?? point.humidity_avg ?? 
                           point.rh_max ?? point.humidity_max ?? null;
        
        // 只有有效数值才计入（跳过 null、undefined、NaN、0）
        const tempValue = rawTemp !== null && rawTemp !== undefined ? 
                         (typeof rawTemp === 'number' ? rawTemp : parseFloat(String(rawTemp))) : null;
        const humidityValue = rawHumidity !== null && rawHumidity !== undefined ? 
                             (typeof rawHumidity === 'number' ? rawHumidity : parseFloat(String(rawHumidity))) : null;
        
        // ✅ 只有当温度或湿度有效时才计入（有效 = 非 null 且非 NaN 且 > 0）
        const validTemp = tempValue !== null && !isNaN(tempValue) && tempValue > 0;
        const validHumidity = humidityValue !== null && !isNaN(humidityValue) && humidityValue > 0;
        
        // 如果两者都无效，跳过这个点
        if (!validTemp && !validHumidity) return;
        
        const existing = hourlyDataMap.get(hour);
        if (existing) {
          // ✅ 分别计数温度和湿度的有效数据点
          hourlyDataMap.set(hour, {
            temp: existing.temp + (validTemp ? tempValue! : 0),
            humidity: existing.humidity + (validHumidity ? humidityValue! : 0),
            count: existing.count + 1,
            tempCount: (existing as any).tempCount + (validTemp ? 1 : 0),
            humidityCount: (existing as any).humidityCount + (validHumidity ? 1 : 0)
          } as any);
        } else {
          hourlyDataMap.set(hour, {
            temp: validTemp ? tempValue! : 0,
            humidity: validHumidity ? humidityValue! : 0,
            count: 1,
            tempCount: validTemp ? 1 : 0,
            humidityCount: validHumidity ? 1 : 0
          } as any);
        }
      } catch (error) {
        logger.warn('⚠️ [convertHourlyClimateData] Error processing point:', time, error);
      }
    });
    
    // 填充 24 小时数据
    // ✅ 尚未到達的時間點使用 null，圖表不會畫出這些點
    const currentHour = new Date().getHours();
    for (let i = 0; i < 24; i++) {
      const hour = `${i.toString().padStart(2, '0')}:00`;
      const existing = hourlyDataMap.get(hour) as any;
      
      // ✅ 未來的小時（尚未有數據）：填充 null
      if (i > currentHour) {
        hourlyData.push({
          hour,
          temp: null as number | null,
          humidity: null as number | null
        });
      } else if (existing) {
        // ✅ 有數據的小時：使用實際數據
        const tempCount = existing.tempCount || existing.count;
        const humidityCount = existing.humidityCount || existing.count;
        hourlyData.push({
          hour,
          temp: tempCount > 0 ? parseFloat((existing.temp / tempCount).toFixed(1)) : null as number | null,
          humidity: humidityCount > 0 ? parseFloat((existing.humidity / humidityCount).toFixed(1)) : null as number | null
        });
      } else {
        // ✅ 過去但無數據的小時：填充 null（圖表不會畫出這些點）
        hourlyData.push({
          hour,
          temp: null as number | null,
          humidity: null as number | null
        });
      }
    }
    
    return hourlyData;
  }, [taipeiFormatter]);

  // ✅ 将小时粒度电力数据转换为图表格式
  const convertHourlyPowerData = useCallback((
    chartData: any[],
    targetDateStr: string
  ): Array<{hour: string, power: number, energy: number}> => {
    const hourlyData: Array<{hour: string, power: number, energy: number}> = [];
    const hourlyDataMap = new Map<string, { power: number, energy: number, count: number }>();
    
    // 按时间排序数据点（安全处理无效时间值）
    const sortedPoints = [...chartData].sort((a, b) => {
      try {
        const dateA = new Date(a.time || a.timestamp);
        const dateB = new Date(b.time || b.timestamp);
        const validA = !isNaN(dateA.getTime());
        const validB = !isNaN(dateB.getTime());
        // 如果时间无效，将无效值排到最后
        if (!validA && !validB) return 0;
        if (!validA) return 1;
        if (!validB) return -1;
        return dateA.getTime() - dateB.getTime();
      } catch {
        return 0;
      }
    });
    
    // 按日期过滤数据点
    const filteredPoints = sortedPoints.filter((point: any) => {
      const time = point.time || point.timestamp;
      if (!time) return false;
      try {
        const pointDate = new Date(time);
        if (isNaN(pointDate.getTime())) return false;
        
        const pointDateStr = taipeiFormatter.format(pointDate);
        return pointDateStr === targetDateStr;
      } catch {
        return false;
      }
    });
    
    logger.log('📊 [convertHourlyPowerData] Processing hourly data:', {
      totalPoints: sortedPoints.length,
      filteredPoints: filteredPoints.length,
      targetDateStr
    });
    
    // 提取每个小时的数据
    filteredPoints.forEach((point: any) => {
      const time = point.time || point.timestamp;
      if (!time) return;
      
      try {
        // ✅ 直接从 ISO 8601 时间字符串提取小时（API 返回 +08:00 时区）
        const hourMatch = String(time).match(/T(\d{2}):/);
        if (!hourMatch) return;
        const hour = `${hourMatch[1]}:00`;
        
        // 提取功率和能量
        const pointPower = point.avgPower || point.power || point.watt || 0;
        let pointEnergy = 0;
        
        // 优先使用 energy 字段（kWh）
        if (point.energy !== undefined && point.energy !== null) {
          pointEnergy = typeof point.energy === 'number' ? point.energy : parseFloat(String(point.energy)) || 0;
        } 
        // 其次使用 periodEnergy（kWh）
        else if (point.periodEnergy !== undefined && point.periodEnergy !== null) {
          pointEnergy = typeof point.periodEnergy === 'number' ? point.periodEnergy : parseFloat(String(point.periodEnergy)) || 0;
        } 
        // 使用 watt_hour（Wh，需要转换为 kWh）
        else if (point.watt_hour !== undefined && point.watt_hour !== null) {
          const wattHour = typeof point.watt_hour === 'number' ? point.watt_hour : parseFloat(String(point.watt_hour)) || 0;
          pointEnergy = wattHour / 1000; // Wh -> kWh
        }
        // 使用 wattHour（Wh，需要转换为 kWh）
        else if (point.wattHour !== undefined && point.wattHour !== null) {
          const wattHour = typeof point.wattHour === 'number' ? point.wattHour : parseFloat(String(point.wattHour)) || 0;
          pointEnergy = wattHour / 1000; // Wh -> kWh
        }
        
        const existing = hourlyDataMap.get(hour);
        if (existing) {
          hourlyDataMap.set(hour, {
            power: existing.power + pointPower,
            energy: existing.energy + pointEnergy,
            count: existing.count + 1
          });
        } else {
          hourlyDataMap.set(hour, {
            power: pointPower,
            energy: pointEnergy,
            count: 1
          });
        }
      } catch (error) {
        logger.warn('⚠️ [convertHourlyPowerData] Error processing point:', time, error);
      }
    });
    
    // 填充 24 小时数据
    for (let i = 0; i < 24; i++) {
      const hour = `${i.toString().padStart(2, '0')}:00`;
      const existing = hourlyDataMap.get(hour);
      if (existing) {
        hourlyData.push({
          hour,
          power: Math.round(existing.power / existing.count), // 平均功率
          energy: parseFloat(existing.energy.toFixed(3))
        });
      } else {
        hourlyData.push({
          hour,
          power: 0,
          energy: 0
        });
      }
    }
    
    return hourlyData;
  }, [taipeiFormatter]);
  
  // Function to fetch chart data
  const fetchChartData = useCallback(async (isInitial: boolean = false) => {
    console.log('🚀 [IntelliSpace] fetchChartData called', {
      isInitial,
      devicesCount: devices.length,
      isLoading: isLoadingChartDataRef.current
    });
    
    if (devices.length === 0) {
      console.warn('⚠️ [IntelliSpace] No devices available, skipping fetchChartData');
      return;
    }
    if (isLoadingChartDataRef.current) {
      console.warn('⚠️ [IntelliSpace] Already loading, skipping concurrent fetch');
      return; // Prevent concurrent fetches
    }
    
    isLoadingChartDataRef.current = true;
    setIsLoadingChartData(true);

    try {
      console.log('📡 [IntelliSpace] Starting API calls...');
      // Get power meter device IDs - use selected or all
      // ✅ 修正：包含所有有電力監測能力的設備，不只是 deviceType === 'power_meter'
      // 這與 GenerateReportPage.tsx 的邏輯一致
      const powerMeters = devices.filter(d => {
        const deviceType = (d.deviceType || '') as string;
        const traits = (d as any).traits || [];
        const attributes = d.attributes || {};
        const sensorProfile = (attributes as any).sensorProfile || (d as any).sensorProfile || {};
        
        // Check by device type (including 3-phase)
        if (deviceType === 'power_meter' || deviceType === 'power_meter_3phase' || deviceType === 'POWER_METER') {
          return true;
        }
        
        // Check by traits
        if (traits.includes('EnergyStorage') || traits.includes('PowerMonitoring')) {
          return true;
        }
        
        // Check by sensor profile (power metrics)
        const hasPowerMetrics = 
          sensorProfile.watt !== undefined || sensorProfile.watt_hour !== undefined ||
          sensorProfile.voltage !== undefined || sensorProfile.current !== undefined ||
          sensorProfile.energy !== undefined || sensorProfile.power !== undefined ||
          sensorProfile.wattHour !== undefined || sensorProfile.kwh !== undefined ||
          d.watt !== undefined || d.wattHour !== undefined ||
          d.voltage !== undefined || d.current !== undefined;
        
        if (hasPowerMetrics) {
          return true;
        }
        
        // Check by attributes directly
        const hasPowerAttributes = 
          attributes.watt !== undefined || (attributes as any).watt_hour !== undefined ||
          attributes.voltage !== undefined || attributes.current !== undefined;
        
        return hasPowerAttributes;
      });
      const selectedPowerMeterDevices = selectedPowerMeters.length > 0
        ? powerMeters.filter(d => selectedPowerMeters.includes(d.deviceId))
        : powerMeters;
      
      // ✅ 每個 deviceSN (#1, #2, #3...) 是獨立的電錶，直接使用所有勾選的 deviceId
      const powerMeterIds = selectedPowerMeterDevices.map(d => d.deviceId);
      
      // Get TH sensor device IDs - use selected or all
      // ⚠️ 日誌證據：API 需要完整的 deviceId（帶 #ULTRON_SENSOR 後綴）才能返回數據
      // 之前使用 baseSN（不帶後綴）導致 API 返回空 chartData
      const thSensors = devices.filter(d => d.deviceType === 'th_sensor');
      const selectedThSensorDevices = selectedThSensors.length > 0
        ? thSensors.filter(d => selectedThSensors.includes(d.deviceId))
        : thSensors;
      // ✅ 修正：使用 deviceId（帶後綴）而非 baseSN，與 GenerateReportPage.tsx 一致
      const thSensorDeviceSNs = Array.from(new Set(selectedThSensorDevices.map(d => d.deviceId)));
      
      // Combine all device IDs for the API call
      // ✅ 所有 deviceIds 都使用帶 endpoint 的 deviceSN（如果有的話）
      // ✅ 包含三相電錶 (selectedThreePhaseMeter)
      const allDeviceIds = [
        ...powerMeterIds, 
        ...thSensorDeviceSNs,
        ...(selectedThreePhaseMeter ? [selectedThreePhaseMeter] : [])
      ];
      
      // Get locationId from first device that has one
      const locationId = devices.find(d => d.groupId)?.groupId;

      
      console.log(`📊 [IntelliSpace] Fetching hourly chart data (${isInitial ? 'initial' : 'refresh'}):`, {
        powerMeterIds,
        thSensorDeviceSNs,
        thSensorDeviceSNsLength: thSensorDeviceSNs.length,
        selectedThreePhaseMeter,
        allDeviceIds,
        allDeviceIdsCount: allDeviceIds.length,
        locationId,
        thSensorsCount: thSensors.length,
        selectedThSensorsCount: selectedThSensors.length
      });
      
      // ✅ 診斷：顯示溫濕度感測器詳情
      if (thSensorDeviceSNs.length > 0) {
        console.log('🌡️ [IntelliSpace] TH Sensors to fetch:', thSensorDeviceSNs);
      } else {
        console.warn('⚠️ [IntelliSpace] No TH sensors to fetch! Check:', {
          allThSensors: thSensors.map(d => d.deviceId),
          selectedThSensors,
          selectedThSensorDevices: selectedThSensorDevices.map(d => d.deviceId)
        });
      }

      // ✅ 使用新的 MCP GenerateReport tool 格式
      // 使用台北时区计算日期
      const now = new Date();
      const todayStr = taipeiFormatter.format(now); // 格式：YYYY-MM-DD
      const [year, month, day] = todayStr.split('-').map(Number);
      
      // 计算昨天（台北时区）：今天-1天
      const yesterdayDate = new Date(year, month - 1, day - 1);
      const yesterdayStr = taipeiFormatter.format(yesterdayDate); // 格式：YYYY-MM-DD
      
      // 计算上周同一天（台北时区）：今天-7天
      const lastWeekDate = new Date(year, month - 1, day - 7);
      const lastWeekStr = taipeiFormatter.format(lastWeekDate); // 格式：YYYY-MM-DD
      
      // 构造 ISO 字符串（台北时区 +08:00）
      const todayStartISO = `${todayStr}T00:00:00+08:00`;
      const yesterdayStartISO = `${yesterdayStr}T00:00:00+08:00`;
      const yesterdayEndISO = `${yesterdayStr}T23:59:59+08:00`;
      const lastWeekEndISO = `${lastWeekStr}T23:59:59+08:00`;
      
      logger.log('📅 [IntelliSpace] Date calculation:', {
        today: todayStr,
        yesterday: yesterdayStr,
        lastWeek: lastWeekStr,
        yesterdayStartISO,
        yesterdayEndISO,
        lastWeekEndISO
      });
      
      // ✅ 使用快取 + 並行載入所有必要數據
      // API 快取會自動避免重複請求（TTL 5分鐘）
      
      // 7 天報告（包含今日、昨日、上週數據）
      const sevenDayReportPromise = powerMeterIds.length > 0
        ? ultronTuringApi.generateReport(powerMeterIds, {
            duration: '8d',
            granularity: 'hour',
            metrics: 'auto',
            format: 'summary',
            utcOffset: '+08:00'
          }).catch(err => {
            logger.warn('⚠️ [IntelliSpace] 7-day report failed:', err);
            return { success: false, data: {} };
          })
        : Promise.resolve({ success: true, data: {} });
      
      // 月度報告
      const monthlyReportPromise = powerMeterIds.length > 0 
        ? ultronTuringApi.generateReport(powerMeterIds, {
            duration: '62d',
            granularity: 'day',
            format: 'summary',
            utcOffset: '+08:00'
          }).catch(err => {
            logger.warn('⚠️ [IntelliSpace] Monthly report failed:', err);
            return { success: false, data: {} };
          })
        : Promise.resolve({ success: true, data: {} });
      
      // 溫濕度數據（快取會避免重複呼叫）
      // ✅ 檢查 TH 感測器的 deviceId 格式
      // ✅ 修正：不傳 metrics 參數，讓 API 自動偵測（與 GenerateReportPage 一致）
      const climateReportPromise = thSensorDeviceSNs.length > 0
        ? ultronTuringApi.generateReport(thSensorDeviceSNs, {
            duration: '24h',
            granularity: 'hour',
            format: 'summary',
            utcOffset: '+08:00'
          }).catch(err => {
            console.error('❌ [IntelliSpace] Climate report failed:', err);
            return { success: false, data: {} };
          })
        : Promise.resolve({ success: true, data: {} });
      
      // 並行載入所有數據（快取會自動處理重複請求）
      const [sevenDayReport, monthlyReportResult, climateReport] = await Promise.all([
        sevenDayReportPromise,
        monthlyReportPromise,
        climateReportPromise
      ]);
      
      // ✅ 从 7 天报告中提取今日、昨日和上周的数据
      const allChartData = extractChartDataFromReport(sevenDayReport);
      logger.log('📊 [IntelliSpace] Extracted chartData from 7-day report:', {
        totalPoints: allChartData.length,
        samplePoints: allChartData.slice(0, 3),
        hasSevenDayReport: !!sevenDayReport,
        sevenDayReportKeys: sevenDayReport?.data ? Object.keys(sevenDayReport.data) : [],
        dateRange: {
          today: todayStr,
          yesterday: yesterdayStr,
          lastWeek: lastWeekStr
        }
      });
      
      // 检查数据点的时间范围
      if (allChartData.length > 0) {
        const timePoints = allChartData
          .map((p: any) => p.time || p.timestamp)
          .filter(Boolean)
          .slice(0, 5);
        
        // 安全地格式化日期，避免 Invalid time value 错误
        const formatDateSafely = (timeValue: any): string => {
          if (!timeValue) return 'N/A';
          try {
            const date = new Date(timeValue);
            if (isNaN(date.getTime())) return 'Invalid';
            return taipeiFormatter.format(date);
          } catch (error) {
            return `Error: ${error}`;
          }
        };
        
        logger.log('📊 [IntelliSpace] Sample time points from chartData:', {
          timePoints,
          firstPointDate: formatDateSafely(timePoints[0]),
          lastPointDate: formatDateSafely(timePoints[timePoints.length - 1])
        });
      }
      
      // 使用新的转换函数提取数据
      const todayPowerData = convertHourlyPowerData(allChartData, todayStr);
      const yesterdayPowerData = convertHourlyPowerData(allChartData, yesterdayStr);
      const lastWeekPowerData = convertHourlyPowerData(allChartData, lastWeekStr);
      
      logger.log('📊 [IntelliSpace] Extracted data from 7-day report:', {
        todayPoints: todayPowerData.length,
        todayTotalEnergy: todayPowerData.reduce((sum, d) => sum + (d.energy || 0), 0),
        todayTotalPower: todayPowerData.reduce((sum, d) => sum + (d.power || 0), 0),
        yesterdayPoints: yesterdayPowerData.length,
        yesterdayTotalEnergy: yesterdayPowerData.reduce((sum, d) => sum + (d.energy || 0), 0),
        lastWeekPoints: lastWeekPowerData.length,
        lastWeekTotalEnergy: lastWeekPowerData.reduce((sum, d) => sum + (d.energy || 0), 0)
      });
      
      // ✅ 从温湿度报告中提取数据
      const climateChartData = extractChartDataFromReport(climateReport);
      const climateData = convertHourlyClimateData(climateChartData, todayStr);

      // ✅ 詳細診斷：溫濕度報告數據
      const deviceKey = thSensorDeviceSNs[0];
      const deviceData = deviceKey ? (climateReport?.data as any)?.[deviceKey] : null;
      console.log('🌡️ [IntelliSpace] Climate Report Debug:', {
        thSensorDeviceSNs,
        thSensorDeviceSNsCount: thSensorDeviceSNs.length,
        climateReportSuccess: climateReport?.success,
        climateReportHasData: !!climateReport?.data,
        climateReportDataKeys: climateReport?.data ? Object.keys(climateReport.data) : [],
        climateReportChartData: (climateReport?.data as any)?.chartData?.length || 0,
        deviceDataKeys: deviceData ? Object.keys(deviceData) : [],
        deviceDataChartData: deviceData?.chartData?.length || 0,
        deviceDataSample: deviceData,
        extractedChartDataLength: climateChartData.length,
        convertedClimateDataLength: climateData.length,
        sampleClimateData: climateData.slice(0, 3),
        rawClimateReport: climateReport
      });

      logger.log('📊 [IntelliSpace] Received today power data:', todayPowerData);
      logger.log('📊 [IntelliSpace] Received yesterday power data:', yesterdayPowerData);
      logger.log('📊 [IntelliSpace] Received last week power data:', lastWeekPowerData);
      logger.log('📊 [IntelliSpace] Received climate data:', climateData);
      logger.log('📊 [IntelliSpace] Received monthly report data:', monthlyReportResult);

      // Update current hour for chart split
      setChartCurrentHour(new Date().getHours());
      
      // ✅ 從 8 天報告提取昨日全日用電量（不再額外呼叫 API）
      // yesterdayPowerData 已從 sevenDayReport 提取，直接計算總和
      const totalYesterdayEnergy = yesterdayPowerData.reduce((sum, d) => sum + (d.energy || 0), 0);
      logger.log('📊 [IntelliSpace] Yesterday daily energy (from 8d report):', {
        yesterdayStr,
        totalYesterdayEnergy,
        dataPoints: yesterdayPowerData.length
      });
      setYesterdayDailyEnergy(totalYesterdayEnergy);
      
      // ✅ 今日用電量改由 fetchTodayBaseline 計算（基準值法：當前 watt_hour - 00:00 watt_hour）
      // 這裡只記錄小時報告的累計值供參考，不設定 todayMinuteEnergy
      const totalTodayEnergyFromHourly = todayPowerData.reduce((sum, d) => sum + (d.energy || 0), 0);
      logger.log('📊 [IntelliSpace] Hourly energy sum (for reference only):', {
        todayStr,
        totalTodayEnergyFromHourly,
        dataPoints: todayPowerData.length
      });
      
      setHourlyPowerData(todayPowerData);
      setYesterdayPowerData(yesterdayPowerData);
      setLastWeekPowerData(lastWeekPowerData);
      
      // ✅ 有數據時才更新溫濕度
      if (climateData.length > 0) {
        setHourlyClimateData(climateData);
      }

      // Fetch today's energy per device for ranking
      // 獲取每個設備的今日用電量（用於排行）
      try {
        const todayEnergy: Record<string, number> = {};
        const now = new Date();
        // ✅ 取得今天的日期字串（YYYY-MM-DD 格式）
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        // Get all devices with energy/meter capability, not just power_meter type
        // 獲取所有有 energy/meter 能力的設備，不只是 power_meter 類型
        // Devices like lights, switches, HVAC may also have meter functions (watt, wattHour)
        // 設備如燈具、開關、空調也可能有 meter 功能（watt, wattHour）
        const allEnergyDevices = devices.filter(d => {
          // Check if device has meter capabilities
          // 檢查設備是否有 meter 能力
          const hasMeterData = d.watt !== undefined || d.wattHour !== undefined || 
                              d.voltage !== undefined || d.current !== undefined;
          const isPowerMeter = d.deviceType === 'power_meter' || d.deviceType === 'power_meter_3phase';
          // Check attributes for meter capabilities (from sensorProfile)
          // 檢查 attributes 中的 meter 能力（來自 sensorProfile）
          // Support both formats: normalized sensorProfile (new) or attributes.sensorProfile (old)
          const sp = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
          const hasMeterAttributes = sp.watt || sp.watt_hour || sp.voltage || sp.current;
          
          return isPowerMeter || hasMeterData || hasMeterAttributes;
        });
        
        logger.log('📊 [IntelliSpace] All energy devices found:', {
          count: allEnergyDevices.length,
          devices: allEnergyDevices.map(d => ({
            deviceId: d.deviceId,
            deviceName: d.deviceName,
            deviceType: d.deviceType,
            watt: d.watt,
            wattHour: d.wattHour
          }))
        });
        
        // Get device IDs for API calls - use ALL devices, not just first per baseSN
        // 獲取設備 ID 用於 API 調用 - 使用所有設備，不只是每個 baseSN 的第一個
        // Each device should have its own todayEnergy, even if they share the same baseSN
        // 每個設備都應該有自己的 todayEnergy，即使它們共享相同的 baseSN
        // ✅ 排除被勾選的設備，不計算它們的用電量（排行榜用）
        const filteredEnergyDevices = allEnergyDevices.filter(d => !excludedRankingDevices.includes(d.deviceId));
        // ✅ 確保選中的電表（selectedPowerMeters）始終被查詢
        const selectedMeterDevices = allEnergyDevices.filter(d => selectedPowerMeters.includes(d.deviceId));
        // 合併：排行榜設備 + 選中的電表（去重）
        const allQueryDevices = [...filteredEnergyDevices];
        selectedMeterDevices.forEach(d => {
          if (!allQueryDevices.find(existing => existing.deviceId === d.deviceId)) {
            allQueryDevices.push(d);
          }
        });
        const energyDeviceIds = allQueryDevices.map(d => d.deviceId);
        
        logger.log('📊 [IntelliSpace] Energy device IDs for API calls (excluding checked devices):', {
          total: allEnergyDevices.length,
          excluded: excludedRankingDevices.length,
          count: energyDeviceIds.length,
          deviceIds: energyDeviceIds,
          devices: filteredEnergyDevices.map(d => ({ deviceId: d.deviceId, deviceName: d.deviceName, baseSN: d.baseSN }))
        });

        if (energyDeviceIds.length > 0) {
          // Create a mapping from baseSN/deviceId to deviceId for matching
          // ✅ 支持多端點設備：同一 baseSN 可能對應多個 deviceId（帶不同 endpoint）
          const baseSNToDeviceId = new Map<string, string>();
          allEnergyDevices.forEach(device => {
            const baseSN = device.baseSN || device.deviceId.split('#')[0];
            // 為每個 deviceId 建立映射（優先使用完整的 deviceId，包括 endpoint）
            baseSNToDeviceId.set(device.deviceId, device.deviceId);
            // 為 baseSN 建立映射（如果還沒有映射，或當前 deviceId 是第一個端點）
            if (!baseSNToDeviceId.has(baseSN) || device.deviceId.endsWith('#1') || !device.deviceId.includes('#')) {
              baseSNToDeviceId.set(baseSN, device.deviceId);
            }
          });

          logger.log('📊 [IntelliSpace] Fetching today energy for ranking:', {
            energyDeviceIds,
            allEnergyDevices: allEnergyDevices.map(d => ({ deviceId: d.deviceId, baseSN: d.baseSN, deviceType: d.deviceType }))
          });

          // ✅ 改用單一批量 API 呼叫（避免 quota exceeded）
          // 使用 generateReport 一次取得所有設備的數據
          const todayHours = Math.max(now.getHours() + 1, 1);
          const todayDuration = `${todayHours}h`;
          
          // ✅ 整合：同時計算 todayMinuteEnergy（選中的電錶總和）
          // 判斷哪些設備是選中的電錶
          const selectedPowerMeterSet = new Set(selectedPowerMeters);
          let totalMinuteEnergy = 0;
          
          try {
            const batchReportResponse = await ultronTuringApi.generateReport(
              energyDeviceIds, // ✅ 所有設備 ID 一次傳入
              {
                duration: todayDuration,
                endTime: now.toISOString(),
                granularity: 'hour',  // ✅ 優化：hour 比 minute 快 60 倍
                metrics: ['watt_hour'],
                format: 'summary',
                utcOffset: '+08:00'
              }
            );

            const reportData = batchReportResponse?.data || batchReportResponse;
            logger.log('📊 [IntelliSpace] Batch report response keys:', Object.keys(reportData || {}));

            // ✅ 處理每個設備的數據（從單一 API 回應中提取）
            energyDeviceIds.forEach((deviceId) => {
              const baseSN = deviceId.split('#')[0];
              
              // API 可能用 deviceId 或 baseSN 作為 key
              const deviceReport: any = reportData?.[deviceId] || reportData?.[baseSN] || null;
              
              // 詳細日誌：查看設備報告的結構
              if (deviceReport) {
                logger.log(`📊 [IntelliSpace] Device ${deviceId} report keys:`, Object.keys(deviceReport));
                if (deviceReport.watt_hour) {
                  logger.log(`📊 [IntelliSpace] Device ${deviceId} watt_hour data:`, deviceReport.watt_hour);
                }
              } else {
                logger.warn(`⚠️ [IntelliSpace] No report data for device ${deviceId}`);
              }
              
              // Find matching deviceId
              let matchingDeviceId = baseSNToDeviceId.get(deviceId);
              if (!matchingDeviceId) {
                matchingDeviceId = baseSNToDeviceId.get(baseSN);
              }
              if (!matchingDeviceId) {
                const matchingDevice = allEnergyDevices.find(d => {
                  const dBaseSN = d.baseSN || d.deviceId.split('#')[0];
                  return dBaseSN === baseSN || d.deviceId === deviceId || d.deviceId === baseSN;
                });
                matchingDeviceId = matchingDevice?.deviceId || deviceId;
              }

              let energyValue = 0;
              let firstWattHour = 0;
              let lastWattHour = 0;

              // ✅ 計算今日用電量（watt_hour 差值）
              // 支援兩種 API 格式：
              // 格式1 (舊): deviceReport.chartData = [{ time, watt_hour, ... }]
              // 格式2 (MCP): deviceReport.watt_hour = [{ value, timestamp, min, max }]
              
              // 嘗試從 watt_hour 數組直接提取數據 (MCP 格式)
              const wattHourArray = deviceReport?.watt_hour;
              if (wattHourArray && Array.isArray(wattHourArray) && wattHourArray.length > 0) {
                // MCP 格式：watt_hour 是一個包含 { value, timestamp, min, max } 的數組
                const sortedPoints = [...wattHourArray].sort((a: any, b: any) => {
                  const timeA = a.timestamp || 0;
                  const timeB = b.timestamp || 0;
                  return timeA - timeB;
                });
                
                if (sortedPoints.length >= 1) {
                  const firstPoint = sortedPoints[0];
                  const lastPoint = sortedPoints[sortedPoints.length - 1];
                  // 使用 min 作為起始值，max 作為結束值
                  firstWattHour = firstPoint.min ?? firstPoint.value ?? 0;
                  lastWattHour = lastPoint.max ?? lastPoint.value ?? 0;
                  
                  if (lastWattHour > firstWattHour) {
                    energyValue = lastWattHour - firstWattHour;
                    logger.log(`✅ [IntelliSpace] Device ${deviceId} today watt_hour diff (MCP format): ${energyValue.toFixed(3)} kWh`);
                  } else if (sortedPoints.length === 1 && lastPoint.value > 0) {
                    // 如果只有一個數據點，使用 max - min 作為差值
                    const pointEnergy = (lastPoint.max ?? 0) - (lastPoint.min ?? 0);
                    if (pointEnergy > 0) {
                      energyValue = pointEnergy;
                      logger.log(`✅ [IntelliSpace] Device ${deviceId} single point energy: ${energyValue.toFixed(3)} kWh`);
                    }
                  }
                  
                  // ✅ baseline 改由 fetchTodayBaseline 設定（不在這裡設定，避免衝突）
                }
              }
              // 回退到舊格式 (chartData)
              else if (deviceReport?.chartData && Array.isArray(deviceReport.chartData) && deviceReport.chartData.length > 0) {
                const todayPoints = deviceReport.chartData.filter((point: any) => {
                  const time = point.time || point.timestamp;
                  if (!time) return false;
                  const dateMatch = String(time).match(/^(\d{4}-\d{2}-\d{2})/);
                  return dateMatch && dateMatch[1] === todayStr;
                });

                const sortedPoints = [...todayPoints].sort((a, b) => {
                  const timeA = new Date(a.time || a.timestamp || 0).getTime();
                  const timeB = new Date(b.time || b.timestamp || 0).getTime();
                  return timeA - timeB;
                });

                if (sortedPoints.length >= 1) {
                  const firstPoint = sortedPoints[0];
                  const lastPoint = sortedPoints[sortedPoints.length - 1];
                  firstWattHour = firstPoint.watt_hour ?? firstPoint.watt_hour_min ?? 0;
                  lastWattHour = lastPoint.watt_hour ?? lastPoint.watt_hour_max ?? 0;

                  if (sortedPoints.length >= 2 && lastWattHour > firstWattHour) {
                    energyValue = lastWattHour - firstWattHour;
                    logger.log(`✅ [IntelliSpace] Device ${deviceId} today watt_hour diff: ${energyValue.toFixed(3)} kWh`);
                  }
                  // ✅ baseline 改由 fetchTodayBaseline 設定（不在這裡設定，避免衝突）
                }
              }

              // Store energy value
              if (energyValue > 0) {
                todayEnergy[matchingDeviceId] = energyValue;
                if (deviceId !== matchingDeviceId) {
                  todayEnergy[deviceId] = energyValue;
                }
                // Store for all devices with the same baseSN
                allEnergyDevices.forEach(device => {
                  const dBaseSN = device.baseSN || device.deviceId.split('#')[0];
                  if (dBaseSN === baseSN && device.deviceId !== matchingDeviceId && device.deviceId !== deviceId) {
                    if (!(device.deviceId in todayEnergy)) {
                      todayEnergy[device.deviceId] = energyValue;
                    }
                  }
                });
                
                // ✅ 整合：只累加選中電錶（selectedPowerMeters）的總用電量
                // 如果沒有選擇任何電表，不計入任何設備
                if (selectedPowerMeterSet.has(deviceId) || selectedPowerMeterSet.has(matchingDeviceId)) {
                  totalMinuteEnergy += energyValue;
                }
              } else {
                todayEnergy[matchingDeviceId] = 0;
                if (deviceId !== matchingDeviceId) {
                  todayEnergy[deviceId] = 0;
                }
              }
            });
            
            // ✅ 今日用電量改由 fetchTodayBaseline 計算（基準值法），這裡只記錄供參考
            logger.log(`📊 [IntelliSpace] Batch energy report sum (for reference): ${totalMinuteEnergy.toFixed(3)} kWh`);
          } catch (error) {
            logger.error('❌ [IntelliSpace] Failed to fetch batch energy report:', error);
          }

          // Also ensure all energy devices have an entry (even if 0 or undefined)
          // 確保所有 energy 設備都有條目（即使為 0 或 undefined）
          allEnergyDevices.forEach(device => {
            if (!(device.deviceId in todayEnergy)) {
              // Device was not queried or matched, set to undefined so it shows "--"
              // 設備未被查詢或匹配，設為 undefined 以顯示 "--"
              logger.warn(`⚠️ [IntelliSpace] Device ${device.deviceId} (${device.deviceName}) was not found in todayEnergy results`);
            }
          });

          logger.log('📊 [IntelliSpace] Today energy per device (final):', todayEnergy);
          logger.log('📊 [IntelliSpace] Energy count:', Object.keys(todayEnergy).length);
          logger.log('📊 [IntelliSpace] All energy devices count:', allEnergyDevices.length);
          logger.log('📊 [IntelliSpace] Missing devices:', allEnergyDevices
            .filter(d => !(d.deviceId in todayEnergy))
            .map(d => ({ deviceId: d.deviceId, deviceName: d.deviceName }))
          );
          
          if (Object.keys(todayEnergy).length === 0) {
            logger.warn('⚠️ [IntelliSpace] No energy data found for any device!');
          }
          
          setTodayEnergyPerDevice(todayEnergy);
        } else {
          logger.warn('⚠️ [IntelliSpace] No energy device IDs to fetch');
        }
      } catch (error) {
        logger.error('❌ [IntelliSpace] Failed to fetch today energy per device:', error);
        // Don't fail the entire fetch if this fails
      }
      
      // ✅ 处理月度数据
      const monthlyChartData = extractChartDataFromReport(monthlyReportResult);
      const { currentMonth: currentMonthData, lastMonth: lastMonthData } = convertDailyPowerData(monthlyChartData, todayStr);
      
      logger.log('📊 [IntelliSpace] Processed monthly data:', {
        chartDataPoints: monthlyChartData.length,
        currentMonthData: currentMonthData.filter(d => d.energy && d.energy > 0),
        lastMonthData: lastMonthData.filter(d => d.energy && d.energy > 0)
      });
      
      setMonthlyPowerData(currentMonthData);
      setLastMonthPowerData(lastMonthData);
    } catch (error) {
      logger.error('❌ [IntelliSpace] Failed to fetch hourly chart data:', error);
    } finally {
      isLoadingChartDataRef.current = false;
      setIsLoadingChartData(false);
    }
  }, [devices, selectedPowerMeters, selectedThSensors, excludedRankingDevices, extractChartDataFromReport, convertHourlyPowerData, convertDailyPowerData, convertHourlyClimateData, taipeiFormatter]);

  // ✅ Fetch weekly energy ranking (7 days)
  // 獲取過去一周能耗排行數據
  const fetchWeeklyEnergyRanking = useCallback(async () => {
    if (devices.length === 0) return;
    
    setIsLoadingWeeklyEnergy(true);
    try {
      // ✅ Debug: 列出所有設備的過濾條件
      console.log('📊 [Weekly Ranking] Filtering devices:', devices.map(d => {
        const hasMeterData = d.watt !== undefined || d.wattHour !== undefined || 
                            d.voltage !== undefined || d.current !== undefined;
        const isPowerMeter = d.deviceType === 'power_meter' || d.deviceType === 'power_meter_3phase';
        const sp = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
        const hasMeterAttributes = sp.watt || sp.watt_hour || sp.voltage || sp.current || sp.real_power;
        return {
          deviceId: d.deviceId,
          deviceType: d.deviceType,
          isPowerMeter,
          hasMeterData,
          hasMeterAttributes,
          watt: d.watt,
          sensorProfile: sp
        };
      }));
      
      // ✅ 查詢所有有 power meter 能力的設備（有 watt/watt_hour/real_power）
      const allEnergyDevices = devices.filter(d => {
        const hasMeterData = d.watt !== undefined || d.wattHour !== undefined || 
                            d.voltage !== undefined || d.current !== undefined;
        const isPowerMeter = d.deviceType === 'power_meter' || d.deviceType === 'power_meter_3phase';
        const sp = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
        const hasMeterAttributes = sp.watt || sp.watt_hour || sp.voltage || sp.current || sp.real_power;
        return isPowerMeter || hasMeterData || hasMeterAttributes;
      });
      
      if (allEnergyDevices.length === 0) {
        console.log('📊 [Weekly Ranking] No energy devices found');
        setWeeklyEnergyPerDevice({});
        return;
      }
      
      // ✅ 使用完整的 deviceSN 查詢（如 UTU500-4J2222M#1）
      // 後端 MCP 已支持跨 locationId 查詢
      const deviceSNsToQuery = allEnergyDevices.map(d => d.deviceId);
      
      console.log('📊 [Weekly Ranking] DeviceSNs to query:', deviceSNsToQuery);
      
      // ✅ 清除舊的 GenerateReport 緩存
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.includes('GenerateReport')) {
          sessionStorage.removeItem(key);
        }
      }
      
      // ✅ 單次調用 GenerateReport（後端已支持跨 locationId）
      const result = await ultronTuringApi.generateReport(deviceSNsToQuery, {
        duration: '7d',
        granularity: 'day',
        format: 'summary',
        utcOffset: '+08:00'
      });
      
      console.log('📊 [Weekly Ranking] API result:', result.success, Object.keys(result.data || {}).length, 'devices');
      
      console.log('📊 [Weekly Ranking] RAW API result (full):', result);
      console.log('📊 [Weekly Ranking] result.success:', result.success);
      console.log('📊 [Weekly Ranking] result.data:', result.data);
      console.log('📊 [Weekly Ranking] result.data keys:', result.data ? Object.keys(result.data) : 'undefined');
      
      if (!result.success && !result.data) {
        logger.warn('⚠️ [IntelliSpace] Weekly energy report failed');
        return;
      }
      
      const weeklyEnergy: Record<string, number> = {};
      
      // Extract energy data from response
      // MCP GenerateReport returns multiple formats:
      // Format A: { success, data: { devices: { [deviceSN]: {...} } } }
      // Format B: { success, data: { [deviceSN]: { summary, dataPoints } } }
      // Format C: { [deviceSN]: { summary, dataPoints } } (direct)
      let reportData: Record<string, any> = {};
      
      if (result.data?.devices) {
        reportData = result.data.devices;
      } else if (result.data) {
        reportData = result.data;
      } else {
        reportData = result;
      }
      
      console.log('📊 [Weekly Ranking] Report data structure:', Object.keys(reportData));
      // 打印每個設備的 totalConsumption 值
      console.log('📊 [Weekly Ranking] totalConsumption values:', Object.fromEntries(
        Object.entries(reportData).map(([k, v]: [string, any]) => [k, v?.totalConsumption])
      ));
      
      // Debug: 列出所有 allEnergyDevices 的 deviceId 和 baseSN
      console.log('📊 [Weekly Ranking] allEnergyDevices mapping:', allEnergyDevices.map(d => ({
        deviceId: d.deviceId,
        baseSN: d.baseSN,
        computed_baseSN: d.deviceId.split('#')[0]
      })));
      
      Object.entries(reportData).forEach(([key, deviceData]: [string, any]) => {
        // Skip non-device keys like 'success', 'message', etc.
        if (key === 'success' || key === 'message' || !deviceData || typeof deviceData !== 'object') {
          console.log(`📊 [Weekly Ranking] Skipping key: ${key}`);
          return;
        }
        
        // ✅ Use new MCP GenerateReport format with baseSN, deviceSN, and totalConsumption
        const baseSN = deviceData.baseSN || key.split('#')[0];
        const deviceSN = deviceData.deviceSN || key;
        
        // ✅ Debug: 顯示設備數據結構
        console.log(`📊 [Weekly Ranking] Device ${key} data:`, {
          hasChartData: !!deviceData.chartData,
          chartDataLength: deviceData.chartData?.length,
          totalConsumption: deviceData.totalConsumption,
          keys: Object.keys(deviceData).slice(0, 10)
        });
        
        // ✅ 優先使用 totalConsumption，如果不存在則從 chartData 計算
        let totalEnergy = deviceData.totalConsumption;
        
        if (totalEnergy === undefined && deviceData.chartData && Array.isArray(deviceData.chartData) && deviceData.chartData.length > 0) {
          // 從 chartData 計算總能耗：使用 energy 字段的總和，或 watt_hour 的差值
          const chartData = deviceData.chartData;
          
          // 方法1：直接使用 energy 字段的總和
          const energySum = chartData.reduce((sum: number, point: any) => {
            return sum + (point.energy ?? 0);
          }, 0);
          
          if (energySum > 0) {
            totalEnergy = energySum;
            console.log(`📊 [Weekly Ranking] Calculated energy from chartData.energy sum: ${totalEnergy} kWh`);
          } else {
            // 方法2：使用 watt_hour 的最大值減最小值
            const wattHourValues = chartData
              .map((point: any) => point.watt_hour ?? point.watt_hour_max ?? 0)
              .filter((v: number) => v > 0);
            
            if (wattHourValues.length > 1) {
              const maxWattHour = Math.max(...wattHourValues);
              const minWattHour = Math.min(...wattHourValues);
              totalEnergy = maxWattHour - minWattHour;
              console.log(`📊 [Weekly Ranking] Calculated energy from watt_hour diff: ${totalEnergy} kWh`);
            }
          }
        }
        
        totalEnergy = totalEnergy ?? 0;
        
        console.log(`📊 [Weekly Ranking] Processing key="${key}": totalEnergy=${totalEnergy} kWh`);
        
        // Accept any non-negative energy value (including 0)
        if (totalEnergy >= 0) {
          // ✅ 只存儲精確的 deviceSN（如 UTU500-4J2222M#1），不使用 baseSN fallback
          // 這樣每個 endpoint 都有自己獨立的能耗值
          weeklyEnergy[deviceSN] = totalEnergy;
          
          // ✅ 只用精確匹配來映射到 device.deviceId
          allEnergyDevices.forEach(device => {
            const dDeviceSN = device.deviceId;
            
            // ✅ 只精確匹配 - 不使用 baseSN fallback
            // 這確保 #1, #2, #3 各自使用自己的 totalConsumption
            if (dDeviceSN === deviceSN) {
              weeklyEnergy[device.deviceId] = totalEnergy;
              console.log(`    -> Exact match: ${deviceSN} → ${device.deviceId}: ${totalEnergy} kWh`);
            }
          });
        }
      });
      
      console.log('📊 [Weekly Ranking] Final weekly energy map:', weeklyEnergy);
      
      logger.log('📊 [IntelliSpace] Weekly energy ranking loaded:', weeklyEnergy);
      setWeeklyEnergyPerDevice(weeklyEnergy);
    } catch (error) {
      logger.error('❌ [IntelliSpace] Failed to fetch weekly energy ranking:', error);
    } finally {
      setIsLoadingWeeklyEnergy(false);
    }
  }, [devices]);

  // ✅ Function to update only today's data (for real-time updates)
  // 只更新今日数据的函数（用于实时更新）
  const updateTodayData = useCallback(async () => {
    if (devices.length === 0) return;
    if (isLoadingChartDataRef.current) return; // Prevent concurrent fetches
    
    try {
      const today = new Date();
      const todayStr = taipeiFormatter.format(today); // 格式：YYYY-MM-DD
      const currentHour = today.getHours();
      
      // ===== 1. 電力數據更新 =====
      const powerMeters = devices.filter(d => d.deviceType === 'power_meter' || d.deviceType === 'power_meter_3phase');
      const selectedPowerMeterDevices = selectedPowerMeters.length > 0
        ? powerMeters.filter(d => selectedPowerMeters.includes(d.deviceId))
        : powerMeters;
      
      // ✅ 每個 deviceSN (#1, #2, #3...) 是獨立的電錶，直接使用所有勾選的 deviceId
      const powerMeterIds = selectedPowerMeterDevices.map(d => d.deviceId);
      
      // ===== 2. 溫濕度數據更新 =====
      const thSensors = devices.filter(d => d.deviceType === 'th_sensor');
      const selectedThSensorDevices = selectedThSensors.length > 0
        ? thSensors.filter(d => selectedThSensors.includes(d.deviceId))
        : thSensors;
      
      // ✅ 每個 deviceSN 是獨立的感測器，直接使用所有勾選的 deviceId
      const thSensorIds = selectedThSensorDevices.map(d => d.deviceId);
      
      // ===== 並行呼叫 API =====
      const [powerReport, climateReport] = await Promise.all([
        // 電力報告
        powerMeterIds.length > 0
          ? ultronTuringApi.generateReport(powerMeterIds, {
              duration: '24h',
              granularity: 'hour',
              metrics: 'auto',
              format: 'summary',
              utcOffset: '+08:00'
            })
          : Promise.resolve({ success: true, data: {} }),
        // 溫濕度報告
        thSensorIds.length > 0
          ? ultronTuringApi.generateReport(thSensorIds, {
              duration: '24h',
              granularity: 'hour',
              metrics: ['temp', 'rh'],
              format: 'summary',
              utcOffset: '+08:00'
            }).catch(err => {
              logger.warn('⚠️ [IntelliSpace] Climate report failed:', err);
              return { success: false, data: {} };
            })
          : Promise.resolve({ success: true, data: {} })
      ]);
      
      // ===== 更新電力數據 =====
      if (powerMeterIds.length > 0) {
        const chartData = extractChartDataFromReport(powerReport);
        const hourlyData = convertHourlyPowerData(chartData, todayStr);
        
        setHourlyPowerData(prev => {
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(hourlyData);
          if (prevStr === newStr) {
            return prev;
          }
          return hourlyData;
        });
      }
      
      // ===== 更新溫濕度數據 =====
      if (thSensorIds.length > 0 && climateReport?.success) {
        const climateChartData = extractChartDataFromReport(climateReport);
        const climateData = convertHourlyClimateData(climateChartData, todayStr);
        
        setHourlyClimateData(prev => {
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(climateData);
          if (prevStr === newStr) {
            return prev;
          }
          return climateData;
        });
      }
      
      // ✅ 只有小時數真正改變時才更新
      setChartCurrentHour(prev => prev === currentHour ? prev : currentHour);
      
      logger.log('📊 [IntelliSpace] Updated today data at :15 (power + climate)');
    } catch (error) {
      logger.error('❌ [IntelliSpace] Failed to update today data:', error);
    }
  }, [devices, selectedPowerMeters, selectedThSensors, extractChartDataFromReport, convertHourlyPowerData, convertHourlyClimateData, taipeiFormatter]);

  // ✅ 取得今日用電量（分粒度，最新 watt_hour - 00:00 watt_hour）
  // ✅ 計算今日用電量：只計算 selectedPowerMeters（用戶選擇的電表們）
  const calculateTodayEnergy = useCallback(() => {
    let totalEnergy = 0;
    
    // ✅ 只計算 selectedPowerMeters 中的設備
    selectedPowerMeters.forEach((deviceId) => {
      const currentValue = currentWattHourRef.current.get(deviceId);
      const baseline = wattHourBaselineRef.current.get(deviceId);
      
      if (baseline !== undefined && currentValue !== undefined && currentValue > baseline) {
        totalEnergy += currentValue - baseline;
      }
    });
    
    return totalEnergy;
  }, [selectedPowerMeters]);

  // ✅ 只獲取一次 00:00 基準值，之後靠 WebSocket 即時更新
  // ✅ 優化：使用 LocalStorage 快取 baseline，避免重複 API 調用
  const fetchTodayBaseline = useCallback(async () => {
    if (devices.length === 0) return;
    
    setIsTodayEnergyLoading(true);
    try {
      // ✅ 使用與電錶選擇器相同的過濾邏輯（基於能力，而非 deviceType）
      const powerMeters = devices.filter(d => {
        const sensorProfile = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
        const hasPowerCapability = sensorProfile.watt || sensorProfile.real_power || sensorProfile.watt_hour;
        const hasPowerData = d.watt !== undefined || d.wattHour !== undefined;
        return hasPowerCapability || hasPowerData;
      });
      
      // ✅ 如果有勾選的設備，只使用勾選的；否則使用所有有電力能力的設備
      const selectedPowerMeterDevices = selectedPowerMeters.length > 0
        ? devices.filter(d => selectedPowerMeters.includes(d.deviceId))
        : powerMeters;
      
      // ✅ 每個 deviceSN (#1, #2, #3...) 是獨立的電錶，直接使用所有勾選的 deviceId
      const powerMeterIds = selectedPowerMeterDevices.map(d => d.deviceId);
      
      if (powerMeterIds.length === 0) return;
      
      // ✅ 清除過期的快取（非今日的）
      baselineCacheService.clearExpiredCache();
      
      // ✅ 一次性修復：清除可能有錯誤 baseline 的舊快取（v2 修復）
      const cacheVersion = localStorage.getItem('baselineCacheVersion');
      if (cacheVersion !== 'v2') {
        console.log('🔄 [TodayEnergy] Clearing old cache (upgrading to v2)');
        baselineCacheService.clearAllCache();
        localStorage.setItem('baselineCacheVersion', 'v2');
      }
      
      // ✅ 檢查快取：哪些設備有今日的 baseline 快取
      const cachedBaselines = baselineCacheService.getCachedBaselines(powerMeterIds);
      const devicesWithCache = Array.from(cachedBaselines.keys());
      const devicesNeedingFetch = powerMeterIds.filter(id => !cachedBaselines.has(id));
      
      console.log(`📊 [TodayEnergy] Cache status: ${devicesWithCache.length} cached, ${devicesNeedingFetch.length} need fetch`);
      
      // ✅ 將快取的 baseline 載入到 ref
      cachedBaselines.forEach((baseline, deviceId) => {
        wattHourBaselineRef.current.set(deviceId, baseline);
      });
      
      // ✅ 只對沒有快取的設備調用 generateReport
      if (devicesNeedingFetch.length > 0) {
        console.log(`📊 [TodayEnergy] Fetching baseline from API for: ${devicesNeedingFetch.join(', ')}`);
        
        const now = new Date();
        // ✅ 修復：改用當前時間往前取整天的數據，找最早的數據點作為 baseline
        // 原本只取 00:00-01:00 一小時，如果設備那時沒數據就會失敗
        const currentHour = now.getHours();
        const hoursToFetch = Math.max(currentHour + 1, 2); // 至少取 2 小時，最多到當前小時
        
        const baselineReport = await ultronTuringApi.generateReport(devicesNeedingFetch, {
          endTime: now.toISOString(),
          duration: `${hoursToFetch}h`,
          granularity: 'hour',
          metrics: ['watt_hour'],
          format: 'detailed', // 使用 detailed 以獲取 watt_hour 陣列
          utcOffset: '+08:00'
        });
        
        console.log(`📊 [TodayEnergy] Baseline API response:`, baselineReport?.success, Object.keys(baselineReport?.data || {}));
        
        // ✅ 從報告中提取最早的數據點作為 baseline
        if (baselineReport?.success && baselineReport?.data) {
          Object.entries(baselineReport.data).forEach(([deviceSN, deviceData]: [string, any]) => {
            // ✅ 優先從 watt_hour 陣列中找最早的（timestamp 最小的）
            if (deviceData?.watt_hour && Array.isArray(deviceData.watt_hour) && deviceData.watt_hour.length > 0) {
              // 按 timestamp 排序，取最早的
              const sorted = [...deviceData.watt_hour].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
              const firstPoint = sorted[0];
              // 使用 min 值作為 baseline（當小時開始時的值）
              const baselineValue = firstPoint.min ?? firstPoint.value ?? 0;
              wattHourBaselineRef.current.set(deviceSN, baselineValue);
              baselineCacheService.saveBaseline(deviceSN, baselineValue);
              console.log(`📊 [TodayEnergy] Baseline fetched & cached for ${deviceSN}: ${baselineValue.toFixed(3)} kWh (from earliest hour)`);
            }
            // 備用：chartData 格式
            else if (deviceData?.chartData && deviceData.chartData.length > 0) {
              const sorted = [...deviceData.chartData].sort((a: any, b: any) => (a.timestamp || 0) - (b.timestamp || 0));
              const firstPoint = sorted[0];
              const baselineValue = firstPoint.watt_hour_min ?? firstPoint.watt_hour ?? 0;
              wattHourBaselineRef.current.set(deviceSN, baselineValue);
              baselineCacheService.saveBaseline(deviceSN, baselineValue);
              console.log(`📊 [TodayEnergy] Baseline fetched & cached for ${deviceSN}: ${baselineValue.toFixed(3)} kWh (from chartData)`);
            }
          });
        }
        
        // ✅ 檢查是否還有設備沒拿到 baseline
        const stillMissing = devicesNeedingFetch.filter(id => !wattHourBaselineRef.current.has(id));
        if (stillMissing.length > 0) {
          console.warn(`⚠️ [TodayEnergy] Could not get baseline for: ${stillMissing.join(', ')}`);
        }
      } else {
        console.log(`📊 [TodayEnergy] All baselines from cache - skipping API call! ⚡`);
      }
      
      // ✅ 用 QueryStatus 獲取「當前」的 watt_hour（即時值）- 這個總是要調用
      const statusResponse = await ultronTuringApi.queryStatus(powerMeterIds);
      
        console.log(`📊 [TodayEnergy] QueryStatus response:`, statusResponse?.success);
        console.log(`📊 [TodayEnergy] QueryStatus data keys:`, Object.keys(statusResponse?.data || {}));
        console.log(`📊 [TodayEnergy] Baseline ref keys:`, Array.from(wattHourBaselineRef.current.keys()));
        console.log(`📊 [TodayEnergy] Selected power meters:`, powerMeterIds);
        
        if (statusResponse?.success && statusResponse?.data) {
        let totalEnergy = 0;
        
        // 處理不同的數據格式
        const processDevice = (deviceSN: string, deviceData: any) => {
          // Helper: 從可能的多種格式中提取數值
          const extractNumber = (val: any): number | undefined => {
            if (val === undefined || val === null) return undefined;
            if (typeof val === 'number') return val;
            if (typeof val === 'string') return Number(val);
            if (Array.isArray(val)) return val.length > 0 ? Number(val[0]) : undefined;
            if (typeof val === 'object' && val.value !== undefined) {
              // {value: 123} or {value: [123, ...]}
              return extractNumber(val.value);
            }
            return undefined;
          };
          
          // 嘗試多種路徑提取 watt_hour
          let currentWattHour: number | undefined;
          
          // 路徑 1: 直接屬性 (可能是 number, array, 或 {value: ...} 格式)
          if (deviceData?.watt_hour !== undefined) {
            currentWattHour = extractNumber(deviceData.watt_hour);
          }
          // 路徑 2: wattHour (camelCase)
          else if (deviceData?.wattHour !== undefined) {
            currentWattHour = extractNumber(deviceData.wattHour);
          }
          // 路徑 3: iotState
          else if (deviceData?.iotState?.watt_hour !== undefined) {
            currentWattHour = extractNumber(deviceData.iotState.watt_hour);
          }
          // 路徑 4: attributes
          else if (deviceData?.attributes?.watt_hour !== undefined) {
            currentWattHour = extractNumber(deviceData.attributes.watt_hour);
          }
          // 路徑 5: state
          else if (deviceData?.state?.watt_hour !== undefined) {
            currentWattHour = extractNumber(deviceData.state.watt_hour);
          }
          // 路徑 6: states 陣列中的第一個
          else if (deviceData?.states && Array.isArray(deviceData.states)) {
            for (const state of deviceData.states) {
              if (state?.watt_hour !== undefined) {
                currentWattHour = extractNumber(state.watt_hour);
                break;
              }
            }
          }
          
          const baseline = wattHourBaselineRef.current.get(deviceSN) ?? 0;
          
          console.log(`📊 [TodayEnergy] Processing ${deviceSN}: watt_hour=${currentWattHour}, baseline=${baseline}`);
          
          if (typeof currentWattHour === 'number' && !isNaN(currentWattHour) && currentWattHour > 0) {
            currentWattHourRef.current.set(deviceSN, currentWattHour);
            
            // ✅ 修復：如果 baseline 是 0 但 currentWattHour 很大，表示沒有可靠的基準值
            // 這種情況下跳過該設備，避免把累積電量算成今日用電
            if (baseline === 0 && currentWattHour > 100) {
              console.log(`⚠️ [TodayEnergy] Skipping ${deviceSN}: no reliable baseline (current=${currentWattHour.toFixed(3)} kWh, baseline=0)`);
              return; // 跳過此設備
            }
            
            // 計算今日用電量
            if (currentWattHour > baseline) {
              const deviceEnergy = currentWattHour - baseline;
              totalEnergy += deviceEnergy;
              console.log(`📊 [TodayEnergy] Device ${deviceSN}: ${deviceEnergy.toFixed(3)} kWh (current: ${currentWattHour.toFixed(3)}, baseline: ${baseline.toFixed(3)})`);
            }
          }
        };
        
        // 處理數據（可能是 object 或 array）
        if (Array.isArray(statusResponse.data)) {
          // Array format: [{system: {...}, states: [...]}, ...] or [{deviceSN, watt_hour, ...}, ...]
          statusResponse.data.forEach((item: any) => {
            // ✅ 檢查 states 陣列格式（常見的 QueryStatus 回傳格式）
            if (item.states && Array.isArray(item.states) && item.states.length > 0) {
              console.log(`📊 [TodayEnergy] Processing array item with states`);
              item.states.forEach((stateItem: any) => {
                const stateSN = stateItem.deviceSN || stateItem.sn || item.system?.baseSN;
                if (stateSN) {
                  // 把 stateItem.values 展開到 stateItem 上以便 processDevice 解析
                  const flattenedData = { ...stateItem, ...stateItem.values };
                  console.log(`📊 [TodayEnergy] Extracted SN from states: ${stateSN}`);
                  processDevice(stateSN, flattenedData);
                }
              });
            } else {
              // 直接格式
              const sn = item.sn || item.deviceSN || item.deviceId || item.baseSN || item.system?.baseSN;
              if (sn) processDevice(sn, item);
            }
          });
        } else if (statusResponse.data.deviceSN || statusResponse.data.baseSN || statusResponse.data.sn) {
          // ✅ 單一設備扁平格式: {deviceSN: "xxx", watt_hour: {...}, ...}
          const sn = statusResponse.data.deviceSN || statusResponse.data.baseSN || statusResponse.data.sn;
          console.log(`📊 [TodayEnergy] Detected flat single-device format, SN: ${sn}`);
          processDevice(sn, statusResponse.data);
        } else if (statusResponse.data.states && Array.isArray(statusResponse.data.states)) {
          // ✅ states 陣列格式: {states: [{deviceSN, watt_hour, ...}, ...]}
          console.log(`📊 [TodayEnergy] Detected states array format`);
          statusResponse.data.states.forEach((item: any) => {
            const sn = item.sn || item.deviceSN || item.deviceId || item.baseSN;
            if (sn) processDevice(sn, item);
          });
        } else {
          // Object format: {"0": {system: {...}, states: [...]}, ...} or {deviceSN1: {data}, ...}
          Object.entries(statusResponse.data).forEach(([key, value]) => {
            // 過濾掉非設備的 key（如 success, message 等）
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
              const deviceData = value as any;
              
              // ✅ 優先從 deviceData 內部提取真正的 device SN（處理 {"0": {...}} 格式）
              let actualSN = key;
              
              // 檢查 states 陣列格式（常見的 QueryStatus 回傳格式）
              if (deviceData.states && Array.isArray(deviceData.states) && deviceData.states.length > 0) {
                const firstState = deviceData.states[0];
                actualSN = firstState.deviceSN || firstState.sn || deviceData.system?.baseSN || key;
                console.log(`📊 [TodayEnergy] Detected nested states format, extracting SN: ${actualSN}`);
                // 處理 states 內的數據
                deviceData.states.forEach((stateItem: any) => {
                  const stateSN = stateItem.deviceSN || stateItem.sn || actualSN;
                  // 把 stateItem.values 展開到 stateItem 上以便 processDevice 解析
                  const flattenedData = { ...stateItem, ...stateItem.values };
                  processDevice(stateSN, flattenedData);
                });
                return; // 已處理，跳過下方邏輯
              }
              
              // 嘗試從 deviceData 直接提取 SN
              if (deviceData.deviceSN || deviceData.sn || deviceData.baseSN) {
                actualSN = deviceData.deviceSN || deviceData.sn || deviceData.baseSN;
              }
              
              // 如果有 watt_hour 或其他電力相關欄位，才視為設備數據
              if (deviceData.watt_hour !== undefined || deviceData.wattHour !== undefined || 
                  deviceData.iotState?.watt_hour !== undefined || deviceData.attributes?.watt_hour !== undefined ||
                  deviceData.values?.watt_hour !== undefined) {
                // 展開 values 物件
                const flattenedData = deviceData.values ? { ...deviceData, ...deviceData.values } : deviceData;
                processDevice(actualSN, flattenedData);
              }
            }
          });
        }
        
        console.log(`📊 [TodayEnergy] Total: ${totalEnergy.toFixed(3)} kWh`);
        // ✅ 只有當新值 > 0 時才更新，避免覆蓋快取的有效值
        if (totalEnergy > 0) {
          setTodayMinuteEnergy(totalEnergy);
        } else {
          console.log(`📊 [TodayEnergy] Keeping cached value (API returned 0)`);
        }
      } else {
        console.warn(`⚠️ [TodayEnergy] QueryStatus failed or no data`);
      }
      
    } catch (error) {
      logger.error('❌ [IntelliSpace] Failed to fetch today baseline:', error);
    } finally {
      setIsTodayEnergyLoading(false);
    }
  }, [devices, selectedPowerMeters]);
  
  // ✅ 舊函數別名（向後兼容）
  const fetchTodayMinuteEnergy = fetchTodayBaseline;

  // ✅ WebSocket fallback polling：當 WebSocket 斷線時，每 5 分鐘 polling 一次
  const wsPollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // WebSocket fallback polling 邏輯
  useEffect(() => {
    if (!chartDataFetchedRef.current) return;
    if (devices.length === 0) return;
    if (selectedPowerMeters.length === 0) return;
    
    const POLLING_INTERVAL = 5 * 60 * 1000; // 5 分鐘
    const WS_TIMEOUT = 2 * 60 * 1000; // 2 分鐘無 WebSocket 更新則視為斷線
    
    const checkAndPoll = async () => {
      const wsState = websocketManager.getState();
      const timeSinceLastWs = Date.now() - lastWsWattHourUpdateRef.current;
      const isWsStale = timeSinceLastWs > WS_TIMEOUT;
      
      console.log(`📡 [WSFallback] Status check: connected=${wsState.isConnected}, authenticated=${wsState.isAuthenticated}, timeSinceLastWs=${Math.round(timeSinceLastWs/1000)}s, isStale=${isWsStale}`);
      
      // 如果 WebSocket 斷線或超過 2 分鐘沒收到更新，執行 polling
      if (!wsState.isConnected || !wsState.isAuthenticated || isWsStale) {
        console.log(`📡 [WSFallback] WebSocket stale/disconnected, polling now...`);
        
        try {
          // 重新獲取當前 watt_hour
          const statusResponse = await ultronTuringApi.queryStatus(selectedPowerMeters);
          
          if (statusResponse?.success && statusResponse?.data) {
            let totalEnergy = 0;
            
            const processDevice = (deviceSN: string, deviceData: any) => {
              let currentWattHour: number | undefined;
              
              if (deviceData?.watt_hour !== undefined) {
                currentWattHour = Number(deviceData.watt_hour);
              } else if (deviceData?.wattHour !== undefined) {
                currentWattHour = Number(deviceData.wattHour);
              } else if (deviceData?.iotState?.watt_hour !== undefined) {
                const val = deviceData.iotState.watt_hour;
                currentWattHour = Array.isArray(val) ? Number(val[0]) : Number(val);
              } else if (deviceData?.attributes?.watt_hour !== undefined) {
                const val = deviceData.attributes.watt_hour;
                currentWattHour = Array.isArray(val) ? Number(val[0]) : Number(val);
              }
              
              const baseline = wattHourBaselineRef.current.get(deviceSN) ?? 0;
              
              if (typeof currentWattHour === 'number' && !isNaN(currentWattHour) && currentWattHour > 0) {
                currentWattHourRef.current.set(deviceSN, currentWattHour);
                if (currentWattHour > baseline) {
                  totalEnergy += currentWattHour - baseline;
                }
              }
            };
            
            if (Array.isArray(statusResponse.data)) {
              statusResponse.data.forEach((item: any) => {
                const sn = item.sn || item.deviceSN || item.deviceId;
                if (sn) processDevice(sn, item);
              });
            } else {
              Object.entries(statusResponse.data).forEach(([deviceSN, deviceData]) => {
                processDevice(deviceSN, deviceData);
              });
            }
            
            console.log(`📡 [WSFallback] Poll result: ${totalEnergy.toFixed(3)} kWh`);
            if (totalEnergy > 0) {
              setTodayMinuteEnergy(totalEnergy);
            }
          }
        } catch (error) {
          console.error(`❌ [WSFallback] Polling failed:`, error);
        }
      } else {
        console.log(`📡 [WSFallback] WebSocket healthy, skipping poll`);
      }
    };
    
    // 啟動定時 polling
    wsPollingIntervalRef.current = setInterval(checkAndPoll, POLLING_INTERVAL);
    console.log(`📡 [WSFallback] Started fallback polling (every 5 min)`);
    
    return () => {
      if (wsPollingIntervalRef.current) {
        clearInterval(wsPollingIntervalRef.current);
        wsPollingIntervalRef.current = null;
        console.log(`📡 [WSFallback] Stopped fallback polling`);
      }
    };
  }, [devices.length, selectedPowerMeters]);

  // ✅ 將 selectedPowerMeters 轉為字串，確保內容變化時能正確偵測
  const selectedPowerMetersKey = useMemo(() => 
    [...selectedPowerMeters].sort().join(','), 
    [selectedPowerMeters]
  );

  // ✅ 頁面載入時立即從 localStorage 讀取快取的今日用電量
  useEffect(() => {
    if (selectedPowerMeters.length === 0) {
      setIsTodayEnergyLoading(false);
      return;
    }
    
    const cachedValue = baselineCacheService.getCachedTodayEnergy(selectedPowerMeters);
    if (cachedValue !== null && cachedValue > 0) {
      setTodayMinuteEnergy(cachedValue);
      setIsTodayEnergyLoading(false);
      console.log(`📊 [TodayEnergy] Loaded from cache: ${cachedValue.toFixed(3)} kWh - instant display!`);
    }
  }, [selectedPowerMetersKey]); // 當選擇的電錶變化時重新檢查快取

  // ✅ 當 todayMinuteEnergy 更新時存到 localStorage
  useEffect(() => {
    if (todayMinuteEnergy > 0 && selectedPowerMeters.length > 0) {
      baselineCacheService.saveTodayEnergy(todayMinuteEnergy, selectedPowerMeters);
    }
  }, [todayMinuteEnergy, selectedPowerMeters]);

  // ✅ 今日用電量：由 fetchChartData 初始化，此 useEffect 只處理跨日更新
  // ✅ 當 selectedPowerMeters 變化時，由 re-fetch useEffect 處理（不在這裡重複呼叫）
  // ✅ 優化：移除初始呼叫 fetchTodayMinuteEnergy，因為 fetchChartData 已整合此功能
  useEffect(() => {
    if (!chartDataFetchedRef.current) return;
    if (devices.length === 0) return;
    
    // ✅ 不再在這裡呼叫 fetchTodayMinuteEnergy，因為 fetchChartData 已整合設定 baseline
    // 只設定每日 00:15 的跨日更新
    
    // 每天 00:15 重新設定基準值（跨日更新）
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 15, 0, 0);
    const msUntilMidnight = tomorrow.getTime() - now.getTime();
    
    logger.log(`📊 [IntelliSpace] Daily baseline reset scheduled in ${Math.round(msUntilMidnight / 1000 / 60)} minutes (at 00:15)`);
    
    const timeoutId = setTimeout(() => {
      // 清空舊基準值，重新取得
      wattHourBaselineRef.current.clear();
      currentWattHourRef.current.clear();
      fetchTodayMinuteEnergy();
      
      // 之後每 24 小時重新設定
      const intervalId = setInterval(() => {
        wattHourBaselineRef.current.clear();
        currentWattHourRef.current.clear();
        fetchTodayMinuteEnergy();
      }, 24 * 60 * 60 * 1000);
      
      return () => clearInterval(intervalId);
    }, msUntilMidnight);
    
    return () => clearTimeout(timeoutId);
  }, [fetchTodayMinuteEnergy, devices.length, selectedPowerMetersKey]);

  // Initial fetch when devices are first loaded
  useEffect(() => {
    // 使用 console.log 確保能看到（繞過 verbose 模式檢查）
    console.log('📊 [IntelliSpace] Initial fetch useEffect triggered:', {
      chartDataFetchedRef: chartDataFetchedRef.current,
      devicesLength: devices.length,
      thSensorsCount: devices.filter(d => d.deviceType === 'th_sensor').length
    });
    
    if (chartDataFetchedRef.current) {
      console.log('📊 [IntelliSpace] Skipping initial fetch - already fetched');
      return;
    }
    if (devices.length === 0) {
      console.log('📊 [IntelliSpace] Skipping initial fetch - no devices');
      return;
    }
    
    console.log('📊 [IntelliSpace] Starting initial fetchChartData...');
    chartDataFetchedRef.current = true;
    fetchChartData(true).then(() => {
      // ✅ fetchChartData 完成後再獲取今日用電量的 baseline
      console.log('📊 [IntelliSpace] fetchChartData done, now fetching today baseline...');
      fetchTodayBaseline();
    });
    
    // Also fetch weekly energy ranking
    fetchWeeklyEnergyRanking();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices.length]);

  // ✅ 時粒度數據更新：每小時的第 15 分鐘更新一次
  // Hourly data update: Update at :15 of each hour
  useEffect(() => {
    if (!chartDataFetchedRef.current) return;
    if (devices.length === 0) return;
    
    // 計算到下一個 :15 的毫秒數
    const getMillisecondsUntilNextQuarter = () => {
      const now = new Date();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();
      const ms = now.getMilliseconds();
      
      // 計算到下一個 :15 需要的分鐘數
      let minutesToNext15: number;
      if (minutes < 15) {
        minutesToNext15 = 15 - minutes;
      } else {
        minutesToNext15 = 60 - minutes + 15; // 到下一個小時的 :15
      }
      
      // 轉換為毫秒，減去當前的秒和毫秒
      return (minutesToNext15 * 60 * 1000) - (seconds * 1000) - ms;
    };
    
    let timeoutId: ReturnType<typeof setTimeout>;
    let intervalId: ReturnType<typeof setInterval>;
    
    // 先設置一個 timeout 到下一個 :15
    const msUntilNext15 = getMillisecondsUntilNextQuarter();
    logger.log(`📊 [IntelliSpace] Hourly update scheduled in ${Math.round(msUntilNext15 / 1000 / 60)} minutes (at :15)`);
    
    timeoutId = setTimeout(() => {
      updateTodayData();
      // 之後每小時更新一次
      intervalId = setInterval(() => {
        updateTodayData();
      }, 60 * 60 * 1000); // 每小時
    }, msUntilNext15);
    
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [updateTodayData, devices.length]);

  // Re-fetch when selections change (after initial load)
  const lastSelectionsRef = useRef<string>('');
  useEffect(() => {
    if (!chartDataFetchedRef.current) return; // Wait for initial load
    if (!selectionsLoaded) return;
    
    const currentSelections = JSON.stringify({ pm: selectedPowerMeters, th: selectedThSensors });
    if (lastSelectionsRef.current === currentSelections) return;
    lastSelectionsRef.current = currentSelections;
    
    // Debounce selection changes
    const timeoutId = setTimeout(() => {
      fetchChartData(false);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [selectedPowerMeters, selectedThSensors, selectionsLoaded, fetchChartData]);

  // ✅ 日粒度數據更新：每天 00:15 更新一次
  // Daily data update: Update at 00:15 each day
  useEffect(() => {
    if (!chartDataFetchedRef.current) return;
    if (devices.length === 0) return;
    
    // 計算到下一個 00:15 的毫秒數
    const getMillisecondsUntilMidnight15 = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 15, 0, 0);
      
      // 如果現在還沒到今天的 00:15，就用今天的
      const today0015 = new Date(now);
      today0015.setHours(0, 15, 0, 0);
      
      if (now < today0015) {
        return today0015.getTime() - now.getTime();
      }
      return tomorrow.getTime() - now.getTime();
    };
    
    let timeoutId: ReturnType<typeof setTimeout>;
    let intervalId: ReturnType<typeof setInterval>;
    
    const msUntilMidnight15 = getMillisecondsUntilMidnight15();
    const hoursUntil = Math.round(msUntilMidnight15 / 1000 / 60 / 60 * 10) / 10;
    logger.log(`📊 [IntelliSpace] Daily update scheduled in ${hoursUntil} hours (at 00:15)`);
    
    timeoutId = setTimeout(() => {
      fetchChartData(false); // 完整更新，包含月度數據
      // 之後每天更新一次
      intervalId = setInterval(() => {
        fetchChartData(false);
      }, 24 * 60 * 60 * 1000); // 每天
    }, msUntilMidnight15);
    
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [fetchChartData, devices.length]);

  // Periodic refresh based on Settings interval (only for device status, not chart data)
  // 設定頁面的自動刷新間隔僅用於設備狀態，圖表數據使用上面的智能排程
  useEffect(() => {
    if (!autoRefresh || devices.length === 0) return;
    if (!chartDataFetchedRef.current) return;

    // 這裡只刷新設備狀態，不刷新圖表數據
    // Chart data is now handled by the smart scheduling above
    const intervalId = setInterval(() => {
      logger.log(`📊 [IntelliSpace] Device status auto-refresh (interval: ${refreshInterval}ms)`);
      // 注意：這裡不再調用 fetchChartData，圖表數據由上面的智能排程處理
      loadDevices(true, false);
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [autoRefresh, refreshInterval, devices.length, loadDevices]);

  // Calculate statistics
  const stats = useMemo(() => {
    let filteredDevices = devices;

    if (selectedZoneFilter !== 'all') {
      const zone = deviceZones.find(z => z.id === selectedZoneFilter);
      if (zone) {
        // ✅ Fix: Check both deviceId and baseSN for zone matching
        filteredDevices = devices.filter(d => 
          zone.devices.includes(d.deviceId) || 
          zone.devices.includes(d.baseSN)
        );
      }
    }

    // ✅ Get valid device IDs for filtering invalid selections
    const validDeviceIds = new Set(devices.map(d => d.deviceId));

    // ✅ 使用與電錶選擇器相同的過濾邏輯（基於 watt/watt_hour 能力）
    // 這樣 stats.powerMeterCount 會與選擇器顯示的數量一致
    const powerMeters = devices.filter(d => {
      const sensorProfile = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
      const hasPowerCapability = sensorProfile.watt || sensorProfile.real_power || sensorProfile.watt_hour;
      const hasPowerData = d.watt !== undefined || d.wattHour !== undefined;
      return hasPowerCapability || hasPowerData;
    });
    const thSensors = devices.filter(d => d.deviceType === 'th_sensor');
    // Use 'devices' instead of 'filteredDevices' to be consistent with the selector modal
    const presenceSensors = devices.filter(d => d.deviceType === 'presence_sensor');
    const hvacDevices = devices.filter(d => d.deviceType === 'hvac');
    const lightDevices = devices.filter(d => d.deviceType === 'light');
    // Use 'devices' instead of 'filteredDevices' to show all switches
    const switches = devices.filter(d => d.deviceType === 'switch');
    
    // ✅ Filter selections to only include valid (existing) devices
    const validSelectedPowerMeters = selectedPowerMeters.filter(id => validDeviceIds.has(id));
    const validSelectedThSensors = selectedThSensors.filter(id => validDeviceIds.has(id));
    const validSelectedHvac = selectedHvac.filter(id => validDeviceIds.has(id));
    const validSelectedLights = selectedLights.filter(id => validDeviceIds.has(id));
    const validSelectedSwitches = selectedSwitches.filter(id => validDeviceIds.has(id));
    const validSelectedPresenceSensors = selectedPresenceSensors.filter(id => validDeviceIds.has(id));
    const validSelectedHvacMeters = selectedHvacMeters.filter(id => validDeviceIds.has(id));
    const validSelectedLightingMeters = selectedLightingMeters.filter(id => validDeviceIds.has(id));

    // Calculate total power based on selected power meters (using valid selections only)
    const powerMetersToCalculate = validSelectedPowerMeters.length > 0
      ? powerMeters.filter(d => validSelectedPowerMeters.includes(d.deviceId))
      : powerMeters;
    const totalPower = powerMetersToCalculate.reduce((sum, d) => sum + (d.watt || 0), 0);
    
    // Calculate average temperature based on selected TH sensors (using valid selections only)
    // ✅ Exclude offline devices and devices without valid state
    const thSensorsToCalculate = (validSelectedThSensors.length > 0
      ? thSensors.filter(d => validSelectedThSensors.includes(d.deviceId))
      : thSensors
    ).filter(d => d.status === 'online'); // Only include online devices
    
    // Filter out invalid values: NaN, null, undefined, and values outside reasonable range (-50 to 60°C)
    // Also exclude 0°C values as they typically indicate no sensor data
    const temps = thSensorsToCalculate
      .map(d => d.temperature)
      .filter((t): t is number => 
        t !== undefined && 
        t !== null && 
        !isNaN(t) && 
        typeof t === 'number' && 
        t >= -50 && 
        t <= 60
      );
    const avgTemp = temps.length > 0 ? temps.reduce((a, b) => a + b, 0) / temps.length : 0;
    
    // Calculate average humidity based on selected TH sensors
    // ✅ Only use online devices with valid state
    // Filter out invalid values: NaN, null, undefined, and values outside 0-100% range
    const humidities = thSensorsToCalculate
      .map(d => d.humidity)
      .filter((h): h is number => 
        h !== undefined && 
        h !== null && 
        !isNaN(h) && 
        typeof h === 'number' && 
        h > 0 && // Exclude 0 as it usually means no data
        h <= 100
      );
    const avgHumidity = humidities.length > 0 ? humidities.reduce((a, b) => a + b, 0) / humidities.length : 0;
    
    // Calculate occupied count based on selected presence sensors (using valid selections only)
    const presenceSensorsToCount = validSelectedPresenceSensors.length > 0
      ? presenceSensors.filter(d => validSelectedPresenceSensors.includes(d.deviceId))
      : presenceSensors;
    const occupiedSensors = presenceSensorsToCount.filter(d => d.presence || d.occupancy);
    const occupiedCount = occupiedSensors.length;
    
    // Calculate active HVAC based on selected HVAC (using valid selections only)
    const hvacToCount = validSelectedHvac.length > 0
      ? hvacDevices.filter(d => validSelectedHvac.includes(d.deviceId))
      : hvacDevices;
    const activeHvacDevices = hvacToCount.filter(d => d.isOn);
    const activeHvac = activeHvacDevices.length;

    // Calculate active switches based on selected switches (using valid selections only)
    const switchesToCount = validSelectedSwitches.length > 0
      ? switches.filter(d => validSelectedSwitches.includes(d.deviceId))
      : switches;
    const activeSwitchDevices = switchesToCount.filter(d => d.switchOn);
    const activeSwitches = activeSwitchDevices.length;

    // Calculate active lights based on selected lights (using valid selections only)
    const lightsToCount = validSelectedLights.length > 0
      ? lightDevices.filter(d => validSelectedLights.includes(d.deviceId))
      : lightDevices;
    const activeLightDevices = lightsToCount.filter(d => d.isOn || d.power);
    const activeLights = activeLightDevices.length;

    // Get all devices with power measurement capability
    const devicesWithPower = devices.filter(d => {
      // Support both formats: normalized sensorProfile (new) or attributes.sensorProfile (old)
      const sensorProfile = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
      const hasPowerCapability = sensorProfile.watt || sensorProfile.real_power || sensorProfile.watt_hour;
      const hasPowerData = d.watt !== undefined || d.wattHour !== undefined;
      return hasPowerCapability || hasPowerData;
    });
    
    // Calculate HVAC power based on selected HVAC meters (using valid selections only)
    const hvacMetersToCalculate = validSelectedHvacMeters.length > 0
      ? devicesWithPower.filter(d => validSelectedHvacMeters.includes(d.deviceId))
      : [];
    const hvacPower = hvacMetersToCalculate.reduce((sum, d) => {
      const watt = typeof d.watt === 'number' ? d.watt : 0;
      return sum + watt;
    }, 0);
    
    // Calculate Lighting power based on selected Lighting meters (using valid selections only)
    const lightingMetersToCalculate = validSelectedLightingMeters.length > 0
      ? devicesWithPower.filter(d => validSelectedLightingMeters.includes(d.deviceId))
      : [];
    const lightingPower = lightingMetersToCalculate.reduce((sum, d) => {
      const watt = typeof d.watt === 'number' ? d.watt : 0;
      return sum + watt;
    }, 0);
    
    // Other power = total - hvac - lighting
    const otherPower = Math.max(0, totalPower - hvacPower - lightingPower);

    return {
      totalDevices: filteredDevices.length,
      onlineCount: filteredDevices.filter(d => d.status === 'online').length,
      totalPower: typeof totalPower === 'number' ? totalPower : 0,
      hvacPower: typeof hvacPower === 'number' ? hvacPower : 0,
      lightingPower: typeof lightingPower === 'number' ? lightingPower : 0,
      otherPower: typeof otherPower === 'number' ? otherPower : 0,
      hvacMeterCount: hvacMetersToCalculate.length,
      lightingMeterCount: lightingMetersToCalculate.length,
      avgTemp,
      avgHumidity,
      occupiedCount,
      occupiedSensors: occupiedSensors.map(d => d.deviceName || d.deviceId),
      presenceSensorCount: presenceSensors.length,
      // ✅ Use validSelected* for accurate counts (filters out removed devices)
      selectedPresenceSensorCount: validSelectedPresenceSensors.length > 0 ? validSelectedPresenceSensors.length : presenceSensors.length,
      activeHvac,
      activeHvacNames: activeHvacDevices.map(d => d.deviceName || d.deviceId),
      hvacCount: hvacDevices.length,
      selectedHvacCount: validSelectedHvac.length > 0 ? validSelectedHvac.length : hvacDevices.length,
      activeSwitches,
      activeSwitchNames: activeSwitchDevices.map(d => d.deviceName || d.deviceId),
      switchCount: switches.length,
      selectedSwitchCount: validSelectedSwitches.length > 0 ? validSelectedSwitches.length : switches.length,
      activeLights,
      activeLightNames: activeLightDevices.map(d => d.deviceName || d.deviceId),
      lightCount: lightDevices.length,
      selectedLightCount: validSelectedLights.length > 0 ? validSelectedLights.length : lightDevices.length,
      powerMeterCount: powerMeters.length,
      thSensorCount: thSensors.length,
      selectedThSensorCount: validSelectedThSensors.length > 0 ? validSelectedThSensors.length : thSensors.length,
      // ✅ Also expose valid selected power meter count
      selectedPowerMeterCount: validSelectedPowerMeters.length > 0 ? validSelectedPowerMeters.length : powerMeters.length
    };
  }, [devices, selectedZoneFilter, deviceZones, selectedPowerMeters, selectedSwitches, selectedHvac, selectedThSensors, selectedLights, selectedPresenceSensors, selectedHvacMeters, selectedLightingMeters]);

  // Memoize chart data to prevent re-renders - ensure 24 hours of data
  const powerChartData = useMemo(() => {
    const result = [];
    const currentHour = new Date().getHours(); // 使用即時時間
    
    for (let i = 0; i < 24; i++) {
      const hour = `${i.toString().padStart(2, '0')}:00`;
      const todayData = hourlyPowerData.find(d => d.hour === hour);
      const yesterdayData = yesterdayPowerData.find(d => d.hour === hour);
      const lastWeekData = lastWeekPowerData.find(d => d.hour === hour);
      
      // ✅ 只有過去的時段才顯示今日數據（i < currentHour 表示已完成的小時）
      // 當前小時也包含進來（i <= currentHour）因為可能有部分數據
      const isPastOrCurrent = i <= currentHour;
      // ✅ 修正：檢查 energy 或 power 是否有數據（energy 優先）
      const todayEnergy = (todayData as any)?.energy || 0;
      const todayPower = todayData?.power || 0;
      const hasTodayData = todayData !== undefined && (todayEnergy > 0 || todayPower > 0);
      // ✅ 優先使用 energy（轉換為 Wh），如果沒有則使用 power
      const displayValue = todayEnergy > 0 ? todayEnergy * 1000 : todayPower; // energy in kWh -> Wh
      const power = (isPastOrCurrent && hasTodayData) ? displayValue : null;
      
      // 同樣處理昨日和上週數據
      const yesterdayEnergy = (yesterdayData as any)?.energy || 0;
      const yesterdayPower = yesterdayData?.power || 0;
      const yesterdayValue = yesterdayEnergy > 0 ? yesterdayEnergy * 1000 : yesterdayPower;
      
      const lastWeekEnergy = (lastWeekData as any)?.energy || 0;
      const lastWeekPower = lastWeekData?.power || 0;
      const lastWeekValue = lastWeekEnergy > 0 ? lastWeekEnergy * 1000 : lastWeekPower;
      
      result.push({
        hour,
        power: power ?? 0, // for tooltip
        // ✅ powerPast: 只顯示過去已完成時段的數據
        powerPast: (i < currentHour && hasTodayData) ? displayValue : null,
        // ✅ powerFuture: 不再顯示未來數據（設為 null）
        powerFuture: null,
        yesterday: yesterdayValue,
        lastWeek: lastWeekValue
      });
    }
    return result;
  }, [hourlyPowerData, yesterdayPowerData, lastWeekPowerData, chartCurrentHour]);

  // Memoize monthly chart data
  // 使用 energy 字段（日總用電量，kWh）來顯示，而不是 power 字段（日平均功率，W）
  const monthlyChartData = useMemo(() => {
    const today = new Date();
    const currentDay = today.getDate();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const result = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dayStr = day.toString().padStart(2, '0');
      const currentData = monthlyPowerData.find(d => d.day === dayStr);
      const lastData = lastMonthPowerData.find(d => d.day === dayStr);
      // 使用 energy 字段（kWh）
      // ✅ 區分「無數據」和「0」：undefined 表示無數據，不繪製該點
      const energy = currentData?.energy; // 可能是 undefined
      const lastMonthEnergy = lastData?.energy ?? 0;
      const hasData = energy !== undefined;
      result.push({
        day: dayStr,
        // 為了兼容現有的格式化函數，將 kWh 轉換為 W（乘以 1000）
        power: hasData ? energy * 1000 : 0,
        // ✅ 修正：無數據時設為 null，圖表不繪製該點
        powerPast: (day <= currentDay && hasData) ? energy * 1000 : null,
        powerFuture: day > currentDay ? (hasData ? energy * 1000 : null) : null,
        lastMonth: lastMonthEnergy * 1000
      });
    }
    
    return result;
  }, [monthlyPowerData, lastMonthPowerData]);

  // Memoize climate chart data - keep all hours but null out future data
  const climateChartData = useMemo(() => {
    return hourlyClimateData.map((d, idx) => ({
      ...d,
      temp: idx <= chartCurrentHour ? d.temp : null,
      humidity: idx <= chartCurrentHour ? d.humidity : null
    }));
  }, [hourlyClimateData, chartCurrentHour]);

  // Check if climate chart has any valid data
  const hasClimateChartData = useMemo(() => {
    return climateChartData.some(d => d.temp !== null || d.humidity !== null);
  }, [climateChartData]);

  // Helper function to check if device matches a capability type
  // Aligned with DeviceManagerSidebar's getDeviceTypeLabels for consistent filtering
  const deviceMatchesType = useCallback((device: IntelliSpaceDevice, filterType: string): boolean => {
    const sensorProfile = (device as any).sensorProfile || (device as any).attributes?.sensorProfile || {};
    const traits = device.traits || [];
    const rawType = (device as any).type || '';
    const deviceAttributes = (device as any).attributes || {};
    const detectorEvents: string[] = deviceAttributes.detectorEvents || [];
    const detectorStates: string[] = deviceAttributes.detectorStates || [];
    
    // === CAPABILITY FLAGS ===
    const hasPowerMetrics = sensorProfile.watt !== undefined || 
                           sensorProfile.watt_hour !== undefined ||
                           sensorProfile.voltage !== undefined || 
                           sensorProfile.current !== undefined ||
                           sensorProfile.real_power !== undefined ||
                           device.watt !== undefined ||
                           device.voltage !== undefined;
    
    const hasTempHumidity = sensorProfile.temp !== undefined || 
                           sensorProfile.rh !== undefined ||
                           sensorProfile.temperature !== undefined || 
                           sensorProfile.humidity !== undefined ||
                           device.temperature !== undefined ||
                           device.humidity !== undefined;
    
    const hasLuxCapability = sensorProfile.lux !== undefined || traits.includes('Illuminance');
    
    const hasOccupancyCapability = detectorStates.includes('occupancy') || 
                                   traits.includes('Occupancy') ||
                                   sensorProfile.occupancy !== undefined || 
                                   sensorProfile.presence !== undefined;
    
    const hasMotionCapability = detectorEvents.includes('motionDetected');
    
    const hasButtonCapability = detectorEvents.includes('click') || 
                                detectorEvents.includes('doubleClick') || 
                                detectorEvents.includes('longPress');
    
    const hasDoorCapability = detectorEvents.includes('opened') || 
                              detectorEvents.includes('closed');
    
    const hasCurtainCapability = rawType === 'CURTAIN' || 
                                 device.deviceType === 'curtain';
    
    const hasBlindCapability = rawType === 'BLIND' ||
                               rawType === 'BLINDS' ||
                               rawType === 'ROLLER_SHADE' ||
                               device.deviceType === 'blind' ||
                               (traits.includes('OpenClose') && !rawType.includes('CURTAIN'));
    
    switch (filterType) {
      case 'ac':
        // AC unit / HVAC devices
        return rawType === 'AC_UNIT' || 
               device.deviceType === 'hvac' ||
               (traits.includes('TemperatureSetting') && traits.includes('FanSpeed'));
      
      case 'fan':
        // Fan devices
        return rawType === 'FAN' || device.deviceType === 'fan';
      
      case 'light':
        // Light devices
        return rawType === 'LIGHT' || rawType === 'BULB' || rawType === 'LED_STRIP' ||
               traits.includes('Brightness') || traits.includes('ColorSetting') ||
               device.deviceType === 'light';
      
      case 'switch':
        // Switch devices
        return rawType === 'SWITCH' || device.deviceType === 'switch';
      
      case 'outlet':
        // Outlet/Plug devices
        return rawType === 'OUTLET' || rawType === 'PLUG';
      
      case 'meter':
        // Power meter capability (standalone or combo, including 3-phase)
        return hasPowerMetrics || rawType === 'POWER_METER' || device.deviceType === 'power_meter' || device.deviceType === 'power_meter_3phase';
      
      case 'button':
        // Button/Scene trigger devices
        return hasButtonCapability;
      
      case 'door':
        // Door sensor devices
        return hasDoorCapability || device.deviceType === 'door_sensor';
      
      case 'motion':
        // Motion sensor devices
        return hasMotionCapability;
      
      case 'presence':
        // Presence/Occupancy sensor devices
        return hasOccupancyCapability || device.deviceType === 'presence_sensor';
      
      case 'lux':
        // Lux/Illuminance sensor devices
        return hasLuxCapability;
      
      case 'th_sensor':
        // TH Sensor (temp/humidity only, exclude HVAC)
        const isHVAC = rawType === 'AC_UNIT' || rawType === 'FAN' || 
                       traits.includes('TemperatureSetting') || traits.includes('FanSpeed') ||
                       device.deviceType === 'hvac' || device.deviceType === 'fan';
        return (hasTempHumidity || device.deviceType === 'th_sensor') && !isHVAC;
      
      case 'curtain':
        // Curtain (窗簾) devices
        return hasCurtainCapability;
      
      case 'blind':
        // Blind (捲簾) devices
        return hasBlindCapability;
      
      case 'heater':
        // Heater / Floor heating (地熱) devices - EXCLUDE ERV devices
        // ERV devices have HEATER type but are classified as 'erv' deviceType
        return (rawType === 'HEATER' || device.deviceType === 'hvac') && device.deviceType !== 'erv';
      
      case 'erv':
        // ERV (Energy Recovery Ventilator / 全熱交換機) devices
        return device.deviceType === 'erv';
      
      case 'controller':
        // Controller/Gateway devices (Matter Controller, etc.)
        return rawType === 'ULTRON_MATTERCONTROLLER' || 
               rawType === 'MATTERCONTROLLER' || 
               rawType === 'GATEWAY' || 
               rawType === 'HUB' ||
               traits.includes('MatterController') ||
               device.deviceType === 'controller';
      
      default:
        return true;
    }
  }, []);

  // Filter devices by group and sort by device type
  const filteredDevices = useMemo(() => {
    let result = devices;
    
    // Filter by zone
    if (selectedZoneFilter !== 'all') {
      const zone = deviceZones.find(z => z.id === selectedZoneFilter);
      if (zone) {
        // ✅ Fix: Check both deviceId (e.g., "UTU32A-xxx#1") and baseSN (e.g., "UTU32A-xxx")
        // zone.devices may store devices without the #N endpoint suffix
        result = result.filter(d => 
          zone.devices.includes(d.deviceId) || 
          zone.devices.includes(d.baseSN)
        );
      }
    }
    
    // Filter out devices that are in control groups (they will be shown as group cards)
    if (deviceControlGroups.length > 0) {
      const devicesInGroups = new Set(deviceControlGroups.flatMap(g => g.devices));
      result = result.filter(d => !devicesInGroups.has(d.deviceId) && !devicesInGroups.has(d.baseSN));
    }
    
    // Apply multi-endpoint device merge/split logic
    // For merged devices, only show the first endpoint (e.g., #1)
    if (mergedDeviceSNs.size > 0) {
      const seenBaseSNs = new Set<string>();
      result = result.filter(d => {
        const deviceId = d.deviceId || '';
        if (!deviceId.includes('#')) return true; // Single endpoint device, always show
        
        const baseSN = deviceId.split('#')[0];
        
        // If this baseSN is set to merged display
        if (mergedDeviceSNs.has(baseSN)) {
          // Only show the first endpoint for this baseSN
          if (seenBaseSNs.has(baseSN)) {
            return false; // Hide subsequent endpoints
          }
          seenBaseSNs.add(baseSN);
          return true; // Show first endpoint
        }
        
        return true; // Not merged, show all endpoints
      });
    }
    
    // Filter by device type (using capability-based matching)
    // Devices can appear in multiple type filters if they have multiple capabilities
    if (sortByDeviceType !== 'all') {
      result = result.filter(d => deviceMatchesType(d, sortByDeviceType));
    } else {
      // Default sort order: AC, Fan, Light, Switch, Curtain, Blind, TH Sensor, Presence Sensor, Power Meter
      const typeOrder: Record<string, number> = {
        'hvac': 1,
        'fan': 2,
        'light': 3,
        'switch': 4,
        'curtain': 5,
        'blind': 6,
        'th_sensor': 7,
        'presence_sensor': 8,
        'door_sensor': 9,
        'power_meter': 10
      };
      result = [...result].sort((a, b) => (typeOrder[a.deviceType] || 99) - (typeOrder[b.deviceType] || 99));
    }
    
    return result;
  }, [devices, selectedZoneFilter, deviceZones, sortByDeviceType, deviceMatchesType, mergedDeviceSNs, deviceControlGroups]);

  // Sort devices by zone order (from Zone Management) or custom order
  const sortedDevices = useMemo(() => {
    // Priority 1: Use zone.devices order when a specific zone is selected
    if (selectedZoneFilter !== 'all') {
      const selectedZone = deviceZones.find(z => z.id === selectedZoneFilter);
      if (selectedZone && selectedZone.devices.length > 0) {
        // Build order map from zone.devices
        const zoneOrderMap = new Map(selectedZone.devices.map((sn, index) => [sn, index]));
        return [...filteredDevices].sort((a, b) => {
          // Match by deviceId or baseSN
          const orderA = zoneOrderMap.get(a.deviceId) ?? zoneOrderMap.get(a.baseSN) ?? Infinity;
          const orderB = zoneOrderMap.get(b.deviceId) ?? zoneOrderMap.get(b.baseSN) ?? Infinity;
          return orderA - orderB;
        });
      }
    }
    
    // Priority 2: When showing all zones, use combined zone order
    if (deviceZones.length > 0) {
      // Combine all zone.devices in order (zone order first, then device order within zone)
      const allZoneDevices: string[] = [];
      deviceZones.forEach(zone => {
        zone.devices.forEach(sn => {
          if (!allZoneDevices.includes(sn)) {
            allZoneDevices.push(sn);
          }
        });
      });
      
      if (allZoneDevices.length > 0) {
        const combinedOrderMap = new Map(allZoneDevices.map((sn, index) => [sn, index]));
        return [...filteredDevices].sort((a, b) => {
          const orderA = combinedOrderMap.get(a.deviceId) ?? combinedOrderMap.get(a.baseSN) ?? Infinity;
          const orderB = combinedOrderMap.get(b.deviceId) ?? combinedOrderMap.get(b.baseSN) ?? Infinity;
          return orderA - orderB;
        });
      }
    }
    
    // Fallback: Use legacy deviceOrder if no zones defined
    if (deviceOrder.length === 0) return filteredDevices;
    
    const orderMap = new Map(deviceOrder.map((id, index) => [id, index]));
    return [...filteredDevices].sort((a, b) => {
      const orderA = orderMap.get(a.deviceId) ?? Infinity;
      const orderB = orderMap.get(b.deviceId) ?? Infinity;
      return orderA - orderB;
    });
  }, [filteredDevices, deviceOrder, selectedZoneFilter, deviceZones]);

  // Memoize sortable item IDs to prevent DnD scroll reset on state updates
  // Use ref to cache previous IDs and only update when they actually change
  const prevSortableIdsRef = useRef<string[]>([]);
  const sortableItemIds = useMemo(() => {
    const newIds = sortedDevices.map(d => d.deviceId);
    // Check if IDs have actually changed (compare by value, not reference)
    const prevIds = prevSortableIdsRef.current;
    if (newIds.length === prevIds.length && newIds.every((id, i) => id === prevIds[i])) {
      // IDs haven't changed, return cached array to maintain reference stability
      return prevIds;
    }
    // IDs changed, update cache and return new array
    prevSortableIdsRef.current = newIds;
    return newIds;
  }, [sortedDevices]);

  // DnD sensors - delay to allow click actions, then drag
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200, // Hold for 200ms before drag starts
        tolerance: 5, // Allow small movement during delay
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ============================================================
  // Device Control Group Handlers
  // ============================================================
  
  // Reload device control groups
  const reloadDeviceControlGroups = useCallback(async () => {
    try {
      const groups = await userPreferencesApi.getDeviceControlGroups();
      setDeviceControlGroups(groups);
    } catch (error) {
      logger.error('Failed to reload device control groups:', error);
    }
  }, []);

  // Handle group toggle (on/off)
  const handleGroupToggle = useCallback(async (group: userPreferencesApi.DeviceControlGroup, newState: boolean) => {
    try {
      // Send command to all devices in the group
      const deviceSNs = group.devices;
      const command = newState ? 'on' : 'off';
      
      logger.log(`🎛️ [GroupControl] Toggling group "${group.name}" to ${command}:`, deviceSNs);
      
      await ultronTuringApi.executeCommand(command, deviceSNs);
      
      // Optimistic update - update local device states
      setDevices(prev => prev.map(d => {
        if (deviceSNs.includes(d.deviceId) || deviceSNs.includes(d.baseSN)) {
          return { ...d, isOn: newState };
        }
        return d;
      }));
    } catch (error) {
      logger.error('Failed to toggle group:', error);
    }
  }, []);

  // Handle group brightness change
  const handleGroupBrightness = useCallback(async (group: userPreferencesApi.DeviceControlGroup, brightness: number) => {
    try {
      const deviceSNs = group.devices;
      
      logger.log(`🎛️ [GroupControl] Setting brightness for "${group.name}" to ${brightness}%:`, deviceSNs);
      
      await ultronTuringApi.executeCommand('setBrightness', deviceSNs, { brightness });
      
      // Optimistic update
      setDevices(prev => prev.map(d => {
        if (deviceSNs.includes(d.deviceId) || deviceSNs.includes(d.baseSN)) {
          return { ...d, brightness, isOn: brightness > 0 };
        }
        return d;
      }));
    } catch (error) {
      logger.error('Failed to set group brightness:', error);
    }
  }, []);

  // Handle group color temperature change
  const handleGroupColorTemp = useCallback(async (group: userPreferencesApi.DeviceControlGroup, colorTemp: number) => {
    try {
      const deviceSNs = group.devices;
      
      logger.log(`🎛️ [GroupControl] Setting color temp for "${group.name}" to ${colorTemp}K:`, deviceSNs);
      
      await ultronTuringApi.executeCommand('setColorTemperature', deviceSNs, { temperature: colorTemp });
      
      // Optimistic update
      setDevices(prev => prev.map(d => {
        if (deviceSNs.includes(d.deviceId) || deviceSNs.includes(d.baseSN)) {
          return { ...d, colorTemp };
        }
        return d;
      }));
    } catch (error) {
      logger.error('Failed to set group color temp:', error);
    }
  }, []);

  // Handle group HVAC temperature change
  const handleGroupTemperature = useCallback(async (group: userPreferencesApi.DeviceControlGroup, temperature: number) => {
    try {
      const deviceSNs = group.devices;
      
      logger.log(`🎛️ [GroupControl] Setting HVAC temp for "${group.name}" to ${temperature}°C:`, deviceSNs);
      
      await ultronTuringApi.executeCommand('setTemperature', deviceSNs, { temperature });
      
      // Optimistic update
      setDevices(prev => prev.map(d => {
        if (deviceSNs.includes(d.deviceId) || deviceSNs.includes(d.baseSN)) {
          return { ...d, setTemperature: temperature };
        }
        return d;
      }));
    } catch (error) {
      logger.error('Failed to set group temperature:', error);
    }
  }, []);

  // Handle group open percent change (curtain/blind)
  const handleGroupOpenPercent = useCallback(async (group: userPreferencesApi.DeviceControlGroup, openPercent: number) => {
    try {
      const deviceSNs = group.devices;
      
      logger.log(`🎛️ [GroupControl] Setting open percent for "${group.name}" to ${openPercent}%:`, deviceSNs);
      
      await ultronTuringApi.executeCommand('open', deviceSNs, { openPercent });
      
      // Optimistic update
      setDevices(prev => prev.map(d => {
        if (deviceSNs.includes(d.deviceId) || deviceSNs.includes(d.baseSN)) {
          return { ...d, openPercent, isOn: openPercent > 0 };
        }
        return d;
      }));
    } catch (error) {
      logger.error('Failed to set group open percent:', error);
    }
  }, []);

  // Handle group fan speed change
  const handleGroupFanSpeed = useCallback(async (group: userPreferencesApi.DeviceControlGroup, fanSpeed: string) => {
    try {
      const deviceSNs = group.devices;
      
      logger.log(`🎛️ [GroupControl] Setting fan speed for "${group.name}" to ${fanSpeed}:`, deviceSNs);
      
      await ultronTuringApi.executeCommand('setFanSpeed', deviceSNs, { fanSpeed });
      
      // Optimistic update
      setDevices(prev => prev.map(d => {
        if (deviceSNs.includes(d.deviceId) || deviceSNs.includes(d.baseSN)) {
          return { ...d, fanSpeed };
        }
        return d;
      }));
    } catch (error) {
      logger.error('Failed to set group fan speed:', error);
    }
  }, []);

  // Handle drag end - unified handling for both groups and devices
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const activeId = active.id as string;
      const overId = over.id as string;
      
      // When a specific zone is selected, update that zone's order (supports mixed groups and devices)
      if (selectedZoneFilter !== 'all') {
        const selectedZone = deviceZones.find(z => z.id === selectedZoneFilter);
        if (selectedZone) {
          // Find indices in zone.devices - supports both group:xxx and deviceSN formats
          const findItemIndex = (itemId: string) => {
            // First try direct match
            let idx = selectedZone.devices.indexOf(itemId);
            if (idx !== -1) return idx;
            
            // For device IDs, also try matching by baseSN
            if (!itemId.startsWith('group:')) {
              const device = devices.find(d => d.deviceId === itemId);
              if (device?.baseSN) {
                idx = selectedZone.devices.indexOf(device.baseSN);
              }
            }
            return idx;
          };
          
          let oldIndex = findItemIndex(activeId);
          let newIndex = findItemIndex(overId);
          
          // If item not found in zone.devices, we need to add it first
          let currentDevices = [...selectedZone.devices];
          
          // Ensure both items are in the list
          if (oldIndex === -1) {
            currentDevices.push(activeId);
            oldIndex = currentDevices.length - 1;
          }
          if (newIndex === -1) {
            currentDevices.push(overId);
            newIndex = currentDevices.indexOf(overId);
          }
          
          // Re-find indices after potential additions
          oldIndex = currentDevices.indexOf(activeId);
          if (oldIndex === -1 && !activeId.startsWith('group:')) {
            const device = devices.find(d => d.deviceId === activeId);
            if (device?.baseSN) oldIndex = currentDevices.indexOf(device.baseSN);
          }
          newIndex = currentDevices.indexOf(overId);
          if (newIndex === -1 && !overId.startsWith('group:')) {
            const device = devices.find(d => d.deviceId === overId);
            if (device?.baseSN) newIndex = currentDevices.indexOf(device.baseSN);
          }
          
          if (oldIndex !== -1 && newIndex !== -1) {
            const newDevicesOrder = arrayMove(currentDevices, oldIndex, newIndex);
            
            // Update local state
            const updatedZones = deviceZones.map(z => 
              z.id === selectedZone.id ? { ...z, devices: newDevicesOrder } : z
            );
            setDeviceZones(updatedZones);
            
            // Dispatch event for real-time sync with Zone Management
            window.dispatchEvent(new CustomEvent('zones-order-changed', { detail: { zones: updatedZones } }));
            
            // Save to Firestore
            userPreferencesApi.saveVirtualZones('intelliSpace', updatedZones)
              .catch(err => logger.error('Failed to save zone device order:', err));
          }
        }
      } else {
        // When showing all zones - only allow reordering within same zone for devices
        // Skip group dragging in "all zones" mode
        if (activeId.startsWith('group:') || overId.startsWith('group:')) {
          return;
        }
        
        // Find which zone contains the active device
        const findDeviceZone = (deviceId: string) => {
          const device = devices.find(d => d.deviceId === deviceId);
          const snToMatch = device?.deviceId || device?.baseSN || deviceId;
          return deviceZones.find(zone => 
            zone.devices.some(sn => sn === snToMatch || sn === device?.baseSN || deviceId.startsWith(sn))
          );
        };
        
        const targetZone = findDeviceZone(activeId);
        if (targetZone) {
          const activeDevice = devices.find(d => d.deviceId === activeId);
          const overDevice = devices.find(d => d.deviceId === overId);
          
          // Check if both devices are in the same zone
          const overZone = findDeviceZone(overId);
          if (overZone && targetZone.id === overZone.id) {
            const findZoneIndex = (device: typeof activeDevice) => {
              if (!device) return -1;
              let idx = targetZone.devices.indexOf(device.deviceId);
              if (idx === -1) idx = targetZone.devices.indexOf(device.baseSN);
              return idx;
            };
            
            const oldIndex = findZoneIndex(activeDevice);
            const newIndex = findZoneIndex(overDevice);
            
            if (oldIndex !== -1 && newIndex !== -1) {
              const newDevicesOrder = arrayMove(targetZone.devices, oldIndex, newIndex);
              
              // Update local state
              const updatedZones = deviceZones.map(z => 
                z.id === targetZone.id ? { ...z, devices: newDevicesOrder } : z
              );
              setDeviceZones(updatedZones);
              
              // Dispatch event for real-time sync with Zone Management
              window.dispatchEvent(new CustomEvent('zones-order-changed', { detail: { zones: updatedZones } }));
              
              // Save to Firestore
              userPreferencesApi.saveVirtualZones('intelliSpace', updatedZones)
                .catch(err => logger.error('Failed to save zone device order:', err));
            }
          }
        }
      }
    }
  }, [sortedDevices, devices, deviceZones, selectedZoneFilter, deviceControlGroups]);

  // Navigation handlers
  const handleNavigate = (view: string) => {
    if (['overview', 'control', 'device-manager', 'automation', 'alerts', 'device-logs', 'generate-report'].includes(view)) {
      setCurrentView(view as ViewType);
      if (view === 'device-manager') {
        setShowDeviceManager(true);
      } else {
        setShowDeviceManager(false);
      }
      // Auto-select pinned zone when navigating to control page, reset device type to all
      if (view === 'control') {
        if (pinnedZone) {
          setSelectedZoneFilter(pinnedZone);
        }
        setSortByDeviceType('all');
      }
    } else if (view === 'devices') {
      // Legacy support for 'devices' view
      setShowDeviceManager(true);
      setCurrentView('device-manager');
    } else if (view === 'settings') {
      onSettingsClick();
    } else {
      setShowDeviceManager(false);
    }
  };

  // Handle pin zone toggle
  const handlePinZone = (zoneId: string) => {
    if (pinnedZone === zoneId) {
      // Unpin
      setPinnedZone(null);
      localStorage.removeItem('intellispace-pinned-zone');
    } else {
      // Pin this zone
      setPinnedZone(zoneId);
      localStorage.setItem('intellispace-pinned-zone', zoneId);
    }
  };

  // Get device icon by type - using unified DeviceIcon system
  // For presence sensors, isOn represents occupancy state (true = occupied, false = unoccupied)
  // Optional device parameter allows context-aware icon selection (e.g., humidity-only vs temp+humidity)
  const getDeviceIcon = (type: IntelliSpaceDevice['deviceType'], isOn?: boolean, size?: 'small' | 'large', device?: IntelliSpaceDevice) => {
    const pixelSize = size === 'large' ? 40 : 20;
    const sizeClass = size === 'large' ? 'w-10 h-10' : 'w-5 h-5';
    
    // Special handling for th_sensor (humidity-only vs temp+humidity)
    if (type === 'th_sensor') {
      const hasTemp = device?.temperature !== undefined;
      const hasHumidity = device?.humidity !== undefined;
      const isHumidityOnly = hasHumidity && !hasTemp;
      
      if (isHumidityOnly) {
        return <Style02_WaterDrop size={pixelSize} />;
      }
      return <Style01_Thermometer size={pixelSize} />;
    }
    
    // Special handling for door_sensor (uses Lucide icons)
    if (type === 'door_sensor') {
      return isOn 
        ? <DoorOpen className={`${sizeClass} text-red-500`} />
        : <DoorClosed className={`${sizeClass} text-green-500`} />;
    }
    
    // Special handling for fan (uses Lucide icon with animation)
    if (type === 'fan') {
      return <Fan className={`${sizeClass} ${isOn ? 'text-cyan-500 fan-spin-medium' : 'text-slate-400'}`} />;
    }
    
    // Use unified DeviceIcon for all other types
    return <DeviceIcon deviceType={type} isOn={isOn} size={pixelSize} animated={true} />;
  };

  // Get state description for device cards (simplified - only returns OFF/ON for most types)
  // Uses trait-based logic for unknown device types to enable auto-support for new devices
  const getStateDescription = (device: IntelliSpaceDevice): string => {
    const isOn = device.isOn || device.switchOn || device.power;
    
    switch (device.deviceType) {
      case 'light':
        if (!isOn) return 'OFF';
        const brightness = device.brightness !== undefined ? `${device.brightness}%` : '';
        const colorTemp = device.colorTemp !== undefined ? `${device.colorTemp}K` : '';
        if (brightness && colorTemp) return `${brightness} ${colorTemp}`;
        return brightness || 'ON';
      case 'hvac':
        return isOn ? 'ON' : 'OFF';
      case 'fan':
        return 'OFF'; // Chinese state handled separately in UI
      case 'switch':
        return isOn ? 'ON' : 'OFF';
      case 'outlet':
        return isOn ? 'ON' : 'OFF';
      case 'curtain':
        if (device.openPercent !== undefined) {
          if (device.openPercent === 0) return '全關';
          if (device.openPercent === 100) return '全開';
          return `${device.openPercent}%`;
        }
        return device.isOpen ? '開啟' : '關閉';
      case 'blind':
        if (device.openPercent !== undefined) {
          if (device.openPercent === 0) return '全關';
          if (device.openPercent === 100) return '全開';
          return `${device.openPercent}%`;
        }
        return device.isOpen ? '開啟' : '關閉';
      case 'button':
        // Buttons don't have ON/OFF state - they are event-based triggers
        return '按鈕';
      case 'controller':
        // Controller/Gateway devices - just show they are running
        return '運行中';
      case 'bridge':
        // Bridge devices - just show they are running
        return '運行中';
      case 'presence_sensor':
      case 'motion_sensor':
        // Presence/motion sensors show occupancy state, not ON/OFF
        if (device.occupancy !== undefined) {
          return device.occupancy ? '有人' : '無人';
        }
        if (device.presence !== undefined) {
          return device.presence ? '有人' : '無人';
        }
        return '無人';
      case 'multi_sensor':
        // Multi-sensor: prioritize occupancy state if available
        if (device.occupancy !== undefined) {
          return device.occupancy ? '有人' : '無人';
        }
        if (device.presence !== undefined) {
          return device.presence ? '有人' : '無人';
        }
        // Fallback to temperature if no presence data
        if (device.temperature !== undefined) {
          return `${device.temperature}°`;
        }
        return '感測器';
      case 'th_sensor':
        // TH sensor: show temperature (primary) 
        if (device.temperature !== undefined) {
          return `${device.temperature}°`;
        }
        if (device.humidity !== undefined) {
          return `${device.humidity}%`;
        }
        return '感測器';
      case 'lux_sensor':
        // Lux sensor: show lux value
        if (device.lux !== undefined) {
          return `${device.lux} lux`;
        }
        return '感測器';
      case 'door_sensor':
        // Door sensor: show open/close state
        return device.isOpen ? '打開' : '關閉';
      case 'power_meter_3phase':
      case 'power_meter':
        // Power meter: show power value
        if (device.watt !== undefined) {
          const wattValue = typeof device.watt === 'number' ? device.watt : Number(device.watt);
          return wattValue >= 1000 ? `${(wattValue/1000).toFixed(1)} kW` : `${wattValue.toFixed(0)} W`;
        }
        return '電錶';
      default:
        // Use trait-based logic for unknown device types
        // This enables auto-support for new device types with correct traits
        const traits = device.traits || getDefaultTraitsForDeviceType(device.deviceType);
        if (traits.length > 0) {
          return getStateDescriptionByTraits(device, traits, device.deviceType);
        }
        return 'OFF';
    }
  };

  // Get state text color based on device type and state
  // Uses trait-based logic for unknown device types to enable auto-support for new devices
  const getStateTextColor = (device: IntelliSpaceDevice): string => {
    const isOn = device.isOn || device.switchOn || device.power;
    
    switch (device.deviceType) {
      case 'fan':
        return isOn ? 'text-cyan-500' : 'text-slate-400';
      case 'light':
        return isOn ? 'text-orange-500' : 'text-slate-400';
      case 'hvac':
      case 'switch':
      case 'outlet':
        return isOn ? 'text-white' : 'text-slate-400';
      case 'curtain':
        const curtainIsOpen = device.openPercent !== undefined ? device.openPercent > 0 : device.isOpen;
        return curtainIsOpen ? 'text-teal-500' : 'text-slate-400';
      case 'blind':
        const blindIsOpen = device.openPercent !== undefined ? device.openPercent > 0 : device.isOpen;
        return blindIsOpen ? 'text-cyan-500' : 'text-slate-400';
      case 'dehumidifier':
        return isOn ? 'text-blue-500' : 'text-slate-400';
      case 'button':
        // Buttons are always shown as "active" when online (they don't have ON/OFF state)
        return 'text-blue-500';
      case 'controller':
        // Controllers/Gateways are always shown as "active" when online
        return 'text-purple-500';
      case 'bridge':
        // Bridge devices are always shown as "active" when online
        return 'text-purple-500';
      default:
        // Use trait-based logic for unknown device types
        // This enables auto-support for new device types with correct traits
        const traits = device.traits || getDefaultTraitsForDeviceType(device.deviceType);
        if (traits.length > 0) {
          return getStateTextColorByTraits(device, traits, device.deviceType);
        }
        return 'text-slate-400';
    }
  };

  // Get trait icon - returns icon element for controllable traits
  const getTraitIcon = (trait: string, isActive?: boolean) => {
    const baseClass = "w-4 h-4";
    const activeColor = isActive ? "text-green-500" : "text-slate-400";
    
    switch (trait) {
      case 'OnOff':
        return <Power className={`${baseClass} ${activeColor}`} />;
      case 'TemperatureSetting':
        return <Thermometer className={`${baseClass} text-orange-400`} />;
      case 'FanSpeed':
        return <Wind className={`${baseClass} text-cyan-400`} />;
      case 'Brightness':
        return <Sun className={`${baseClass} text-yellow-400`} />;
      case 'ColorSetting':
        return <Palette className={`${baseClass} text-purple-400`} />;
      case 'Volume':
        return <Volume2 className={`${baseClass} text-blue-400`} />;
      case 'Timer':
      case 'StartStop':
        return <Timer className={`${baseClass} text-indigo-400`} />;
      case 'LockUnlock':
        return isActive ? <Lock className={`${baseClass} text-red-400`} /> : <Unlock className={`${baseClass} text-green-400`} />;
      case 'Occupancy':
      case 'OccupancySensing':
        return <Eye className={`${baseClass} text-purple-400`} />;
      case 'EnergyStorage':
        return <Gauge className={`${baseClass} text-green-400`} />;
      case 'Modes':
      case 'ThermostatMode':
        return <Settings2 className={`${baseClass} text-slate-400`} />;
      case 'TemperatureControl':
        return <Thermometer className={`${baseClass} text-red-400`} />;
      case 'HumiditySetting':
        return <Droplets className={`${baseClass} text-blue-400`} />;
      default:
        return null;
    }
  };

  // Controllable traits (traits that can be manipulated by user)
  const controllableTraits = [
    'OnOff', 'TemperatureSetting', 'FanSpeed', 'Brightness', 
    'ColorSetting', 'Volume', 'Timer', 'StartStop', 'LockUnlock',
    'Modes', 'ThermostatMode', 'TemperatureControl', 'HumiditySetting'
  ];

  // Trait control state
  const [activeTraitControl, setActiveTraitControl] = useState<{
    deviceId: string;
    trait: string;
    device: IntelliSpaceDevice;
    position?: { x: number; y: number };
  } | null>(null);
  
  // Slider temporary value state (for live display while dragging)
  const [sliderValue, setSliderValue] = useState<{ brightness?: number; colorTemp?: number }>({});

  /**
   * Schedule a status verification query for a device after command execution.
   * This is the "Trust but Verify" strategy - we do optimistic updates immediately,
   * but verify the actual device state after timeout to catch failed commands.
   * 
   * @param deviceId - The device ID to verify
   * @param commandId - The command ID that triggered this verification
   * @param timeoutMs - Time to wait before verification (default: 30s for slow devices)
   */
  const scheduleStatusVerification = useCallback((
    deviceId: string, 
    commandId: number, 
    timeoutMs: number = 30000
  ) => {
    // Cancel any existing verification timer for this device
    const existingTimer = pendingVerificationTimers.current.get(deviceId);
    if (existingTimer) {
      clearTimeout(existingTimer);
      pendingVerificationTimers.current.delete(deviceId);
    }
    
    logger.log(`⏰ [IntelliSpace] Scheduling status verification for ${deviceId} in ${timeoutMs/1000}s (cmd #${commandId})`);
    
    const timer = setTimeout(async () => {
      // Check if this command is still pending (hasn't been superseded or confirmed)
      const pending = pendingCommandUpdates.current.get(deviceId);
      
      // Only verify if:
      // 1. There's still a pending command
      // 2. It's the same command ID (not superseded by a newer command)
      if (!pending) {
        logger.log(`✅ [IntelliSpace] Verification skipped for ${deviceId} - no pending command (already confirmed)`);
        pendingVerificationTimers.current.delete(deviceId);
        return;
      }
      
      if (pending.commandId !== commandId) {
        logger.log(`⏭️ [IntelliSpace] Verification skipped for ${deviceId} - command superseded (pending: #${pending.commandId}, this: #${commandId})`);
        pendingVerificationTimers.current.delete(deviceId);
        return;
      }
      
      logger.log(`🔍 [IntelliSpace] Verifying status for ${deviceId} (cmd #${commandId})...`);
      
      try {
        // Query the actual device status
        const statusResponse = await ultronTuringApi.queryStatus([deviceId], { detailed: true });
        
        if (statusResponse?.data && Array.isArray(statusResponse.data)) {
          const latestStatus = statusResponse.data[0];
          if (latestStatus?.state) {
            const state = latestStatus.state;
            
            // Update device with verified status
            setDevices(prev => prev.map(d => {
              if (d.deviceId !== deviceId) return d;
              
              const updates: Partial<IntelliSpaceDevice> = {};
              
              // Update all relevant state fields from the verified status
              if (state.on !== undefined) {
                updates.isOn = state.on;
                updates.power = state.on;
                updates.switchOn = state.on;
              }
              if (state.openPercent !== undefined) {
                updates.openPercent = state.openPercent;
                updates.isOpen = state.openPercent > 0;
              }
              if (state.brightness !== undefined) {
                updates.brightness = state.brightness;
              }
              if (state.color?.temperatureK !== undefined) {
                updates.colorTemp = state.color.temperatureK;
              }
              if (state.thermostatTemperatureSetpoint !== undefined) {
                updates.setTemperature = state.thermostatTemperatureSetpoint;
              }
              if (state.thermostatMode !== undefined) {
                updates.mode = state.thermostatMode;
              }
              if (state.currentModeSettings?.FanSpeed !== undefined) {
                updates.fanSpeed = state.currentModeSettings.FanSpeed;
              }
              
              logger.log(`✅ [IntelliSpace] Verified status for ${deviceId}:`, updates);
              return { ...d, ...updates };
            }));
          }
        }
      } catch (error) {
        logger.error(`❌ [IntelliSpace] Status verification failed for ${deviceId}:`, error);
      } finally {
        // Clear the pending state and timer
        pendingCommandUpdates.current.delete(deviceId);
        pendingVerificationTimers.current.delete(deviceId);
      }
    }, timeoutMs);
    
    pendingVerificationTimers.current.set(deviceId, timer);
  }, []);
  
  // Update ref when function is defined
  scheduleStatusVerificationRef.current = scheduleStatusVerification;

  // Handle trait control action
  const handleTraitControl = async (device: IntelliSpaceDevice, trait: string, action: string, params?: Record<string, any>) => {
    setIsExecutingCommand(true);
    executingDeviceIdRef.current = device.deviceId;

    // Register expected state lock to prevent stale data race conditions (e.g. Polling returning old state)
    // This covers ALL controllable traits to prevent the "flicker" issue
    const expectedState: { 
      on?: boolean; 
      fanSpeed?: string; 
      brightness?: number; 
      thermostatTemperatureSetpoint?: number; 
      thermostatMode?: string;
      openPercent?: number;
      colorTemp?: number;
      humiditySetpointPercent?: number;
      currentModeSettings?: Record<string, string>;
      currentToggleSettings?: Record<string, boolean>;
    } = {};
    
    // OnOff trait
    if (trait === 'OnOff') {
      if (action === 'on') expectedState.on = true;
      if (action === 'off') expectedState.on = false;
    }
    // FanSpeed via Modes trait (HVAC)
    if (trait === 'Modes' && params?.updateModeSettings?.FanSpeed !== undefined) {
      expectedState.fanSpeed = String(params.updateModeSettings.FanSpeed);
    }
    // ThermostatMode via Modes trait (HVAC)
    if ((trait === 'Modes' || trait === 'ThermostatMode') && (params?.mode !== undefined || params?.thermostatMode !== undefined)) {
      expectedState.thermostatMode = String(params.thermostatMode ?? params.mode);
    }
    // CustomModes settings (for dehumidifier and other devices)
    if ((trait === 'Modes' || trait === 'CustomModes') && params?.updateModeSettings) {
      expectedState.currentModeSettings = { ...params.updateModeSettings };
    }
    // CustomToggles settings (for dehumidifier: AutoDirection, AirClean, Lock)
    if ((trait === 'Toggles' || trait === 'CustomToggles') && params?.updateToggleSettings) {
      expectedState.currentToggleSettings = { ...params.updateToggleSettings };
    }
    // Direct FanSpeed trait - convert to numeric format for WebSocket comparison
    if (trait === 'FanSpeed' && params?.fanSpeed !== undefined) {
      const nameToNumeric: Record<string, string> = {
        'auto': '0', 'low': '1', 'medium': '2', 'med': '2', 'high': '3'
      };
      const speedValue = String(params.fanSpeed).toLowerCase();
      expectedState.fanSpeed = nameToNumeric[speedValue] || speedValue;
    }
    // Brightness control for lights
    if (trait === 'Brightness' && params?.brightness !== undefined) {
      expectedState.brightness = Number(params.brightness);
    }
    // ColorSetting (colorTemp) for lights
    if (trait === 'ColorSetting' && params?.colorTemp !== undefined) {
      expectedState.colorTemp = Number(params.colorTemp);
    }
    // TemperatureSetting for HVAC - support both parameter names
    const tempParam = params?.thermostatTemperatureSetpoint ?? params?.temperature;
    if ((trait === 'TemperatureSetting' || trait === 'TemperatureControl') && tempParam !== undefined) {
      expectedState.thermostatTemperatureSetpoint = Number(tempParam);
    }
    // HumiditySetting for dehumidifiers
    if (trait === 'HumiditySetting' && params?.humiditySetpointPercent !== undefined) {
      expectedState.humiditySetpointPercent = Number(params.humiditySetpointPercent);
    }
    // OpenClose for curtains/blinds
    if (trait === 'OpenClose' && params?.openPercent !== undefined) {
      expectedState.openPercent = Number(params.openPercent);
    }
    
    // Track command ID for verification timer (needs to be accessible after API call)
    let currentCommandId: number | null = null;
    
    if (Object.keys(expectedState).length > 0) {
      // Increment command ID counter - this ensures each command has a unique ID
      commandIdCounter.current += 1;
      currentCommandId = commandIdCounter.current;
      
      // Set up pending command state with hybrid strategy fields
      // This completely replaces any previous pending state for this device
      pendingCommandUpdates.current.set(device.deviceId, {
        expectedState,
        timestamp: Date.now(),
        commandId: currentCommandId
      });
      
      logger.log(`🔒 [IntelliSpace] Set pending command #${currentCommandId} for ${device.deviceId}:`, expectedState);
    }

    try {
      // Use full deviceId (with #endpoint suffix) for command execution
      const deviceId = device.deviceId;
      
      let command = action;
      let commandParams: Record<string, any> = {};
      
      // Check if device is a FAN type or ERV (using deviceType, NOT SN prefix)
      const isFanDevice = device.deviceType === 'fan' ||
                          device.deviceType === 'erv' ||
                          device.attributes?.type === 'FAN' ||
                          device.attributes?.type === 'ERV';
      
      // Map trait actions to API commands
      switch (trait) {
        case 'OnOff':
          // Check if device should use simple 'on'/'off' command
          // Switch, outlet, FAN, and dehumidifier all use simplified commands
          const isSimpleOnOff = device.deviceType === 'switch' || 
                                device.deviceType === 'outlet' || 
                                device.deviceType === 'dehumidifier' ||
                                isFanDevice;
          
          if (isSimpleOnOff) {
            // These devices use simple 'on'/'off' command without params
            command = action; // 'on' or 'off'
            commandParams = {};
          } else {
            // AC and other devices use 'OnOff' command with on param
            command = 'OnOff';
            commandParams = { on: action === 'on' };
          }
          break;
        case 'TemperatureSetting':
        case 'TemperatureControl':
          command = 'ThermostatTemperatureSetpoint';
          // Support both parameter names: temperature (legacy) or thermostatTemperatureSetpoint (MCP standard)
          const tempValue = params?.thermostatTemperatureSetpoint ?? params?.temperature;
          commandParams = { thermostatTemperatureSetpoint: tempValue };
          break;
        case 'ThermostatMode':
          command = 'ThermostatSetMode';
          // Support both thermostatMode and mode parameter names
          commandParams = { thermostatMode: params?.thermostatMode ?? params?.mode };
          break;
        case 'Modes':
          // Generic SetModes command - pass updateModeSettings directly
          command = 'SetModes';
          commandParams = params?.updateModeSettings 
            ? { updateModeSettings: params.updateModeSettings }
            : { thermostatMode: params?.thermostatMode ?? params?.mode }; // Fallback for thermostat mode
          break;
        case 'FanSpeed':
          command = 'SetFanSpeed';
          commandParams = { fanSpeed: params?.fanSpeed?.toLowerCase() };
          break;
        case 'Brightness':
          command = 'setBrightness';
          commandParams = { brightness: params?.brightness };
          break;
        case 'ColorSetting':
          command = 'ColorAbsolute';
          // MCP API expects: { color: { temperature: value } }
          commandParams = { color: { temperature: params?.colorTemp ?? params?.color?.temperature } };
          break;
        case 'Toggles':
          command = 'SetToggles';
          commandParams = { updateToggleSettings: { [params?.toggle]: params?.on } };
          break;
        case 'HumiditySetting':
          command = 'SetHumidity';
          commandParams = { humiditySetpointPercent: params?.humiditySetpointPercent };
          break;
        case 'OpenClose':
          command = 'OpenClose';
          commandParams = { openPercent: params?.openPercent };
          break;
        case 'CustomModes':
          // Handle CustomModes trait (used by dehumidifier for Mode, Timer, FanSpeed)
          command = 'SetModes';
          commandParams = params?.updateModeSettings 
            ? { updateModeSettings: params.updateModeSettings }
            : {};
          break;
        case 'CustomToggles':
          // Handle CustomToggles trait (used by dehumidifier for AutoDirection, AirClean, Lock)
          command = 'SetToggles';
          commandParams = params?.updateToggleSettings 
            ? { updateToggleSettings: params.updateToggleSettings }
            : {};
          break;
      }
      
      logger.log(`🎮 [IntelliSpace] Executing ${trait} control: ${command}`, { 
        deviceId, 
        deviceType: device.deviceType,
        commandParams
      });
      
      // ✅ Calculate updates first for optimistic update
      const getUpdates = (): Partial<IntelliSpaceDevice> => {
        const updates: Partial<IntelliSpaceDevice> = {};
        switch (trait) {
          case 'OnOff':
            const newOnState = action === 'on';
            updates.isOn = newOnState;
            updates.power = newOnState;
            updates.switchOn = newOnState;
            break;
          case 'TemperatureSetting':
          case 'TemperatureControl':
            // Support both parameter names - update BOTH setTemperature and thermostatTemperatureSetpoint
            const tempUpdate = params?.thermostatTemperatureSetpoint ?? params?.temperature;
            if (tempUpdate) {
              updates.setTemperature = tempUpdate;
              updates.thermostatTemperatureSetpoint = tempUpdate;
            }
            break;
          case 'ThermostatMode':
          case 'Modes':
            // Support both mode and thermostatMode parameter names
            const modeValue = params?.thermostatMode ?? params?.mode;
            if (modeValue) {
              updates.mode = modeValue;
              updates.thermostatMode = modeValue;
            }
            // Handle FanSpeed via updateModeSettings
            if (params?.updateModeSettings?.FanSpeed !== undefined) {
              updates.fanSpeed = params.updateModeSettings.FanSpeed;
            }
            break;
          case 'FanSpeed':
            if (params?.fanSpeed) {
              // Convert speed name to numeric format for consistency with WebSocket updates
              // API stores: '0'=auto, '1'=low, '2'=medium, '3'=high
              const nameToNumeric: Record<string, string> = {
                'auto': '0', 'low': '1', 'medium': '2', 'med': '2', 'high': '3'
              };
              const speedValue = params.fanSpeed.toLowerCase();
              updates.fanSpeed = nameToNumeric[speedValue] || speedValue;
            }
            break;
          case 'Brightness':
            if (params?.brightness !== undefined) {
              updates.brightness = params.brightness;
              updates.isOn = params.brightness > 0;
              updates.power = params.brightness > 0;
            }
            break;
          case 'ColorSetting':
            if (params?.colorTemp !== undefined) {
              updates.colorTemp = params.colorTemp;
            }
            break;
          case 'OpenClose':
            if (params?.openPercent !== undefined) {
              updates.openPercent = params.openPercent;
              updates.isOpen = params.openPercent > 0;
            }
            break;
          case 'HumiditySetting':
            if (params?.humiditySetpointPercent !== undefined) {
              updates.humiditySetpointPercent = params.humiditySetpointPercent;
            }
            break;
          case 'CustomModes':
            if (params?.updateModeSettings) {
              updates.currentModeSettings = {
                ...device.currentModeSettings,
                ...params.updateModeSettings
              };
              // Also update fanSpeed if FanSpeed is being set
              if (params.updateModeSettings.FanSpeed !== undefined) {
                updates.fanSpeed = params.updateModeSettings.FanSpeed;
              }
            }
            break;
          case 'CustomToggles':
            if (params?.updateToggleSettings) {
              updates.currentToggleSettings = {
                ...device.currentToggleSettings,
                ...params.updateToggleSettings
              };
            }
            break;
        }
        return updates;
      };
      
      const updates = getUpdates();
      
      // ✅ OPTIMISTIC UPDATE: Update UI immediately BEFORE API call
      // Save previous state for rollback if API fails
      const previousDevice = { ...device };
      
      // ✅ FIX: Only match the EXACT deviceId being controlled
      // Each endpoint should be updated independently
      // Use the deviceId from the command (which includes #endpoint)
      const targetDeviceId = deviceId; // deviceId already includes endpoint (e.g., "baseSN#FAN")
      
      setDevices(prev => prev.map(d => {
        if (d.deviceId !== targetDeviceId) return d;
        logger.log(`🔄 [IntelliSpace] Optimistic update for ${d.deviceId}:`, updates);
        return { ...d, ...updates };
      }));
      
      // ✅ Also update modal snapshot if it's the same device (for user-initiated changes)
      setModalDeviceSnapshot(prev => {
        if (!prev || prev.deviceId !== targetDeviceId) return prev;
        return { ...prev, ...updates };
      });
      
      // Execute API call
      const result = await ultronTuringApi.executeCommand(command, [deviceId], commandParams);
      
      if (result?.error || result?.success === false) {
        // ✅ API failed - rollback to previous state
        logger.error(`❌ [IntelliSpace] Command failed, rolling back:`, result);
        setDevices(prev => prev.map(d => {
          if (d.deviceId !== targetDeviceId) return d;
          return previousDevice;
        }));
        // Also rollback modal snapshot
        setModalDeviceSnapshot(prev => {
          if (!prev || prev.deviceId !== targetDeviceId) return prev;
          return previousDevice;
        });
        // Clear pending state on failure
        pendingCommandUpdates.current.delete(device.deviceId);
        // Cancel any pending verification timer
        const existingTimer = pendingVerificationTimers.current.get(device.deviceId);
        if (existingTimer) {
          clearTimeout(existingTimer);
          pendingVerificationTimers.current.delete(device.deviceId);
        }
      } else {
        logger.log(`✅ [IntelliSpace] Command result:`, result);
        
        // Schedule status verification for slow devices (Trust but Verify strategy)
        // This ensures we detect failed commands that the API didn't report as errors
        if (currentCommandId !== null) {
          // Determine verification timeout based on device type/trait
          let verificationTimeout = 8000; // Default: 8 seconds
          
          if (trait === 'OpenClose') {
            // Curtains/blinds have slow physical movement - verify after 30 seconds
            verificationTimeout = 30000;
          } else if (trait === 'TemperatureSetting' || trait === 'TemperatureControl') {
            // HVAC temperature changes take time - verify after 15 seconds
            verificationTimeout = 15000;
          } else if (trait === 'ThermostatMode' || trait === 'Modes') {
            // HVAC mode changes - verify after 10 seconds
            verificationTimeout = 10000;
          }
          
          scheduleStatusVerification(device.deviceId, currentCommandId, verificationTimeout);
        }
      }
      
      // Close modal if open
      setActiveTraitControl(null);
      setSliderValue({});
      
      // Clear executing state after a short delay
      setTimeout(() => {
        setIsExecutingCommand(false);
        executingDeviceIdRef.current = null;
      }, 500);
      
      // For Light devices, query latest status after a short delay to get updated brightness/colorTemp
      // NOTE: Only update brightness/colorTemp, NOT isOn - to prevent overwriting optimistic update
      if (device.deviceType === 'light' && trait === 'OnOff') {
        setTimeout(async () => {
          try {
            // Skip if there's still a pending command for this device
            const pending = pendingCommandUpdates.current.get(device.deviceId);
            if (pending) {
              logger.log(`⏸️ [IntelliSpace] Skipping queryStatus for ${device.deviceId} - pending command exists`);
              return;
            }
            
            logger.log(`🔄 [IntelliSpace] Querying latest status for light ${device.deviceId}`);
            const statusResponse = await ultronTuringApi.queryStatus([device.deviceId], { detailed: true });
            if (statusResponse?.data && Array.isArray(statusResponse.data)) {
              const latestStatus = statusResponse.data[0];
              if (latestStatus?.state) {
                const state = latestStatus.state;
                setDevices(prev => prev.map(d => {
                  if (d.deviceId !== device.deviceId && d.baseSN !== device.baseSN) return d;
                  // Only update brightness and colorTemp, preserve isOn from optimistic update
                  return {
                    ...d,
                    // Only update isOn if it matches our optimistic state (to avoid flicker)
                    ...(d.isOn === state.on ? { isOn: state.on, power: state.on } : {}),
                    brightness: state.brightness ?? d.brightness,
                    colorTemp: state.color?.temperatureK ?? d.colorTemp
                  };
                }));
                logger.log(`✅ [IntelliSpace] Updated light status:`, {
                  on: state.on,
                  brightness: state.brightness,
                  colorTemp: state.color?.temperatureK
                });
              }
            }
          } catch (error) {
            logger.warn('⚠️ [IntelliSpace] Failed to query light status after command:', error);
          }
        }, 1500); // Increased delay to allow WebSocket updates to settle
      }
      
    } catch (error) {
      logger.error('❌ [IntelliSpace] Failed to execute trait control:', error);
      // Clean up pending command state on exception
      pendingCommandUpdates.current.delete(device.deviceId);
      // Cancel any pending verification timer
      const existingTimer = pendingVerificationTimers.current.get(device.deviceId);
      if (existingTimer) {
        clearTimeout(existingTimer);
        pendingVerificationTimers.current.delete(device.deviceId);
      }
      // Clear command execution state
      setIsExecutingCommand(false);
      executingDeviceIdRef.current = null;
    }
  };

  // Save favorite openPercent for curtain/blind devices
  // Uses device.deviceId as key (consistent with maxCurrent and other device settings)
  const saveFavoriteOpenPercent = async (deviceId: string, favoriteValue: number) => {
    try {
      logger.log(`⭐ [IntelliSpace] Saving favorite openPercent for ${deviceId}: ${favoriteValue}%`);
      
      // Update local state immediately
      setDeviceFavorites(prev => ({ ...prev, [deviceId]: favoriteValue }));
      
      // Save to Firestore
      const prefs = await userPreferencesApi.getUserPreferences();
      const updatedPrefs = {
        ...prefs,
        deviceSettings: {
          ...(prefs.deviceSettings || {}),
          [deviceId]: {
            ...(prefs.deviceSettings?.[deviceId] || {}),
            favoriteOpenPercent: favoriteValue
          }
        }
      };
      await userPreferencesApi.saveUserPreferences(updatedPrefs);
      logger.log(`✅ [IntelliSpace] Saved favorite openPercent for ${deviceId}: ${favoriteValue}%`);
    } catch (error) {
      logger.error(`❌ [IntelliSpace] Failed to save favorite openPercent:`, error);
    }
  };

  // Render trait control popover
  const renderTraitControlPopover = () => {
    if (!activeTraitControl) return null;
    
    const { trait, position, deviceId } = activeTraitControl;
    // ✅ Fix: Always get the latest device state from the devices array
    // This ensures the modal shows the updated state after executing commands
    const device = devices.find(d => d.deviceId === deviceId) || activeTraitControl.device;
    
    // Calculate position - appear directly above the clicked element
    const modalHeight = 220; // Approximate modal height
    const modalWidth = 280;
    const modalStyle: React.CSSProperties = position ? {
      position: 'fixed',
      left: Math.max(10, Math.min(position.x - modalWidth / 2, window.innerWidth - modalWidth - 10)),
      // If there's enough space above, show above; otherwise show below
      top: position.y > modalHeight + 50 
        ? position.y - modalHeight - 10  // Above the element
        : position.y + 40,  // Below the element
      zIndex: 50
    } : {};
    
    return (
      <div 
        className="fixed inset-0 z-40 bg-black/30"
        onClick={() => { setActiveTraitControl(null); setSliderValue({}); }}
      >
        <div 
          style={modalStyle}
          className="bg-[#0C2340] rounded-xl shadow-xl p-4 min-w-[280px] max-w-sm"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {getTraitIcon(trait, device.isOn || device.switchOn)}
              <span className="font-medium text-white">
                {getTraitLabel(trait)}
              </span>
            </div>
            <button 
              onClick={() => { setActiveTraitControl(null); setSliderValue({}); }}
              className="text-slate-400 hover:text-slate-300 hover:text-slate-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <p className="text-sm text-slate-400 mb-4 truncate">
            {device.deviceName}
          </p>
          
          {/* OnOff Control */}
          {trait === 'OnOff' && (
            <div className="flex gap-2">
              <button
                onClick={() => handleTraitControl(device, trait, 'on')}
                disabled={isExecutingCommand}
                className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                  device.isOn || device.switchOn
                    ? 'bg-green-500 text-white'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-green-100 hover:bg-green-900/30'
                }`}
              >
                {isExecutingCommand ? '...' : 'ON'}
              </button>
              <button
                onClick={() => handleTraitControl(device, trait, 'off')}
                disabled={isExecutingCommand}
                className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                  !(device.isOn || device.switchOn)
                    ? 'bg-slate-600 text-white'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {isExecutingCommand ? '...' : 'OFF'}
              </button>
            </div>
          )}
          
          {/* Temperature Control */}
          {(trait === 'TemperatureSetting' || trait === 'TemperatureControl') && (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => handleTraitControl(device, trait, 'setTemp', { 
                    temperature: Math.max(16, (device.setTemperature || 24) - 1) 
                  })}
                  disabled={isExecutingCommand}
                  className="w-12 h-12 rounded-full bg-blue-900/30 text-blue-400 text-xl font-bold hover:bg-blue-800/50 transition-colors"
                >
                  -
                </button>
                <div className="text-center">
                  <span className="text-3xl font-bold text-white">
                    {device.setTemperature || 24}
                  </span>
                  <span className="text-lg text-slate-400">°C</span>
                </div>
                <button
                  onClick={() => handleTraitControl(device, trait, 'setTemp', { 
                    temperature: Math.min(30, (device.setTemperature || 24) + 1) 
                  })}
                  disabled={isExecutingCommand}
                  className="w-12 h-12 rounded-full bg-red-900/30 text-red-400 text-xl font-bold hover:bg-red-800/50 transition-colors"
                >
                  +
                </button>
              </div>
              <div className="flex gap-1">
                {[18, 20, 22, 24, 26, 28].map(temp => (
                  <button
                    key={temp}
                    onClick={() => handleTraitControl(device, trait, 'setTemp', { temperature: temp })}
                    disabled={isExecutingCommand}
                    className={`flex-1 py-2 text-sm rounded transition-colors ${
                      device.setTemperature === temp
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-orange-100 hover:bg-orange-900/30'
                    }`}
                  >
                    {temp}°
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Mode Control */}
          {(trait === 'ThermostatMode' || trait === 'Modes') && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                {['cool', 'heat', 'fan-only', 'dry'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => handleTraitControl(device, trait, 'setMode', { mode })}
                    disabled={isExecutingCommand}
                    className={`py-3 px-3 rounded-lg text-sm font-medium transition-colors ${
                      device.mode === mode
                        ? 'bg-cyan-500 text-white'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-cyan-100 hover:bg-cyan-900/30'
                    }`}
                  >
                    {mode === 'cool' ? '❄️ 冷氣' : 
                     mode === 'heat' ? '🔥 暖氣' : 
                     mode === 'fan-only' ? '💨 送風' : 
                     '💧 除濕'}
                  </button>
                ))}
              </div>
              <button
                onClick={() => handleTraitControl(device, trait, 'setMode', { mode: 'auto' })}
                disabled={isExecutingCommand}
                className={`w-full py-3 px-3 rounded-lg text-sm font-medium transition-colors ${
                  device.mode === 'auto'
                    ? 'bg-cyan-500 text-white'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-cyan-100 hover:bg-cyan-900/30'
                }`}
              >
                🔄 自動
              </button>
            </div>
          )}
          
          {/* Fan Speed Control */}
          {trait === 'FanSpeed' && (
            <div className="grid grid-cols-4 gap-2">
              {['low', 'medium', 'high', 'auto'].map(speed => (
                <button
                  key={speed}
                  onClick={() => handleTraitControl(device, trait, 'setFanSpeed', { fanSpeed: speed })}
                  disabled={isExecutingCommand}
                  className={`py-3 rounded-lg text-sm font-medium transition-colors ${
                    device.fanSpeed?.toLowerCase() === speed
                      ? 'bg-cyan-500 text-white'
                      : 'bg-slate-800/50 text-slate-300 hover:bg-cyan-100 hover:bg-cyan-900/30'
                  }`}
                >
                  {speed === 'low' ? '弱' : 
                   speed === 'medium' ? '中' : 
                   speed === 'high' ? '強' : 
                   '自動'}
                </button>
              ))}
            </div>
          )}
          
          {/* Brightness Control */}
          {trait === 'Brightness' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">亮度</span>
                <span className="text-lg font-bold text-yellow-500">
                  {sliderValue.brightness ?? device.brightness ?? 50}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={sliderValue.brightness ?? device.brightness ?? 50}
                onChange={(e) => setSliderValue(prev => ({ ...prev, brightness: parseInt(e.target.value) }))}
                onMouseUp={(e) => {
                  const brightness = parseInt((e.target as HTMLInputElement).value);
                  handleTraitControl(device, trait, 'setBrightness', { brightness });
                  setSliderValue(prev => ({ ...prev, brightness: undefined }));
                }}
                onTouchEnd={(e) => {
                  const brightness = parseInt((e.target as HTMLInputElement).value);
                  handleTraitControl(device, trait, 'setBrightness', { brightness });
                  setSliderValue(prev => ({ ...prev, brightness: undefined }));
                }}
                disabled={isExecutingCommand}
                className="w-full h-4 rounded-full cursor-pointer appearance-none bg-gradient-to-r from-gray-300 via-yellow-300 to-yellow-500 border border-slate-600 shadow-inner [&::-webkit-slider-runnable-track]:h-4 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:mt-[-4px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-yellow-500 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-yellow-500 [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          )}
          
          {/* ColorSetting (Color Temperature) Control */}
          {trait === 'ColorSetting' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">色溫</span>
                <span className="text-lg font-bold text-blue-400">
                  {sliderValue.colorTemp ?? device.colorTemp ?? 4000}K
                </span>
              </div>
              <input
                type="range"
                min={device.colorTempMin ?? 2700}
                max={device.colorTempMax ?? 6500}
                value={sliderValue.colorTemp ?? device.colorTemp ?? 4000}
                onChange={(e) => setSliderValue(prev => ({ ...prev, colorTemp: parseInt(e.target.value) }))}
                onMouseUp={(e) => {
                  const colorTemp = parseInt((e.target as HTMLInputElement).value);
                  handleTraitControl(device, 'ColorSetting', 'setColorTemp', { colorTemp });
                  setSliderValue(prev => ({ ...prev, colorTemp: undefined }));
                }}
                onTouchEnd={(e) => {
                  const colorTemp = parseInt((e.target as HTMLInputElement).value);
                  handleTraitControl(device, 'ColorSetting', 'setColorTemp', { colorTemp });
                  setSliderValue(prev => ({ ...prev, colorTemp: undefined }));
                }}
                disabled={isExecutingCommand}
                className="w-full h-4 rounded-full cursor-pointer appearance-none bg-gradient-to-r from-orange-400 via-yellow-200 to-blue-400 border border-slate-600 shadow-inner [&::-webkit-slider-runnable-track]:h-4 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:mt-[-4px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-blue-500 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-blue-500 [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span className="text-orange-500">暖色 {device.colorTempMin ?? 2700}K</span>
                <span className="text-blue-400">冷色 {device.colorTempMax ?? 6500}K</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Get short label for trait
  const getTraitLabel = (trait: string): string => {
    const labels: Record<string, string> = {
      'OnOff': 'ON/OFF',
      'TemperatureSetting': '溫控',
      'FanSpeed': '風速',
      'Brightness': '亮度',
      'ColorSetting': '色溫',
      'Volume': '音量',
      'Timer': '定時',
      'StartStop': '啟停',
      'LockUnlock': '鎖定',
      'Modes': '模式',
      'ThermostatMode': '模式',
      'TemperatureControl': '溫控',
      'HumiditySetting': '濕度'
    };
    return labels[trait] || trait;
  };

  // Render trait badges for a device
  const renderTraitBadges = (traits: string[] | undefined, device: IntelliSpaceDevice) => {
    // TH Sensor, Presence Sensor, Lux Sensor, and Multi-Sensor are passive sensors - no control traits
    if (device.deviceType === 'th_sensor' || device.deviceType === 'presence_sensor' || device.deviceType === 'lux_sensor' || device.deviceType === 'multi_sensor') {
      return null;
    }
    
    // Power Meter is also read-only (including 3-phase)
    if (device.deviceType === 'power_meter' || device.deviceType === 'power_meter_3phase') {
      return null;
    }
    
    let deviceTraits = traits ? [...traits] : [];
    
    // For HVAC devices, ensure common control traits are available
    if (device.deviceType === 'hvac') {
      // Add standard HVAC traits if not present
      const hvacTraits = ['OnOff', 'FanSpeed', 'ThermostatMode'];
      // Check if it's an AC (not just fan) - AC has temperature control (using deviceType/traits, NOT SN prefix)
      const isAC = device.attributes?.type === 'AC_UNIT' ||
                   device.setTemperature !== undefined ||
                   (device.traits || []).includes('TemperatureSetting');
      if (isAC) {
        hvacTraits.push('TemperatureSetting');
      }
      hvacTraits.forEach(t => {
        if (!deviceTraits.includes(t)) {
          deviceTraits.push(t);
        }
      });
    }
    
    // For switch/outlet devices, ensure OnOff trait
    if ((device.deviceType === 'switch' || device.deviceType === 'outlet') && !deviceTraits.includes('OnOff')) {
      deviceTraits.push('OnOff');
    }
    
    // For light devices, ensure OnOff, Brightness, and ColorSetting traits
    if (device.deviceType === 'light') {
      if (!deviceTraits.includes('OnOff')) {
        deviceTraits.push('OnOff');
      }
      if (!deviceTraits.includes('Brightness')) {
        deviceTraits.push('Brightness');
      }
      // Add ColorSetting if device supports color temperature
      if ((device.colorTempMin || device.colorTempMax) && !deviceTraits.includes('ColorSetting')) {
        deviceTraits.push('ColorSetting');
      }
    }
    
    // Filter to show only controllable traits
    const deviceControllableTraits = deviceTraits.filter(t => controllableTraits.includes(t));
    if (deviceControllableTraits.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {deviceControllableTraits.slice(0, 5).map(trait => {
          const icon = getTraitIcon(trait, device.isOn || device.power || device.switchOn);
          if (!icon) return null;
          
          // For OnOff trait, toggle directly without modal
          if (trait === 'OnOff') {
            // Check isOn, power (for lights), or switchOn (for switches)
            const isOn = device.isOn || device.power || device.switchOn;
            const isThisDeviceExecuting = executingDeviceIdRef.current === device.deviceId;
            return (
              <button
                key={trait}
                onClick={(e) => {
                  e.stopPropagation();
                  // Toggle: if ON -> turn OFF, if OFF -> turn ON
                  handleTraitControl(device, trait, isOn ? 'off' : 'on');
                }}
                disabled={isThisDeviceExecuting}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs cursor-pointer transition-all ${
                  isThisDeviceExecuting
                    ? 'bg-blue-900/30 text-blue-400'
                    : isOn 
                      ? 'bg-green-900/30 text-green-400 hover:bg-green-800/50' 
                      : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'
                }`}
                title={isOn ? '點擊關閉' : '點擊開啟'}
              >
                {isThisDeviceExecuting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  icon
                )}
                <span>{isThisDeviceExecuting ? '...' : (isOn ? 'ON' : 'OFF')}</span>
              </button>
            );
          }
          
          return (
            <button
              key={trait}
              onClick={(e) => {
                e.stopPropagation();
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setActiveTraitControl({ 
                  deviceId: device.deviceId, 
                  trait, 
                  device,
                  position: { x: rect.left + rect.width / 2, y: rect.top }
                });
              }}
              className="flex items-center gap-1 px-2 py-1 bg-slate-800/50 rounded-md text-xs cursor-pointer hover:bg-blue-100 hover:bg-blue-900/30 hover:ring-1 hover:ring-blue-400 transition-all"
              title={`控制 ${trait}`}
            >
              {icon}
              <span className="text-slate-300">
                {getTraitLabel(trait)}
              </span>
            </button>
          );
        })}
        {deviceControllableTraits.length > 4 && (
          <div className="flex items-center px-2 py-1 bg-slate-800/50 rounded-md text-xs text-slate-400">
            +{deviceControllableTraits.length - 4}
          </div>
        )}
      </div>
    );
  };

  // Handle switch toggle (ON/OFF for selected switches)
  const handleSwitchToggle = useCallback(async () => {
    // Use 'devices' instead of 'filteredDevices' to ignore view filters
    const allSwitches = devices.filter(d => d.deviceType === 'switch');
    const switchesToControl = selectedSwitches.length > 0
      ? allSwitches.filter(d => selectedSwitches.includes(d.deviceId))
      : allSwitches;
    
    logger.log('🔌 [Switch Toggle]', {
      selectedSwitches,
      allSwitchesCount: allSwitches.length,
      toControl: switchesToControl.map(s => ({ id: s.deviceId, on: s.switchOn }))
    });
    
    if (switchesToControl.length === 0) return;
    
    // Determine current state: if all selected switches are ON, turn them all OFF; otherwise turn them all ON
    const anyOn = switchesToControl.some(s => s.switchOn);
    const command = anyOn ? 'off' : 'on';
    const newOnState = command === 'on';
    
    // Set up pending locks for all devices
    const deviceIds = switchesToControl.map(s => s.deviceId);
    commandIdCounter.current += 1;
    const newCommandId = commandIdCounter.current;
    deviceIds.forEach(deviceId => {
      pendingCommandUpdates.current.set(deviceId, {
        expectedState: { on: newOnState },
        timestamp: Date.now(),
        commandId: newCommandId
      });
    });
    
    // Optimistic update
    setDevices(prev => prev.map(d => {
      if (!deviceIds.includes(d.deviceId)) return d;
      return { ...d, switchOn: newOnState, isOn: newOnState, power: newOnState };
    }));
    
    setIsSwitchToggling(true);
    try {
      logger.log('🔌 [Switch Toggle] Executing:', { command, deviceIds });
      await ultronTuringApi.executeCommand(command, deviceIds);
      // WebSocket will update the state - no need to call loadDevices
    } catch (error) {
      logger.error('Failed to toggle switches:', error);
      // Clean up pending locks on error
      deviceIds.forEach(deviceId => {
        pendingCommandUpdates.current.delete(deviceId);
        const timer = pendingVerificationTimers.current.get(deviceId);
        if (timer) {
          clearTimeout(timer);
          pendingVerificationTimers.current.delete(deviceId);
        }
      });
      // Rollback on error
      setDevices(prev => prev.map(d => {
        if (!deviceIds.includes(d.deviceId)) return d;
        return { ...d, switchOn: !newOnState, isOn: !newOnState, power: !newOnState };
      }));
    } finally {
      setIsSwitchToggling(false);
    }
  }, [selectedSwitches, devices]);

  // Handle HVAC toggle (ON/OFF for selected HVAC units only)
  const handleHvacToggle = useCallback(async () => {
    // Only control selected HVACs
    if (selectedHvac.length === 0) return;
    const hvacToControl = devices.filter(d => d.deviceType === 'hvac' && selectedHvac.includes(d.deviceId));
    if (hvacToControl.length === 0) return;
    
    // Determine current state: if all selected HVAC are ON, turn them all OFF; otherwise turn them all ON
    const anyOn = hvacToControl.some(h => h.isOn);
    const command = anyOn ? 'off' : 'on';
    const newOnState = command === 'on';
    
    // Use full deviceId (including #endpoint) for control
    const deviceIds = hvacToControl.map(h => h.deviceId);
    
    // Set up pending locks for all devices
    commandIdCounter.current += 1;
    const newCommandId = commandIdCounter.current;
    deviceIds.forEach(deviceId => {
      pendingCommandUpdates.current.set(deviceId, {
        expectedState: { on: newOnState },
        timestamp: Date.now(),
        commandId: newCommandId
      });
    });
    
    // Optimistic update
    setDevices(prev => prev.map(d => {
      if (!deviceIds.includes(d.deviceId)) return d;
      return { ...d, isOn: newOnState, power: newOnState };
    }));
    
    setIsHvacToggling(true);
    try {
      logger.log('❄️ HVAC toggle - devices:', deviceIds, 'command:', command);
      await ultronTuringApi.executeCommand(command, deviceIds);
      // WebSocket will update the state - no need to call loadDevices
    } catch (error) {
      logger.error('Failed to toggle HVAC:', error);
      // Clean up pending locks on error
      deviceIds.forEach(deviceId => {
        pendingCommandUpdates.current.delete(deviceId);
        const timer = pendingVerificationTimers.current.get(deviceId);
        if (timer) {
          clearTimeout(timer);
          pendingVerificationTimers.current.delete(deviceId);
        }
      });
      // Rollback on error
      setDevices(prev => prev.map(d => {
        if (!deviceIds.includes(d.deviceId)) return d;
        return { ...d, isOn: !newOnState, power: !newOnState };
      }));
    } finally {
      setIsHvacToggling(false);
    }
  }, [selectedHvac, devices]);

  // Handle Light toggle (ON/OFF for selected lights only)
  const handleLightToggle = useCallback(async () => {
    // Only control selected lights
    if (selectedLights.length === 0) return;
    const lightsToControl = devices.filter(d => d.deviceType === 'light' && selectedLights.includes(d.deviceId));
    if (lightsToControl.length === 0) return;
    
    // Determine current state: if all selected lights are ON, turn them all OFF; otherwise turn them all ON
    const anyOn = lightsToControl.some(l => l.isOn || l.power);
    const command = anyOn ? 'off' : 'on';
    const newOnState = command === 'on';
    
    const deviceIds = lightsToControl.map(l => l.deviceId);
    
    // Set up pending locks for all devices
    commandIdCounter.current += 1;
    const newCommandId = commandIdCounter.current;
    deviceIds.forEach(deviceId => {
      pendingCommandUpdates.current.set(deviceId, {
        expectedState: { on: newOnState },
        timestamp: Date.now(),
        commandId: newCommandId
      });
    });
    
    // Optimistic update
    setDevices(prev => prev.map(d => {
      if (!deviceIds.includes(d.deviceId)) return d;
      return { ...d, isOn: newOnState, power: newOnState };
    }));
    
    setIsLightToggling(true);
    try {
      logger.log('💡 [Light Toggle] Executing:', { command, deviceIds });
      await ultronTuringApi.executeCommand(command, deviceIds);
      // WebSocket will update the state - no need to call loadDevices
    } catch (error) {
      logger.error('Failed to toggle lights:', error);
      // Clean up pending locks on error
      deviceIds.forEach(deviceId => {
        pendingCommandUpdates.current.delete(deviceId);
        const timer = pendingVerificationTimers.current.get(deviceId);
        if (timer) {
          clearTimeout(timer);
          pendingVerificationTimers.current.delete(deviceId);
        }
      });
      // Rollback on error
      setDevices(prev => prev.map(d => {
        if (!deviceIds.includes(d.deviceId)) return d;
        return { ...d, isOn: !newOnState, power: !newOnState };
      }));
    } finally {
      setIsLightToggling(false);
    }
  }, [selectedLights, devices]);

  // Handle Light brightness change
  const handleLightBrightness = useCallback(async (deviceId: string, brightness: number) => {
    try {
      logger.log(`💡 [Light Brightness] Setting ${deviceId} to ${brightness}%`);
      await ultronTuringApi.executeCommand('setBrightness', [deviceId], { brightness });
      // Update local state immediately for responsive UI
      setDevices(prev => prev.map(d => 
        d.deviceId === deviceId ? { ...d, brightness, isOn: brightness > 0, power: brightness > 0 } : d
      ));
    } catch (error) {
      logger.error('Failed to set brightness:', error);
    }
  }, []);

  // Handle Light color temperature change
  const handleLightColorTemp = useCallback(async (deviceId: string, colorTemp: number) => {
    try {
      logger.log(`💡 [Light ColorTemp] Setting ${deviceId} to ${colorTemp}K`);
      await ultronTuringApi.executeCommand('ColorAbsolute', [deviceId], { colorTemp });
      // Update local state immediately for responsive UI
      setDevices(prev => prev.map(d => 
        d.deviceId === deviceId ? { ...d, colorTemp } : d
      ));
    } catch (error) {
      logger.error('Failed to set color temperature:', error);
    }
  }, []);

  // Handle single light toggle
  // Save device name to Firestore, localStorage, and sync to cloud
  // For merged multi-endpoint devices, updates global deviceName
  // For single-endpoint devices, updates tenant-local nickname
  const handleSaveNickname = useCallback(async (deviceId: string, newName: string, isMergedDevice: boolean = false) => {
    try {
      const baseSN = deviceId.split('#')[0];
      const targetSN = isMergedDevice ? baseSN : deviceId;
      
      logger.log(`📝 [IntelliSpace] Saving ${isMergedDevice ? 'deviceName' : 'nickname'} for ${targetSN}: ${newName}`);
      
      // Track pending nickname update to prevent stale data from overwriting during loadDevices
      // This ensures the new nickname persists even if the next loadDevices gets old data from API
      pendingNicknameUpdates.current.set(deviceId, {
        nickname: newName,
        timestamp: Date.now()
      });
      // For merged devices, also track by baseSN
      if (isMergedDevice && baseSN !== deviceId) {
        pendingNicknameUpdates.current.set(baseSN, {
          nickname: newName,
          timestamp: Date.now()
        });
      }
      // Clear tracking after 60 seconds (sufficient time for cloud sync to complete)
      setTimeout(() => {
        pendingNicknameUpdates.current.delete(deviceId);
        if (isMergedDevice && baseSN !== deviceId) {
          pendingNicknameUpdates.current.delete(baseSN);
        }
        logger.log(`🧹 [IntelliSpace] Cleared pending nickname tracking for ${deviceId}`);
      }, 60000);
      
      // Update local state - update both deviceName and nickname for immediate UI feedback
      if (isMergedDevice) {
        // For merged devices, update all endpoints with the same baseSN
        setDevices(prev => prev.map(d => {
          const dBaseSN = d.deviceId.split('#')[0];
          if (dBaseSN === baseSN) {
            return { ...d, deviceName: newName, nickname: newName, systemDeviceName: newName };
          }
          return d;
        }));
      } else {
        setDevices(prev => prev.map(d => 
          d.deviceId === deviceId ? { ...d, deviceName: newName, nickname: newName } : d
        ));
      }
      
      // Also update modalDeviceSnapshot to reflect the new name in the open modal
      setModalDeviceSnapshot(prev => {
        if (!prev) return prev;
        const prevBaseSN = prev.deviceId.split('#')[0];
        if (isMergedDevice && prevBaseSN === baseSN) {
          return { ...prev, deviceName: newName, nickname: newName };
        } else if (!isMergedDevice && prev.deviceId === deviceId) {
          return { ...prev, deviceName: newName, nickname: newName };
        }
        return prev;
      });
      
      // Dispatch event to notify other components (DeviceManagerSidebar, etc.)
      window.dispatchEvent(new CustomEvent('device-name-updated', {
        detail: { sn: targetSN, name: newName }
      }));
      
      // Update imported devices in Firestore
      const importedDevices = await userPreferencesApi.getImportedDevices('intelliSpace');
      const updatedDevices = importedDevices.map((d: any) => {
        const sn = d.sn || d.deviceId;
        // For merged devices, match by baseSN; for single devices, match exact deviceId only
        if (isMergedDevice) {
          const dBaseSN = sn.split('#')[0];
          if (dBaseSN === baseSN) {
            return { ...d, nickname: newName, displayName: newName };
          }
        } else {
          if (sn === deviceId) {
            return { ...d, nickname: newName, displayName: newName };
          }
        }
        return d;
      });
      await userPreferencesApi.saveImportedDevices('intelliSpace', updatedDevices);
      
      // Also update localStorage
      const localKey = 'imported-devices-intelliSpace';
      localStorage.setItem(localKey, JSON.stringify(updatedDevices));
      
      // Update via MCP ConfigureDevice API
      // For merged devices: use deviceName (global, affects all apps)
      // For non-merged devices: use nickname (tenant-local)
      try {
        if (isMergedDevice) {
          await ultronTuringApi.configureDevice(targetSN, { deviceName: newName });
          logger.log(`✅ [IntelliSpace] DeviceName synced via MCP: ${targetSN} -> ${newName}`);
        } else {
          await ultronTuringApi.configureDevice(targetSN, { nickname: newName });
          logger.log(`✅ [IntelliSpace] Nickname synced via MCP: ${targetSN} -> ${newName}`);
        }
      } catch (mcpError) {
        logger.warn('⚠️ [IntelliSpace] MCP ConfigureDevice failed (may not be supported):', mcpError);
      }
      
      setEditingNickname(false);
      logger.log(`✅ [IntelliSpace] Device name saved successfully`);
    } catch (error) {
      logger.error('❌ [IntelliSpace] Failed to save device name:', error);
    }
  }, []);

  // Handle single light toggle
  const handleSingleLightToggle = useCallback(async (deviceId: string, currentState: boolean) => {
    const command = currentState ? 'off' : 'on';
    try {
      logger.log(`💡 [Light Toggle] ${deviceId} -> ${command}`);
      await ultronTuringApi.executeCommand(command, [deviceId]);
      // Update local state immediately
      setDevices(prev => prev.map(d => 
        d.deviceId === deviceId ? { ...d, isOn: !currentState, power: !currentState } : d
      ));
    } catch (error) {
      logger.error('Failed to toggle light:', error);
    }
  }, []);


  // Overview Zone Selector Modal
  const openOverviewZoneSelector = () => {
    setTempSelectedOverviewZones([...selectedOverviewZones]);
    setShowOverviewZoneSelector(true);
  };

  const renderOverviewZoneSelectorModal = () => {
    if (!showOverviewZoneSelector) return null;

    const toggleZone = (zoneId: string) => {
      setTempSelectedOverviewZones(prev =>
        prev.includes(zoneId) ? prev.filter(id => id !== zoneId) : [...prev, zoneId]
      );
    };

    const selectAll = () => {
      setTempSelectedOverviewZones(deviceZones.map(z => z.id));
    };

    const clearAll = () => {
      setTempSelectedOverviewZones([]);
    };

    const handleSave = () => {
      setSelectedOverviewZonesByTab(prev => {
        const next = { ...prev, [overviewViewMode]: tempSelectedOverviewZones };
        try {
          localStorage.setItem(OVERVIEW_ZONE_SELECTION_KEY, JSON.stringify(next));
        } catch {}
        return next;
      });
      setShowOverviewZoneSelector(false);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowOverviewZoneSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-cyan-500" />
              {'選擇區域'}
            </h3>
            <button onClick={() => setShowOverviewZoneSelector(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 border-b border-slate-700/30 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              {t.intelliSpace?.selectAll || '全選'}
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              {t.intelliSpace?.clearSelection || '清除選擇'}
            </button>
          </div>

          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {deviceZones.length === 0 ? (
              <p className="text-center text-slate-400 py-4">目前沒有可用的區域</p>
            ) : (
              deviceZones.map(zone => (
                <div
                  key={zone.id}
                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                    tempSelectedOverviewZones.includes(zone.id)
                      ? 'bg-cyan-900/20 border border-cyan-700/50'
                      : 'bg-slate-800/50 hover:bg-slate-700/50'
                  }`}
                  onClick={() => toggleZone(zone.id)}
                >
                  <div className="flex items-center gap-3">
                    {tempSelectedOverviewZones.includes(zone.id) ? (
                      <CheckSquare className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                    ) : (
                      <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    )}
                    <span className="text-white">{zone.name}</span>
                  </div>
                  <span className="text-xs text-slate-500">{zone.devices?.length || 0} devices</span>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-slate-700/30 flex justify-end">
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-medium"
            >
              {'套用'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Power Meter Selector Modal
  const openPowerMeterSelector = () => {
    setTempSelectedPowerMeters([...selectedPowerMeters]);
    setShowPowerMeterSelector(true);
  };

  const renderPowerMeterSelectorModal = () => {
    if (!showPowerMeterSelector) return null;

    // ✅ FIX: Use same filter as HVAC/Lighting meter selector
    // Filter devices that have power measurement capability (from sensorProfile or actual data)
    const powerMeters = devices.filter(d => {
      const sensorProfile = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
      const hasPowerCapability = sensorProfile.watt || sensorProfile.real_power || sensorProfile.watt_hour;
      const hasPowerData = d.watt !== undefined || d.wattHour !== undefined;
      return hasPowerCapability || hasPowerData;
    });

    const togglePowerMeter = (deviceId: string) => {
      setTempSelectedPowerMeters(prev =>
        prev.includes(deviceId)
          ? prev.filter(id => id !== deviceId)
          : [...prev, deviceId]
      );
    };

    const selectAll = () => {
      setTempSelectedPowerMeters(powerMeters.map(d => d.deviceId));
    };

    const clearAll = () => {
      setTempSelectedPowerMeters([]);
    };

    const handleSave = () => {
      setSelectedPowerMeters(tempSelectedPowerMeters);
      setShowPowerMeterSelector(false);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPowerMeterSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              {t.intelliSpace?.selectPowerMeters || '選擇電錶'}
            </h3>
            <button onClick={() => setShowPowerMeterSelector(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 border-b border-slate-700/30 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              {t.intelliSpace?.selectAll || '全選'}
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              {t.intelliSpace?.clearSelection || '清除選擇'}
            </button>
          </div>

          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {powerMeters.length === 0 ? (
              <p className="text-center text-slate-400 py-4">{t.intelliSpace?.noPowerMeters || '沒有電錶'}</p>
            ) : (
              powerMeters.map(device => (
                <div
                  key={device.deviceId}
                  onClick={() => togglePowerMeter(device.deviceId)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    tempSelectedPowerMeters.includes(device.deviceId)
                      ? 'border-yellow-500 bg-yellow-900/20'
                      : 'border-slate-700/50 hover:bg-slate-700/50'
                  }`}
                >
                  {tempSelectedPowerMeters.includes(device.deviceId) ? (
                    <CheckSquare className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{device.deviceName}</p>
                    <p className="text-xs text-slate-400">
                      {typeof device.watt === 'number' ? `${device.watt.toFixed(0)} W` : '0 W'}
                    </p>
                  </div>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${device.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempSelectedPowerMeters.length > 0
                ? `${tempSelectedPowerMeters.length}/${powerMeters.length} ${t.common?.selected || 'selected'}`
                : t.intelliSpace?.allMetersIncluded || '包含所有電錶'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  // HVAC Meter Selector Modal
  // ✅ Track if HVAC modal selection has been initialized to prevent WebSocket updates from resetting it
  const hvacMeterSelectorInitializedRef = useRef(false);
  const prevShowHvacMeterSelectorRef = useRef(false);
  
  // ✅ Use useEffect to normalize selection ONLY when modal FIRST opens (not on every devices update)
  // ✅ IMPORTANT: Only initialize when modal transitions from closed to open, not on devices array changes
  useEffect(() => {
    const wasOpen = prevShowHvacMeterSelectorRef.current;
    const isOpen = showHvacMeterSelector;
    
    // Modal just opened: initialize selection
    if (isOpen && !wasOpen && devices.length > 0 && !hvacMeterSelectorInitializedRef.current) {
      // ✅ Normalize selectedHvacMeters to match device.deviceId format (WITH endpoint #)
      // ✅ IMPORTANT: Keep endpoints separate - UTU500-xxx#1 and UTU500-xxx#2 are different devices
      const powerMeters = devices.filter(d => {
        // Support both formats: normalized sensorProfile (new) or attributes.sensorProfile (old)
        const sensorProfile = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
        const hasPowerCapability = sensorProfile.watt || sensorProfile.real_power || sensorProfile.watt_hour;
        const hasPowerData = d.watt !== undefined || d.wattHour !== undefined;
        return hasPowerCapability || hasPowerData;
      });
      
      const normalizedSelected = powerMeters
        .filter(d => {
          // ✅ Match by exact deviceId (with endpoint) OR by baseSN if selectedId doesn't have endpoint
          return selectedHvacMeters.some(selectedId => {
            // Exact match (both have endpoint or both don't)
            if (d.deviceId === selectedId) return true;
            
            // If selectedId has no endpoint, match by baseSN
            const selectedBaseSN = selectedId.split('#')[0];
            const deviceBaseSN = d.baseSN || d.deviceId.split('#')[0];
            if (!selectedId.includes('#') && deviceBaseSN === selectedBaseSN) {
              // If device has endpoint, use the first endpoint (#1)
              return d.deviceId.includes('#') ? d.deviceId === `${deviceBaseSN}#1` : d.deviceId === deviceBaseSN;
            }
            
            return false;
          });
        })
        .map(d => d.deviceId); // ✅ Keep full deviceId with endpoint
      
      setTempSelectedHvacMeters(normalizedSelected);
      hvacMeterSelectorInitializedRef.current = true;
    } else if (!isOpen && wasOpen) {
      // Modal just closed: reset initialization flag
      hvacMeterSelectorInitializedRef.current = false;
    }
    
    prevShowHvacMeterSelectorRef.current = isOpen;
  }, [showHvacMeterSelector, devices, selectedHvacMeters]);
  
  const openHvacMeterSelector = () => {
    setShowHvacMeterSelector(true);
    // Note: Normalization will happen in useEffect when modal opens
  };

  const renderHvacMeterSelectorModal = () => {
    if (!showHvacMeterSelector) return null;
    
    // Filter devices that have power measurement capability (from sensorProfile or actual data)
    const powerMeters = devices.filter(d => {
      // Check if device has power metrics in attributes
      // Support both formats: normalized sensorProfile (new) or attributes.sensorProfile (old)
      const sensorProfile = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
      const hasPowerCapability = sensorProfile.watt || sensorProfile.real_power || sensorProfile.watt_hour;
      // Or check if device has actual power data
      const hasPowerData = d.watt !== undefined || d.wattHour !== undefined;
      return hasPowerCapability || hasPowerData;
    });
    
    // ✅ Helper function to check if device is selected (WITH endpoint separation)
    // ✅ IMPORTANT: UTU500-xxx#1 and UTU500-xxx#2 are treated as DIFFERENT devices
    const isDeviceSelected = (device: IntelliSpaceDevice): boolean => {
      return tempSelectedHvacMeters.some(selectedId => {
        // ✅ Exact match only - endpoints are separate
        return device.deviceId === selectedId;
      });
    };
    
    const toggleMeter = (deviceId: string) => {
      setTempSelectedHvacMeters(prev => {
        // ✅ Exact match only - endpoints are separate
        const isSelected = prev.includes(deviceId);
        return isSelected 
          ? prev.filter(id => id !== deviceId)
          : [...prev, deviceId];
      });
    };
    
    const selectAll = () => {
      setTempSelectedHvacMeters(powerMeters.map(d => d.deviceId));
    };
    
    const clearAll = () => {
      setTempSelectedHvacMeters([]);
    };

    const handleSave = () => {
      setSelectedHvacMeters(tempSelectedHvacMeters);
      setShowHvacMeterSelector(false);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowHvacMeterSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Wind className="w-5 h-5 text-cyan-500" />
              選擇空調用電電錶
            </h3>
            <button onClick={() => setShowHvacMeterSelector(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 border-b border-slate-700/30 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              全選
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              清除選擇
            </button>
          </div>
          
          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {powerMeters.length === 0 ? (
              <p className="text-center text-slate-400 py-4">沒有電錶</p>
            ) : (
              powerMeters.map(device => {
                const isSelected = isDeviceSelected(device);
                return (
                  <div
                    key={device.deviceId}
                    onClick={() => toggleMeter(device.deviceId)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                      isSelected
                        ? 'border-cyan-500 bg-cyan-900/20'
                        : 'border-slate-700/50 hover:bg-slate-700/50'
                    }`}
                  >
                    {isSelected ? (
                      <CheckSquare className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                    ) : (
                      <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{device.deviceName}</p>
                      <p className="text-xs text-slate-400">
                        {typeof device.watt === 'number' ? `${device.watt.toFixed(0)} W` : '0 W'}
                      </p>
                    </div>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${device.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                  </div>
                );
              })
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempSelectedHvacMeters.length > 0 
                ? `${tempSelectedHvacMeters.length}/${powerMeters.length} 已選擇`
                : '尚未選擇電錶'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Lighting Meter Selector Modal
  // ✅ Track if lighting modal selection has been initialized to prevent WebSocket updates from resetting it
  const lightingMeterSelectorInitializedRef = useRef(false);
  const prevShowLightingMeterSelectorRef = useRef(false);
  
  // ✅ Use useEffect to normalize selection ONLY when modal FIRST opens (not on every devices update)
  // ✅ IMPORTANT: Only initialize when modal transitions from closed to open, not on devices array changes
  useEffect(() => {
    const wasOpen = prevShowLightingMeterSelectorRef.current;
    const isOpen = showLightingMeterSelector;
    
    // Modal just opened: initialize selection
    if (isOpen && !wasOpen && devices.length > 0 && !lightingMeterSelectorInitializedRef.current) {
      // ✅ Normalize selectedLightingMeters to match device.deviceId format (WITH endpoint #)
      // ✅ IMPORTANT: Keep endpoints separate - UTU500-xxx#1 and UTU500-xxx#2 are different devices
      const powerMeters = devices.filter(d => {
        // Support both formats: normalized sensorProfile (new) or attributes.sensorProfile (old)
        const sensorProfile = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
        const hasPowerCapability = sensorProfile.watt || sensorProfile.real_power || sensorProfile.watt_hour;
        const hasPowerData = d.watt !== undefined || d.wattHour !== undefined;
        return hasPowerCapability || hasPowerData;
      });
      
      const normalizedSelected = powerMeters
        .filter(d => {
          // ✅ Match by exact deviceId (with endpoint) OR by baseSN if selectedId doesn't have endpoint
          return selectedLightingMeters.some(selectedId => {
            // Exact match (both have endpoint or both don't)
            if (d.deviceId === selectedId) return true;
            
            // If selectedId has no endpoint, match by baseSN
            const selectedBaseSN = selectedId.split('#')[0];
            const deviceBaseSN = d.baseSN || d.deviceId.split('#')[0];
            if (!selectedId.includes('#') && deviceBaseSN === selectedBaseSN) {
              // If device has endpoint, use the first endpoint (#1)
              return d.deviceId.includes('#') ? d.deviceId === `${deviceBaseSN}#1` : d.deviceId === deviceBaseSN;
            }
            
            return false;
          });
        })
        .map(d => d.deviceId); // ✅ Keep full deviceId with endpoint
      
      setTempSelectedLightingMeters(normalizedSelected);
      lightingMeterSelectorInitializedRef.current = true;
    } else if (!isOpen && wasOpen) {
      // Modal just closed: reset initialization flag
      lightingMeterSelectorInitializedRef.current = false;
    }
    
    prevShowLightingMeterSelectorRef.current = isOpen;
  }, [showLightingMeterSelector, devices, selectedLightingMeters]);
  
  const openLightingMeterSelector = () => {
    setShowLightingMeterSelector(true);
    // Note: Normalization will happen in useEffect when modal opens
  };

  const renderLightingMeterSelectorModal = () => {
    if (!showLightingMeterSelector) return null;
    
    // Filter devices that have power measurement capability (from sensorProfile or actual data)
    const powerMeters = devices.filter(d => {
      // Check if device has power metrics in attributes
      // Support both formats: normalized sensorProfile (new) or attributes.sensorProfile (old)
      const sensorProfile = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
      const hasPowerCapability = sensorProfile.watt || sensorProfile.real_power || sensorProfile.watt_hour;
      // Or check if device has actual power data
      const hasPowerData = d.watt !== undefined || d.wattHour !== undefined;
      return hasPowerCapability || hasPowerData;
    });
    
    // ✅ Helper function to check if device is selected (WITH endpoint separation)
    // ✅ IMPORTANT: UTU500-xxx#1 and UTU500-xxx#2 are treated as DIFFERENT devices
    const isDeviceSelected = (device: IntelliSpaceDevice): boolean => {
      return tempSelectedLightingMeters.some(selectedId => {
        // ✅ Exact match only - endpoints are separate
        return device.deviceId === selectedId;
      });
    };
    
    const toggleMeter = (deviceId: string) => {
      setTempSelectedLightingMeters(prev => {
        // ✅ Exact match only - endpoints are separate
        const isSelected = prev.includes(deviceId);
        return isSelected 
          ? prev.filter(id => id !== deviceId)
          : [...prev, deviceId];
      });
    };
    
    const selectAll = () => {
      setTempSelectedLightingMeters(powerMeters.map(d => d.deviceId));
    };
    
    const clearAll = () => {
      setTempSelectedLightingMeters([]);
    };

    const handleSave = () => {
      setSelectedLightingMeters(tempSelectedLightingMeters);
      setShowLightingMeterSelector(false);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowLightingMeterSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              選擇照明用電電錶
            </h3>
            <button onClick={() => setShowLightingMeterSelector(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 border-b border-slate-700/30 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              全選
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              清除選擇
            </button>
          </div>
          
          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {powerMeters.length === 0 ? (
              <p className="text-center text-slate-400 py-4">沒有電錶</p>
            ) : (
              powerMeters.map(device => {
                const isSelected = isDeviceSelected(device);
                return (
                  <div
                    key={device.deviceId}
                    onClick={() => toggleMeter(device.deviceId)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                      isSelected
                        ? 'border-yellow-500 bg-yellow-900/20'
                        : 'border-slate-700/50 hover:bg-slate-700/50'
                    }`}
                  >
                    {isSelected ? (
                      <CheckSquare className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    ) : (
                      <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{device.deviceName}</p>
                      <p className="text-xs text-slate-400">
                        {typeof device.watt === 'number' ? `${device.watt.toFixed(0)} W` : '0 W'}
                      </p>
                    </div>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${device.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                  </div>
                );
              })
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempSelectedLightingMeters.length > 0 
                ? `${tempSelectedLightingMeters.length}/${powerMeters.length} 已選擇`
                : '尚未選擇電錶'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  // TH Sensor Selector Modal
  const openThSensorSelector = () => {
    setTempSelectedThSensors([...selectedThSensors]);
    setShowThSensorSelector(true);
  };

  const renderThSensorSelectorModal = () => {
    if (!showThSensorSelector) return null;
    
    // ✅ Use all devices, not filtered by group - modal should show all available TH sensors
    const thSensors = devices.filter(d => d.deviceType === 'th_sensor');
    
    // Debug logging
    if (thSensors.length === 0 && devices.length > 0) {
      logger.warn('⚠️ [TH Sensor Selector] No TH sensors found in devices:', {
        totalDevices: devices.length,
        deviceTypes: Array.from(new Set(devices.map(d => d.deviceType))),
        isLoading,
        devices: devices.map(d => ({ deviceId: d.deviceId, deviceType: d.deviceType, deviceName: d.deviceName }))
      });
    } else {
      logger.log('📊 [TH Sensor Selector] Available TH sensors:', {
        totalDevices: devices.length,
        thSensorCount: thSensors.length,
        thSensors: thSensors.map(d => ({ deviceId: d.deviceId, deviceName: d.deviceName, baseSN: d.baseSN })),
        tempSelectedThSensors
      });
    }
    
    const toggleThSensor = (deviceId: string) => {
      setTempSelectedThSensors(prev => 
        prev.includes(deviceId) 
          ? prev.filter(id => id !== deviceId)
          : [...prev, deviceId]
      );
    };
    
    const selectAll = () => {
      setTempSelectedThSensors(thSensors.map(d => d.deviceId));
    };
    
    const clearAll = () => {
      setTempSelectedThSensors([]);
    };

    const handleSave = () => {
      setSelectedThSensors(tempSelectedThSensors);
      setShowThSensorSelector(false);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowThSensorSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Thermometer className="w-5 h-5 text-orange-500" />
              {'選擇溫濕度感測器'}
            </h3>
            <button onClick={() => setShowThSensorSelector(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 border-b border-slate-700/30 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              {t.intelliSpace?.selectAll || '全選'}
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              {t.intelliSpace?.clearSelection || '清除選擇'}
            </button>
          </div>
          
          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {thSensors.length === 0 ? (
              <p className="text-center text-slate-400 py-4">
                {t.intelliSpace?.noSensors || '無感測器'}
                {isLoading && <span className="block text-xs mt-2">載入中...</span>}
              </p>
            ) : (
              thSensors.map(device => (
                <div
                  key={device.deviceId}
                  onClick={() => toggleThSensor(device.deviceId)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    tempSelectedThSensors.includes(device.deviceId)
                      ? 'border-orange-500 bg-orange-900/20'
                      : 'border-slate-700/50 hover:bg-slate-700/50'
                  }`}
                >
                  {tempSelectedThSensors.includes(device.deviceId) ? (
                    <CheckSquare className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{device.deviceName}</p>
                    <p className="text-xs text-slate-400">
                      {typeof device.temperature === 'number' ? `${device.temperature.toFixed(1)}°C` : '--'} / {typeof device.humidity === 'number' ? `${device.humidity.toFixed(0)}%` : '--'}
                    </p>
                    <p className="text-xs text-slate-400">{device.baseSN || device.deviceId}</p>
                  </div>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${device.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                </div>
              ))
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempSelectedThSensors.length > 0 
                ? `${tempSelectedThSensors.length}/${thSensors.length} ${t.common?.selected || 'selected'}`
                : '包含所有感測器'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Three-Phase Meter Selector Modal (Single Select)
  const openThreePhaseMeterSelector = () => {
    setTempSelectedThreePhaseMeter(selectedThreePhaseMeter);
    setShowThreePhaseMeterSelector(true);
  };

  const renderThreePhaseMeterSelectorModal = () => {
    if (!showThreePhaseMeterSelector) return null;
    
    // Filter only power meters with three-phase data
    const powerMetersList = devices.filter(d => {
      if (d.deviceType !== 'power_meter' && d.deviceType !== 'power_meter_3phase') return false;
      const attrs = d.attributes?.["1"] || d.attributes || {};
      return attrs.voltage_r !== undefined || attrs.real_power_r !== undefined;
    });
    
    const selectMeter = (deviceId: string) => {
      setTempSelectedThreePhaseMeter(deviceId);
    };
    
    const clearSelection = () => {
      setTempSelectedThreePhaseMeter(null);
    };

    const handleSave = () => {
      setSelectedThreePhaseMeter(tempSelectedThreePhaseMeter);
      setShowThreePhaseMeterSelector(false);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowThreePhaseMeterSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-500" />
              選擇三相電錶
            </h3>
            <button onClick={() => setShowThreePhaseMeterSelector(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 border-b border-slate-700/30 flex justify-end">
            <button onClick={clearSelection} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              清除選擇
            </button>
          </div>
          
          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {powerMetersList.length === 0 ? (
              <p className="text-center text-slate-400 py-4">沒有電錶</p>
            ) : (
              powerMetersList.map(device => (
                <div
                  key={device.deviceId}
                  onClick={() => selectMeter(device.deviceId)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    tempSelectedThreePhaseMeter === device.deviceId
                      ? 'border-purple-500 bg-purple-900/20'
                      : 'border-slate-700/50 hover:bg-slate-700/50'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    tempSelectedThreePhaseMeter === device.deviceId
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-slate-600'
                  }`}>
                    {tempSelectedThreePhaseMeter === device.deviceId && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{device.deviceName}</p>
                    <p className="text-xs text-slate-400">{device.location || device.baseSN || device.deviceId}</p>
                  </div>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${device.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                </div>
              ))
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempSelectedThreePhaseMeter 
                ? `已選擇 1 個電錶`
                : '請選擇一個電錶'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Switch Selector Modal
  const openSwitchSelector = () => {
    setTempSelectedSwitches([...selectedSwitches]);
    setShowSwitchSelector(true);
  };

  const renderSwitchSelectorModal = () => {
    if (!showSwitchSelector) return null;

    // Use 'devices' instead of 'filteredDevices' to show all switches
    const switches = devices.filter(d => d.deviceType === 'switch');

    const toggleSwitch = (deviceId: string) => {
      setTempSelectedSwitches(prev =>
        prev.includes(deviceId)
          ? prev.filter(id => id !== deviceId)
          : [...prev, deviceId]
      );
    };

    const selectAll = () => {
      setTempSelectedSwitches(switches.map(d => d.deviceId));
    };

    const clearAll = () => {
      setTempSelectedSwitches([]);
    };

    const handleSave = () => {
      setSelectedSwitches(tempSelectedSwitches);
      setShowSwitchSelector(false);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSwitchSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Power className="w-5 h-5 text-green-500" />
              {t.intelliSpace?.selectSwitches || '選擇開關'}
            </h3>
            <button onClick={() => setShowSwitchSelector(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 border-b border-slate-700/30 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              {t.intelliSpace?.selectAll || '全選'}
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              {t.intelliSpace?.clearSelection || '清除選擇'}
            </button>
          </div>

          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {switches.length === 0 ? (
              <p className="text-center text-slate-400 py-4">{t.intelliSpace?.noSwitches || '沒有開關'}</p>
            ) : (
              switches.map(device => (
                <div
                  key={device.deviceId}
                  onClick={() => toggleSwitch(device.deviceId)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    tempSelectedSwitches.includes(device.deviceId)
                      ? 'border-green-500 bg-green-50 bg-green-900/20'
                      : 'border-slate-700/50 hover:bg-slate-700/50'
                  }`}
                >
                  {tempSelectedSwitches.includes(device.deviceId) ? (
                    <CheckSquare className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{device.deviceName}</p>
                    <p className="text-xs text-slate-400">{device.location || device.deviceId}</p>
                  </div>
                  <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                    device.switchOn
                      ? 'bg-green-900/30 text-green-400'
                      : 'bg-slate-700 text-slate-400'
                  }`}>
                    {device.switchOn ? 'ON' : 'OFF'}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempSelectedSwitches.length > 0
                ? `${tempSelectedSwitches.length}/${switches.length} ${t.common?.selected || 'selected'}`
                : t.intelliSpace?.allSwitchesIncluded || '包含所有開關'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Light Selector Modal - Simple selector for batch operations
  const openLightSelector = () => {
    setTempSelectedLights([...selectedLights]);
    setShowLightSelector(true);
  };

  const renderLightSelectorModal = () => {
    if (!showLightSelector) return null;
    
    const lightDevices = devices.filter(d => d.deviceType === 'light');
    
    const toggleLight = (deviceId: string) => {
      setTempSelectedLights(prev => 
        prev.includes(deviceId) 
          ? prev.filter(id => id !== deviceId)
          : [...prev, deviceId]
      );
    };
    
    const selectAll = () => {
      setTempSelectedLights(lightDevices.map(d => d.deviceId));
    };
    
    const clearAll = () => {
      setTempSelectedLights([]);
    };

    const handleSave = () => {
      setSelectedLights(tempSelectedLights);
      setShowLightSelector(false);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowLightSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              選擇燈光
            </h3>
            <button onClick={() => setShowLightSelector(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 border-b border-slate-700/30 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              {t.intelliSpace?.selectAll || '全選'}
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              {t.intelliSpace?.clearSelection || '清除選擇'}
            </button>
          </div>
          
          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {lightDevices.length === 0 ? (
              <p className="text-center text-slate-400 py-4">沒有燈光設備</p>
            ) : (
              lightDevices.map(device => (
                <div
                  key={device.deviceId}
                  onClick={() => toggleLight(device.deviceId)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    tempSelectedLights.includes(device.deviceId)
                      ? 'border-yellow-500 bg-yellow-900/20'
                      : 'border-slate-700/50 hover:bg-slate-700/50'
                  }`}
                >
                  {tempSelectedLights.includes(device.deviceId) ? (
                    <CheckSquare className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{device.deviceName}</p>
                    <p className="text-xs text-slate-400">{device.location || device.deviceId}</p>
                  </div>
                  <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                    device.isOn || device.power
                      ? 'bg-yellow-900/30 text-yellow-400' 
                      : 'bg-slate-700 text-slate-400'
                  }`}>
                    {device.isOn || device.power ? 'ON' : 'OFF'}
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempSelectedLights.length > 0 
                ? `${tempSelectedLights.length}/${lightDevices.length} ${t.common?.selected || 'selected'}`
                : '包含所有燈光'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Presence Sensor Selector Modal
  const renderPresenceSensorSelectorModal = () => {
    if (!showPresenceSensorSelector) return null;
    
    // Use 'devices' to show all presence sensors (consistent with other selector modals)
    const presenceDevices = devices.filter(d => d.deviceType === 'presence_sensor');
    
    const togglePresenceSensor = (deviceId: string) => {
      setTempSelectedPresenceSensors(prev => 
        prev.includes(deviceId) 
          ? prev.filter(id => id !== deviceId)
          : [...prev, deviceId]
      );
    };
    
    const selectAll = () => {
      setTempSelectedPresenceSensors(presenceDevices.map(d => d.deviceId));
    };
    
    const clearAll = () => {
      setTempSelectedPresenceSensors([]);
    };

    const handleSave = () => {
      setSelectedPresenceSensors(tempSelectedPresenceSensors);
      setShowPresenceSensorSelector(false);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPresenceSensorSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              選擇人體感應器
            </h3>
            <button onClick={() => setShowPresenceSensorSelector(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 border-b border-slate-700/30 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              {t.intelliSpace?.selectAll || '全選'}
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              {t.intelliSpace?.clearSelection || '清除選擇'}
            </button>
          </div>
          
          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {presenceDevices.length === 0 ? (
              <p className="text-center text-slate-400 py-4">沒有人體感應器</p>
            ) : (
              presenceDevices.map(device => (
                <div
                  key={device.deviceId}
                  onClick={() => togglePresenceSensor(device.deviceId)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    tempSelectedPresenceSensors.includes(device.deviceId)
                      ? 'border-purple-500 bg-purple-900/20'
                      : 'border-slate-700/50 hover:bg-slate-700/50'
                  }`}
                >
                  {tempSelectedPresenceSensors.includes(device.deviceId) ? (
                    <CheckSquare className="w-5 h-5 text-purple-500 flex-shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{device.deviceName}</p>
                    <p className="text-xs text-slate-400">{device.location || device.deviceId}</p>
                  </div>
                  <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                    device.presence || device.occupancy
                      ? 'bg-purple-900/30 text-purple-400' 
                      : 'bg-slate-700 text-slate-400'
                  }`}>
                    {device.presence || device.occupancy ? '有人' : '空置'}
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempSelectedPresenceSensors.length > 0 
                ? `${tempSelectedPresenceSensors.length}/${presenceDevices.length} ${t.common?.selected || 'selected'}`
                : '包含所有人體感應器'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Lux Sensor Selector Modal
  const renderLuxSensorSelectorModal = () => {
    if (!showLuxSensorSelector) return null;
    
    const luxDevices = devices.filter(d => d.luxValue !== undefined || d.lux !== undefined);
    
    const toggleLuxSensor = (deviceId: string) => {
      setTempSelectedLuxSensors(prev => 
        prev.includes(deviceId) 
          ? prev.filter(id => id !== deviceId)
          : [...prev, deviceId]
      );
    };
    
    const selectAll = () => {
      setTempSelectedLuxSensors(luxDevices.map(d => d.deviceId));
    };
    
    const clearAll = () => {
      setTempSelectedLuxSensors([]);
    };

    const handleSave = () => {
      setSelectedLuxSensors(tempSelectedLuxSensors);
      try {
        localStorage.setItem('intellispace-selected-lux-sensors', JSON.stringify(tempSelectedLuxSensors));
      } catch {}
      setShowLuxSensorSelector(false);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowLuxSensorSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Sun className="w-5 h-5 text-amber-500" />
              選擇照度感測器
            </h3>
            <button onClick={() => setShowLuxSensorSelector(false)} className="text-slate-400 hover:text-slate-300">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 border-b border-slate-700/30 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              全選
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 hover:text-slate-300">
              清除選擇
            </button>
          </div>
          
          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {luxDevices.length === 0 ? (
              <p className="text-center text-slate-400 py-4">沒有照度感測器</p>
            ) : (
              luxDevices.map(device => (
                <div
                  key={device.deviceId}
                  onClick={() => toggleLuxSensor(device.deviceId)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    tempSelectedLuxSensors.includes(device.deviceId)
                      ? 'border-amber-500 bg-amber-900/20'
                      : 'border-slate-700/50 hover:bg-slate-700/50'
                  }`}
                >
                  {tempSelectedLuxSensors.includes(device.deviceId) ? (
                    <CheckSquare className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{device.nickname || device.deviceName || device.deviceId}</p>
                    <p className="text-xs text-slate-400">{device.location || device.deviceId}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-semibold text-amber-400">
                      {device.luxValue || device.lux || 0} lux
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempSelectedLuxSensors.length > 0 
                ? `${tempSelectedLuxSensors.length}/${luxDevices.length} 已選擇`
                : '包含所有照度感測器'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Blind/Curtain Selector Modal
  const renderBlindCurtainSelectorModal = () => {
    if (!showBlindCurtainSelector) return null;
    
    const blindCurtainDevices = devices.filter(d => d.deviceType === 'curtain' || d.deviceType === 'blind');
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowBlindCurtainSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Blinds className="w-5 h-5 text-slate-400" />
              窗簾/捲簾
            </h3>
            <button onClick={() => setShowBlindCurtainSelector(false)} className="text-slate-400 hover:text-slate-300">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2">
            {blindCurtainDevices.length === 0 ? (
              <p className="text-center text-slate-400 py-4">沒有窗簾/捲簾設備</p>
            ) : (
              blindCurtainDevices.map(device => (
                <div
                  key={device.deviceId}
                  className="p-3 rounded-lg border border-slate-700/50 bg-slate-800/30 flex items-center gap-3"
                >
                  <Blinds className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{device.nickname || device.deviceName || device.deviceId}</p>
                    <p className="text-xs text-slate-400">{device.deviceType === 'blind' ? '捲簾' : '窗簾'}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-lg font-semibold ${(device.openPercent || 0) > 0 ? 'text-green-400' : 'text-slate-400'}`}>
                      {device.openPercent !== undefined ? `${device.openPercent}%` : '--'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 text-center">
            <p className="text-sm text-slate-400">
              共 {blindCurtainDevices.length} 個窗簾/捲簾
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Baseline Power Input Modal
  const renderBaselinePowerModal = () => {
    if (!showBaselinePowerModal) return null;
    
    const isLighting = showBaselinePowerModal === 'lighting';
    const currentBaseline = isLighting ? baselineLightingPower : baselineHvacPower;
    const title = isLighting ? '設定照明基準功率' : '設定空調基準功率';
    const description = isLighting 
      ? '輸入照明系統的基準功率（瓦特），用於計算節能百分比'
      : '輸入空調系統的基準功率（瓦特），用於計算節能百分比';
    
    const handleSave = async () => {
      const power = parseFloat(tempBaselinePower) || 0;
      if (isLighting) {
        setBaselineLightingPower(power);
      } else {
        setBaselineHvacPower(power);
      }
      
      // Save to Firestore
      try {
        const currentSelections = await userPreferencesApi.getIntelliSpaceSelections();
        await userPreferencesApi.saveIntelliSpaceSelections({
          ...currentSelections,
          ...(isLighting 
            ? { baselineLightingPower: power }
            : { baselineHvacPower: power }
          ),
        });
        logger.log(`✅ [IntelliSpace] Saved ${isLighting ? 'lighting' : 'HVAC'} baseline power: ${power}W`);
      } catch (error) {
        logger.error('Failed to save baseline power:', error);
      }
      
      setShowBaselinePowerModal(null);
      setTempBaselinePower('');
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowBaselinePowerModal(null)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              {title}
            </h3>
            <button onClick={() => setShowBaselinePowerModal(null)} className="text-slate-400 hover:text-slate-300">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 space-y-4">
            <p className="text-sm text-slate-400">{description}</p>
            
            <div className="space-y-2">
              <label className="text-sm text-slate-300">基準功率 (W)</label>
              <input
                type="number"
                value={tempBaselinePower}
                onChange={(e) => setTempBaselinePower(e.target.value)}
                placeholder={currentBaseline > 0 ? `目前: ${currentBaseline} W` : '輸入功率值'}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white text-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            
            {currentBaseline > 0 && (
              <p className="text-xs text-slate-500">
                目前設定: {currentBaseline} W
              </p>
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex justify-end gap-3">
            <button
              onClick={() => setShowBaselinePowerModal(null)}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  // NOTE: renderLightControlModal has been removed - Light devices now use the unified template system via DeviceModalRenderer

  // Helper function to check if a device is part of a multi-endpoint device
  const isMultiEndpointDevice = useCallback((device: IntelliSpaceDevice): boolean => {
    const deviceId = device.deviceId || '';
    // If deviceId contains #, it's an endpoint of a multi-endpoint device
    if (!deviceId.includes('#')) return false;
    
    // Check if there are other devices with the same baseSN
    const baseSN = deviceId.split('#')[0];
    const siblingEndpoints = devices.filter(d => 
      d.deviceId.startsWith(baseSN + '#') && d.deviceId !== deviceId
    );
    return siblingEndpoints.length > 0;
  }, [devices]);

  // Helper function to get all endpoints of a multi-endpoint device
  const getDeviceEndpoints = useCallback((baseSN: string): IntelliSpaceDevice[] => {
    return devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
  }, [devices]);

  // Toggle merged/separated display for multi-endpoint device
  const toggleMergedDevice = useCallback(async (baseSN: string) => {
    setMergedDeviceSNs(prev => {
      const newSet = new Set(prev);
      const wasMerged = newSet.has(baseSN);
      
      if (wasMerged) {
        newSet.delete(baseSN);
      } else {
        newSet.add(baseSN);
      }
      
      // Save to Firestore (async, don't await)
      const newMode = wasMerged ? 'expanded' : 'merged';
      userPreferencesApi.setMultiChannelDisplayMode(baseSN, newMode).catch(error => {
        logger.error('Failed to save multi-channel display mode:', error);
      });
      
      return newSet;
    });
  }, []);

  // Device Control Modal - For all device types except Light
  const renderDeviceControlModal = () => {
    if (!selectedDevice || !modalDeviceSnapshot) return null;
    
    // ✅ Use snapshot to prevent background WebSocket updates from causing re-renders
    // The snapshot is created when modal opens and stays stable until modal closes
    const device = modalDeviceSnapshot;
    
    // ✅ Use modular DeviceModalRenderer for dehumidifier
    if (device.deviceType === 'dehumidifier') {
      // For composite devices like dehumidifier, ensure deviceId includes endpoint
      // MCP ExecuteCommand requires format: "baseSN#DEHUMIDIFIER"
      const baseSN = device.deviceId.split('#')[0];
      const dehumidifierDeviceId = device.deviceId.includes('#') 
        ? device.deviceId 
        : `${baseSN}#DEHUMIDIFIER`;
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: dehumidifierDeviceId,  // Use endpoint-specific deviceId
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '除濕機',
            nickname: device.nickname,  // Tenant-local nickname for individual display
            deviceType: device.deviceType,
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            isOn: device.isOn,
            power: device.power,
            humidity: device.humidity,
            humidityAmbientPercent: device.humidityAmbientPercent,
            humiditySetpointPercent: device.humiditySetpointPercent,
            currentModeSettings: device.currentModeSettings,
            currentToggleSettings: device.currentToggleSettings,
            bucket: device.bucket,  // Water tank status
            notify: device.notify,  // Device notifications (bucket full, etc.)
            isMultiChannel: device.isMultiChannel,  // Multi-channel flag for merge toggle
          }}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async (dev, trait, action, params) => {
            // Create a device copy with correct deviceId for command execution
            const deviceWithEndpoint = { ...device, deviceId: dehumidifierDeviceId };
            await handleTraitControl(deviceWithEndpoint, trait, action, params);
          }}
          onClose={() => setSelectedDevice(null)}
          onNicknameEdit={(deviceId, nickname) => {
            setTempNickname(nickname);
            setEditingNickname(true);
          }}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, false);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
        />
      );
    }
    
    // ✅ Use modular DeviceModalRenderer for HVAC
    if (device.deviceType === 'hvac') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      
      // ✅ HVAC: Use exact deviceId (with endpoint) from the current device
      const hvacDeviceId = device.deviceId;
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: hvacDeviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '空調',
            nickname: device.nickname,
            deviceType: device.deviceType,
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            isOn: device.isOn,
            power: device.power,
            temperature: device.temperature,
            thermostatTemperatureSetpoint: device.thermostatTemperatureSetpoint,
            thermostatMode: device.thermostatMode,
            fanSpeed: device.fanSpeed,
            isMultiChannel: device.isMultiChannel,
          }}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async (dev, trait, action, params) => {
            // ✅ Use device with correct endpoint from modal, not the closure variable
            await handleTraitControl({ ...device, deviceId: hvacDeviceId }, trait, action, params);
          }}
          onClose={() => setSelectedDevice(null)}
          onNicknameEdit={(deviceId, nickname) => {
            setTempNickname(nickname);
            setEditingNickname(true);
          }}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, false);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
        />
      );
    }
    
    // ✅ Use modular DeviceModalRenderer for Fan (電風扇)
    // Fan 與 HVAC 不同：沒有溫度控制，只有電源和風速
    if (device.deviceType === 'fan') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      
      // ✅ FAN: Use exact deviceId (with endpoint) from the current device
      const fanDeviceId = device.deviceId;
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: fanDeviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '風扇',
            nickname: device.nickname,
            deviceType: device.deviceType,
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            isOn: device.isOn,
            power: device.power,
            fanSpeed: device.fanSpeed,
            isMultiChannel: device.isMultiChannel,
          }}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async (dev, trait, action, params) => {
            // ✅ Use device with correct endpoint from modal, not the closure variable
            await handleTraitControl({ ...device, deviceId: fanDeviceId }, trait, action, params);
          }}
          onClose={() => setSelectedDevice(null)}
          onNicknameEdit={(deviceId, nickname) => {
            setTempNickname(nickname);
            setEditingNickname(true);
          }}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, false);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
        />
      );
    }
    
    // ✅ Use DeviceModalRenderer for ERV (全熱交換機) with Firestore theme support
    if (device.deviceType === 'erv') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      const ervDeviceId = device.deviceId;
      
      // Extract ERV-specific sensor data from device
      // Check multiple possible locations for the data
      const currentModeSettings = device.currentModeSettings || 
                                  (device.attributes as any)?.currentModeSettings?.value || 
                                  (device.attributes as any)?.currentModeSettings || {};
      const currentSensorStateData = device.currentSensorStateData || 
                                     (device.attributes as any)?.currentSensorStateData?.value ||
                                     (device.attributes as any)?.currentSensorStateData || [];
      
      // Debug log to verify data extraction
      console.log('🌀 [ERV Modal] Device data:', {
        deviceId: ervDeviceId,
        currentModeSettings,
        currentSensorStateData,
        temperature: device.temperature,
        humidity: device.humidity,
        thermostatMode: device.thermostatMode
      });
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: ervDeviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '全熱交換機',
            nickname: device.nickname,
            deviceType: 'erv',
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: {
              ...device.attributes,
              // Ensure availableThermostatModes is available
              availableThermostatModes: device.attributes?.availableThermostatModes || 'fan-only,heat',
              // Ensure currentModeSettings and currentSensorStateData are in attributes too
              currentModeSettings: { value: currentModeSettings },
              currentSensorStateData: { value: currentSensorStateData },
            },
            isOn: device.isOn,
            power: device.power,
            fanSpeed: device.fanSpeed,
            temperature: device.temperature,
            humidity: device.humidity,
            thermostatMode: device.thermostatMode || device.mode,
            thermostatTemperatureAmbient: device.temperature,
            humidityAmbientPercent: device.humidity,
            isMultiChannel: device.isMultiChannel,
            // ERV specific: CustomModes and SensorState data at root level
            currentModeSettings,
            currentSensorStateData,
          } as any}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async (dev, trait, action, params) => {
            await handleTraitControl({ ...device, deviceId: ervDeviceId }, trait, action, params);
          }}
          onClose={() => setSelectedDevice(null)}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, isMerged);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
        />
      );
    }
    
    // ✅ Use DeviceModalRenderer for Curtain (窗簾) with Firestore theme support
    if (device.deviceType === 'curtain') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      const curtainDeviceId = device.deviceId;
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: curtainDeviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '窗簾',
            nickname: device.nickname,
            deviceType: 'curtain',
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            openPercent: device.openPercent ?? 0,
            isMultiChannel: device.isMultiChannel,
          }}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async (dev, trait, action, params) => {
            await handleTraitControl({ ...device, deviceId: curtainDeviceId }, trait, action, params);
          }}
          onClose={() => setSelectedDevice(null)}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, isMerged);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
        />
      );
    }
    
    // ✅ Use DeviceModalRenderer for Blind (捲簾) with Firestore theme support
    if (device.deviceType === 'blind') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      const blindDeviceId = device.deviceId;
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: blindDeviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '捲簾',
            nickname: device.nickname,
            deviceType: 'blind',
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            openPercent: device.openPercent ?? 0,
            isMultiChannel: device.isMultiChannel,
          }}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async (dev, trait, action, params) => {
            await handleTraitControl({ ...device, deviceId: blindDeviceId }, trait, action, params);
          }}
          onClose={() => setSelectedDevice(null)}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, isMerged);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
        />
      );
    }
    
    // ✅ Use DeviceModalRenderer for PowerMeter (電錶) - read-only with Firestore theme support
    if (device.deviceType === 'power_meter') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      const powerMeterDeviceId = device.deviceId;
      
      // Determine online status from data sources
      const isPowerMeterOnline = device.status === 'online' || device.online === true;
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: powerMeterDeviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '電錶',
            nickname: device.nickname,
            deviceType: 'power_meter',
            status: isPowerMeterOnline ? 'online' : 'offline',
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            watt: device.watt,
            voltage: device.voltage,
            current: device.current,
            wattHour: device.wattHour,
            energy: device.energy,
            powerFactor: device.powerFactor,
            isMultiChannel: device.isMultiChannel,
            // ✅ Add online for template to use
            online: isPowerMeterOnline,
          } as any}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async () => {}} // Power meter is read-only
          onClose={() => setSelectedDevice(null)}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, isMerged);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
          energyChartData={energyChartData.map(d => ({ time: d.hour, value: d.watt ?? 0 }))}
          isLoadingEnergyChart={isLoadingEnergyChart}
        />
      );
    }
    
    // ✅ Use DeviceModalRenderer for 3-Phase PowerMeter (三相電錶) - read-only with Firestore theme support
    if (device.deviceType === 'power_meter_3phase') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      const powerMeterDeviceId = device.deviceId;
      
      // Determine online status from data sources
      const is3PhaseOnline = device.status === 'online' || device.online === true;
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: powerMeterDeviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '三相電錶',
            nickname: device.nickname,
            deviceType: 'power_meter_3phase',
            status: is3PhaseOnline ? 'online' : 'offline',
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            watt: device.watt,
            voltage: device.voltage,
            current: device.current,
            wattHour: device.wattHour,
            energy: device.energy,
            isMultiChannel: device.isMultiChannel,
            // ✅ Add online for template to use
            online: is3PhaseOnline,
          } as any}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async () => {}} // Power meter is read-only
          onClose={() => setSelectedDevice(null)}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, isMerged);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
          energyChartData={energyChartData.map(d => ({ time: d.hour, value: d.watt ?? 0 }))}
          isLoadingEnergyChart={isLoadingEnergyChart}
        />
      );
    }
    
    // ✅ Use DeviceModalRenderer for DoorSensor (門窗感應器) - read-only with Firestore theme support
    if (device.deviceType === 'door_sensor') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      const doorSensorDeviceId = device.deviceId;
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: doorSensorDeviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '門窗感應器',
            nickname: device.nickname,
            deviceType: 'door_sensor',
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            detected: device.detected,
            openPercent: device.openPercent,
            isMultiChannel: device.isMultiChannel,
          }}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async () => {}} // Door sensor is read-only
          onClose={() => setSelectedDevice(null)}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, isMerged);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
          occupancyChartData={doorSensorChartData}
          isLoadingOccupancyChart={isLoadingDoorSensorChart}
        />
      );
    }
    
    // ✅ Use DeviceModalRenderer for Button (按鈕/場景觸發) - read-only with Firestore theme support
    if (device.deviceType === 'button') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      const buttonDeviceId = device.deviceId;
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: buttonDeviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '按鈕',
            nickname: device.nickname,
            deviceType: 'button',
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            isMultiChannel: device.isMultiChannel,
          }}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async () => {}} // Button is read-only
          onClose={() => setSelectedDevice(null)}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, isMerged);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
          occupancyChartData={occupancyChartData}
          isLoadingOccupancyChart={isLoadingOccupancyChart}
        />
      );
    }
    
    // ✅ Use modular DeviceModalRenderer for Light (燈光)
    if (device.deviceType === 'light') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      const lightDeviceId = device.deviceId;
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: lightDeviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '燈光',
            nickname: device.nickname,
            deviceType: device.deviceType,
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            isOn: device.isOn,
            brightness: device.brightness,
            colorTemp: device.colorTemp,
            isMultiChannel: device.isMultiChannel,
          }}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async (dev, trait, action, params) => {
            await handleTraitControl({ ...device, deviceId: lightDeviceId }, trait, action, params);
          }}
          onClose={() => setSelectedDevice(null)}
          onNicknameEdit={(deviceId, nickname) => {
            setTempNickname(nickname);
            setEditingNickname(true);
          }}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, false);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
        />
      );
    }
    
    // ✅ Use modular DeviceModalRenderer for Switch/Outlet (開關/插座)
    if (device.deviceType === 'switch' || device.deviceType === 'outlet') {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      const switchDeviceId = device.deviceId;
      
      // For merged display: use systemDeviceName as title, pass all endpoints
      const displayName = isMerged 
        ? (device.systemDeviceName || device.originalName || device.deviceName || (device.deviceType === 'outlet' ? '智慧插座' : '開關'))
        : (device.nickname || device.deviceName || device.originalName || (device.deviceType === 'outlet' ? '智慧插座' : '開關'));
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: switchDeviceId,
            baseSN: baseSN,
            deviceName: displayName,
            nickname: isMerged ? undefined : device.nickname, // Hide nickname when merged
            systemDeviceName: device.systemDeviceName,
            deviceType: device.deviceType,
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            isOn: device.isOn,
            power: device.power,
            watt: device.watt,
            voltage: device.voltage,
            current: device.current,
            wattHour: device.wattHour,
            isMultiChannel: device.isMultiChannel,
          }}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async (dev, trait, action, params) => {
            // Use the dev from callback (for multi-endpoint, this is the specific endpoint device)
            const targetDevice = devices.find(d => d.deviceId === dev.deviceId) || device;
            await handleTraitControl({ ...targetDevice, deviceId: dev.deviceId }, trait, action, params);
          }}
          onClose={() => setSelectedDevice(null)}
          onNicknameEdit={(deviceId, nickname) => {
            setTempNickname(nickname);
            setEditingNickname(true);
          }}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, isMerged);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
          allEndpoints={isMerged && endpoints.length > 1 ? endpoints : undefined}
          // Pass 24H energy chart data for power monitoring
          energyChartData={energyChartData.map(d => ({ time: d.hour, value: d.watt ?? 0 }))}
          isLoadingEnergyChart={isLoadingEnergyChart}
        />
      );
    }
    
    // ✅ Use modular DeviceModalRenderer for TH Sensor / Presence Sensor / Lux Sensor (感測器)
    const sensorTypes = ['th_sensor', 'presence_sensor', 'lux_sensor', 'multi_sensor', 'motion_sensor'];
    if (sensorTypes.includes(device.deviceType as string)) {
      const baseSN = device.deviceId.split('#')[0];
      const endpoints = devices.filter(d => d.deviceId.startsWith(baseSN + '#'));
      const endpointCount = endpoints.length > 1 ? endpoints.length : undefined;
      const isMerged = mergedDeviceSNs.has(baseSN);
      
      return (
        <DeviceModalRenderer
          device={{
            deviceId: device.deviceId,
            baseSN: baseSN,
            deviceName: device.deviceName || device.originalName || '感測器',
            nickname: device.nickname,
            deviceType: device.deviceType,
            status: device.status,
            location: device.location,
            groupId: device.groupId,
            groupName: device.groupName,
            traits: device.traits,
            attributes: device.attributes,
            temperature: device.temperature,
            humidity: device.humidity,
            lux: device.lux,
            occupancy: device.occupancy,
            presence: device.presence,
            isMultiChannel: device.isMultiChannel,
          }}
          isExecutingCommand={isExecutingCommand}
          onTraitControl={async (dev, trait, action, params) => {
            await handleTraitControl(dev as any, trait, action, params);
          }}
          onClose={() => setSelectedDevice(null)}
          onNicknameSave={async (deviceId, newName) => {
            await handleSaveNickname(deviceId, newName, isMerged);
          }}
          isMerged={isMerged}
          endpointCount={endpointCount}
          onToggleMerge={toggleMergedDevice}
          // Pass 24H chart data based on sensor type
          thSensorChartData={thSensorChartData.map(d => ({ time: d.hour, value: d.temp ?? 0, temperature: d.temp ?? undefined, humidity: d.humidity ?? undefined }))}
          isLoadingThSensorChart={isLoadingThSensorChart}
          luxChartData={luxChartData.map(d => ({ time: d.hour, value: d.lux ?? 0 }))}
          isLoadingLuxChart={isLoadingLuxChart}
          occupancyChartData={occupancyChartData.map(d => ({ time: d.time, timestamp: d.timestamp, value: d.value, label: d.label }))}
          isLoadingOccupancyChart={isLoadingOccupancyChart}
        />
      );
    }
    
    // Check if this is a merged multi-endpoint device
    const baseSN = device.deviceId.split('#')[0];
    const isMergedMultiEndpoint = device.deviceId.includes('#') && mergedDeviceSNs.has(baseSN);
    const allEndpoints = isMergedMultiEndpoint ? getDeviceEndpoints(baseSN) : [device];
    
    const getDeviceTypeLabel = (type: string) => {
      switch (type) {
        case 'hvac': return 'HVAC 空調';
        case 'fan': return '風扇';
        case 'switch': return '開關';
        case 'power_meter_3phase': return '三相電錶';
        case 'power_meter': return '電錶';
        case 'th_sensor': 
          // Check if it's humidity-only sensor
          const hasTemp = device.temperature !== undefined;
          const hasHumidity = device.humidity !== undefined;
          if (hasHumidity && !hasTemp) return '濕度感測器';
          if (hasTemp && !hasHumidity) return '溫度感測器';
          return '溫濕度感測器';
        case 'presence_sensor': return '人體感應器';
        case 'lux_sensor': return '光照感測器';
        case 'light': return '燈光';
        case 'door_sensor': return '門窗感應器';
        case 'button': return '按鈕';
        case 'controller': return '控制器';
        case 'bridge': return '橋接器';
        case 'motion_sensor': return '移動感應器';
        case 'outlet': return '智慧插座';
        case 'curtain': return '窗簾';
        case 'blind': return '捲簾';
        case 'dehumidifier': return '除濕機';
        case 'multi_sensor': return '多功能感測器';
        case 'erv': return '全熱交換機';
        default: return '設備';
      }
    };
    
    const getDeviceTypeIcon = (type: string) => {
      // Special handling for th_sensor (humidity-only vs temp+humidity)
      if (type === 'th_sensor') {
        const hasTemp = device.temperature !== undefined;
        const hasHumidity = device.humidity !== undefined;
        if (hasHumidity && !hasTemp) {
          return <Style02_WaterDrop size={20} />;
        }
        return <Style01_Thermometer size={20} />;
      }
      
      // Special handling for door_sensor and fan (uses Lucide icons)
      if (type === 'door_sensor') return <DoorOpen className="w-5 h-5 text-red-500" />;
      if (type === 'fan') return <Fan className="w-5 h-5 text-cyan-500" />;
      
      // Use unified DeviceIcon for all other types
      return <DeviceIcon deviceType={type as any} isOn={true} size={20} />;
    };

    const getAccentColor = (type: string) => {
      switch (type) {
        case 'hvac': return 'cyan';
        case 'switch': return 'green';
        case 'power_meter_3phase': return 'yellow';
        case 'power_meter': return 'yellow';
        case 'th_sensor': return 'orange';
        case 'presence_sensor': return 'purple';
        case 'multi_sensor': return 'indigo';
        case 'curtain': return 'teal';
        case 'blind': return 'cyan';
        default: return 'blue';
      }
    };

    const accent = getAccentColor(device.deviceType);
    const isOn = device.isOn || device.switchOn || device.power;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedDevice(null)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              {getDeviceTypeIcon(device.deviceType)}
              <div className="flex-1 min-w-0">
                {editingNickname ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={tempNickname}
                      onChange={(e) => setTempNickname(e.target.value)}
                      className="flex-1 px-2 py-1 text-lg font-semibold border border-slate-700/50 rounded bg-slate-800/50 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveNickname(device.deviceId, tempNickname, isMergedMultiEndpoint);
                          setEditingNickname(false);
                        } else if (e.key === 'Escape') {
                          setEditingNickname(false);
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        handleSaveNickname(device.deviceId, tempNickname, isMergedMultiEndpoint);
                        setEditingNickname(false);
                      }}
                      className="p-1 text-green-500 hover:text-green-600"
                      title="儲存"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setEditingNickname(false)}
                      className="p-1 text-slate-400 hover:text-slate-300"
                      title="取消"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <span className="truncate">
                      {isMergedMultiEndpoint 
                        ? (device.systemDeviceName || device.originalName || device.baseSN || baseSN) 
                        : (device.nickname || device.deviceName)}
                    </span>
                    <button
                      onClick={() => {
                        // For merged devices, edit systemDeviceName (global); for single devices, edit nickname
                        const nameToEdit = isMergedMultiEndpoint 
                          ? (device.systemDeviceName || device.originalName || device.baseSN || baseSN)
                          : (device.nickname || device.deviceName);
                        setTempNickname(nameToEdit);
                        setEditingNickname(true);
                      }}
                      className="p-1 text-slate-400 hover:text-slate-300 hover:text-slate-200 flex-shrink-0"
                      title={isMergedMultiEndpoint ? "編輯設備名稱" : "編輯暱稱"}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </h3>
                )}
                <p className="text-xs text-slate-400">
                  {isMergedMultiEndpoint 
                    ? `${getDeviceTypeLabel(device.deviceType)} (${allEndpoints.length} 個端點)`
                    : getDeviceTypeLabel(device.deviceType)}
                </p>
              </div>
            </div>
            {!editingNickname && (
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${device.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                <button onClick={() => setSelectedDevice(null)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
          
          {/* Content based on device traits - dynamically generated */}
          <div className="p-6 space-y-4">
            {/* Multi-endpoint device grid - show all endpoints when merged */}
            {isMergedMultiEndpoint && allEndpoints.length > 1 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-300">所有端點</p>
                <div className="grid grid-cols-2 gap-3">
                  {[...allEndpoints].sort((a, b) => {
                    // Sort by endpoint number (e.g., #1, #2, #3)
                    const aEndpoint = a.deviceId.split('#')[1] || '0';
                    const bEndpoint = b.deviceId.split('#')[1] || '0';
                    // Try to parse as number, fallback to string comparison
                    const aNum = parseInt(aEndpoint, 10);
                    const bNum = parseInt(bEndpoint, 10);
                    if (!isNaN(aNum) && !isNaN(bNum)) {
                      return aNum - bNum;
                    }
                    return aEndpoint.localeCompare(bEndpoint);
                  }).map((ep) => {
                    const epIsOn = ep.isOn || ep.switchOn || ep.power;
                    const epEndpoint = ep.deviceId.split('#')[1] || '1';
                    return (
                      <div
                        key={ep.deviceId}
                        className={`p-3 rounded-lg border transition-all ${
                          epIsOn 
                            ? 'bg-slate-700/50 border-green-500/50' 
                            : 'bg-slate-800/50 border-slate-600/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">
                              {ep.nickname || `端點 #${epEndpoint}`}
                            </p>
                            <p className="text-xs text-slate-400">#{epEndpoint}</p>
                          </div>
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            ep.status === 'online' ? 'bg-green-500' : 'bg-red-500'
                          }`} />
                        </div>
                        {/* Show ON/OFF toggle for controllable devices */}
                        {(ep.deviceType === 'switch' || ep.deviceType === 'outlet' || ep.deviceType === 'light') && (
                          <button
                            onClick={() => handleTraitControl(ep, 'OnOff', epIsOn ? 'off' : 'on')}
                            disabled={isExecutingCommand}
                            className={`w-full py-1.5 rounded text-xs font-medium transition-colors ${
                              epIsOn 
                                ? 'bg-green-500 text-white hover:bg-green-600' 
                                : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                            }`}
                          >
                            {isExecutingCommand ? '...' : epIsOn ? 'ON' : 'OFF'}
                          </button>
                        )}
                        {/* Show state for sensors */}
                        {(ep.deviceType === 'th_sensor' || ep.deviceType === 'presence_sensor' || ep.deviceType === 'multi_sensor') && (
                          <div className="text-center">
                            {ep.temperature !== undefined && (
                              <span className="text-lg font-bold text-orange-500">{ep.temperature}°</span>
                            )}
                            {ep.humidity !== undefined && (
                              <span className="text-lg font-bold text-blue-500 ml-2">{ep.humidity}%</span>
                            )}
                            {ep.occupancy !== undefined && (
                              <span className={`text-sm font-medium ${ep.occupancy ? 'text-green-500' : 'text-slate-400'}`}>
                                {ep.occupancy ? '有人' : '無人'}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* HVAC/Fan controls now handled by DeviceModalRenderer (returns early) */}
            {false && (device.deviceType as string) === 'hvac' && (
              <>
                {/* Power Toggle - if has OnOff trait */}
                {device.traits?.includes('OnOff') && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-300">電源</span>
                    <button
                      onClick={() => handleTraitControl(device, 'OnOff', isOn ? 'off' : 'on')}
                      disabled={isExecutingCommand}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        isOn 
                          ? 'bg-cyan-500 text-white hover:bg-cyan-600' 
                          : 'bg-slate-800/50 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {isExecutingCommand ? '...' : isOn ? 'ON' : 'OFF'}
                    </button>
                  </div>
                )}
                
                {/* Temperature Control - only if has TemperatureSetting trait */}
                {device.traits?.includes('TemperatureSetting') && device.setTemperature !== undefined && (
                  <div>
                    <span className="text-sm font-medium text-slate-300 block mb-3">溫度設定</span>
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={() => handleTraitControl(device, 'TemperatureSetting', 'setTemp', { temperature: Math.max(16, (device.setTemperature || 24) - 1) })}
                        disabled={isExecutingCommand}
                        className="w-12 h-12 rounded-full bg-blue-900/30 text-blue-400 text-xl font-bold hover:bg-blue-800/50 transition-colors"
                      >−</button>
                      <div className="text-4xl font-bold text-white w-24 text-center">
                        {device.setTemperature}°C
                      </div>
                      <button
                        onClick={() => handleTraitControl(device, 'TemperatureSetting', 'setTemp', { temperature: Math.min(30, (device.setTemperature || 24) + 1) })}
                        disabled={isExecutingCommand}
                        className="w-12 h-12 rounded-full bg-red-900/30 text-red-400 text-xl font-bold hover:bg-red-800/50 transition-colors"
                      >+</button>
                    </div>
                  </div>
                )}
                
                {/* Mode Selection - if has ThermostatMode trait OR availableThermostatModes attribute */}
                {(device.traits?.includes('ThermostatMode') || device.attributes?.availableThermostatModes) && (
                  <div>
                    <span className="text-sm font-medium text-slate-300 block mb-2">模式</span>
                    <div className="grid grid-cols-4 gap-2">
                      {(() => {
                        // Get available modes from attributes or use defaults
                        const modesStr = device.attributes?.availableThermostatModes || 'cool,heat,dry,fan-only';
                        const availableModes = typeof modesStr === 'string' ? modesStr.split(',') : modesStr;
                        const currentMode = device.mode || 'cool';
                        
                        const getModeLabel = (mode: string) => {
                          switch (mode.trim()) {
                            case 'cool': return '❄️ 冷氣';
                            case 'heat': return '🔥 暖氣';
                            case 'dry': return '💧 除濕';
                            case 'fan-only': return '🌀 送風';
                            case 'auto': return '🔄 自動';
                            default: return mode;
                          }
                        };
                        
                        return availableModes.map((mode: string) => (
                          <button
                            key={mode}
                            onClick={() => handleTraitControl(device, 'ThermostatMode', 'setMode', { mode: mode.trim() })}
                            disabled={isExecutingCommand}
                            className={`py-2 px-2 rounded-lg text-xs font-medium transition-colors ${
                              currentMode === mode.trim()
                                ? 'bg-cyan-500 text-white'
                                : 'bg-slate-800/50 text-slate-300 hover:bg-cyan-100 hover:bg-cyan-900/30'
                            }`}
                          >
                            {getModeLabel(mode)}
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                )}
                
                {/* Fan Speed - if has FanSpeed trait OR CustomModes with FanSpeed */}
                {(device.traits?.includes('FanSpeed') || device.traits?.includes('CustomModes')) && (
                  <div>
                    <span className="text-sm font-medium text-slate-300 block mb-2">風速</span>
                    {(() => {
                      // Get available speeds from attributes
                      const customModes = device.attributes?.availableCustomModes || [];
                      const fanSpeedMode = customModes.find((m: any) => m.name === 'FanSpeed');
                      const speeds = fanSpeedMode?.settings || ['0', '1', '2', '3'];
                      
                      // ✅ Standard speed mapping (numeric <-> name) for bidirectional conversion
                      const speedMap: Record<string, string> = {
                        '0': 'auto', '1': 'low', '2': 'medium', '3': 'high',
                        'auto': '0', 'low': '1', 'medium': '2', 'med': '2', 'high': '3'
                      };
                      
                      // ✅ Get current fan speed for button comparison
                      // IMPORTANT: Convert to string first - device.fanSpeed may be number (1) or string ("1")
                      // Keep rawSpeed in original format (numeric "0", "1", "2", "3") for button comparison
                      const rawSpeed = String(device.fanSpeed ?? '0');
                      
                      // Build speed name mapping from availableFanSpeeds (API provides the correct mapping)
                      const speedNameMap: Record<string, string> = {};
                      const availableFanSpeeds = device.attributes?.availableFanSpeeds?.speeds || [];
                      availableFanSpeeds.forEach((s: any) => {
                        const speedName = s.speed_name; // 'auto', 'low', 'medium', 'high'
                        const synonyms = s.speed_values?.[0]?.speed_synonym || [];
                        synonyms.forEach((syn: string) => {
                          speedNameMap[syn] = speedName;
                        });
                      });
                      
                      // ✅ Map speed to Chinese label - based on speeds.length
                      const getSpeedLabel = (speed: string): string => {
                        // First check if API provides speed name mapping
                        const apiName = speedNameMap[speed];
                        if (apiName) {
                          switch (apiName.toLowerCase()) {
                            case 'auto': return '自動';
                            case 'low': return '低速';
                            case 'medium': case 'med': return '中速';
                            case 'high': return '高速';
                          }
                        }
                        
                        // Use speeds.length to determine correct mapping
                        // 3-speed fans: 0=低速, 1=中速, 2=高速
                        // 4-speed fans: 0=自動, 1=低速, 2=中速, 3=高速
                        if (speeds.length === 3) {
                          switch (speed) {
                            case '0': return '低速';
                            case '1': return '中速';
                            case '2': return '高速';
                            default: return `速度 ${speed}`;
                          }
                        } else if (speeds.length === 4) {
                          switch (speed) {
                            case '0': return '自動';
                            case '1': return '低速';
                            case '2': return '中速';
                            case '3': return '高速';
                            default: return `速度 ${speed}`;
                          }
                        }
                        
                        // Fallback for other cases
                        return `速度 ${speed}`;
                      };
                      
                      // Dynamic grid columns based on number of speeds
                      const gridCols = speeds.length <= 3 ? 'grid-cols-3' : speeds.length === 4 ? 'grid-cols-4' : 'grid-cols-3';
                      
                      return (
                        <div className={`grid ${gridCols} gap-2`}>
                          {speeds.map((speed: string) => (
                            <button
                              key={speed}
                              onClick={() => handleTraitControl(device, 'Modes', 'setMode', { updateModeSettings: { FanSpeed: speed } })}
                              disabled={isExecutingCommand}
                              className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                                rawSpeed === speed
                                  ? 'bg-cyan-500 text-white'
                                  : 'bg-slate-800/50 text-slate-300 hover:bg-cyan-100 hover:bg-cyan-900/30'
                              }`}
                            >
                              {getSpeedLabel(speed)}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
                
                {/* Custom Toggles - Light, Buzzer, etc. */}
                {device.traits?.includes('CustomToggles') && device.attributes?.availableCustomToggles && (
                  <div>
                    <span className="text-sm font-medium text-slate-300 block mb-2">功能開關</span>
                    <div className="grid grid-cols-2 gap-2">
                      {device.attributes.availableCustomToggles.map((toggle: string) => {
                        const toggleSettings = (device as any).currentToggleSettings || {};
                        const isToggleOn = toggleSettings[toggle] === true;
                        const toggleLabel = toggle === 'Light' ? '💡 燈光' : toggle === 'Buzzer' ? '🔔 蜂鳴器' : toggle;
                        
                        return (
                          <button
                            key={toggle}
                            onClick={() => handleTraitControl(device, 'Toggles', 'setToggle', { toggle, on: !isToggleOn })}
                            disabled={isExecutingCommand}
                            className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                              isToggleOn
                                ? 'bg-cyan-500 text-white'
                                : 'bg-slate-800/50 text-slate-300 hover:bg-cyan-100 hover:bg-cyan-900/30'
                            }`}
                          >
                            {toggleLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Switch/Outlet Controls (fallback - normally handled by dedicated template) */}
            {((device.deviceType as string) === 'switch' || (device.deviceType as string) === 'outlet') && (
              <div className="space-y-4">
                {/* Power Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-300">電源開關</span>
                  <button
                    onClick={async () => {
                      // If merged multi-endpoint, control all endpoints
                      if (isMergedMultiEndpoint && allEndpoints.length > 1) {
                        const newState = isOn ? 'off' : 'on';
                        for (const ep of allEndpoints) {
                          await handleTraitControl(ep, 'OnOff', newState);
                        }
                      } else {
                        handleTraitControl(device, 'OnOff', isOn ? 'off' : 'on');
                      }
                    }}
                    disabled={isExecutingCommand}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                      isOn 
                        ? 'bg-green-500 text-white hover:bg-green-600' 
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {isExecutingCommand ? '...' : isOn ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                {/* Power Meter Info (if available) */}
                {(device.watt !== undefined || device.voltage !== undefined || device.current !== undefined) && (
                  <div className="space-y-3 pt-3 border-t border-slate-700/30">
                    <p className="text-sm font-medium text-slate-300">電力監測</p>
                    <div className="grid grid-cols-2 gap-3">
                      {device.watt !== undefined && (
                        <div className="bg-slate-700/20 bg-slate-800/50 rounded-lg p-3">
                          <p className="text-xs text-slate-400 mb-1">即時功率</p>
                          <p className="text-lg font-bold text-yellow-500">
                            {typeof device.watt === 'number' && device.watt >= 1000
                              ? `${(device.watt / 1000).toFixed(2)} kW`
                              : typeof device.watt === 'number'
                              ? `${device.watt.toFixed(0)} W`
                              : '--'}
                          </p>
                        </div>
                      )}
                      {device.wattHour !== undefined && (
                        <div className="bg-slate-700/20 bg-slate-800/50 rounded-lg p-3">
                          <p className="text-xs text-slate-400 mb-1">累計用電</p>
                          <p className="text-lg font-bold text-blue-500">
                            {(device.wattHour / 1000).toFixed(2)} kWh
                          </p>
                        </div>
                      )}
                      {device.voltage !== undefined && (
                        <div className="bg-slate-700/20 bg-slate-800/50 rounded-lg p-3">
                          <p className="text-xs text-slate-400 mb-1">電壓</p>
                          <p className="text-lg font-semibold text-white">
                            {typeof device.voltage === 'number' ? device.voltage.toFixed(1) : Array.isArray(device.voltage) ? Number(device.voltage[0]).toFixed(1) : '--'} V
                          </p>
                        </div>
                      )}
                      {device.current !== undefined && (
                        <div className="bg-slate-700/20 bg-slate-800/50 rounded-lg p-3">
                          <p className="text-xs text-slate-400 mb-1">電流</p>
                          <p className="text-lg font-semibold text-white">
                            {/* API returns current in mA, convert to A for display */}
                            {typeof device.current === 'number' ? (device.current / 1000).toFixed(2) : Array.isArray(device.current) ? (Number(device.current[0]) / 1000).toFixed(2) : '--'} A
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* 24h Energy Chart for Switch/Outlet with Power Monitoring */}
                    <div className="mt-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-slate-300">過去 24 小時功率趨勢</h4>
                        {isLoadingEnergyChart && (
                          <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                        )}
                      </div>
                      <div className="bg-slate-800/30 rounded-xl p-4">
                        {isLoadingEnergyChart ? (
                          <div className="h-32 flex items-center justify-center">
                            <div className="text-slate-400 text-sm">載入中...</div>
                          </div>
                        ) : energyChartData.length > 0 ? (
                          <div className="h-32">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={energyChartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                  <linearGradient id="switchEnergyGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#eab308" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis 
                                  dataKey="hour" 
                                  stroke="#64748b" 
                                  tick={{ fill: '#94a3b8', fontSize: 9 }}
                                  tickLine={false}
                                  interval="preserveStartEnd"
                                />
                                <YAxis 
                                  stroke="#64748b"
                                  tick={{ fill: '#94a3b8', fontSize: 9 }}
                                  tickLine={false}
                                  tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(1)}k` : `${value}`}
                                />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: '#1e293b',
                                    border: '1px solid #334155',
                                    borderRadius: '8px',
                                    color: '#ffffff'
                                  }}
                                  labelStyle={{ color: '#94a3b8' }}
                                  formatter={(value) => {
                                    if (typeof value !== 'number') return ['--', '功率'];
                                    return [`${value.toFixed(1)} W`, '功率'];
                                  }}
                                />
                                <Area
                                  type="monotone"
                                  dataKey="watt"
                                  stroke="#eab308"
                                  strokeWidth={2}
                                  fill="url(#switchEnergyGradient)"
                                  dot={false}
                                  connectNulls
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div className="h-32 flex items-center justify-center">
                            <div className="text-slate-500 text-sm">暫無歷史數據</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Power Meter Info (Read-only) - fallback */}
            {(device.deviceType as string) === 'power_meter' && (() => {
              const attrs = device.attributes?.["1"] || device.attributes || {};
              const hasThreePhase = attrs.voltage_r !== undefined || attrs.real_power_r !== undefined;
              
              if (hasThreePhase) {
                // Three-phase power meter
                const phases = [
                  { name: 'R', color: 'red', voltage: Number(attrs.voltage_r) || 0, current: Number(attrs.current_r) || 0, power: Number(attrs.real_power_r) || 0 },
                  { name: 'S', color: 'yellow', voltage: Number(attrs.voltage_s) || 0, current: Number(attrs.current_s) || 0, power: Number(attrs.real_power_s) || 0 },
                  { name: 'T', color: 'blue', voltage: Number(attrs.voltage_t) || 0, current: Number(attrs.current_t) || 0, power: Number(attrs.real_power_t) || 0 },
                ];
                const totalPower = Number(attrs.real_power_r || 0) + Number(attrs.real_power_s || 0) + Number(attrs.real_power_t || 0);
                
                return (
                  <div className="space-y-4">
                    {/* Total Power & Energy */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-700/20 bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">總功率</p>
                        <p className="text-xl font-bold text-yellow-500">
                          {totalPower >= 1000 ? `${(totalPower / 1000).toFixed(2)} kW` : `${totalPower.toFixed(0)} W`}
                        </p>
                      </div>
                      <div className="bg-slate-700/20 bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">累計用電</p>
                        <p className="text-xl font-bold text-blue-500">
                          {device.wattHour !== undefined ? `${(device.wattHour / 1000).toFixed(2)} kWh` : '--'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Three Phase Details */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-300">三相數據</p>
                      <div className="grid grid-cols-3 gap-2">
                        {phases.map(phase => (
                          <div key={phase.name} className={`rounded-lg p-3 ${
                            phase.color === 'red' ? 'bg-red-900/20 border border-red-200 border-red-800' :
                            phase.color === 'yellow' ? 'bg-yellow-900/20 border border-yellow-200 border-yellow-800' :
                            'bg-blue-900/20 border border-blue-200 border-blue-800'
                          }`}>
                            <p className={`text-sm font-bold mb-2 ${
                              phase.color === 'red' ? 'text-red-400' :
                              phase.color === 'yellow' ? 'text-yellow-600 text-yellow-400' :
                              'text-blue-400'
                            }`}>{phase.name} 相</p>
                            <div className="space-y-1.5 text-sm">
                              <p className="text-slate-200">
                                <span className="text-slate-400">V:</span> <span className="font-bold">{phase.voltage?.toFixed(1) ?? '--'}</span>
                              </p>
                              <p className="text-slate-200">
                                <span className="text-slate-400">A:</span> <span className="font-bold">{phase.current?.toFixed(2) ?? '--'}</span>
                              </p>
                              <p className="text-slate-200">
                                <span className="text-slate-400">W:</span> <span className="font-bold">{phase.power?.toFixed(0) ?? '--'}</span>
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              } else {
                // Single-phase power meter
                return (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-700/20 bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">即時功率</p>
                        <p className="text-xl font-bold text-yellow-500">
                          {typeof device.watt === 'number' ? (
                            device.watt >= 1000 ? `${(device.watt / 1000).toFixed(2)} kW` : `${device.watt.toFixed(0)} W`
                          ) : '--'}
                        </p>
                      </div>
                      <div className="bg-slate-700/20 bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">累計用電</p>
                        <p className="text-xl font-bold text-blue-500">
                          {device.wattHour !== undefined ? `${(device.wattHour / 1000).toFixed(2)} kWh` : '--'}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-700/20 bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">電壓</p>
                        <p className="text-lg font-semibold text-white">
                          {device.voltage !== undefined 
                            ? `${typeof device.voltage === 'number' ? device.voltage.toFixed(1) : Array.isArray(device.voltage) ? Number(device.voltage[0]).toFixed(1) : '--'} V` 
                            : '--'}
                        </p>
                      </div>
                      <div className="bg-slate-700/20 bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">電流</p>
                        <p className="text-lg font-semibold text-white">
                          {device.current !== undefined 
                            ? `${typeof device.current === 'number' ? device.current.toFixed(2) : Array.isArray(device.current) ? Number(device.current[0]).toFixed(2) : '--'} A` 
                            : '--'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }
            })()}
            
            {/* Power Meter 24h History Chart - fallback */}
            {(device.deviceType as string) === 'power_meter' && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-300">過去 24 小時功率趨勢</h4>
                  {isLoadingEnergyChart && (
                    <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                  )}
                </div>
                <div className="bg-slate-800/30 rounded-xl p-4">
                  {isLoadingEnergyChart ? (
                    <div className="h-40 flex items-center justify-center">
                      <div className="text-slate-400 text-sm">載入中...</div>
                    </div>
                  ) : energyChartData.length > 0 ? (
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={energyChartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="energyModalGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis 
                            dataKey="hour" 
                            stroke="#64748b" 
                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                            tickLine={false}
                            interval="preserveStartEnd"
                          />
                          <YAxis 
                            stroke="#64748b" 
                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                            tickLine={false}
                            domain={[0, 'auto']}
                            tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(1)}k` : value}
                          />
                          <Tooltip
                            contentStyle={{ 
                              backgroundColor: '#1e293b', 
                              border: '1px solid #334155', 
                              borderRadius: '8px',
                              color: '#ffffff'
                            }}
                            labelStyle={{ color: '#94a3b8' }}
                            formatter={(value) => {
                              if (typeof value !== 'number') return ['--', '功率'];
                              const displayValue = value >= 1000
                                ? `${(value / 1000).toFixed(2)} kW`
                                : `${value.toFixed(0)} W`;
                              return [displayValue, '功率'];
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="watt"
                            stroke="#22c55e"
                            strokeWidth={2}
                            fill="url(#energyModalGradient)"
                            dot={false}
                            connectNulls
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-40 flex items-center justify-center">
                      <div className="text-slate-500 text-sm">暫無歷史數據</div>
                    </div>
                  )}
                  {/* Legend */}
                  {energyChartData.length > 0 && (
                    <div className="flex justify-center gap-6 mt-3 text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-0.5 bg-green-500 rounded"></div>
                        <span className="text-slate-400">總功率 (W)</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Sensor Data Display (Data-driven - renders based on available data) */}
            {(() => {
              // Check what sensor data is available
              const hasTemp = typeof device.temperature === 'number';
              const hasHumidity = typeof device.humidity === 'number';
              const hasLux = (device.luxValue !== undefined && device.luxValue !== null) || 
                             (typeof device.lux === 'number');
              const luxDisplayValue = device.luxValue ?? device.lux;
              const hasPresence = device.presence !== undefined || device.occupancy !== undefined;
              const hasMotion = device.motionDetected !== undefined;
              const hasDoor = device.isOpen !== undefined;
              
              // Count how many sensor data types we have
              const sensorCards: React.ReactNode[] = [];
              
              // Temperature card
              if (hasTemp) {
                sensorCards.push(
                  <div key="temp" className="bg-orange-900/20 rounded-xl p-4 text-center">
                    <Thermometer className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                    <p className="text-xs text-slate-400 mb-1">溫度</p>
                    <p className="text-3xl font-bold text-orange-500">
                      {device.temperature!.toFixed(1)}°
                    </p>
                  </div>
                );
              }
              
              // Humidity card
              if (hasHumidity) {
                sensorCards.push(
                  <div key="humidity" className="bg-blue-900/20 rounded-xl p-4 text-center">
                    <Droplets className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-xs text-slate-400 mb-1">濕度</p>
                    <p className="text-3xl font-bold text-blue-500">
                      {device.humidity!.toFixed(0)}%
                    </p>
                  </div>
                );
              }
              
              // Lux card (only if no presence - presence sensors show lux inline)
              if (hasLux && !hasPresence) {
                sensorCards.push(
                  <div key="lux" className="bg-amber-900/20 rounded-xl p-4 text-center">
                    <Sun className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-xs text-slate-400 mb-1">光照度</p>
                    <p className="text-2xl font-bold text-amber-500">
                      {typeof luxDisplayValue === 'number' ? luxDisplayValue.toFixed(0) : '--'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">lux</p>
                  </div>
                );
              }
              
              // Presence/Occupancy card
              if (hasPresence) {
                const isOccupied = device.presence || device.occupancy;
                sensorCards.push(
                  <div key="presence" className={`rounded-xl p-4 text-center ${
                    isOccupied
                      ? 'bg-purple-900/20'
                      : 'bg-slate-700/20 bg-slate-800/50'
                  }`}>
                    <Users className={`w-8 h-8 mx-auto mb-2 ${
                      isOccupied ? 'text-purple-500' : 'text-slate-400'
                    }`} />
                    <p className="text-xs text-slate-400 mb-1">佔用狀態</p>
                    <p className={`text-2xl font-bold ${
                      isOccupied ? 'text-purple-500' : 'text-slate-400'
                    }`}>
                      {isOccupied ? '有人' : '空置'}
                    </p>
                  </div>
                );
                // Add lux alongside presence if available
                if (hasLux) {
                  sensorCards.push(
                    <div key="lux" className="bg-yellow-900/20 rounded-xl p-4 text-center">
                      <Sun className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                      <p className="text-xs text-slate-400 mb-1">光照度</p>
                      <p className="text-2xl font-bold text-yellow-500">
                        {typeof luxDisplayValue === 'number' ? luxDisplayValue.toFixed(0) : '--'}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">lux</p>
                    </div>
                  );
                }
              }
              
              // Motion sensor card
              if (hasMotion && !hasPresence) {
                sensorCards.push(
                  <div key="motion" className={`rounded-xl p-4 text-center ${
                    device.motionDetected
                      ? 'bg-purple-900/20'
                      : 'bg-slate-700/20 bg-slate-800/50'
                  }`}>
                    <span 
                      className={`material-symbols-outlined mx-auto mb-2 block ${
                        device.motionDetected ? 'text-purple-500' : 'text-slate-400'
                      }`}
                      style={{ fontSize: '32px' }}
                    >
                      sensor_occupied
                    </span>
                    <p className="text-xs text-slate-400 mb-1">人員狀態</p>
                    <p className={`text-2xl font-bold ${
                      device.motionDetected ? 'text-purple-500' : 'text-slate-400'
                    }`}>
                      {device.motionDetected ? '有人' : '無人'}
                    </p>
                  </div>
                );
              }
              
              // If we have sensor cards to display
              if (sensorCards.length > 0) {
                const gridCols = sensorCards.length === 1 ? 'grid-cols-1 justify-items-center' : 'grid-cols-2';
                return (
                  <div className={`grid gap-4 ${gridCols}`}>
                    {sensorCards.length === 1 ? (
                      <div className="w-2/3">{sensorCards[0]}</div>
                    ) : (
                      sensorCards
                    )}
                  </div>
                );
              }
              
              return null;
            })()}
            
            {/* TH Sensor 24h History Chart */}
            {device.deviceType === 'th_sensor' && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-300">過去 24 小時趨勢</h4>
                  {isLoadingThSensorChart && (
                    <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                  )}
                </div>
                <div className="bg-slate-800/30 rounded-xl p-4">
                  {isLoadingThSensorChart ? (
                    <div className="h-40 flex items-center justify-center">
                      <div className="text-slate-400 text-sm">載入中...</div>
                    </div>
                  ) : thSensorChartData.length > 0 ? (
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={thSensorChartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="thModalTempGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="thModalHumidityGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis 
                            dataKey="hour" 
                            stroke="#64748b" 
                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                            tickLine={false}
                            interval="preserveStartEnd"
                          />
                          <YAxis 
                            yAxisId="temp"
                            stroke="#64748b" 
                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                            tickLine={false}
                            domain={['auto', 'auto']}
                          />
                          <YAxis 
                            yAxisId="humidity"
                            orientation="right"
                            stroke="#64748b" 
                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                            tickLine={false}
                            domain={[0, 100]}
                            hide
                          />
                          <Tooltip
                            contentStyle={{ 
                              backgroundColor: '#1e293b', 
                              border: '1px solid #334155', 
                              borderRadius: '8px',
                              color: '#ffffff'
                            }}
                            labelStyle={{ color: '#94a3b8' }}
                            formatter={(value, name) => {
                              if (typeof value !== 'number') return ['--', typeof name === 'string' ? name : ''];
                              if (name === 'temp') return [`${value.toFixed(1)}°C`, '溫度'];
                              if (name === 'humidity') return [`${value.toFixed(0)}%`, '濕度'];
                              return [value.toString(), typeof name === 'string' ? name : ''];
                            }}
                          />
                          <Area
                            yAxisId="temp"
                            type="monotone"
                            dataKey="temp"
                            stroke="#f97316"
                            strokeWidth={2}
                            fill="url(#thModalTempGradient)"
                            dot={false}
                            connectNulls
                          />
                          <Area
                            yAxisId="humidity"
                            type="monotone"
                            dataKey="humidity"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            fill="url(#thModalHumidityGradient)"
                            dot={false}
                            connectNulls
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-40 flex items-center justify-center">
                      <div className="text-slate-500 text-sm">暫無歷史數據</div>
                    </div>
                  )}
                  {/* Legend */}
                  {thSensorChartData.length > 0 && (
                    <div className="flex justify-center gap-6 mt-3 text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-0.5 bg-orange-500 rounded"></div>
                        <span className="text-slate-400">溫度</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-0.5 bg-blue-500 rounded"></div>
                        <span className="text-slate-400">濕度</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Lux Sensor 24h History Chart - only when device actually has lux data */}
            {device.lux !== undefined && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-300">過去 24 小時光照度趨勢</h4>
                  {isLoadingLuxChart && (
                    <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                  )}
                </div>
                <div className="bg-slate-800/30 rounded-xl p-4">
                  {isLoadingLuxChart ? (
                    <div className="h-40 flex items-center justify-center">
                      <div className="text-slate-400 text-sm">載入中...</div>
                    </div>
                  ) : luxChartData.length > 0 ? (
                    <div className="h-40">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={luxChartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="luxModalGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#eab308" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#eab308" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis 
                            dataKey="hour" 
                            stroke="#64748b" 
                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                            tickLine={false}
                            interval="preserveStartEnd"
                          />
                          <YAxis 
                            stroke="#64748b" 
                            tick={{ fill: '#94a3b8', fontSize: 10 }}
                            tickLine={false}
                            domain={[0, 'auto']}
                          />
                          <Tooltip
                            contentStyle={{ 
                              backgroundColor: '#1e293b', 
                              border: '1px solid #334155', 
                              borderRadius: '8px',
                              color: '#ffffff'
                            }}
                            labelStyle={{ color: '#94a3b8' }}
                            formatter={(value) => {
                              if (typeof value !== 'number') return ['--', '光照度'];
                              return [`${value.toFixed(0)} lux`, '光照度'];
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="lux"
                            stroke="#eab308"
                            strokeWidth={2}
                            fill="url(#luxModalGradient)"
                            dot={false}
                            connectNulls
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-40 flex items-center justify-center">
                      <div className="text-slate-500 text-sm">暫無歷史數據</div>
                    </div>
                  )}
                  {/* Legend */}
                  {luxChartData.length > 0 && (
                    <div className="flex justify-center gap-6 mt-3 text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-0.5 bg-yellow-500 rounded"></div>
                        <span className="text-slate-400">光照度 (lux)</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Occupancy/Presence 24h History Timeline View */}
            {(device.deviceType === 'presence_sensor' || device.deviceType === 'motion_sensor' || device.occupancy !== undefined || device.presence !== undefined) && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-300">過去 24 小時佔用統計</h4>
                  {isLoadingOccupancyChart && (
                    <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                  )}
                </div>
                <div className="bg-slate-800/30 rounded-xl p-4">
                  {isLoadingOccupancyChart ? (
                    <div className="h-32 flex items-center justify-center">
                      <div className="text-slate-400 text-sm">載入中...</div>
                    </div>
                  ) : occupancyChartData.length > 0 ? (
                    (() => {
                      // Calculate occupied/unoccupied duration
                      const sortedData = [...occupancyChartData].sort((a, b) => a.timestamp - b.timestamp);
                      let occupiedMs = 0;
                      let unoccupiedMs = 0;
                      
                      for (let i = 0; i < sortedData.length - 1; i++) {
                        const duration = sortedData[i + 1].timestamp - sortedData[i].timestamp;
                        if (sortedData[i].value === 1) {
                          occupiedMs += duration;
                        } else {
                          unoccupiedMs += duration;
                        }
                      }
                      
                      const totalMs = occupiedMs + unoccupiedMs;
                      const occupiedPercent = totalMs > 0 ? (occupiedMs / totalMs) * 100 : 0;
                      const unoccupiedPercent = totalMs > 0 ? (unoccupiedMs / totalMs) * 100 : 0;
                      
                      const formatDuration = (ms: number) => {
                        const hours = Math.floor(ms / (1000 * 60 * 60));
                        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
                        if (hours > 0) {
                          return `${hours}小時${minutes > 0 ? ` ${minutes}分` : ''}`;
                        }
                        return `${minutes}分鐘`;
                      };

                      // Build timeline segments for visualization
                      const now = Date.now();
                      const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
                      const timelineSegments: Array<{ start: number; end: number; occupied: boolean }> = [];
                      
                      for (let i = 0; i < sortedData.length - 1; i++) {
                        const segmentStart = Math.max(sortedData[i].timestamp, twentyFourHoursAgo);
                        const segmentEnd = Math.min(sortedData[i + 1].timestamp, now);
                        if (segmentEnd > segmentStart) {
                          timelineSegments.push({
                            start: segmentStart,
                            end: segmentEnd,
                            occupied: sortedData[i].value === 1
                          });
                        }
                      }

                      return (
                        <div className="space-y-4">
                          {/* Statistics Summary */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-green-900/20 rounded-lg p-3 border border-green-800/30">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="text-xs text-slate-400">有人</span>
                              </div>
                              <div className="text-lg font-semibold text-green-400">
                                {formatDuration(occupiedMs)}
                              </div>
                              <div className="text-xs text-slate-500">
                                {occupiedPercent.toFixed(1)}%
                              </div>
                            </div>
                            <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600/30">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 rounded-full bg-slate-500"></div>
                                <span className="text-xs text-slate-400">無人</span>
                              </div>
                              <div className="text-lg font-semibold text-slate-300">
                                {formatDuration(unoccupiedMs)}
                              </div>
                              <div className="text-xs text-slate-500">
                                {unoccupiedPercent.toFixed(1)}%
                              </div>
                            </div>
                          </div>

                          {/* Timeline Bar */}
                          <div className="space-y-2">
                            <div className="text-xs text-slate-500">時間軸</div>
                            <div className="relative h-8 bg-slate-700/50 rounded-lg overflow-hidden">
                              {timelineSegments.map((segment, idx) => {
                                const leftPercent = ((segment.start - twentyFourHoursAgo) / (24 * 60 * 60 * 1000)) * 100;
                                const widthPercent = ((segment.end - segment.start) / (24 * 60 * 60 * 1000)) * 100;
                                return (
                                  <div
                                    key={idx}
                                    className={`absolute top-0 bottom-0 ${segment.occupied ? 'bg-green-500/70' : 'bg-slate-600/50'}`}
                                    style={{
                                      left: `${leftPercent}%`,
                                      width: `${Math.max(widthPercent, 0.5)}%`
                                    }}
                                    title={`${segment.occupied ? '有人' : '無人'}: ${new Date(segment.start).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })} - ${new Date(segment.end).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}`}
                                  />
                                );
                              })}
                            </div>
                            {/* Time labels */}
                            <div className="flex justify-between text-xs text-slate-500">
                              <span>{new Date(twentyFourHoursAgo).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</span>
                              <span>{new Date(twentyFourHoursAgo + 6 * 60 * 60 * 1000).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</span>
                              <span>{new Date(twentyFourHoursAgo + 12 * 60 * 60 * 1000).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</span>
                              <span>{new Date(twentyFourHoursAgo + 18 * 60 * 60 * 1000).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</span>
                              <span>現在</span>
                            </div>
                          </div>

                          {/* Legend */}
                          <div className="flex justify-center gap-6 text-xs">
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 rounded bg-green-500/70"></div>
                              <span className="text-slate-400">有人</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-3 rounded bg-slate-600/50"></div>
                              <span className="text-slate-400">無人</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="h-32 flex items-center justify-center">
                      <div className="text-slate-500 text-sm">暫無歷史數據</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Door Sensor Info (Read-only) - Special handling for door state - fallback */}
            {device.isOpen !== undefined && (device.deviceType as string) === 'door_sensor' && (
              <div className="space-y-4">
                {/* Door Open/Close Status */}
                <div className="grid grid-cols-1 justify-items-center">
                  <div className={`rounded-xl p-4 text-center w-full ${
                    device.isOpen
                      ? 'bg-red-900/20'
                      : 'bg-green-50 bg-green-900/20'
                  }`}>
                    {device.isOpen 
                      ? <DoorOpen className="w-10 h-10 mx-auto mb-2 text-red-500" />
                      : <DoorClosed className="w-10 h-10 mx-auto mb-2 text-green-500" />
                    }
                    <p className="text-xs text-slate-400 mb-1">門窗狀態</p>
                    <p className={`text-3xl font-bold ${
                      device.isOpen 
                        ? 'text-red-500' 
                        : 'text-green-500'
                    }`}>
                      {device.isOpen ? 'OPEN' : 'CLOSE'}
                    </p>
                    <p className="text-sm text-slate-400 mt-2">
                      {device.isOpen ? '門窗已開啟' : '門窗已關閉'}
                    </p>
                  </div>
                </div>

                {/* Door Sensor 24h History Timeline */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-slate-300">過去 24 小時開關統計</h4>
                    {isLoadingDoorSensorChart && (
                      <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                    )}
                  </div>
                  <div className="bg-slate-800/30 rounded-xl p-4">
                    {isLoadingDoorSensorChart ? (
                      <div className="h-32 flex items-center justify-center">
                        <div className="text-slate-400 text-sm">載入中...</div>
                      </div>
                    ) : doorSensorChartData.length > 0 ? (
                      (() => {
                        // Calculate open/closed duration
                        const sortedData = [...doorSensorChartData].sort((a, b) => a.timestamp - b.timestamp);
                        let openMs = 0;
                        let closedMs = 0;
                        
                        for (let i = 0; i < sortedData.length - 1; i++) {
                          const duration = sortedData[i + 1].timestamp - sortedData[i].timestamp;
                          if (sortedData[i].value === 1) {
                            openMs += duration;
                          } else {
                            closedMs += duration;
                          }
                        }
                        
                        const totalMs = openMs + closedMs;
                        const openPercent = totalMs > 0 ? (openMs / totalMs) * 100 : 0;
                        const closedPercent = totalMs > 0 ? (closedMs / totalMs) * 100 : 0;
                        
                        const formatDuration = (ms: number) => {
                          const hours = Math.floor(ms / (1000 * 60 * 60));
                          const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
                          if (hours > 0) {
                            return `${hours}小時${minutes > 0 ? ` ${minutes}分` : ''}`;
                          }
                          return `${minutes}分鐘`;
                        };

                        // Build timeline segments for visualization
                        const now = Date.now();
                        const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
                        const timelineSegments: Array<{ start: number; end: number; isOpen: boolean }> = [];
                        
                        for (let i = 0; i < sortedData.length - 1; i++) {
                          const segmentStart = Math.max(sortedData[i].timestamp, twentyFourHoursAgo);
                          const segmentEnd = Math.min(sortedData[i + 1].timestamp, now);
                          if (segmentEnd > segmentStart) {
                            timelineSegments.push({
                              start: segmentStart,
                              end: segmentEnd,
                              isOpen: sortedData[i].value === 1
                            });
                          }
                        }

                        return (
                          <div className="space-y-4">
                            {/* Statistics Summary */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-red-900/20 rounded-lg p-3 border border-red-800/30">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                  <span className="text-xs text-slate-400">打開</span>
                                </div>
                                <div className="text-lg font-semibold text-red-400">
                                  {formatDuration(openMs)}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {openPercent.toFixed(1)}%
                                </div>
                              </div>
                              <div className="bg-green-900/20 rounded-lg p-3 border border-green-800/30">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                  <span className="text-xs text-slate-400">關閉</span>
                                </div>
                                <div className="text-lg font-semibold text-green-400">
                                  {formatDuration(closedMs)}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {closedPercent.toFixed(1)}%
                                </div>
                              </div>
                            </div>

                            {/* Timeline Bar */}
                            <div className="space-y-2">
                              <div className="text-xs text-slate-500">時間軸</div>
                              <div className="relative h-8 bg-slate-700/50 rounded-lg overflow-hidden">
                                {timelineSegments.map((segment, idx) => {
                                  const leftPercent = ((segment.start - twentyFourHoursAgo) / (24 * 60 * 60 * 1000)) * 100;
                                  const widthPercent = ((segment.end - segment.start) / (24 * 60 * 60 * 1000)) * 100;
                                  return (
                                    <div
                                      key={idx}
                                      className={`absolute top-0 bottom-0 ${segment.isOpen ? 'bg-red-500/70' : 'bg-green-500/70'}`}
                                      style={{
                                        left: `${leftPercent}%`,
                                        width: `${Math.max(widthPercent, 0.5)}%`
                                      }}
                                      title={`${segment.isOpen ? '打開' : '關閉'}: ${new Date(segment.start).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })} - ${new Date(segment.end).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}`}
                                    />
                                  );
                                })}
                              </div>
                              {/* Time labels */}
                              <div className="flex justify-between text-xs text-slate-500">
                                <span>{new Date(twentyFourHoursAgo).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</span>
                                <span>{new Date(twentyFourHoursAgo + 6 * 60 * 60 * 1000).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</span>
                                <span>{new Date(twentyFourHoursAgo + 12 * 60 * 60 * 1000).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</span>
                                <span>{new Date(twentyFourHoursAgo + 18 * 60 * 60 * 1000).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</span>
                                <span>現在</span>
                              </div>
                            </div>

                            {/* Legend */}
                            <div className="flex justify-center gap-6 text-xs">
                              <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded bg-red-500/70"></div>
                                <span className="text-slate-400">打開</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 rounded bg-green-500/70"></div>
                                <span className="text-slate-400">關閉</span>
                              </div>
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="h-32 flex items-center justify-center">
                        <div className="text-slate-500 text-sm">暫無歷史數據</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Curtain/Blind Controls - fallback */}
            {((device.deviceType as string) === 'curtain' || (device.deviceType as string) === 'blind') && (
              <div className="space-y-5">
                {/* Open Percentage Display */}
                <div className={`rounded-xl p-5 text-center ${
                  (device.deviceType as string) === 'curtain' 
                    ? 'bg-teal-900/20' 
                    : 'bg-cyan-900/20'
                }`}>
                  <span 
                    className={`material-symbols-outlined mx-auto mb-2 block ${
                      (device.deviceType as string) === 'curtain' ? 'text-teal-500' : 'text-cyan-500'
                    }`}
                    style={{ fontSize: '48px' }}
                  >
                    {(device.deviceType as string) === 'curtain' ? 'roller_shades' : 'blinds'}
                  </span>
                  <p className={`text-4xl font-bold ${
                    (device.deviceType as string) === 'curtain' ? 'text-teal-500' : 'text-cyan-500'
                  }`}>
                    {sliderDragValue !== null ? sliderDragValue : (device.openPercent ?? 0)}%
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    {(sliderDragValue !== null ? sliderDragValue : (device.openPercent ?? 0)) === 0 ? '全關' : 
                     (sliderDragValue !== null ? sliderDragValue : (device.openPercent ?? 0)) === 100 ? '全開' : '開啟程度'}
                  </p>
                </div>

                {/* Slider Control - debounced: execute on release */}
                <div className="px-2">
                  <div className="flex justify-between text-xs text-slate-400 mb-2">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={sliderDragValue !== null ? sliderDragValue : (device.openPercent ?? 0)}
                    onChange={(e) => {
                      // Update local state only during drag (no API call)
                      const newValue = parseInt(e.target.value);
                      setSliderDragValue(newValue);
                    }}
                    onMouseUp={(e) => {
                      // Execute command on release - read directly from event target
                      // (React state updates are async, so sliderDragValue may not be committed yet)
                      const finalValue = parseInt((e.target as HTMLInputElement).value);
                      if (!isNaN(finalValue)) {
                        handleTraitControl(device, 'OpenClose', 'set', { openPercent: finalValue });
                        setSliderDragValue(null);
                      }
                    }}
                    onTouchEnd={(e) => {
                      // Execute command on touch release (mobile) - read directly from event target
                      const finalValue = parseInt((e.target as HTMLInputElement).value);
                      if (!isNaN(finalValue)) {
                        handleTraitControl(device, 'OpenClose', 'set', { openPercent: finalValue });
                        setSliderDragValue(null);
                      }
                    }}
                    disabled={isExecutingCommand}
                    className="apple-slider w-full h-2 rounded-lg cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, ${(device.deviceType as string) === 'curtain' ? '#14b8a6' : '#06b6d4'} ${sliderDragValue !== null ? sliderDragValue : (device.openPercent ?? 0)}%, #374151 ${sliderDragValue !== null ? sliderDragValue : (device.openPercent ?? 0)}%)`
                    }}
                  />
                </div>

                {/* Quick Buttons */}
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => handleTraitControl(device, 'OpenClose', 'set', { openPercent: 0 })}
                    disabled={isExecutingCommand}
                    className={`py-3 rounded-lg font-medium transition-colors ${
                      device.openPercent === 0
                        ? (device.deviceType as string) === 'curtain'
                          ? 'bg-teal-500 text-white'
                          : 'bg-cyan-500 text-white'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    全關
                  </button>
                  {/* Favorite Button - split: left 70% execute, right 30% set */}
                  <div className="flex rounded-lg overflow-hidden border border-slate-700/50">
                    {/* Left zone: Execute favorite value */}
                    <button
                      onClick={() => {
                        const favoriteValue = deviceFavorites[device.deviceId] ?? 50;
                        handleTraitControl(device, 'OpenClose', 'set', { openPercent: favoriteValue });
                      }}
                      disabled={isExecutingCommand}
                      className={`flex-[7] py-3 font-medium transition-colors ${
                        device.openPercent === (deviceFavorites[device.deviceId] ?? 50)
                          ? (device.deviceType as string) === 'curtain'
                            ? 'bg-teal-500 text-white'
                            : 'bg-cyan-500 text-white'
                          : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'
                      }`}
                      title={`移動到 ${deviceFavorites[device.deviceId] ?? 50}%`}
                    >
                      {deviceFavorites[device.deviceId] ?? 50}%
                    </button>
                    {/* Right zone: Set current position as favorite */}
                    <button
                      onClick={() => {
                        const currentValue = device.openPercent ?? 0;
                        saveFavoriteOpenPercent(device.deviceId, currentValue);
                      }}
                      disabled={isExecutingCommand}
                      className="flex-[3] py-3 transition-colors bg-slate-800 hover:bg-slate-700 border-l border-slate-600"
                      title={`設定 ${device.openPercent ?? 0}% 為我的最愛`}
                    >
                      <span className="material-symbols-outlined text-slate-400 text-slate-300" style={{ fontSize: '18px' }}>
                        push_pin
                      </span>
                    </button>
                  </div>
                  <button
                    onClick={() => handleTraitControl(device, 'OpenClose', 'set', { openPercent: 100 })}
                    disabled={isExecutingCommand}
                    className={`py-3 rounded-lg font-medium transition-colors ${
                      device.openPercent === 100
                        ? (device.deviceType as string) === 'curtain'
                          ? 'bg-teal-500 text-white'
                          : 'bg-cyan-500 text-white'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    全開
                  </button>
                </div>
              </div>
            )}
            
            {/* Dehumidifier Controls - Now handled by DeviceModalRenderer at function start */}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 space-y-2">
            {/* Multi-endpoint device merge toggle */}
            {isMultiEndpointDevice(device) && (() => {
              const baseSN = device.deviceId.split('#')[0];
              const endpoints = getDeviceEndpoints(baseSN);
              const isMerged = mergedDeviceSNs.has(baseSN);
              
              return (
                <div className="flex items-center justify-between py-1 px-2 bg-slate-700/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">
                      多端點設備 ({endpoints.length} 個)
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMergedDevice(baseSN);
                    }}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      isMerged 
                        ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                        : 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {isMerged ? '顯示為個別方塊' : '顯示為單一方塊'}
                  </button>
                </div>
              );
            })()}
            <p className="text-xs text-slate-400">
              {device.location || device.groupName} • {isMergedMultiEndpoint ? baseSN : device.deviceId}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(isMergedMultiEndpoint ? baseSN : device.deviceId);
                }}
                className="ml-1 p-1 rounded hover:bg-slate-700 transition-colors"
                title="複製設備 SN"
              >
                <Copy className="w-3 h-3" />
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  };

  // HVAC Selector Modal
  const openHvacSelector = () => {
    setTempSelectedHvac([...selectedHvac]);
    setShowHvacSelector(true);
  };

  // Exclude Ranking Devices Modal
  const renderExcludeRankingModal = () => {
    if (!showExcludeRankingModal) return null;

    // ✅ 查詢所有有 power meter 能力的設備（與 fetchWeeklyEnergyRanking 一致）
    const devicesWithEnergy = devices.filter(d => {
      const hasMeterData = d.watt !== undefined || d.wattHour !== undefined || 
                          d.voltage !== undefined || d.current !== undefined;
      const isPowerMeter = d.deviceType === 'power_meter' || d.deviceType === 'power_meter_3phase';
      const sp = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
      const hasMeterAttributes = sp.watt || sp.watt_hour || sp.voltage || sp.current || sp.real_power;
      return isPowerMeter || hasMeterData || hasMeterAttributes;
    });

    const toggleExclude = (deviceId: string) => {
      setTempExcludedRankingDevices(prev =>
        prev.includes(deviceId)
          ? prev.filter(id => id !== deviceId)
          : [...prev, deviceId]
      );
    };

    const selectAll = () => {
      setTempExcludedRankingDevices(devicesWithEnergy.map(d => d.deviceId));
    };

    const clearAll = () => {
      setTempExcludedRankingDevices([]);
    };

    const handleSave = () => {
      setExcludedRankingDevices(tempExcludedRankingDevices);
      localStorage.setItem('intellispace-excluded-ranking-devices', JSON.stringify(tempExcludedRankingDevices));
      
      // Save to Firestore
      userPreferencesApi.saveIntelliSpaceSelections({
        excludedRankingDevices: tempExcludedRankingDevices
      }).catch(err => logger.error('Failed to save excluded ranking devices:', err));
      
      setShowExcludeRankingModal(false);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowExcludeRankingModal(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              排除能耗排行設備
            </h3>
            <button onClick={() => setShowExcludeRankingModal(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 border-b border-slate-700/30 bg-slate-800/50 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              全選
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              清除選擇
            </button>
          </div>

          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {devicesWithEnergy.length === 0 ? (
              <p className="text-center text-slate-400 py-4">沒有可用設備</p>
            ) : (
              devicesWithEnergy.map(device => (
                <div
                  key={device.deviceId}
                  onClick={() => toggleExclude(device.deviceId)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    tempExcludedRankingDevices.includes(device.deviceId)
                      ? 'border-yellow-500 bg-yellow-900/20'
                      : 'border-slate-700/50 hover:bg-slate-700/50'
                  }`}
                >
                  {tempExcludedRankingDevices.includes(device.deviceId) ? (
                    <CheckSquare className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{device.deviceName}</p>
                    <p className="text-xs text-slate-400 font-mono">
                      {device.deviceId}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempExcludedRankingDevices.length > 0
                ? `${tempExcludedRankingDevices.length} 設備已排除`
                : '未排除任何設備'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderHvacSelectorModal = () => {
    if (!showHvacSelector) return null;
    
    const hvacDevices = devices.filter(d => d.deviceType === 'hvac');
    
    const toggleHvac = (deviceId: string) => {
      setTempSelectedHvac(prev => 
        prev.includes(deviceId) 
          ? prev.filter(id => id !== deviceId)
          : [...prev, deviceId]
      );
    };
    
    const selectAll = () => {
      setTempSelectedHvac(hvacDevices.map(d => d.deviceId));
    };
    
    const clearAll = () => {
      setTempSelectedHvac([]);
    };

    const handleSave = () => {
      setSelectedHvac(tempSelectedHvac);
      setShowHvacSelector(false);
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowHvacSelector(false)}>
        <div className="bg-[#0C2340] rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-slate-700/30 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Wind className="w-5 h-5 text-cyan-500" />
              {t.intelliSpace?.selectHvac || '選擇空調'}
            </h3>
            <button onClick={() => setShowHvacSelector(false)} className="text-slate-400 hover:text-slate-300 hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 border-b border-slate-700/30 flex justify-between">
            <button onClick={selectAll} className="text-sm text-blue-400">
              {t.intelliSpace?.selectAll || '全選'}
            </button>
            <button onClick={clearAll} className="text-sm text-slate-400 text-slate-400 hover:text-slate-300">
              {t.intelliSpace?.clearSelection || '清除選擇'}
            </button>
          </div>
          
          <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
            {hvacDevices.length === 0 ? (
              <p className="text-center text-slate-400 py-4">{t.intelliSpace?.noHvac || '沒有空調'}</p>
            ) : (
              hvacDevices.map(device => (
                <div
                  key={device.deviceId}
                  onClick={() => toggleHvac(device.deviceId)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    tempSelectedHvac.includes(device.deviceId)
                      ? 'border-cyan-500 bg-cyan-900/20'
                      : 'border-slate-700/50 hover:bg-slate-700/50'
                  }`}
                >
                  {tempSelectedHvac.includes(device.deviceId) ? (
                    <CheckSquare className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{device.deviceName}</p>
                    <p className="text-xs text-slate-400">{device.location || device.deviceId}</p>
                  </div>
                  <div className={`px-2 py-0.5 rounded text-xs font-medium ${
                    device.isOn 
                      ? 'bg-cyan-900/30 text-cyan-400' 
                      : 'bg-slate-700 text-slate-400'
                  }`}>
                    {device.isOn ? 'ON' : 'OFF'}
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="p-4 border-t border-slate-700/30 bg-slate-800/50 flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {tempSelectedHvac.length > 0 
                ? `${tempSelectedHvac.length}/${hvacDevices.length} ${t.common?.selected || 'selected'}`
                : t.intelliSpace?.allHvacIncluded || '包含所有空調'}
            </p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              儲存
            </button>
          </div>
        </div>
      </div>
    );
  };


  const selectedOverviewZones = useMemo(() => {
    const list = selectedOverviewZonesByTab[overviewViewMode] || [];
    return Array.isArray(list) ? list : [];
  }, [selectedOverviewZonesByTab, overviewViewMode]);

  const overviewZones = useMemo(() => {
    if (selectedOverviewZones.length === 0) return [];
    return deviceZones.filter(z => selectedOverviewZones.includes(z.id));
  }, [deviceZones, selectedOverviewZones]);

  const overviewZoneDeviceIds = useMemo(() => {
    const set = new Set<string>();
    overviewZones.forEach(z => {
      z.devices?.forEach(id => set.add(id));
    });
    return set;
  }, [overviewZones]);

  const filterDevicesByOverviewZones = useCallback((list: IntelliSpaceDevice[]) => {
    if (overviewZoneDeviceIds.size === 0 || overviewZones.length === 0) return [];
    return list.filter(d => {
      const candidates = [d.deviceId, d.baseSN, (d as any).deviceSN, (d as any).sn].filter(Boolean) as string[];
      return candidates.some(id => overviewZoneDeviceIds.has(id));
    });
  }, [overviewZoneDeviceIds, overviewZones]);

  const overviewDevices = useMemo(() => filterDevicesByOverviewZones(sortedDevices), [filterDevicesByOverviewZones, sortedDevices]);
  const overviewLightingDevices = useMemo(() => filterDevicesByOverviewZones(devices), [filterDevicesByOverviewZones, devices]);

  // Render Overview View - switches between Energy, Lighting, HVAC, Occupancy, and Blinds views
  const renderOverviewView = () => {
    if (deviceZones.length > 0 && selectedOverviewZones.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center text-slate-400">
            <Settings2 className="w-8 h-8 mx-auto mb-2 text-slate-500" />
            <div className="text-sm">請先選擇要顯示的區域</div>
          </div>
        </div>
      );
    }
    switch (overviewViewMode) {
      case 'adaptive-lighting':
        // Get available power meters for energy optimization - same filter as renderPowerMeterSelectorModal
        const availablePowerMetersForLighting = devices
          .filter(d => {
            const sensorProfile = (d as any).sensorProfile || d.attributes?.sensorProfile || {};
            const hasPowerCapability = sensorProfile.watt || sensorProfile.real_power || sensorProfile.watt_hour;
            const hasPowerData = d.watt !== undefined || d.wattHour !== undefined;
            return hasPowerCapability || hasPowerData;
          })
          .map(d => ({
            id: d.deviceId,
            name: d.deviceName || d.nickname || d.deviceId,
            power: d.watt || d.real_power || 0,
          }));
        
        return (
          <AdaptiveLightingView 
            devices={overviewLightingDevices} 
            zones={overviewZones}
            availablePowerMeters={availablePowerMetersForLighting}
            onOpenZoneSelector={openOverviewZoneSelector}
            selectedZoneCount={selectedOverviewZones.length}
            allDevices={devices}
            energyOptimizationConfig={{
              selectedMeterIds: selectedLightingMeters,
              baselinePower: baselineLightingPower,
              baselineSchedule: baselineLightingSchedule.length === 24 ? baselineLightingSchedule : undefined,
            }}
            onEnergyOptimizationConfigChange={(config) => {
              setSelectedLightingMeters(config.selectedMeterIds);
              setBaselineLightingPower(config.baselinePower);
              if (config.baselineSchedule) {
                setBaselineLightingSchedule(config.baselineSchedule);
                localStorage.setItem('intellispace-baseline-lighting-schedule', JSON.stringify(config.baselineSchedule));
              }
              // Save to localStorage
              localStorage.setItem('intellispace-selected-lighting-meters', JSON.stringify(config.selectedMeterIds));
              localStorage.setItem('intellispace-baseline-lighting-power', config.baselinePower.toString());
            }}
          />
        );
      case 'hvac-control':
        return (
          <HVACControlView 
            devices={overviewDevices} 
            zones={overviewZones}
            climateChartData={climateChartData}
            onOpenZoneSelector={openOverviewZoneSelector}
            selectedZoneCount={selectedOverviewZones.length}
            allDevices={devices}
          />
        );
      case 'occupancy-analytics':
        return (
          <OccupancyAnalyticsView 
            devices={overviewDevices} 
            zones={overviewZones} 
            onOpenZoneSelector={openOverviewZoneSelector}
            selectedZoneCount={selectedOverviewZones.length}
          />
        );
      case 'blind-curtain':
        return (
          <BlindCurtainControlView 
            devices={overviewDevices} 
            zones={overviewZones} 
            onOpenZoneSelector={openOverviewZoneSelector}
            selectedZoneCount={selectedOverviewZones.length}
          />
        );
      case 'energy':
      default:
        return (
          <EnergyView
            devices={devices}
            stats={stats}
            selectedPowerMeters={selectedPowerMeters}
            todayMinuteEnergy={todayMinuteEnergy}
            yesterdayDailyEnergy={yesterdayDailyEnergy}
            powerChartData={powerChartData}
            monthlyChartData={monthlyChartData}
            climateChartData={climateChartData}
            hasClimateChartData={hasClimateChartData}
            hourlyPowerData={hourlyPowerData}
            weeklyEnergyPerDevice={weeklyEnergyPerDevice}
            excludedRankingDevices={excludedRankingDevices}
            selectedThreePhaseMeter={selectedThreePhaseMeter}
            selectedThSensors={selectedThSensors}
            isLoadingChartData={isLoadingChartData}
            isLoadingWeeklyEnergy={isLoadingWeeklyEnergy}
            isTodayEnergyLoading={isTodayEnergyLoading}
            isDebugMode={isDebugMode}
            openPowerMeterSelector={openPowerMeterSelector}
            openHvacMeterSelector={openHvacMeterSelector}
            openLightingMeterSelector={openLightingMeterSelector}
            openThreePhaseMeterSelector={openThreePhaseMeterSelector}
            fetchWeeklyEnergyRanking={fetchWeeklyEnergyRanking}
            setTempExcludedRankingDevices={setTempExcludedRankingDevices}
            setShowExcludeRankingModal={setShowExcludeRankingModal}
            setSelectedDevice={setSelectedDevice}
            t={t}
          />
        );
    }
  };

  // NOTE: Legacy Overview content (energy dashboard) has been moved to EnergyView component
  // See src/components/control-views/EnergyView.tsx
  // Refs for callbacks used in SortableDeviceCard to avoid stale closures
  const handleTraitControlRef = useRef(handleTraitControl);
  const getDeviceIconRef = useRef(getDeviceIcon);
  const getStateTextColorRef = useRef(getStateTextColor);
  const getStateDescriptionRef = useRef(getStateDescription);
  const getDeviceEndpointsRef = useRef(getDeviceEndpoints);
  
  // Keep refs updated when callbacks change
  useEffect(() => {
    handleTraitControlRef.current = handleTraitControl;
  }, [handleTraitControl]);
  
  useEffect(() => {
    getDeviceIconRef.current = getDeviceIcon;
  }, [getDeviceIcon]);
  
  useEffect(() => {
    getStateTextColorRef.current = getStateTextColor;
  }, [getStateTextColor]);
  
  useEffect(() => {
    getDeviceEndpointsRef.current = getDeviceEndpoints;
  }, [getDeviceEndpoints]);
  
  useEffect(() => {
    getStateDescriptionRef.current = getStateDescription;
  }, [getStateDescription]);

  // Sortable Device Card Component - using useMemo + React.memo to prevent unnecessary re-renders
  // Note: This is defined inside the parent to access refs and state setters
  // Using useMemo to create a stable component reference that won't change on parent re-renders
  // This prevents the "flicker" issue when hovering over cards during WebSocket updates
  const SortableDeviceCard = useMemo(() => React.memo(({ device }: { device: IntelliSpaceDevice }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: device.deviceId });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      zIndex: isDragging ? 1000 : 1,
    };

    // Check controllable status - uses trait-based check with device type fallback
    // This enables auto-support for new device types with correct traits
    const deviceTraitsForControl = device.traits || getDefaultTraitsForDeviceType(device.deviceType);
    const isControllable = isControllableByTraits(deviceTraitsForControl) ||
                           device.deviceType === 'hvac' ||
                           device.deviceType === 'switch' ||
                           device.deviceType === 'outlet' ||
                           device.deviceType === 'light' ||
                           device.deviceType === 'fan' ||
                           device.deviceType === 'erv';
    
    // Check if device has OpenClose control (curtains, blinds)
    const hasOpenCloseControl = device.traits?.includes('OpenClose') ||
                                device.deviceType === 'curtain' ||
                                device.deviceType === 'blind';

    // Determine ON/OFF state
    const isDeviceOn = device.status !== 'online' 
      ? false 
      : isControllable 
        ? (device.isOn || device.switchOn || device.power || false)
        : true; // Online sensors = ON
    
    // For presence sensors, calculate occupancy state for icon display
    const isOccupied = device.deviceType === 'presence_sensor' 
      ? Boolean(device.presence || device.occupancy)
      : isDeviceOn;
    
    // State to pass to icon (special handling for different device types)
    // - presence_sensor: use occupancy state
    // - door_sensor: use isOpen state (true = open, false = closed)
    // - curtain/blind (OpenClose): use openPercent or isOpen state
    // - others: use isDeviceOn
    const iconState = device.deviceType === 'presence_sensor' 
      ? isOccupied 
      : device.deviceType === 'door_sensor'
        ? Boolean(device.isOpen)
        : hasOpenCloseControl
          ? (device.openPercent !== undefined ? device.openPercent > 0 : Boolean(device.isOpen))
          : isDeviceOn;

    // Card styling based on ON/OFF state (Apple Home style)
    const getCardStyle = () => {
      if (device.status !== 'online') {
        // Offline - muted with red indicator
        return 'border-transparent';
      }
      if (isDeviceOn) {
        // ON state - bright white/light background (Apple style)
        return 'border-transparent shadow-lg';
      }
      // OFF state - muted translucent dark (Apple style)
      return 'border-transparent';
    };

    // Info area background style with texture - stronger effect
    const getInfoAreaStyle = () => {
      if (device.status !== 'online') {
        return 'bg-gradient-to-br from-gray-800 to-gray-900';
      }
      if (isDeviceOn) {
        // ON state - pure white background
        return 'bg-slate-800/50';
      }
      // OFF state - dark/black background
      return 'bg-gradient-to-br from-gray-800 to-gray-900';
    };

    // Check if device is controllable (has ON/OFF or OpenClose capability)
    const hasOnOffControl = isControllable && device.status === 'online';
    const hasQuickControl = (hasOnOffControl || hasOpenCloseControl) && device.status === 'online';

    // Handle control area click for quick toggle (only if not dragging)
    const handleControlAreaClick = async (e: React.MouseEvent) => {
      e.preventDefault(); // Prevent any default click behavior
      e.stopPropagation();
      // Blur any focused element to prevent scroll-on-focus issues
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      // Don't trigger click if we're dragging
      if (isDragging) return;
      if (!hasQuickControl) return;
      
      // Check if this is a merged multi-endpoint device
      const cardBaseSN = device.deviceId.split('#')[0];
      const isMerged = device.deviceId.includes('#') && mergedDeviceSNs.has(cardBaseSN);
      
      // Handle OpenClose devices (curtains, blinds)
      if (hasOpenCloseControl) {
        const isOpen = device.openPercent !== undefined ? device.openPercent > 0 : device.isOpen;
        if (isMerged) {
          const allEndpoints = getDeviceEndpointsRef.current(cardBaseSN);
          for (const ep of allEndpoints) {
            await handleTraitControlRef.current(ep, 'OpenClose', 'set', { openPercent: isOpen ? 0 : 100 });
          }
        } else {
          handleTraitControlRef.current(device, 'OpenClose', 'set', { openPercent: isOpen ? 0 : 100 });
        }
        return;
      }
      
      // Handle OnOff devices
      const currentState = device.isOn || device.switchOn || device.power;
      const newState = currentState ? 'off' : 'on';
      if (isMerged) {
        const allEndpoints = getDeviceEndpointsRef.current(cardBaseSN);
        for (const ep of allEndpoints) {
          await handleTraitControlRef.current(ep, 'OnOff', newState);
        }
      } else {
        handleTraitControlRef.current(device, 'OnOff', newState);
      }
    };

    // Text color based on ON/OFF state
    const getTextStyle = () => {
      if (isDeviceOn && device.status === 'online') {
        return 'text-white'; // Dark text on white info area
      }
      return 'text-slate-300 text-slate-300'; // Light text on dark background
    };

    const getSubTextStyle = () => {
      if (isDeviceOn && device.status === 'online') {
        return 'text-slate-400'; // Gray sub text on white info area
      }
      return 'text-slate-400';
    };

    // Icon area background style - with subtle texture
    const getIconAreaStyle = () => {
      if (device.status !== 'online') {
        // OFFLINE - dark red
        return 'bg-gradient-to-br from-red-900 to-red-950';
      }
      if (isDeviceOn) {
        // ON state - subtle gray with texture
        return 'bg-gradient-to-br from-gray-100 via-gray-200 to-gray-100 shadow-[inset_0_1px_3px_rgba(0,0,0,0.08)]';
      }
      // OFF state - gray (lighter than info area which is black)
      return 'bg-gradient-to-br from-gray-500 to-gray-600';
    };

    // Get device type label
    const getTypeLabel = () => {
      switch (device.deviceType) {
        case 'hvac': return 'HVAC';
        case 'fan': return 'Fan';
        case 'light': return 'Light';
        case 'th_sensor': return 'TH';
        case 'presence_sensor': return 'Presence';
        case 'power_meter_3phase': return '3P Meter';
        case 'power_meter': return 'Meter';
        case 'switch': return 'Switch';
        case 'outlet': return 'Outlet';
        case 'door_sensor': return 'Door';
        case 'dehumidifier': return 'Dehumid';
        case 'multi_sensor': return 'Multi';
        case 'erv': return 'ERV';
        default: return 'Device';
      }
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        onClick={() => {
          // All device types now use the unified template system
          setSelectedDevice(device);
        }}
        className={`group flex flex-row rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200 ease-out will-change-transform h-[162px] ${getCardStyle()} ${isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''}`}
      >
        {/* Left: Icon Area - 20% drag, 60% icon, 20% TAP hint */}
        <div className={`w-[100px] flex-shrink-0 flex flex-col py-3 transition-all duration-150 ${getIconAreaStyle()}`}>
          {/* Drag Handle (20%) */}
          <div 
            {...listeners}
            {...attributes}
            className="flex-[1] flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-white/5 transition-colors"
          >
            <GripVertical className="w-4 h-4 text-slate-400" />
          </div>
          
          {/* Icon area (60%) - clickable for ON/OFF control */}
          <div 
            onClick={hasQuickControl ? handleControlAreaClick : undefined}
            className={`flex-[3] flex items-center justify-center relative ${
              hasQuickControl 
                ? 'cursor-pointer' 
                : 'cursor-default'
            }`}
          >
            {/* OFF 狀態時顯示兩個 icon，hover 時切換 */}
            {hasQuickControl && !isDeviceOn && device.status === 'online' ? (
              <>
                {/* OFF icon - 正常顯示，hover 時隱藏 */}
                <div className="group-hover:opacity-0 transition-opacity duration-150">
                  {getDeviceIconRef.current(device.deviceType, false, 'large', device)}
                </div>
                {/* ON icon - 正常隱藏，hover 時顯示 */}
                <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  {getDeviceIconRef.current(device.deviceType, true, 'large', device)}
                </div>
              </>
            ) : (
              getDeviceIconRef.current(device.deviceType, iconState, 'large', device)
            )}
          </div>
          
          {/* TAP hint area (20%) */}
          <div className="flex-[1] flex items-center justify-center">
            {hasQuickControl && (
              <span className={`text-[10px] font-bold transition-colors duration-150 ${
                isDeviceOn 
                  ? 'text-slate-800' 
                  : 'text-slate-400 group-hover:text-white group-hover:drop-shadow-[0_0_4px_rgba(255,255,255,0.5)]'
              }`}>
                {hasOpenCloseControl 
                  ? (device.openPercent !== undefined ? (device.openPercent > 0 ? 'TAP CLOSE' : 'TAP OPEN') : (device.isOpen ? 'TAP CLOSE' : 'TAP OPEN'))
                  : (isDeviceOn ? 'TAP OFF' : 'TAP ON')
                }
              </span>
            )}
          </div>
        </div>
        
        {/* Right: Info Area - 3 sections: Name (20%), State (60%), Location+Battery (20%) */}
        <div className={`flex-1 p-3 flex flex-col min-w-0 ${getInfoAreaStyle()}`}>
          {/* Section 1: Device Name (20%) */}
          <div className="flex-[1] flex items-center">
            <h4 className="font-bold truncate text-sm text-white">
            {(() => {
              const cardBaseSN = device.deviceId.split('#')[0];
              const isMerged = device.deviceId.includes('#') && mergedDeviceSNs.has(cardBaseSN);
              return isMerged 
                ? (device.systemDeviceName || device.originalName || cardBaseSN) 
                : (device.nickname || device.deviceName);
            })()}
            </h4>
          </div>
          
          {/* Section 2: State Area (60%) - Two columns layout */}
          <div className="flex-[3] flex items-center gap-3">
            {(() => {
              // Helper function to get state values for each device type
              // Returns number + unit separately for different font sizes
              type StateValue = { num: string; unit?: string; color: string } | null;
              const getDeviceStates = (): { state1: StateValue; state2: StateValue } => {
                const isOnline = device.status === 'online';
                const offlineColor = 'text-slate-400';
                
                // Light: brightness % only (icon shows ON/OFF)
                if (device.deviceType === 'light') {
                  const isOn = device.isOn || device.switchOn || device.power;
                  if (!isOn) {
                    return { state1: null, state2: null };
                  }
                  return {
                    state1: device.brightness !== undefined 
                      ? { num: `${device.brightness}`, unit: '%', color: isOnline ? 'text-orange-500' : offlineColor }
                      : null,
                    state2: null
                  };
                }
                
                // TH Sensor: temperature + humidity
                if (device.deviceType === 'th_sensor' || device.deviceType === 'multi_sensor') {
                  const hasTemp = typeof device.temperature === 'number';
                  const hasHumidity = typeof device.humidity === 'number';
                  return {
                    state1: hasTemp 
                      ? { num: `${device.temperature!.toFixed(1)}`, unit: '°', color: isOnline ? 'text-orange-500' : offlineColor }
                      : null,
                    state2: hasHumidity 
                      ? { num: `${device.humidity!.toFixed(0)}`, unit: '%', color: isOnline ? 'text-teal-500' : offlineColor }
                      : null
                  };
                }
                
                // Power Meter: watt only (including 3-phase)
                if (device.deviceType === 'power_meter' || device.deviceType === 'power_meter_3phase') {
                  const wattNum = typeof device.watt === 'number' ? device.watt : 0;
                  return {
                    state1: wattNum >= 1000 
                      ? { num: `${(wattNum / 1000).toFixed(1)}`, unit: 'kW', color: isOnline ? 'text-orange-500' : offlineColor }
                      : { num: `${wattNum.toFixed(0)}`, unit: 'W', color: isOnline ? 'text-orange-500' : offlineColor },
                    state2: null
                  };
                }
                
                // HVAC: setTemperature + TARGET label (icon shows ON/OFF)
                if (device.deviceType === 'hvac') {
                  const isOn = device.isOn || device.power;
                  if (!isOn) {
                    return { state1: null, state2: null };
                  }
                  return {
                    state1: device.setTemperature !== undefined 
                      ? { num: `${device.setTemperature}`, unit: '°C', color: isOnline ? 'text-cyan-500' : offlineColor }
                      : null,
                    state2: device.setTemperature !== undefined
                      ? { num: '', unit: 'TARGET', color: isOnline ? 'text-slate-400' : offlineColor }
                      : null
                  };
                }
                
                // Switch/Outlet: only show watt if available (icon shows ON/OFF)
                if (device.deviceType === 'switch' || device.deviceType === 'outlet') {
                  const wattNum = typeof device.watt === 'number' ? device.watt : undefined;
                  return {
                    state1: wattNum !== undefined 
                      ? (wattNum >= 1000 
                        ? { num: `${(wattNum / 1000).toFixed(1)}`, unit: 'kW', color: isOnline ? 'text-orange-500' : offlineColor }
                        : { num: `${wattNum.toFixed(0)}`, unit: 'W', color: isOnline ? 'text-orange-500' : offlineColor })
                      : null,
                    state2: null
                  };
                }
                
                // Presence Sensor: only show Lux (icon already shows 有人/無人)
                if (device.deviceType === 'presence_sensor') {
                  const luxValue = device.luxValue ?? device.lux;
                  return {
                    state1: typeof luxValue === 'number' 
                      ? { num: `${luxValue.toFixed(0)}`, unit: 'lux', color: isOnline ? 'text-amber-500' : offlineColor }
                      : null,
                    state2: null
                  };
                }
                
                // Door Sensor: no text needed (icon already shows OPEN/CLOSE)
                if (device.deviceType === 'door_sensor') {
                  return { state1: null, state2: null };
                }
                
                // Fan: only show speed in English (icon already shows running state)
                if (device.deviceType === 'fan') {
                  const isOn = device.isOn || device.power;
                  if (!isOn) {
                    return { state1: null, state2: null };
                  }
                  const speedMap: Record<string, string> = { '0': 'Low', '1': 'Medium', '2': 'High' };
                  return {
                    state1: device.fanSpeed 
                      ? { num: speedMap[device.fanSpeed] || device.fanSpeed, color: isOnline ? 'text-cyan-500' : offlineColor }
                      : null,
                    state2: null
                  };
                }
                
                // Dehumidifier: humidity % (icon shows ON/OFF)
                if (device.deviceType === 'dehumidifier') {
                  const isOn = device.isOn || device.power;
                  const humidity = device.humidityAmbientPercent ?? device.humidity;
                  if (!isOn) {
                    return { state1: null, state2: null };
                  }
                  return {
                    state1: humidity !== undefined 
                      ? { num: `${humidity.toFixed(0)}`, unit: '%', color: isOnline ? 'text-blue-500' : offlineColor }
                      : null,
                    state2: null
                  };
                }
                
                // Button: no text needed (icon is sufficient)
                if (device.deviceType === 'button') {
                  return { state1: null, state2: null };
                }
                
                // Controller/Bridge: no text needed (icon is sufficient)
                if (device.deviceType === 'controller' || device.deviceType === 'bridge') {
                  return { state1: null, state2: null };
                }
                
                // Motion Sensor: no text needed (icon already shows 有人/無人)
                if (device.deviceType === 'motion_sensor') {
                  return { state1: null, state2: null };
                }
                
                // Lux Sensor
                if (device.deviceType === 'lux_sensor') {
                  const luxValue = device.luxValue ?? device.lux;
                  return {
                    state1: typeof luxValue === 'number' 
                      ? { num: `${luxValue.toFixed(0)}`, unit: 'lux', color: isOnline ? 'text-amber-500' : offlineColor }
                      : null,
                    state2: null
                  };
                }
                
                // Default fallback: no text needed (icon shows state)
                return { state1: null, state2: null };
              };
              
              const { state1, state2 } = getDeviceStates();
              
              // Render state with number and unit separately
              // For degree symbol (°, °C), use superscript alignment
              // For text values (non-numeric like Low/Medium/High), use smaller size
              // isPrimary: true for state1 (larger), false for state2 (smaller)
              const renderState = (state: StateValue, isPrimary: boolean) => {
                if (!state) return null;
                const isDegree = state.unit === '°' || state.unit === '°C';
                // Check if num is a pure number or contains numbers
                const isNumeric = /^[\d.,\-]+$/.test(state.num);
                // Primary state: text-5xl for numbers, text-4xl for text
                // Secondary state: text-4xl for numbers, text-3xl for text (one level smaller)
                const numSize = isPrimary 
                  ? (isNumeric ? 'text-5xl' : 'text-4xl')
                  : (isNumeric ? 'text-4xl' : 'text-3xl');
                const unitSize = isPrimary ? 'text-xl' : 'text-lg';
                return (
                  <span className={`font-bold ${state.color}`}>
                    <span className={numSize}>{state.num}</span>
                    {state.unit && (
                      <span className={`${unitSize} ${isDegree ? 'align-top' : ''}`}>{state.unit}</span>
                    )}
                  </span>
                );
              };
              
              return (
                <>
                  {renderState(state1, true)}
                  {renderState(state2, false)}
                </>
              );
            })()}
          </div>
          
          {/* Section 3: Location + Battery (20%) */}
          <div className="flex-[1] flex items-center justify-between">
            <p className={`text-xs truncate ${getSubTextStyle()}`}>
              {device.location || device.groupName || '奧創智慧'}
            </p>
            {/* Battery indicator - only for sensors */}
            {(device.deviceType === 'th_sensor' || 
              device.deviceType === 'presence_sensor' || 
              device.deviceType === 'lux_sensor' ||
              device.deviceType === 'door_sensor' ||
              device.deviceType === 'motion_sensor') && 
              device.battery !== undefined && (
              <div className={`flex items-center gap-0.5 text-xs font-medium flex-shrink-0 ${
                device.battery <= 10 
                  ? 'text-red-500' 
                  : device.battery <= 30 
                  ? 'text-orange-500'
                  : device.battery <= 60
                  ? 'text-yellow-500'
                  : 'text-green-500'
              }`}>
                {device.battery <= 10 ? <BatteryWarning className="w-3.5 h-3.5" /> :
                 device.battery <= 30 ? <BatteryLow className="w-3.5 h-3.5" /> :
                 device.battery <= 60 ? <BatteryMedium className="w-3.5 h-3.5" /> :
                 <BatteryFull className="w-3.5 h-3.5" />}
                {Math.round(device.battery)}%
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Only re-render if device data has actually changed
    const prev = prevProps.device;
    const next = nextProps.device;
    
    // Compare key properties that affect rendering
    // Note: deviceType and traits are needed for isControllable/hasOpenCloseControl logic
    return (
      prev.deviceId === next.deviceId &&
      prev.deviceType === next.deviceType &&
      prev.status === next.status &&
      prev.isOn === next.isOn &&
      prev.switchOn === next.switchOn &&
      prev.power === next.power &&
      prev.watt === next.watt &&
      prev.temperature === next.temperature &&
      prev.humidity === next.humidity &&
      prev.presence === next.presence &&
      prev.occupancy === next.occupancy &&
      prev.luxValue === next.luxValue &&
      prev.mode === next.mode &&
      prev.fanSpeed === next.fanSpeed &&
      prev.setTemperature === next.setTemperature &&
      prev.brightness === next.brightness &&
      prev.colorTemp === next.colorTemp &&
      prev.isOpen === next.isOpen &&
      prev.openPercent === next.openPercent &&
      prev.motionDetected === next.motionDetected &&
      prev.battery === next.battery &&
      prev.deviceName === next.deviceName &&
      // Location/group info displayed on the card
      prev.location === next.location &&
      prev.groupName === next.groupName &&
      // Shallow compare traits array (reference equality is sufficient for most cases)
      prev.traits === next.traits
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [mergedDeviceSNs, setSelectedDevice]);  // Re-create when mergedDeviceSNs changes for proper name display

  // Render Control View - Grid only (other views moved to Overview)
  const renderControlView = () => {
    // Filter control groups for current zone
    let zoneControlGroups = selectedZoneFilter === 'all'
          ? deviceControlGroups
          : deviceControlGroups.filter(g => g.zoneId === selectedZoneFilter);
    
    // Filter control groups by device type when a specific type is selected
    if (sortByDeviceType !== 'all') {
      zoneControlGroups = zoneControlGroups.filter(group => {
        // Check if any device in the group matches the selected device type
        return group.devices.some(deviceSN => {
          const device = devices.find(d => d.deviceId === deviceSN || d.baseSN === deviceSN);
          return device && deviceMatchesType(device, sortByDeviceType);
        });
      });
    }
        
        // Build device status map for groups
        const groupDeviceStatuses: Record<string, any> = {};
        devices.forEach(d => {
          groupDeviceStatuses[d.deviceId] = {
            deviceId: d.deviceId,
            displayName: d.nickname || d.deviceName || d.deviceId,
            isOn: d.isOn,
            online: d.status !== 'offline',
            brightness: d.brightness,
            colorTemp: d.colorTemp,
            temperature: d.setTemperature,
            openPercent: (d as any).openPercent,
            fanSpeed: (d as any).fanSpeed
          };
          // Also map by baseSN for lookup
          if (d.baseSN && d.baseSN !== d.deviceId) {
            groupDeviceStatuses[d.baseSN] = groupDeviceStatuses[d.deviceId];
          }
        });
        
        // Create a map for quick group lookup
        const groupMap = new Map(zoneControlGroups.map(g => [`group:${g.id}`, g]));
        
        // Combine groups and devices into unified list with type info
        const allItems: Array<{ id: string; type: 'group' | 'device'; data: any }> = [
          ...zoneControlGroups.map(g => ({ id: `group:${g.id}`, type: 'group' as const, data: g })),
          ...sortedDevices.map(d => ({ id: d.deviceId, type: 'device' as const, data: d }))
        ];
        
        // Get the order from zone.devices (which should contain both group:xxx and deviceSN)
        const selectedZone = deviceZones.find(z => z.id === selectedZoneFilter);
        const orderList = selectedZone?.devices || [];
        
        // Sort combined items based on the order in zone.devices
        const sortedItems = [...allItems].sort((a, b) => {
          const indexA = orderList.indexOf(a.id);
          const indexB = orderList.indexOf(b.id);
          // Items not in orderList go to the end
          const posA = indexA === -1 ? Infinity : indexA;
          const posB = indexB === -1 ? Infinity : indexB;
          return posA - posB;
        });
        
        const combinedSortableIds = sortedItems.map(item => item.id);
        
        return (
          <div className="space-y-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              autoScroll={false}
            >
              <SortableContext
                items={combinedSortableIds}
                strategy={rectSortingStrategy}
              >
                <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(288px, 1fr))' }}>
                  {/* Render groups and devices in unified sorted order */}
                  {sortedItems.map(item => {
                    if (item.type === 'group') {
                      const group = item.data;
                      return (
                        <SortableDeviceGroupCard
                          key={item.id}
                          id={item.id}
                          group={group}
                          deviceStatuses={groupDeviceStatuses}
                          onClick={(g) => setSelectedControlGroup(g)}
                          onToggle={handleGroupToggle}
                        />
                      );
                    } else {
                      const device = item.data;
                      return (
                        <SortableDeviceCard device={device} key={item.id} />
                      );
                    }
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        );
  };

  // Render Automation View
  const renderAutomationView = () => (
    <div className="text-center py-20">
      <Workflow className="w-16 h-16 text-slate-400 mx-auto mb-4" />
      <p className="text-slate-400 mb-4">
        尚未配置自動化規則
      </p>
      <p className="text-sm text-slate-400 bg-slate-800/50 px-4 py-3 rounded-lg">
        本功能需購買進階功能版本
      </p>
    </div>
  );

  // Main render
  return (
    <div className="h-screen flex bg-[#080c14]">
      {/* App Sidebar */}
      <AppSidebar
        onExpandedChange={setSidebarExpanded}
        onNavigate={handleNavigate}
        onSettingsClick={onSettingsClick}
        activeView={currentView}
      />

      {/* Device Manager Sidebar */}
      {showDeviceManager && (
        <DeviceManagerSidebar
          onDeviceSelectionChange={(selectedSNs) => {
            loadDevices(false, false);
          }}
          onDevicesImported={() => {
            // Refresh dashboard immediately after devices are imported
            logger.log('🔄 [Dashboard] Refreshing after device import...');
            loadDevices(true, false);
          }}
          currentBoard="intelliSpace"
          appSidebarExpanded={sidebarExpanded}
          onZonesChange={(updatedZones) => {
            // Real-time sync: Update Control Page when Zone Management changes order
            setDeviceZones(updatedZones);
          }}
        />
      )}

      {/* Main Content Area */}
      <div
        className="flex-1 flex flex-col"
        onClick={() => {
          if (showDeviceManager) {
            setShowDeviceManager(false);
            setCurrentView('overview');
          }
        }}
      >
        {/* Header */}
        <header className="bg-[#0C2340] shadow-sm border-b border-slate-700/30 flex-shrink-0">
          <div className="w-full h-[64px] px-4 sm:px-6 lg:px-8 flex items-center">
            <div className="flex justify-between items-center w-full">
              <div className="flex items-center space-x-6">
                <h1 className="text-xl font-bold text-white">
                  {t.intelliSpace?.title || 'IntelliSpace'} | {t.intelliSpace?.boardName || 'Smart Space Management'}
                </h1>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* WebSocket Status */}
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  wsConnected && wsSubscribed
                    ? 'bg-green-900/20 text-green-400'
                    : wsConnected
                    ? 'bg-yellow-900/20 text-yellow-400'
                    : 'bg-slate-800/50 text-slate-400'
                }`}>
                  {wsConnected && wsSubscribed ? 'WS Live' : wsConnected ? 'WS Connecting' : 'WS Off'}
                </span>

                {/* Last Refresh */}
                <span className="text-sm text-slate-400">
                  {t.climate?.lastUpdate || 'Last update:'} {lastRefresh.toLocaleTimeString()}
                </span>

                {/* Auto Refresh Toggle */}
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    autoRefresh
                      ? 'bg-green-900/20 text-green-400'
                      : 'bg-slate-800/50 text-slate-300'
                  }`}
                >
                  {autoRefresh ? t.climate?.auto || 'Auto' : t.climate?.manual || 'Manual'}
                </button>

                {/* Alert Center Button */}
                <button
                  onClick={() => setShowAlertCenter(!showAlertCenter)}
                  className={`relative p-2 transition-colors ${
                    alertUnreadCount > 0 
                      ? 'text-yellow-500 hover:text-yellow-600' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  title={t.climate?.alertCenter || 'Alert Center'}
                >
                  {alertUnreadCount > 0 ? (
                    <BellRing className="w-5 h-5 fill-yellow-500 animate-pulse" />
                  ) : (
                    <Bell className="w-5 h-5" />
                  )}
                </button>

                {/* Refresh Button */}
                <button
                  onClick={() => loadDevices(true, true)}
                  disabled={isRefreshing}
                  className="p-2 text-slate-400 text-slate-400 hover:text-slate-300 hover:text-slate-200 transition-colors"
                >
                  <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Stats Bar - Group Selector and Sort */}
        <div className="bg-[#0C2340] border-b border-slate-700/30 px-4 sm:px-6 lg:px-8 h-14 flex-shrink-0">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-4">
              {/* View Switcher - show on overview page */}
              {currentView === 'overview' && (
                <ViewSwitcher
                  value={overviewViewMode}
                  onChange={setOverviewViewMode}
                />
              )}

              {/* ✅ 暫時隱藏 Energy tab 的區域選擇器 */}
              {/* {currentView === 'overview' && overviewViewMode === 'energy' && (
                <button
                  onClick={openOverviewZoneSelector}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
                  title="選擇區域"
                >
                  <Settings2 className="w-4 h-4" />
                  <span className="text-sm">區域</span>
                  {selectedOverviewZones.length > 0 && (
                    <span className="text-xs text-slate-500">{selectedOverviewZones.length}</span>
                  )}
                </button>
              )} */}
              
              {/* Zone Selector - show only on control page */}
              {currentView === 'control' && (
                <ZoneSelector
                  value={selectedZoneFilter}
                  onChange={setSelectedZoneFilter}
                  zones={deviceZones}
                  pinnedZone={pinnedZone}
                  onPinToggle={handlePinZone}
                />
              )}
              
              {/* Overview Mini Stats Bar - content changes based on active view */}
              {currentView === 'overview' && (
                <div className="flex items-center gap-4 overflow-x-auto">
                  {/* Energy View Stats */}
                  {overviewViewMode === 'energy' && (
                    <>
                      <div className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:-translate-y-0.5 transition-transform" onClick={openPowerMeterSelector}>
                        <Zap className="w-5 h-5 text-yellow-500" />
                        <span className="text-sm text-slate-400">今日</span>
                        <span className="text-base font-semibold text-white">
                          {selectedPowerMeters.length > 0 ? `${todayMinuteEnergy.toFixed(2)} kWh` : '--'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:-translate-y-0.5 transition-transform" onClick={openPowerMeterSelector}>
                        <Activity className="w-5 h-5 text-cyan-500" />
                        <span className="text-sm text-slate-400">即時</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            const power = selectedPowerMeters.length > 0
                              ? devices.filter(d => selectedPowerMeters.includes(d.deviceId)).reduce((sum, d) => sum + (d.watt || 0), 0)
                              : stats.totalPower;
                            return power >= 1000 ? `${(power / 1000).toFixed(2)} kW` : `${power.toFixed(0)} W`;
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5">
                        <TrendingUp className="w-5 h-5 text-orange-500" />
                        <span className="text-sm text-slate-400">峰值</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            const maxPower = hourlyPowerData.reduce((max, d) => Math.max(max, d.power || 0), 0);
                            return maxPower >= 1000 ? `${(maxPower / 1000).toFixed(2)} kW` : `${maxPower.toFixed(0)} W`;
                          })()}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Lighting View Stats */}
                  {overviewViewMode === 'adaptive-lighting' && (
                    <>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={openLightingMeterSelector}
                        title="點擊選擇電錶"
                      >
                        <Zap className="w-5 h-5 text-yellow-500" />
                        <span className="text-sm text-slate-400">總功率</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            // Use selected lighting meters if available, otherwise sum all light device watt
                            const lightPower = selectedLightingMeters.length > 0
                              ? devices.filter(d => selectedLightingMeters.includes(d.deviceId)).reduce((sum, d) => sum + (d.watt || 0), 0)
                              : devices.filter(d => d.deviceType === 'light').reduce((sum, d) => sum + (d.watt || 0), 0);
                            return lightPower >= 1000 ? `${(lightPower / 1000).toFixed(2)} kW` : `${lightPower.toFixed(0)} W`;
                          })()}
                        </span>
                      </div>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5"
                        title="節能百分比 (在 Energy Optimization 設定)"
                      >
                        <TrendingDown className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-slate-400">節能</span>
                        <span className="text-base font-semibold text-green-400">
                          {(() => {
                            // Calculate energy saving based on baseline power
                            if (baselineLightingPower > 0) {
                              // Use selected lighting meters for current power
                              const currentPower = selectedLightingMeters.length > 0
                                ? devices.filter(d => selectedLightingMeters.includes(d.deviceId)).reduce((sum, d) => sum + (d.watt || 0), 0)
                                : devices.filter(d => d.deviceType === 'light').reduce((sum, d) => sum + (d.watt || 0), 0);
                              const savingPct = Math.round(((baselineLightingPower - currentPower) / baselineLightingPower) * 100);
                              return `${savingPct >= 0 ? '-' : '+'}${Math.abs(savingPct)}%`;
                            }
                            // Fallback: calculate based on lights on/off ratio
                            const targetLights = selectedLights.length > 0
                              ? devices.filter(d => selectedLights.includes(d.deviceId))
                              : devices.filter(d => d.deviceType === 'light');
                            const lightsOn = targetLights.filter(d => d.isOn).length;
                            const totalLights = targetLights.length;
                            const savingPct = totalLights > 0 ? Math.round((1 - lightsOn / totalLights) * 100) : 0;
                            return `${savingPct > 0 ? '-' : ''}${savingPct}%`;
                          })()}
                        </span>
                      </div>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={() => {
                          setTempSelectedLuxSensors(selectedLuxSensors);
                          setShowLuxSensorSelector(true);
                        }}
                        title="點擊選擇照度感測器"
                      >
                        <Sun className="w-5 h-5 text-amber-500" />
                        <span className="text-sm text-slate-400">平均照度</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            // Use selected lux sensors if available
                            const luxSensors = selectedLuxSensors.length > 0
                              ? devices.filter(d => selectedLuxSensors.includes(d.deviceId))
                              : devices.filter(d => d.luxValue !== undefined || d.lux !== undefined);
                            const avgLux = luxSensors.length > 0 
                              ? luxSensors.reduce((sum, d) => sum + (d.luxValue || d.lux || 0), 0) / luxSensors.length
                              : 0;
                            return `${Math.round(avgLux)} lux`;
                          })()}
                        </span>
                      </div>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={() => setShowLightSelector(true)}
                        title="點擊選擇燈具"
                      >
                        <Lightbulb className="w-5 h-5 text-yellow-400" />
                        <span className="text-sm text-slate-400">燈具</span>
                        <span className="text-base font-semibold text-white">
                          {selectedLights.length > 0 ? selectedLights.length : devices.filter(d => d.deviceType === 'light').length}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5">
                        <Clock className="w-5 h-5 text-purple-500" />
                        <span className="text-sm text-slate-400">時段</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            const hour = new Date().getHours();
                            if (hour >= 6 && hour < 12) return 'Morning';
                            if (hour >= 12 && hour < 18) return 'Afternoon';
                            if (hour >= 18 && hour < 22) return 'Evening';
                            return 'Night';
                          })()}
                        </span>
                      </div>
                    </>
                  )}

                  {/* HVAC View Stats */}
                  {overviewViewMode === 'hvac-control' && (
                    <>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={openHvacMeterSelector}
                        title="點擊選擇電錶"
                      >
                        <Zap className="w-5 h-5 text-yellow-500" />
                        <span className="text-sm text-slate-400">總功率</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            // Use selected HVAC meters if available
                            const hvacPower = selectedHvacMeters.length > 0
                              ? devices.filter(d => selectedHvacMeters.includes(d.deviceId)).reduce((sum, d) => sum + (d.watt || 0), 0)
                              : devices.filter(d => d.deviceType === 'hvac').reduce((sum, d) => sum + (d.watt || 0), 0);
                            return hvacPower >= 1000 ? `${(hvacPower / 1000).toFixed(2)} kW` : `${hvacPower.toFixed(0)} W`;
                          })()}
                        </span>
                      </div>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={() => setShowHvacSelector(true)}
                        title="點擊選擇冷氣/空調"
                      >
                        <Wind className="w-5 h-5 text-cyan-500" />
                        <span className="text-sm text-slate-400">運行中</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            // Use selected HVACs if available
                            const targetHvacs = selectedHvac.length > 0
                              ? devices.filter(d => selectedHvac.includes(d.deviceId))
                              : devices.filter(d => d.deviceType === 'hvac');
                            const onCount = targetHvacs.filter(d => d.isOn).length;
                            return `${onCount}/${targetHvacs.length}`;
                          })()}
                        </span>
                      </div>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={() => setShowHvacSelector(true)}
                        title="點擊選擇冷氣/空調"
                      >
                        <Thermometer className="w-5 h-5 text-orange-500" />
                        <span className="text-sm text-slate-400">平均設定</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            // Use selected HVACs if available
                            const targetHvacs = selectedHvac.length > 0
                              ? devices.filter(d => selectedHvac.includes(d.deviceId) && d.isOn && (d.setTemperature || d.thermostatTemperatureSetpoint))
                              : devices.filter(d => d.deviceType === 'hvac' && d.isOn && (d.setTemperature || d.thermostatTemperatureSetpoint));
                            const avgTemp = targetHvacs.length > 0 
                              ? targetHvacs.reduce((sum, d) => sum + (d.setTemperature || d.thermostatTemperatureSetpoint || 0), 0) / targetHvacs.length
                              : 0;
                            return targetHvacs.length > 0 ? `${avgTemp.toFixed(0)}°C` : '--';
                          })()}
                        </span>
                      </div>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={() => setShowHvacSelector(true)}
                        title="點擊選擇冷氣/空調"
                      >
                        <Settings2 className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-400">模式</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            // Use selected HVACs if available
                            const targetHvacs = selectedHvac.length > 0
                              ? devices.filter(d => selectedHvac.includes(d.deviceId) && d.isOn)
                              : devices.filter(d => d.deviceType === 'hvac' && d.isOn);
                            const modes = targetHvacs.map(d => d.mode || d.thermostatMode).filter(Boolean);
                            const coolCount = modes.filter(m => m?.toLowerCase().includes('cool')).length;
                            const heatCount = modes.filter(m => m?.toLowerCase().includes('heat')).length;
                            if (coolCount > heatCount) return 'Cool';
                            if (heatCount > coolCount) return 'Heat';
                            return modes[0] || '--';
                          })()}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Occupancy View Stats */}
                  {overviewViewMode === 'occupancy-analytics' && (
                    <>
                      <div className="flex items-center gap-2 px-3 py-1.5">
                        <Users className="w-5 h-5 text-purple-500" />
                        <span className="text-sm text-slate-400">佔用區域</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            const presenceMapRaw = localStorage.getItem('occupancy-zone-presence-sensors');
                            const presenceMap = presenceMapRaw ? JSON.parse(presenceMapRaw) : {};
                            const sensors = overviewDevices.filter(d =>
                              d.deviceType === 'presence_sensor' ||
                              d.deviceType === 'motion_sensor' ||
                              d.traits?.some(t => ['OccupancySensing', 'MotionSensing'].includes(t))
                            );
                            const zoneStats = overviewZones.map(zone => {
                              const mapped = presenceMap?.[zone.id] || [];
                              const zoneSensors = mapped.length > 0
                                ? sensors.filter(d => mapped.includes(d.deviceId) || mapped.includes(d.baseSN))
                                : [];
                              const occupied = zoneSensors.some(d =>
                                Boolean(
                                  d.occupancy ??
                                  d.presence ??
                                  (d as any).occupied ??
                                  d.attributes?.values?.occupancy?.value ??
                                  d.attributes?.values?.presence?.value ??
                                  d.attributes?.occupancy ??
                                  d.attributes?.presence ??
                                  (d as any).motionDetected ??
                                  (d as any).detected ??
                                  d.isOn
                                )
                              );
                              return { occupied };
                            });
                            const total = overviewZones.length;
                            const occupiedCount = zoneStats.filter(z => z.occupied).length;
                            return `${occupiedCount}/${total}`;
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5">
                        <Activity className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-slate-400">偵測器</span>
                        <span className="text-base font-semibold text-white">
                          {overviewDevices.filter(d =>
                            d.deviceType === 'presence_sensor' ||
                            d.deviceType === 'motion_sensor' ||
                            d.traits?.some(t => ['OccupancySensing', 'MotionSensing'].includes(t))
                          ).length}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5">
                        <Clock className="w-5 h-5 text-amber-500" />
                        <span className="text-sm text-slate-400">今日高峰</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            try {
                              const presenceMapRaw = localStorage.getItem('occupancy-zone-presence-sensors');
                              const presenceMap = presenceMapRaw ? JSON.parse(presenceMapRaw) : {};
                              const sensors = overviewDevices.filter(d =>
                                d.deviceType === 'presence_sensor' ||
                                d.deviceType === 'motion_sensor' ||
                                d.traits?.some(t => ['OccupancySensing', 'MotionSensing'].includes(t))
                              );
                              const zoneStats = overviewZones.map(zone => {
                                const mapped = presenceMap?.[zone.id] || [];
                                const zoneSensors = mapped.length > 0
                                  ? sensors.filter(d => mapped.includes(d.deviceId) || mapped.includes(d.baseSN))
                                  : [];
                                const occupied = zoneSensors.some(d =>
                                  Boolean(
                                    d.occupancy ??
                                    d.presence ??
                                    (d as any).occupied ??
                                    d.attributes?.values?.occupancy?.value ??
                                    d.attributes?.values?.presence?.value ??
                                    d.attributes?.occupancy ??
                                    d.attributes?.presence ??
                                    (d as any).motionDetected ??
                                    (d as any).detected ??
                                    d.isOn
                                  )
                                );
                                return { occupied };
                              });
                              const total = overviewZones.length;
                              const occupiedCount = zoneStats.filter(z => z.occupied).length;
                              const currentPercent = total > 0 ? Math.round((occupiedCount / total) * 100) : null;

                              const raw = localStorage.getItem('occupancy-analytics-cache');
                              const cached = raw ? JSON.parse(raw) : null;
                              const hourlyData = cached?.hourlyData || [];
                              const peak = hourlyData.reduce((best: any, cur: any) => {
                                if (typeof cur?.presenceRate !== 'number') return best;
                                if (!best || cur.presenceRate > best.presenceRate) return cur;
                                return best;
                              }, null);
                              const peakPercent = typeof peak?.presenceRate === 'number' ? peak.presenceRate : null;

                              if (currentPercent === null && peakPercent === null) return '--';
                              if (peakPercent !== null && (currentPercent === null || peakPercent >= currentPercent)) {
                                return `${peak.time} · ${peakPercent}%`;
                              }
                              return `現在 · ${currentPercent}%`;
                            } catch {
                              return '--';
                            }
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5">
                        <BarChart3 className="w-5 h-5 text-blue-500" />
                        <span className="text-sm text-slate-400">平均佔用率</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            // Use selected presence sensors if available
                            const targetSensors = selectedPresenceSensors.length > 0
                              ? devices.filter(d => selectedPresenceSensors.includes(d.deviceId))
                              : devices.filter(d => d.deviceType === 'presence_sensor');
                            const occupied = targetSensors.filter(d => d.presence || d.occupancy).length;
                            return targetSensors.length > 0 
                              ? `${Math.round((occupied / targetSensors.length) * 100)}%` 
                              : '--';
                          })()}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Blinds View Stats */}
                  {overviewViewMode === 'blind-curtain' && (
                    <>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={() => setShowBlindCurtainSelector(true)}
                        title="點擊選擇窗簾/捲簾"
                      >
                        <Blinds className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-slate-400">窗簾</span>
                        <span className="text-base font-semibold text-white">
                          {devices.filter(d => d.deviceType === 'curtain' || d.deviceType === 'blind').length}
                        </span>
                      </div>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={() => setShowBlindCurtainSelector(true)}
                        title="點擊選擇窗簾/捲簾"
                      >
                        <Activity className="w-5 h-5 text-cyan-500" />
                        <span className="text-sm text-slate-400">平均開啟</span>
                        <span className="text-base font-semibold text-white">
                          {(() => {
                            const blinds = devices.filter(d => (d.deviceType === 'curtain' || d.deviceType === 'blind') && d.openPercent !== undefined);
                            const avgOpen = blinds.length > 0 
                              ? blinds.reduce((sum, d) => sum + (d.openPercent || 0), 0) / blinds.length
                              : 0;
                            return `${Math.round(avgOpen)}%`;
                          })()}
                        </span>
                      </div>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={() => setShowBlindCurtainSelector(true)}
                        title="點擊選擇窗簾/捲簾"
                      >
                        <ArrowUp className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-slate-400">全開</span>
                        <span className="text-base font-semibold text-white">
                          {devices.filter(d => (d.deviceType === 'curtain' || d.deviceType === 'blind') && d.openPercent === 100).length}
                        </span>
                      </div>
                      <div 
                        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors"
                        onClick={() => setShowBlindCurtainSelector(true)}
                        title="點擊選擇窗簾/捲簾"
                      >
                        <ArrowDown className="w-5 h-5 text-red-500" />
                        <span className="text-sm text-slate-400">全關</span>
                        <span className="text-base font-semibold text-white">
                          {devices.filter(d => (d.deviceType === 'curtain' || d.deviceType === 'blind') && d.openPercent === 0).length}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Device Type Sort/Filter - Only show on Control page */}
              {currentView === 'control' && (
                <select
                  value={sortByDeviceType}
                  onChange={(e) => setSortByDeviceType(e.target.value)}
                  className="min-w-[220px] px-3 py-1.5 text-sm border border-slate-700/50 rounded-md bg-[#0C2340] text-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="all">All Types</option>
                  <option value="ac">AC</option>
                  <option value="blind">Blind (捲簾)</option>
                  <option value="button">Button</option>
                  <option value="curtain">Curtain (窗簾)</option>
                  <option value="door">Door</option>
                  <option value="fan">Fan</option>
                  <option value="heater">Heater (地熱)</option>
                  <option value="light">Light</option>
                  <option value="lux">Lux</option>
                  <option value="meter">Meter</option>
                  <option value="motion">Motion</option>
                  <option value="outlet">Outlet</option>
                  <option value="presence">Presence</option>
                  <option value="switch">Switch</option>
                  <option value="th_sensor">TH Sensor</option>
                </select>
              )}
              
              {/* Alerts page title */}
              {currentView === 'alerts' && (
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-500" />
                  {t.alerts?.alertManagement || '警報管理'}
                </h2>
              )}

              {/* Device Logs page title */}
              {currentView === 'device-logs' && (
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Server className="w-5 h-5 text-indigo-500" />
                  {t.sidebar?.deviceLogs || '設備日誌'}
                </h2>
              )}

              {/* Generate Report page title */}
              {currentView === 'generate-report' && (
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-orange-500" />
                  {t.sidebar?.generateReport || '報表產生'}
                </h2>
              )}
              
              {/* Automation page title */}
              {currentView === 'automation' && (
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Workflow className="w-5 h-5 text-purple-500" />
                  {t.intelliSpace?.automationTitle || '自動化'}
                </h2>
              )}
            </div>
            
            {/* Alerts page - New Alert button */}
            {currentView === 'alerts' && (
              <button
                onClick={() => {
                  const event = new CustomEvent('openCreateAlert');
                  window.dispatchEvent(event);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                {t.alerts?.newAlert || '新增警報'}
              </button>
            )}
            
            {/* Automation page - Create Rule button */}
            {currentView === 'automation' && (
              <button
                disabled
                className="flex items-center gap-2 px-4 py-2 bg-slate-500 text-white rounded-lg cursor-not-allowed text-sm opacity-60"
              >
                <Plus className="w-4 h-4" />
                建立規則
              </button>
            )}
            
            {/* Create Device Group Button - only show on control page when a specific zone is selected */}
            {currentView === 'control' && selectedZoneFilter !== 'all' && (
              <button
                onClick={() => setShowCreateGroupModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600/20 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-600/30 hover:text-blue-300 transition-colors text-sm"
                title="創建群組控制"
              >
                <LayersIcon className="w-4 h-4" />
                <span className="hidden sm:inline">創建群組</span>
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {isLoading && devices.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-slate-400 animate-spin mx-auto mb-4" />
                <p className="text-slate-400">{t.intelliSpace?.loading || 'Loading devices...'}</p>
              </div>
            </div>
          ) : error || devices.length === 0 ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center max-w-lg mx-auto px-6">
                {/* Animated Icon Container */}
                <div className="relative mb-10">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-blue-500/20 bg-blue-500/10 rounded-full blur-3xl scale-150" />
                  
                  {/* Icon box */}
                  <div className="relative inline-flex items-center justify-center w-28 h-28 bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-3xl border border-blue-200 border-slate-700/50 shadow-xl shadow-2xl backdrop-blur-sm">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 from-blue-500/5 to-cyan-500/5 rounded-3xl" />
                    <Server className="w-12 h-12 text-blue-400 relative z-10" />
                  </div>
                </div>
                
                {/* Title */}
                <h3 className="text-2xl font-semibold text-white mb-4">
                  {t.intelliSpace?.welcomeTitle || '歡迎使用 IntelliSpace'}
                </h3>
                
                {/* Description */}
                <p className="text-slate-400 mb-8 leading-relaxed text-base whitespace-pre-line">
                  {t.intelliSpace?.noDevicesDescription || '您尚未匯入任何設備。\n請前往設備管理器選擇並匯入您想要監控的設備。'}
                </p>
                
                {/* Feature Hints - Unified blue theme */}
                <div className="grid grid-cols-3 gap-3 mb-10">
                  <div className="group bg-gradient-to-b from-slate-800/60 to-slate-800/30 hover:from-slate-700/60 hover:to-slate-800/40 rounded-2xl p-5 border border-slate-700/40 hover:border-blue-500/30 transition-all duration-300 shadow-sm hover:shadow-md">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Thermometer className="w-5 h-5 text-blue-400" />
                    </div>
                    <p className="text-xs font-medium text-slate-400 group-hover:text-blue-700 group-hover:text-slate-200 transition-colors">{t.intelliSpace?.featureClimate || '溫濕度監控'}</p>
                  </div>
                  <div className="group bg-gradient-to-b from-slate-800/60 to-slate-800/30 hover:from-slate-700/60 hover:to-slate-800/40 rounded-2xl p-5 border border-slate-700/40 hover:border-cyan-500/30 transition-all duration-300 shadow-sm hover:shadow-md">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Zap className="w-5 h-5 text-cyan-400" />
                    </div>
                    <p className="text-xs font-medium text-slate-400 group-hover:text-cyan-700 group-hover:text-slate-200 transition-colors">{t.intelliSpace?.featurePower || '用電分析'}</p>
                  </div>
                  <div className="group bg-gradient-to-b from-slate-800/60 to-slate-800/30 hover:from-slate-700/60 hover:to-slate-800/40 rounded-2xl p-5 border border-slate-700/40 hover:border-indigo-500/30 transition-all duration-300 shadow-sm hover:shadow-md">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-gradient-to-br from-indigo-500/20 to-indigo-600/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Wind className="w-5 h-5 text-indigo-400" />
                    </div>
                    <p className="text-xs font-medium text-slate-400 group-hover:text-indigo-700 group-hover:text-slate-200 transition-colors">{t.intelliSpace?.featureHvac || '空調控制'}</p>
                  </div>
                </div>
                
                {/* Action Button */}
                <button
                  onClick={() => { setCurrentView('device-manager'); setShowDeviceManager(true); }}
                  className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 text-white font-medium rounded-xl hover:from-blue-500 hover:via-blue-400 hover:to-cyan-400 transition-all duration-300 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Server className="w-5 h-5" />
                  {t.intelliSpace?.goToDeviceManager || '前往設備管理器'}
                </button>
                
                {/* Secondary Action */}
                <p className="mt-5 text-sm text-slate-400">
                  {t.intelliSpace?.or || '或'}
                  <button
                    onClick={() => loadDevices(true, true)}
                    className="text-blue-400 hover:text-cyan-400 ml-1.5 underline underline-offset-4 decoration-blue-300 decoration-blue-400/30 hover:decoration-cyan-400 hover:decoration-cyan-400/50 transition-colors"
                  >
                    {t.intelliSpace?.refreshDevices || '重新整理'}
                  </button>
                </p>
              </div>
            </div>
          ) : (
            <>
              {currentView === 'overview' && (
                <div className="min-h-full flex flex-col">
                  {renderOverviewView()}
                </div>
              )}
              {currentView === 'control' && renderControlView()}
              {currentView === 'device-manager' && !showDeviceManager && (
                <div className="flex items-center justify-center py-20">
                  <button
                    onClick={() => setShowDeviceManager(true)}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {t.intelliSpace?.openDeviceManager || 'Open Device Manager'}
                  </button>
                </div>
              )}
              {currentView === 'automation' && renderAutomationView()}
              {currentView === 'alerts' && (
                <AlertsPage onBack={() => setCurrentView('overview')} />
              )}
              {currentView === 'device-logs' && (
                <DeviceLogsPage devices={devices.map(d => ({
                  sn: d.baseSN,  // Use baseSN, not deviceId - backend needs base SN without endpoint suffix
                  deviceId: d.deviceId,
                  name: d.deviceName,
                  type: d.deviceType
                }))} />
              )}
              {currentView === 'generate-report' && (
                <GenerateReportPage />
              )}
            </>
          )}
        </div>

        {/* Alert Center Panel */}
        {showAlertCenter && (
          <>
            {/* Backdrop - click to close */}
            <div 
              className="fixed inset-0 z-40"
              onClick={() => setShowAlertCenter(false)}
            />
            <div 
              className="fixed top-16 right-4 w-96 max-h-[600px] bg-[#0C2340] rounded-lg shadow-2xl border border-slate-700/30 z-50 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4">
                <AlertCenter />
              </div>
              <button
                onClick={() => setShowAlertCenter(false)}
                className="absolute top-5 right-4 text-slate-400 hover:text-slate-300 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </>
        )}

        {/* Footer */}
        <Footer companyName={companyName} />
      </div>

      {/* Notification Toast */}
      <NotificationToast autoHide={15000} autoRefresh={false} />

      {/* Overview Zone Selector Modal */}
      {renderOverviewZoneSelectorModal()}

      {/* Power Meter Selector Modal */}
      {renderPowerMeterSelectorModal()}

      {/* HVAC Meter Selector Modal */}
      {renderHvacMeterSelectorModal()}

      {/* Lighting Meter Selector Modal */}
      {renderLightingMeterSelectorModal()}

      {/* TH Sensor Selector Modal */}
      {renderThSensorSelectorModal()}

      {/* Three-Phase Meter Selector Modal */}
      {renderThreePhaseMeterSelectorModal()}

      {/* Switch Selector Modal */}
      {renderSwitchSelectorModal()}

      {/* Light Selector Modal */}
      {renderLightSelectorModal()}

      {/* Presence Sensor Selector Modal */}
      {renderPresenceSensorSelectorModal()}

      {/* Lux Sensor Selector Modal */}
      {renderLuxSensorSelectorModal()}

      {/* Blind/Curtain Selector Modal */}
      {renderBlindCurtainSelectorModal()}

      {/* Baseline Power Modal */}
      {renderBaselinePowerModal()}

      {/* Device Control Modal (for all device types including Light) */}
      {renderDeviceControlModal()}

      {/* HVAC Selector Modal */}
      {renderHvacSelectorModal()}

      {/* Exclude Ranking Devices Modal */}
      {renderExcludeRankingModal()}

      {/* Trait Control Popover */}
      {renderTraitControlPopover()}

      {/* Create Device Group Modal */}
      <CreateDeviceGroupModal
        isOpen={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        onCreated={async () => {
          // Reload groups and add new group to zone's device list
          const groups = await userPreferencesApi.getDeviceControlGroups();
          setDeviceControlGroups(groups);
          
          // Find the newly created group (the one not in zone.devices yet)
          const currentZone = deviceZones.find(z => z.id === selectedZoneFilter);
          if (currentZone) {
            const existingGroupIds = currentZone.devices.filter(id => id.startsWith('group:'));
            const newGroup = groups.find(g => 
              g.zoneId === selectedZoneFilter && 
              !existingGroupIds.includes(`group:${g.id}`)
            );
            
            if (newGroup) {
              // Add the new group ID to the zone's devices list
              const updatedDevices = [`group:${newGroup.id}`, ...currentZone.devices];
              const updatedZones = deviceZones.map(z => 
                z.id === selectedZoneFilter ? { ...z, devices: updatedDevices } : z
              );
              setDeviceZones(updatedZones);
              
              // Save to Firestore
              userPreferencesApi.saveVirtualZones('intelliSpace', updatedZones)
                .catch(err => logger.error('Failed to save zone with new group:', err));
              
              // Dispatch event for real-time sync
              window.dispatchEvent(new CustomEvent('zones-order-changed', { detail: { zones: updatedZones } }));
            }
          }
        }}
        zones={deviceZones}
        devices={devices.map(d => ({
          deviceId: d.deviceId,
          baseSN: d.baseSN,
          deviceType: d.deviceType,
          nickname: d.nickname,
          deviceName: d.deviceName,
          isOn: d.isOn,
          online: d.status !== 'offline'
        }))}
        existingGroups={deviceControlGroups}
        preSelectedZoneId={selectedZoneFilter !== 'all' ? selectedZoneFilter : undefined}
      />

      {/* Device Group Control Modal */}
      <DeviceGroupModal
        isOpen={selectedControlGroup !== null}
        group={selectedControlGroup}
        deviceStatuses={(() => {
          const statuses: Record<string, any> = {};
          devices.forEach(d => {
            statuses[d.deviceId] = {
              deviceId: d.deviceId,
              displayName: d.nickname || d.deviceName || d.deviceId,
              isOn: d.isOn,
              online: d.status !== 'offline',
              brightness: d.brightness,
              colorTemp: d.colorTemp,
              temperature: d.setTemperature,
              openPercent: d.openPercent
            };
            if (d.baseSN && d.baseSN !== d.deviceId) {
              statuses[d.baseSN] = statuses[d.deviceId];
            }
          });
          return statuses;
        })()}
        onClose={() => setSelectedControlGroup(null)}
        onToggleGroup={handleGroupToggle}
        onSetBrightness={handleGroupBrightness}
        onSetColorTemp={handleGroupColorTemp}
        onSetTemperature={handleGroupTemperature}
        onSetOpenPercent={handleGroupOpenPercent}
        onSetFanSpeed={handleGroupFanSpeed}
        onGroupUpdated={reloadDeviceControlGroups}
        onGroupDeleted={async () => {
          // Reload groups and remove deleted group from zone's device list
          const groups = await userPreferencesApi.getDeviceControlGroups();
          setDeviceControlGroups(groups);
          
          // Clean up zone.devices - remove any group:xxx that no longer exists
          const validGroupIds = new Set(groups.map(g => `group:${g.id}`));
          const updatedZones = deviceZones.map(zone => ({
            ...zone,
            devices: zone.devices.filter(id => 
              !id.startsWith('group:') || validGroupIds.has(id)
            )
          }));
          
          // Only save if something changed
          const hasChanges = deviceZones.some((zone, i) => 
            zone.devices.length !== updatedZones[i].devices.length
          );
          
          if (hasChanges) {
            setDeviceZones(updatedZones);
            userPreferencesApi.saveVirtualZones('intelliSpace', updatedZones)
              .catch(err => logger.error('Failed to clean up zone after group deletion:', err));
            window.dispatchEvent(new CustomEvent('zones-order-changed', { detail: { zones: updatedZones } }));
          }
        }}
      />
    </div>
  );
};

export default IntelliSpaceDashboard;

